import { buildSystemDesignFromLayout, layoutPreviewConfig } from "./config/layoutPreview";
import { hasSavedRoofLayout, roofLayoutSource } from "../utils/roofInitialGrid";
import { physicalLayoutDesignKey, physicalLayoutCount, layoutDesignChanged } from "../utils/physicalLayoutDesign";
import { syncRoofLayoutsAfterDesign } from "./config/roofLayoutSync";
import { mergeElectricalSaveSnapshot, electricalSaveConflict, electricalSaveError } from "./config/electricalSaveSnapshot";
import { applyGroundPlanDesign } from "./config/groundPlanDesign";
import { mergePlanDesign, structuredPlanDesign } from "./config/designFromPlan";
import { roofWorkspaceFrames, roofWorkspacePlane } from "../utils/roofWorkspace";
import { getReportLanguage } from "../utils/reportLanguage";
import { isRoofInstallation, withInstallationLayout } from "./config/autoDesignLayout";
import { LEGACY_CAPACITY_RATIOS, tolerancePercent, toleranceRatio, validCapacityRatios } from "./config/autoDesignTolerance";
import { runWithInverterFallback, inverterReplacementMessage } from "./config/autoDesignInverter";
import { canSaveSimpleAutoLayout, saveSimpleAutoLayout, preserveAutoDesignLayout } from "./config/simpleAutoLayout";
import PvSizingEntryLink from "./pv/PvSizingEntryLink";
import { catalogRatedPower, sortCatalogOptionsByPower } from "./pv/catalogOptionOrder";
import CatalogVisibilityFields from "./catalog/CatalogVisibilityFields";
// src/components/ConfigForm.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo, useId } from "react";
import { AccessibleSelect as Select, AccessibleSelectTextField } from "./form/AccessibleSelectFields";
import {
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  FormHelperText,
  Button,
  Box,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Link,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  Dialog,
  Alert,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  Snackbar,
  Stack,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useNavigate } from "react-router-dom";
import { QUICK_STUDY_PATHS, quickStep, withoutQuick } from "./quickStudyRun";
import { DEFAULT_CONFIG } from "./config/defaultConfig";
import { horizonShadingEnabled, withHorizonShading } from "./config/horizonShading";
import HorizonShadingControl from "./pv/HorizonShadingControl";
import { RANKING_DEFAULTS } from "./config/autoDesignRanking";
import {
  capacityFromDesign,
  capacityNeedsUpdate,
  formatCapacityKw,
} from "./config/installedCapacity";
import {
  capacityIsDerived,
  resolveTargetKw,
  storedTargetKw,
  targetSource,
} from "./config/autoDesignTarget";
import {
  AC_INSTALLATION_TYPE_OPTIONS,
  AC_WIRING_MATERIAL_OPTIONS,
  DC_WIRING_MATERIAL_OPTIONS,
  normalizeProjectPvLosses,
} from "./config/pvLosses";
import { useEngine } from "../context/EngineContext";
import Autocomplete from "@mui/material/Autocomplete";
import DesignSystemDialog from "../components/DesignSystemDialog"; // ⬅️ adjust path
import PhysicalLayoutDialog from "../components/PhysicalLayoutDialog";
import PvTmyEstimate from "../components/PvTmyEstimate";
import PvGenerationTabs from "./PvGenerationTabs";
import PvLoadingRegion from "./pv/PvLoadingRegion";
import PvUncertaintyPanel from "../components/PvUncertaintyPanel";
import SelectedWeatherSummary from "./weather/SelectedWeatherSummary";
import {
  buildHeaders,
  getModuleDetails,
  getInverterDetails,
  getInverterDesign,
  validatePvDesign,
  fetchStringLayout,
  runAutoDesign,
  optimizeOrientation,
  fetchUtcOffset,
  downloadAutoDesignReportExcel,
  listAutoDesignReports,
} from "../services/api";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import LabeledSwitchWithHint from "../components/LabeledSwitchWithHint";
import { createModule, getCatalogOrganizations, getCatalogParameterSets, fetchProjectHorizon } from "../services/api";
import { createInverter } from "../services/api";
import { deleteModule, deleteInverter } from "../services/api";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import InputAdornment from "@mui/material/InputAdornment";
import { useAuth } from "../context/AuthContext";
import { useHistory } from "../context/HistoryContext";
import { useFile } from "../context/FileContext";
import MaybeTooltip from "../components/MaybeTooltip";
import MonthlyValueGrid from "./MonthlyValueGrid";
import OrientationProfileChart from "./pv/OrientationProfileChart";
import { sliceOrientationGrid } from "./pv/orientationInterpolation";
import { toFiniteNumber } from "../utils/formValue";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ModulePerformanceDialog from "../components/ModulePerformanceDialog";
import { useCurrentUserAccess } from "../hooks/useCurrentUserAccess";
import { formatApiError } from "../utils/apiErrors";
import SetupPageHeader from "../components/SetupPageHeader";
import SldPreviewDialog from "../components/sld/SldPreviewDialog";
import PvOrientationDiagram from "../components/pv/PvOrientationDiagram";
import PvAdvancedModeling from "../components/pv/PvAdvancedModeling";
import InverterCurveFitDialog from "../components/pv/InverterCurveFitDialog";
import PvwattsEuroControls from "./pv/PvwattsEuroControls";
import { pvwattsFromEuro } from "./pv/pvwattsEuro";
import EquipmentPercentageField from "./pv/EquipmentPercentageField";
import { currentSandiaFit, curveCharacteristicsForForm, sandiaEfficiencies } from "./pv/inverterForm";
import PanModuleImport from "./pv/PanModuleImport";
import OndInverterImport from "./pv/OndInverterImport";
import OndConnectionDialog from "./pv/OndConnectionDialog";
import { ondConnectionComplete } from "./pv/OndConnectionSettings";
import { sizingVoltages } from "../utils/sizingVoltages";
import EquipmentSpecTabs from "../components/pv/EquipmentSpecTabs";
import InverterMpptSummary from "./pv/InverterMpptSummary";
import DegradationCurveDialog from "../components/pv/DegradationCurveDialog";
import DetailedShadingControls from "../components/pv/DetailedShadingControls";
import { describe as describeDegradation } from "../utils/degradationCurve";
import ModuleParameterFields, { missingForSet } from "../components/pv/ModuleParameterFields";
import { mountingPlaneFor, describeMountingPlane } from "../utils/mounting";
import { OrientationPanels, OrientationStatus } from "./pv/OrientationsSection";
import {
  PRIMARY_ID,
  distributeDesignAcrossOrientations,
  orientationShares,
  orientationsFromConfig,
  orientationSummary,
  layoutParcel,
  mpptAssignments,
  sharesTotal,
  systemDesignForOrientation,
  planesForLayoutSheet,
  promoteRoofFace,
  demoteRoofFace,
  removeOrientation,
  updateOrientation,
  setFlushRoofFace,
} from "./config/orientations";
import LayoutOrientationPicker from "./pv/LayoutOrientationPicker";
import { orientationReportLayouts, projectSceneReadiness, applySceneFrames, applySceneLayouts, saveOrientationShading, sceneShadingKey } from "./config/orientationEvidence";
import { orientationOptimizationKey, restoredOrientationResult, orientationGcr } from "./config/orientationOptimization";
import { isSceneCalculationCancelled, prepareSceneInBackground } from "../services/sceneCalculation";
import { detailedSceneInputs } from "../utils/detailedShading";
import { scenePreparationIssue, scenePreparationMessage } from "../utils/scenePreparationIssue";
import { prepareLayoutShading } from "../utils/prepareLayoutShading";
import { sceneShadingPending } from "./config/sceneSimulationIssues";
import { calculateSavedSceneShading } from "../utils/calculateSavedSceneShading";
import { translateLegacyText } from "../i18n/legacyTranslation";
import AutoDesignShares from "./pv/AutoDesignShares";
import { autoDesignBlockers, autoDesignInputName } from "./pv/autoDesignBlockers";
import { useIsGreek, useTranslateText } from "../context/LanguageContext";
import { describeAutoDesignFailure } from "./config/autoDesignFailure";
import { autoDesignCandidateFilters } from "./config/autoDesignCandidateFilters";

// Utility to update nested paths like "loc_info.lat"
function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

function normalizeChars(raw) {
  if (!raw) return {};
  if (!Array.isArray(raw)) return raw; // already an object

  const out = {};
  for (const row of raw) {
    const k = row.CharacteristicName || row.characteristic_name;
    const v = row.CharacteristicValue || row.characteristic_value;
    if (k != null) out[k] = v;
  }
  return out;
}

// A typed value held inside [min, max]; an empty field stays empty (the
// engine then uses its default) and a half-typed one is left alone.
function clampedInput(raw, min, max) {
  const text = String(raw ?? "");
  if (text.trim() === "" || text.trim() === "-") return text;
  const value = Number(text.replace(",", "."));
  if (!Number.isFinite(value)) return text;
  if (min != null && value < min) return min;
  if (max != null && value > max) return max;
  return text;
}

function normalizeTransformerDets(config) {
  const dets = config?.transformerDets || {};
  const rawAnnualDegFactor = config?.annualDegFactor;
  const annualDegFactor =
    rawAnnualDegFactor != null && typeof rawAnnualDegFactor === "object"
      ? rawAnnualDegFactor.value ??
        rawAnnualDegFactor.percent ??
        rawAnnualDegFactor.pct ??
        rawAnnualDegFactor.amount ??
        ""
      : rawAnnualDegFactor;
  return {
    ...config,
    annualDegFactor: annualDegFactor ?? DEFAULT_CONFIG.annualDegFactor,
    transformerDets: {
      // Spread FIRST so the POI-chain fields (topology, chainLossMode,
      // powerFactor, chainSegments, battery, ...) survive normalization —
      // rebuilding only the three legacy keys silently erased every new
      // field on each render and made the chain panel un-persistable.
      ...dets,
      rated: dets.rated ?? dets.ratedPower ?? "",
      noLoadLoss: dets.noLoadLoss ?? dets.noLoad ?? "",
      loadLoss: dets.loadLoss ?? dets.load ?? "",
    },
  };
}

function normalizeProjectConfig(config) {
  return normalizeProjectPvLosses(normalizeTransformerDets(config));
}

// What Optimize actually does. Stated because the result is a recommendation
// the user is expected to accept or reject, and that judgement needs to know
// what was and was not considered.
// The System & Orientation choices, each with the sentence that says what it
// means. Shown under the option in the open menu -- where the choice is made --
// while the closed field shows the label alone (renderValue). One table per
// field so the label in the menu and the label in the field cannot drift.
const MOUNT_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed tilt",
    hint: "The modules stay at one angle all year. The usual choice." },
  { value: "single_axis", label: "Single axis",
    hint: "A tracker turns the rows from east to west through the day. Tilt and azimuth are then set by the tracker, hour by hour." },
  { value: "dual_axis", label: "Dual axis",
    hint: "Follows the sun in both direction and height. The most energy per module, and the most mechanics to maintain." },
];
const INSTALLATION_OPTIONS = [
  { value: "ground", label: "Ground-mounted",
    hint: "A park on open ground. Air reaches both sides of the modules, so they run coolest (U ≈ 29 W/m²K)." },
  { value: "roof_frame", label: "Roof — on a frame / ballasted",
    hint: "On a roof, on frames with their own tilt. There is a gap underneath but less airflow (U ≈ 20 W/m²K). The panel tilt is set separately from the roof's." },
  { value: "roof_flush", label: "Roof — flush to the surface",
    hint: "The modules lie parallel to a pitched roof, so the roof's pitch and facing become the panel tilt and azimuth. The hottest case (U ≈ 15 W/m²K)." },
];
const TILT_REFERENCE_OPTIONS = [
  { value: "horizontal", label: "The horizontal (standard convention)",
    hint: "The tilt is the angle to the horizontal, whatever the slope of the land; the posts are cut to suit." },
  { value: "ground", label: "The ground — racking follows the terrain",
    hint: "Equal-length posts that follow the slope. The real angle is the tilt plus or minus the terrain slope in the facing direction." },
];
const ORIENTATION_OBJECTIVE_OPTIONS = [
  { value: "year", label: "Yearly yield",
    hint: "The most energy over the whole year. The usual choice." },
  { value: "summer", label: "Summer (Apr–Sep)",
    hint: "The most energy from April to September. Gives a shallower tilt." },
  { value: "winter", label: "Winter (Oct–Mar)",
    hint: "The most energy from October to March, e.g. for self-consumption with a winter load. Gives a steeper tilt." },
];
const optionLabel = (options, value) =>
  options.find((o) => o.value === value)?.label ?? value;
const optionMenuItem = (o) => (
  <MenuItem key={o.value} value={o.value} sx={{ alignItems: "flex-start", py: 0.9 }}>
    <Box sx={{ maxWidth: 440, whiteSpace: "normal" }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.label}</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.35 }}>
        {o.hint}
      </Typography>
    </Box>
  </MenuItem>
);

const ORIENTATION_METHODOLOGY = (
  <span style={{ display: "block", lineHeight: 1.5 }}>
    <b>How the optimum is found</b>
    <br />
    A full year of hourly weather for this location is transposed onto every
    candidate tilt and azimuth, cell temperature is computed for each hour, and
    the pair with the highest annual DC yield per kW wins. Tilt is searched in
    1° steps, azimuth in 2° steps around the equator-facing direction.
    <br />
    <br />
    <b>Uses your current settings</b>
    <br />
    Coordinates, surface type (albedo), sky model, cell-temperature model,
    mounting configuration and terrain profile — as they are on screen now,
    including changes you have not saved. Row spacing is used only once a
    physical layout has been saved; before that the sweep runs free-standing and
    may suggest a tilt about two degrees steeper than the final layout prefers.
    <br />
    <br />
    <b>Not considered</b>
    <br />
    Module and inverter choice, DC/AC ratio and clipping, string layout, soiling
    and other loss percentages, and electricity prices. It maximises energy, not
    revenue — a battery or a price-shaped tariff may favour a different azimuth.
    <br />
    <br />
    The result is written into the Tilt and Azimuth fields, which you can edit.
  </span>
);

const ORIENTATION_METHODOLOGY_EL = (
  <span style={{ display: "block", lineHeight: 1.5 }}>
    <b>Πώς προκύπτει το βέλτιστο</b>
    <br />
    Για κάθε υποψήφια κλίση και αζιμούθιο υπολογίζεται η προσπίπτουσα ακτινοβολία
    και η θερμοκρασία κυψέλης ανά ώρα, με μετεωρολογικά δεδομένα ενός πλήρους έτους.
    Επιλέγεται το ζεύγος με τη μεγαλύτερη ετήσια παραγωγή DC ανά kW.
    Η κλίση ελέγχεται ανά 1° και το αζιμούθιο ανά 2° γύρω από την κατεύθυνση του ισημερινού.
    <br /><br />
    <b>Χρησιμοποιούνται οι τρέχουσες ρυθμίσεις</b>
    <br />
    Συντεταγμένες, τύπος επιφάνειας (λευκαύγεια), μοντέλο ουρανού και θερμοκρασίας
    κυψέλης, τρόπος στήριξης και ανάγλυφο, όπως εμφανίζονται τώρα στην οθόνη,
    ακόμη και αν δεν έχουν αποθηκευτεί. Η απόσταση σειρών λαμβάνεται υπόψη μόνο
    αν έχει αποθηκευτεί φυσική διάταξη. Διαφορετικά η προτεινόμενη κλίση μπορεί
    να είναι περίπου δύο μοίρες μεγαλύτερη από αυτήν που ευνοεί η τελική διάταξη.
    <br /><br />
    <b>Δεν λαμβάνονται υπόψη</b>
    <br />
    Η επιλογή πλαισίου και αντιστροφέα, ο λόγος DC/AC και η περικοπή ισχύος,
    τα strings, η ρύπανση και οι λοιπές ποσοστιαίες απώλειες, καθώς και οι τιμές
    ηλεκτρικής ενέργειας. Βελτιστοποιείται η ενέργεια, όχι τα έσοδα· η μπαταρία
    ή ένα τιμολόγιο με διαφορετικές ωριαίες τιμές μπορεί να ευνοεί άλλο αζιμούθιο.
    <br /><br />
    Το αποτέλεσμα συμπληρώνει τα πεδία κλίσης και αζιμουθίου και μπορείτε να το επεξεργαστείτε.
  </span>
);

const PV_LOSS_TOOLTIPS = {
  "pvLosses.dc.soiling": "Percent DC loss from dirt, dust, and surface fouling on modules.",
  "pvLosses.dc.shading": "Percent DC loss from shading the model cannot see: nearby objects such as poles, trees, buildings and chimneys. Row-to-row shading is now computed hour by hour from your layout — including the bypass-diode penalty — under Advanced modeling, as is terrain. Counting any of those here would double them. Leave at 0 for an open site with nothing overshadowing it.",
  "pvLosses.dc.snow": "Percent DC loss due to snow coverage on modules.",
  "pvLosses.dc.mismatch": "Percent DC loss from electrical mismatch between modules/strings.",
  "pvLosses.dc.connections": "Percent DC loss from connector and contact resistance.",
  "pvLosses.dc.lid": "Percent DC loss from light-induced degradation.",
  "pvLosses.dc.nameplate_rating": "Percent DC loss due to module nameplate tolerance / rating deviation.",
  "pvLosses.dc.age": "One-time loss already accumulated by an existing installation before the modeled first year. Leave at 0 for a new system. A module degradation curve supersedes this field.",
  "pvLosses.dc.availability": "Percent DC loss due to PV system unavailability / downtime.",
  "pvLosses.dc.voltageDropPercent": "Target allowable DC voltage-drop percentage used for DC cable sizing.",
  "pvLosses.dc.wiringMode": "Select how DC wiring losses are modeled: fixed percentage or branch-by-branch dynamic calculation.",
  "pvLosses.dc.wiringStaticPercent": "Fixed DC wiring-loss percentage applied when static mode is selected.",
  "pvLosses.dc.wiringDynamic[INV]": "DC wiring settings for this inverter and its MPPT branches.",
  "pvLosses.dc.wiringDynamic[INV].material": "Conductor material used for this inverter’s DC wiring branches.",
  "pvLosses.dc.wiringDynamic[INV].capPercent": "Maximum DC wiring loss allowed per branch as a percentage of branch DC power.",
  "pvLosses.dc.wiringDynamic[INV].autoSize": "If enabled, DC cable sections are calculated automatically from voltage-drop rules.",
  "pvLosses.dc.wiringDynamic[INV].MPPTx.panelLinkLengthM": "Cable length between modules within the string / MPPT branch.",
  "pvLosses.dc.wiringDynamic[INV].MPPTx.stringHomerunLengthM": "DC homerun cable length from the string/MPPT branch to the inverter.",
  "pvLosses.dc.wiringDynamic[INV].MPPTx.panelCableSectionMm2": "Manual panel-link cable cross-section in mm², used only when autosizing is disabled.",
  "pvLosses.dc.wiringDynamic[INV].MPPTx.homerunCableSectionMm2": "Manual homerun cable cross-section in mm², used only when autosizing is disabled.",
  "pvLosses.ac.voltageDropPercent": "Target allowable AC voltage-drop percentage used for AC cable sizing.",
  "pvLosses.ac.wiringMode": "Select how AC wiring losses are modeled: fixed percentage or dynamic branch/main-cable calculation.",
  "pvLosses.ac.wiringStaticPercent": "Fixed AC wiring-loss percentage applied when static mode is selected.",
  "pvLosses.ac.wiringDynamic[INV]": "AC branch-cable settings for this inverter output.",
  "pvLosses.ac.wiringDynamic[INV].lengthM": "AC cable length from this inverter to the collection point / main AC connection.",
  "pvLosses.ac.wiringDynamic[INV].material": "Conductor material used for this inverter’s AC cable.",
  "pvLosses.ac.wiringDynamic[INV].powerFactor": "Assumed AC power factor used in current and cable-loss calculations.",
  "pvLosses.ac.wiringDynamic[INV].installationType": "Installation condition used for AC cable ampacity derating.",
  "pvLosses.ac.wiringDynamic[INV].conductorTemperatureC": "Assumed conductor operating temperature used to adjust cable resistance.",
  "pvLosses.ac.wiringDynamic[INV].capPercent": "Maximum AC wiring loss allowed for this inverter branch as a percentage of branch AC power.",
  "pvLosses.ac.wiringDynamic[INV].autoSize": "If enabled, AC cable section is calculated automatically from voltage-drop and ampacity rules.",
  "pvLosses.ac.wiringDynamic[INV].parallelRuns": "Number of parallel AC cable runs used per phase for this inverter branch.",
  "pvLosses.ac.wiringDynamic[INV].cableSectionMm2": "Manual AC cable cross-section in mm², used only when autosizing is disabled.",
  "pvLosses.ac.wiringDynamic.main.enabled": "Enable modeling of the plant-level main AC cable after inverter branches are combined.",
  "pvLosses.ac.wiringDynamic.main.lengthM": "Main AC cable length from the collection point toward the transformer/export point.",
  "pvLosses.ac.wiringDynamic.main.material": "Conductor material used for the main AC cable.",
  "pvLosses.ac.wiringDynamic.main.powerFactor": "Assumed power factor used for main AC cable current/loss calculation.",
  "pvLosses.ac.wiringDynamic.main.installationType": "Installation condition used for main AC cable ampacity derating.",
  "pvLosses.ac.wiringDynamic.main.conductorTemperatureC": "Assumed conductor operating temperature for main AC cable resistance calculation.",
  "pvLosses.ac.wiringDynamic.main.capPercent": "Maximum main-cable loss allowed as a percentage of AC power flowing through it.",
  "pvLosses.ac.wiringDynamic.main.autoSize": "If enabled, main AC cable section is calculated automatically.",
  "pvLosses.ac.wiringDynamic.main.parallelRuns": "Number of parallel cable runs used per phase for the main AC cable.",
  "pvLosses.ac.wiringDynamic.main.cableSectionMm2": "Manual main AC cable cross-section in mm², used only when autosizing is disabled.",
  "material.Cu": "Copper conductor.",
  "material.Al": "Aluminum conductor.",
  "installationType.air": "Cable installed in open air.",
  "installationType.buried": "Cable installed directly in ground.",
  "installationType.conduit": "Cable installed in duct / conduit.",
};


function hasPvSystemDesign(config) {
  const systemDesign = config?.systemDesign;
  return Boolean(
    systemDesign &&
      typeof systemDesign === "object" &&
      Object.keys(systemDesign).length
  );
}

const baseLabelSx = {
  display: "flex",
  gap: 0.5,
  mb: 0.5,
  color: "primary.main",
  fontWeight: 600,
  letterSpacing: "0.02em",
  // Keep single-line labels close to their fields. Longer labels can grow
  // naturally instead of reserving a blank second line for every control.
  alignItems: "flex-start",
  minHeight: 24,
};

function PvLossFieldLabel({ label, tooltip, htmlFor, id }) {
  return (
    <Box sx={baseLabelSx}>
      <Typography
        variant="body2"
        component={htmlFor ? "label" : "span"}
        htmlFor={htmlFor}
        id={id}
        sx={{ fontWeight: 600 }}
      >
        {label}
      </Typography>
      {tooltip ? (
        <MaybeTooltip title={tooltip} arrow>
          <span style={{ display: "inline-flex" }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7, mt: "3px" }} />
          </span>
        </MaybeTooltip>
      ) : null}
    </Box>
  );
}

function PvLossNumberField({
  label,
  tooltip,
  value,
  onChange,
  disabled = false,
  helperText,
  // A loss that will make the engine refuse the run has to look different from
  // one that is merely unusual.
  error = false,
  min,
  step = "any",
  adornment,
  size = "small",
  // Compact by default. These hold a percentage — one to four characters — and
  // under MUI v7 the surrounding <Grid item xs/md> no longer constrains them
  // (that sizing API moved to GridLegacy), so without an explicit width each
  // one stretched to roughly 250px and four fields filled a whole row.
  //
  // 165 rather than tighter: the longest label here is "String Homerun Length"
  // at 21 characters, and anything narrower wraps it to two lines, leaving the
  // row of fields at uneven heights.
  width = 165,
}) {
  const inputId = useId();
  return (
    <Box sx={{ width: { xs: "100%", sm: width } }}>
      <PvLossFieldLabel label={label} tooltip={tooltip} htmlFor={inputId} />
      <TextField
        id={inputId}
        fullWidth
        size={size}
        type="number"
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        error={error}
        helperText={helperText}
        inputProps={{ min, step }}
        InputProps={
          adornment
            ? {
                endAdornment: (
                  <InputAdornment position="end">{adornment}</InputAdornment>
                ),
              }
            : undefined
        }
      />
    </Box>
  );
}

function PvLossSelectField({
  label,
  tooltip,
  value,
  onChange,
  disabled = false,
  options,
  helperText,
  size = "small",
}) {
  const selectId = useId();
  const labelId = `${selectId}-label`;
  return (
    <Box sx={{ width: { xs: "100%", sm: 180 } }}>
      <PvLossFieldLabel label={label} tooltip={tooltip} id={labelId} />
      <FormControl fullWidth size={size} disabled={disabled}>
        <Select id={selectId} labelId={labelId} value={value ?? ""} onChange={onChange}>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    </Box>
  );
}

// Catalogue search: every word typed must appear somewhere in the name, in any
// order, ignoring case, accents and the separators catalogue names use
// (spaces, "_", "-", ".", ":", ","). So "10KTL" finds
// "Huawei Technologies Co., Ltd.: SUN2000-10KTL-M1" and "huawei m1 10ktl" does
// too; matching only from the start of the name found nothing unless the
// manufacturer was typed first.
const normalizeCatalogSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s_\-.:,]+/g, " ")
    .trim();

const catalogFilterOptions = (options, { inputValue, getOptionLabel }) => {
  const tokens = normalizeCatalogSearch(inputValue).split(" ").filter(Boolean);
  if (tokens.length === 0) return options;
  return options.filter((option) => {
    const label = normalizeCatalogSearch(getOptionLabel ? getOptionLabel(option) : option);
    return tokens.every((token) => label.includes(token));
  });
};

/**
 * Display groupings for the module Technology filter.
 *
 * The database holds raw pvlib/CEC values (2-a-Si, 3-a-Si, c-Si, CdTe, CIS,
 * EFG mc-Si, GaAs, HIT-Si, mc-Si, Mono-c-Si, Si-Film). Those stay untouched as
 * source data; this maps them to labels an engineer recognises. The backend does
 * not know about these groups — it matches raw values — so a category spanning
 * several values sends all of them.
 *
 * Two decisions worth preserving:
 *
 *   c-Si is its own "unspecified" category and is NOT folded into
 *   Monocrystalline. "c-Si" states only that the cell is crystalline silicon; it
 *   does not say mono or multi, and quietly filing it under Mono would put
 *   modules in a result set the user did not ask for.
 *
 *   HIT-Si displays as Heterojunction (HJT). HIT is Sanyo/Panasonic trademark
 *   terminology; HJT is what the datasheets a user is holding today will say.
 */
// Each group also claims the value the Add-module form writes for the same
// technology, which is a DIFFERENT vocabulary: that form stores keys understood
// by typeConverter in pythia-backend/BESS/PV_output/insert_module.py
// (Mono-c-Si, Multi-c-Si, CIGS, Thin Film, CdTe), while imported catalogue rows
// carry CEC spellings (mc-Si, HIT-Si, 2-a-Si, ...). Without the aliases below, a
// user's own custom multicrystalline module would be filed under "Other /
// Legacy" and never appear when they filtered for Multicrystalline — their own
// module, missing from their own filter.
//
// The two vocabularies are deliberately NOT merged. The Add-module values are
// lookup keys for the pvlib CEC fit (typeConverter); an unrecognised key falls
// through to monoSi silently. Measured, that fallback does not change the fitted
// parameters — the datasheet values constrain the solve — so this is a
// housekeeping concern rather than a numerical one. Renaming would simply create
// two spellings for one technology in the database for no benefit.
const TECHNOLOGY_GROUPS = [
  { label: "Monocrystalline Silicon", values: ["Mono-c-Si"] },
  { label: "Multicrystalline Silicon", values: ["mc-Si", "EFG mc-Si", "Multi-c-Si"] },
  { label: "Crystalline Silicon – Unspecified", values: ["c-Si"] },
  { label: "Heterojunction (HJT)", values: ["HIT-Si"] },
  // "Thin Film" is what the Add-module form stores; the engine fits it with
  // pvlib's "amorphous" celltype, so it belongs with the a-Si rows rather than in
  // Other. Grouping it by how it is actually modelled keeps the filter honest.
  { label: "Amorphous Silicon", values: ["2-a-Si", "3-a-Si", "Thin Film"] },
  { label: "CdTe", values: ["CdTe"] },
  // CIGS is listed although no imported row carries it today: the Add-module form
  // writes exactly "CIGS", and the label promises it.
  { label: "CIS / CIGS", values: ["CIS", "CIGS"] },
  { label: "GaAs", values: ["GaAs"] },
];

// Si-Film is not listed in any group above, so it lands here by the same rule as
// an unrecognised value: "Other / Legacy" is everything the mapping does not
// claim. Naming it in a second list as well would create a value that could be
// claimed twice and appear in two categories.
const TECHNOLOGY_OTHER_LABEL = "Other / Legacy";

/**
 * Turn the raw technology values present in the catalog into grouped options.
 *
 * Only groups with at least one value actually present are offered, so the
 * dropdown never lists a category that would return nothing.
 *
 * Anything the mapping does not recognise falls into "Other / Legacy" rather
 * than being dropped. That is the important behaviour: a future catalog import
 * introducing a new technology string would otherwise make those modules
 * unreachable through this filter, silently and with no error anywhere.
 */
function buildTechnologyOptions(rawValues) {
  const present = (rawValues || []).filter(Boolean);
  const fold = (v) => String(v).trim().toLowerCase();
  const presentFolded = new Set(present.map(fold));

  const options = TECHNOLOGY_GROUPS.map((group) => ({
    label: group.label,
    values: group.values.filter((v) => presentFolded.has(fold(v))),
  })).filter((group) => group.values.length > 0);

  const claimed = new Set(
    TECHNOLOGY_GROUPS.flatMap((g) => g.values).map(fold)
  );
  const otherValues = present.filter((v) => !claimed.has(fold(v)));
  if (otherValues.length) {
    options.push({ label: TECHNOLOGY_OTHER_LABEL, values: otherValues });
  }
  return options;
}

/**
 * Helper text under a catalog dropdown when filters are on.
 *
 * States a count rather than naming the active filters. The previous wording
 * listed them ("power and bifaciality") and silently went stale as soon as vendor
 * and technology were added; a count cannot rot, and it answers what the user is
 * actually asking — did that filter do what I expected.
 *
 * The denominator matters more than it looks: "142 of 3,204" says the filter bit
 * hard, "142 of 145" says it barely did anything, and the bare "142" says
 * neither. It is omitted rather than guessed when the total is not yet known,
 * because "142 of 0" during the first paint would look like a bug.
 */
function catalogFilterSummary(noun, shown, total, isGreek = false) {
  if (isGreek) {
    const singular = noun === "module" ? "ΦΒ πλαίσιο" : "αντιστροφέας";
    const plural = noun === "module" ? "ΦΒ πλαίσια" : "αντιστροφείς";
    if (shown === 0) {
      return total ? `Δεν βρέθηκαν ${plural} με τα ενεργά φίλτρα (${total} στον κατάλογο).`
                   : `Δεν βρέθηκαν ${plural} με τα ενεργά φίλτρα.`;
    }
    if (total == null) {
      return `${shown} ${shown === 1 ? singular : plural} ${shown === 1 ? "αντιστοιχεί" : "αντιστοιχούν"} στα ενεργά φίλτρα.`;
    }
    return `Εμφανίζονται ${shown} από ${total} ${plural}.`;
  }
  const plural = `${noun}s`;
  if (shown === 0) {
    return total ? `No ${plural} match the active filters (${total} in catalog).`
                 : `No ${plural} match the active filters.`;
  }
  const subject = shown === 1 ? noun : plural;
  const verb = shown === 1 ? "matches" : "match";
  if (total == null) return `${shown} ${subject} ${verb} the active filters.`;
  return `Showing ${shown} of ${total} ${plural}.`;
}

function equipmentOptionLabel(name, item, kind, isGreek) {
  const label = String(name || "");
  const watts = catalogRatedPower(item, kind);
  if (watts == null) return label;
  const value = kind === "module" ? watts : watts / 1000;
  // One number format across the UI: dot for decimals, comma for thousands.
  const formatted = value.toLocaleString("en-US", {
    maximumFractionDigits: kind === "module" ? 1 : 2,
  });
  return formatted + " " + (kind === "module" ? "W" : "kW") + " · " + label;
}

function dedupeCatalogOptions(items) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const name = String(item?.name || "").trim();
    if (!name || seen.has(name)) return false;
    seen.add(name);
    return true;
  });
}

function downloadBlobUrl(blobUrl, filename) {
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;          // ✅ forces default name
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ---------- validation helpers ----------
const NUM_RE = /^-?\d*(?:[.,]\d*)?$/;

// Technology choices when ADDING a custom module. Labels are aligned with the
// catalog filter's categories (TECHNOLOGY_GROUPS) so the two dropdowns read the
// same, but the `value` of each is a lookup key for typeConverter in
// pythia-backend/BESS/PV_output/insert_module.py and MUST NOT be changed to the
// CEC catalogue's spellings. An unrecognised key falls back to monocrystalline
// silently, so a renamed value would fit the wrong cell type in pvlib's CEC
// single-diode model and yield plausible but wrong energy numbers.
//
// This list is short because it is limited to what typeConverter can model, not
// by what exists in the world. Adding an option here without a matching
// typeConverter entry would look like new capability and quietly behave as
// monocrystalline — which is why there is no "Other" here, unlike in the filter.
const MODULE_TECHNOLOGY_OPTIONS = [
  { value: "Mono-c-Si", label: "Monocrystalline Silicon" },
  { value: "Multi-c-Si", label: "Multicrystalline Silicon" },
  { value: "c-Si", label: "Crystalline Silicon – Unspecified" },
  { value: "HIT-Si", label: "Heterojunction (HJT)" },
  // Labels are byte-identical to the filter's TECHNOLOGY_GROUPS labels. The
  // stored `value` for amorphous stays "Thin Film" because modules already carry
  // it; only the label changed.
  { value: "Thin Film", label: "Amorphous Silicon" },
  { value: "CdTe", label: "CdTe" },
  { value: "CIGS", label: "CIS / CIGS" },
  { value: "GaAs", label: "GaAs" },
];

function isEmpty(v) {
  return v == null || String(v).trim() === "";
}


function validateField(def, raw) {
  const v = String(raw ?? "").trim();

  if (def.required && isEmpty(v)) return "Required";

  if (def.kind === "number") {
    if (isEmpty(v)) return ""; // optional number can be empty
    const n = Number(v.replace(",", "."));
    if (!Number.isFinite(n)) return "Enter a valid number";
    if (def.min != null && n < def.min) return `Min ${def.min}`;
    if (def.max != null && n > def.max) return `Max ${def.max}`;
  }

  if (def.kind === "text") {
    if (def.required && v.length < (def.minLen ?? 1)) return "Too short";
    if (def.maxLen != null && v.length > def.maxLen) return "Too long";
  }

  return "";
}

function validateAll(fields, values) {
  const next = {};
  for (const f of fields) {
    const msg = validateField(f, values[f.key]);
    if (msg) next[f.key] = msg;
  }
  return next;
}

function formatCatalogUploadSuccessMessage(kind, response, fallbackName, isGreek = false) {
  const action = String(response?.action || "saved").trim().toLowerCase();
  const itemName = String(response?.name || fallbackName || "").trim();
  if (isGreek) {
    const subject = kind === "Module" ? "Το ΦΒ πλαίσιο" : "Ο αντιστροφέας";
    const verb = action === "created" ? "δημιουργήθηκε" : action === "updated" ? "ενημερώθηκε" : "αποθηκεύτηκε";
    return `${subject}${itemName ? ` «${itemName}»` : ""} ${verb} επιτυχώς.`;
  }
  return itemName
    ? `${kind} "${itemName}" ${action} successfully.`
    : `${kind} ${action} successfully.`;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}


// ---------- minimal required sets ----------
const MODULE_FIELDS = [
  { key: "module_name", label: "Module name", kind: "text", required: true, minLen: 2, maxLen: 160, helper: "Unique name" },
  { key: "Vendor", label: "Manufacturer", kind: "text", required: false, maxLen: 80, helper: "" },
  { key: "Price", label: "Price", kind: "number", required: true, min: 0, unit: "€" },

  { key: "STC", label: "Nominal Power", kind: "number", required: true, min: 0, unit: "W", helper: "Nominal module power under STC (1000 W/m², 25°C)" },
  { key: "V_mp_ref", label: "Vmp0", kind: "number", required: true, min: 0, unit: "V" , helper: "Voltage at maximum power point under STC (1000 W/m², 25°C)"},
  { key: "I_mp_ref", label: "Imp0", kind: "number", required: true, min: 0, unit: "A" , helper: "Current at maximum power point under STC"},
  { key: "V_oc_ref", label: "Voc0", kind: "number", required: true, min: 0, unit: "V" , helper: "Open-circuit voltage under STC"},
  { key: "I_sc_ref", label: "Isc0", kind: "number", required: true, min: 0, unit: "A" , helper: "Short-circuit current under STC"},
  { key: "alpha_sc", label: "Isc Temp. Coefficient", kind: "number", required: true, unit: "%/°C", helper: "Temperature coefficient of short-circuit current", md: 12 },
  { key: "beta_oc", label: "Voc Temp. Coefficient", kind: "number", required: true, unit: "%/°C", helper: "Temperature coefficient of open-circuit voltage", md: 12 },
  { key: "gamma_r", label: "Power Temp. Coefficient", kind: "number", required: true, unit: "%/°C", helper: "Temperature coefficient of power", md: 12 },
  { key: "T_NOCT", label: "NOCT", kind: "number", required: true, min: 0, unit: "°C", helper: "Nominal operating cell temperature" },

  {
    key: "Technology",
    label: "Technology",
    kind: "select",
    required: false,
    helper: "",
    options: MODULE_TECHNOLOGY_OPTIONS,
  },
  { key: "N_s", label: "Cells in series", kind: "number", required: true, min: 1 },
  // Read by the string-length check (Voc at the coldest cell must stay under
  // the LOWER of this and the inverter's Vdcmax) and printed on the single-line
  // diagram. Optional: without it the inverter's limit is the only ceiling.
  { key: "V_max_system", label: "Max system voltage", kind: "number", required: false, min: 0, unit: "V", helper: "IEC 61730 maximum system voltage, typically 1000 or 1500 V. Caps the string with the inverter's Vdcmax" },
  { key: "length", label: "Length", kind: "number", required: true, min: 0, unit: "m" },
  { key: "width", label: "Width", kind: "number", required: true, min: 0, unit: "m" },
  { key: "area", label: "Module area", kind: "number", required: false, min: 0, unit: "m²", helper: "Optional (will be inferred from L×W if empty)" },
  {
    key: "Bifacial",
    label: "Bifacial",
    kind: "select",
    required: true,
    helper: "",
    parseAs: "number",
    options: [
      { value: "1", label: "Yes" },
      { value: "0", label: "No" },
    ],
  },
  {
    key: "bifaciality",
    label: "Bifaciality",
    kind: "number",
    required: false,
    min: 0,
    max: 1,
    helper: "",
    showWhen: (values) => String(values?.Bifacial ?? "") === "1",
  },
];

/**
 * How the custom-inverter form is grouped.
 *
 * Nineteen fields in one column made the reader re-sort them mentally: Paco is
 * the AC rating, Pdco the DC reference, and they arrived adjacent with nothing
 * to say which side of the inverter each belongs to. These are the groups a
 * datasheet already uses, in the order a datasheet reads.
 */
const INVERTER_FIELD_GROUPS = [
  { key: "identity", label: "Identity", note: "" },
  {
    key: "dc",
    label: "Input side — DC PV field",
    note: "What the array may present: the MPPT window, the voltage and current ceilings, and how many trackers share them.",
  },
  {
    key: "ac",
    label: "Output side — AC grid",
    note: "What the inverter delivers to the network.",
  },
  {
    key: "efficiency",
    label: "Conversion efficiency",
    note: "Headline figures. Efficiency against load at three DC voltages is entered below, and is what the Sandia model is fitted from.",
  },
  {
    key: "physical",
    label: "Physical and commercial",
    note: "Catalogue dimensions and price. Equipment price is used in AutoDesign cost ranking; feasibility investment assumptions are set separately.",
  },
];

const INVERTER_FIELDS = [
  { key: "inverter_name", group: "identity", label: "Inverter name", kind: "text", required: true, minLen: 2, maxLen: 160, helper: "Unique name" },
  { key: "Vendor", group: "identity", label: "Manufacturer", kind: "text", required: false, maxLen: 80, helper: "" },
  { key: "Price", group: "physical", label: "Price", kind: "number", required: false, min: 0, unit: "€" },

  { key: "Paco", group: "ac", label: "Paco (AC power)", kind: "number", required: true, min: 0, unit: "W" , helper: "Nominal AC output power (rated)" },
  { key: "Pdco", group: "dc", label: "Pdco (DC power)", kind: "number", required: true, min: 0, unit: "W" , helper: "Nominal DC input power at which the inverter efficiency model is referenced" },
  { key: "Pdcmax", group: "dc", label: "Pdcmax", kind: "number", required: false, min: 0, unit: "Wp" , helper: "Manufacturer maximum/recommended PV array nameplate power, when specified" },
  { key: "pv_array_power_limit_kind", group: "dc", label: "PV array power limit", labelEl: "Χαρακτήρας ορίου ισχύος ΦΒ", kind: "select", required: true,
    options: [{ value: "required", label: "Mandatory limit", labelEl: "Δεσμευτικό όριο" }, { value: "recommended", label: "Manufacturer recommendation", labelEl: "Σύσταση κατασκευαστή" }],
    helper: "Select recommendation only when stated in the datasheet.", helperEl: "Επιλέξτε σύσταση μόνο αν έτσι αναφέρεται στο datasheet." },
  { key: "Vdco", group: "dc", label: "Vdco", kind: "number", required: true, min: 0, unit: "V" , helper: "Reference DC voltage for the efficiency model" },
  { key: "Vdcmax", group: "dc", label: "Vdcmax", kind: "number", required: true, min: 0, unit: "V" , helper: "Maximum DC voltage allowed at the inverter input" },
  { key: "Mppt_low", group: "dc", label: "MPPT low", kind: "number", required: true, min: 0, unit: "V" , helper: "Lower bound of the MPPT operating window" },
  { key: "Mppt_high", group: "dc", label: "MPPT high", kind: "number", required: true, min: 0, unit: "V" , helper: "Upper bound of the MPPT operating window" },
  { key: "Idcmax", group: "dc", label: "Idcmax", kind: "number", required: true, min: 0, unit: "A" , helper: "Maximum total operating DC input current across the inverter" },

  // Optional, and the design engine prefers it when present: without it the
  // total is split evenly across trackers, which real datasheets do not do.
  { key: "Idcmax_per_mppt", group: "dc", label: "Idcmax per MPPT", kind: "number", required: false, min: 0, unit: "A", helper: "Maximum DC input current of ONE tracker. Leave blank to split Idcmax evenly across the MPPTs" },
  // The same short-circuit rating for every tracker, entered once. On save it
  // is written as the per-MPPT arrays, exactly as if the same figure had been
  // typed into each row of "Separate limits for each MPPT": that is the only
  // form in which the engine checks Isc against the short-circuit rating and
  // Imp against the operating one. A scalar alone is read conservatively, as
  // the lower of the two ratings, and would change nothing.
  { key: "Iscmax_per_mppt", group: "dc", label: "Max short-circuit current per MPPT", kind: "number", required: false, min: 0.001, unit: "A",
    labelEl: "Μέγιστο ρεύμα βραχυκύκλωσης ανά MPPT",
    helper: "Requires Idcmax per MPPT; use Separate limits for each MPPT when the trackers differ. Leave blank if not in the datasheet.",
    helperEl: "Απαιτεί και Idcmax ανά MPPT· αν οι MPPT διαφέρουν, χρησιμοποιήστε τα ξεχωριστά όρια ανά MPPT. Αν δεν αναφέρεται στο datasheet, αφήστε το κενό." },
  { key: "Idcmax_per_input", group: "dc", label: "Maximum current per physical input", kind: "number", required: false, min: 0.001, unit: "A", helper: "Manufacturer operating-current limit per connector: one string per input in this design. The same limit applies to every input, independently of the total MPPT limit. Leave blank if unknown; it is not calculated by dividing the MPPT limit. With explicit per-MPPT ratings, it also limits hourly DC input power." },
  { key: "mppt_isc_stc_multiplier", group: "dc", label: "Isc(STC) design multiplier", kind: "number", required: false, min: 1, helper: "Only when the manufacturer requires it. With per-MPPT short-circuit ratings, compare the greater of hot-cell Isc and this factor × Isc(STC). Leave blank otherwise." },

  { key: "Num MPPTs", group: "dc", label: "Num MPPTs", kind: "number", required: true, min: 1 , helper: "Number of independent MPPT trackers"},
  { key: "Num Inputs per MPPT", group: "dc", label: "Inputs per MPPT", kind: "number", required: true, min: 1 , helper: "How many DC string inputs (connectors) exist per MPPT"},

  { key: "Phases", group: "ac", label: "AC connection", kind: "select", required: true, options: [{ value: "1", label: "Single-phase" }, { value: "3", label: "Three-phase" }] },
  { key: "Vac", group: "ac", label: "Vac", kind: "number", required: true, min: 0, unit: "V" , helper: "Nominal AC voltage of the inverter output" },
  { key: "eff_nom", group: "efficiency", label: "eff_nom", kind: "number", required: true, min: 0, max: 1, helper: "Nominal conversion efficiency (0–1)" },
  { key: "eff_max", group: "efficiency", label: "eff_max", kind: "number", required: true, min: 0, max: 1, helper: "Maximum conversion efficiency (0–1)" },
  // Night tare. Optional on its own, but the Sandia fit cannot run without it,
  // so it is collected here rather than only inside the curve dialog.
  { key: "Pnt", group: "ac", label: "Pnt (night tare)", kind: "number", required: false, min: 0, unit: "W", helper: "Standby/night consumption. Needed for the detailed Sandia model" },
  // No ADRCoefficients field: the engine's AC cascade tests it with a scalar
  // validity check, so a coefficient list can never satisfy it and the inverter
  // would silently run as PVWatts. See the note in pv_core/parameter_sets.py.

  { key: "Length", group: "physical", label: "Length", kind: "number", required: true, min: 0, unit: "m" },
  { key: "Width", group: "physical", label: "Width", kind: "number", required: true, min: 0, unit: "m" },
];


function ModuleDiagram({ details }) {
  if (!details) return null;
  const chars = details.characteristics || {};

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const pstc = toNum(chars.STC || chars.Impo * chars.Vmpo);
  const vmp = toNum(chars.V_mp_ref || chars.Vmpo);
  const imp = toNum(chars.I_mp_ref || chars.Impo);
  const voc = toNum(chars.V_oc_ref || chars.Voco);
  const isc = toNum(chars.I_sc_ref || chars.Isco);
  const ns = toNum(chars.N_s || chars.Cells_in_Series);
  const tNoct = toNum(chars.T_NOCT);
  const alphaScNum = toNum(chars.alpha_sc ?? chars.Aisc);
  const alphaSc = alphaScNum != null ? alphaScNum.toFixed(5) : undefined;
  const betaOc = toNum(chars.beta_oc || chars.Bvoco);
  const width = toNum(chars.Width);
  const length = toNum(chars.Length);
  const areaDb = toNum(chars.Area);
  const area = areaDb || (width && length ? width * length : undefined);
  const technology = chars.Technology || chars.Material || "—";
  // Unit price from the catalogue. Worth showing because autodesign ranks
  // candidate designs partly on it (see BESS/PV_output/system_design.py: the
  // module list is sorted by Price and Total_cost carries a user weight in the
  // design score). It does NOT feed feasibility CAPEX, which is built from the
  // EUR/Wdc figures on the feasibility Economics page.
  const price = toNum(chars.Price);
  const vendor = chars.Vendor || chars.vendor;
  const bifFlag = toNum(chars.Bifacial);
  const isBifacial = bifFlag != null ? bifFlag >= 0.5 : false;
  const bifLabel = isBifacial ? "BIFACIAL" : "MONOFACIAL";

  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
      <Box
        sx={{
          width: 220,
          flexShrink: 0,
          borderRadius: 3,
          boxShadow: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: 1.4,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
          >
            {bifLabel}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.4 }}>
            {details.name}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              P<sub>STC</sub>
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {pstc != null ? `${pstc} W` : "—"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Technology
            </Typography>
            <Typography variant="body2">{technology}</Typography>
          </Box>

          {area && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Area
              </Typography>
              <Typography variant="body2">
                {area.toFixed(2)} m²
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 260 }}>
        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.78rem", letterSpacing: 0.5, color: "text.secondary", pb: 0.5, mb: 1, borderBottom: "1px solid", borderColor: "divider" }}
        >
          Electrical
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              V<sub>mp</sub>
            </Typography>
            <Typography variant="body2">{vmp != null ? `${vmp} V` : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              I<sub>mp</sub>
            </Typography>
            <Typography variant="body2">{imp != null ? `${imp} A` : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              N<sub>s</sub> (cells in series)
            </Typography>
            <Typography variant="body2">{ns != null ? ns : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              V<sub>oc</sub>
            </Typography>
            <Typography variant="body2">{voc != null ? `${voc} V` : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              I<sub>sc</sub>
            </Typography>
            <Typography variant="body2">{isc != null ? `${isc} A` : "—"}</Typography>
          </Grid>
        </Grid>

        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.78rem", letterSpacing: 0.5, color: "text.secondary", pb: 0.5, mb: 1, borderBottom: "1px solid", borderColor: "divider" }}
        >
          Performance
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              NOCT
            </Typography>
            <Typography variant="body2">{tNoct != null ? `${tNoct} °C` : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              α<sub>sc</sub> (I coeff)
            </Typography>
            <Typography variant="body2">{alphaSc != null ? alphaSc : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              β<sub>oc</sub> (V coeff)
            </Typography>
            <Typography variant="body2">{betaOc != null ? betaOc : "—"}</Typography>
          </Grid>
        </Grid>

        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.78rem", letterSpacing: 0.5, color: "text.secondary", pb: 0.5, mb: 1, borderBottom: "1px solid", borderColor: "divider" }}
        >
          Physical
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              Width
            </Typography>
            <Typography variant="body2">{width != null ? `${width} m` : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              Length
            </Typography>
            <Typography variant="body2">{length != null ? `${length} m` : "—"}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              Bifacial?
            </Typography>
            <Typography variant="body2">
              {isBifacial ? "Yes (bifacial)" : "No (monofacial)"}
            </Typography>
          </Grid>
        </Grid>

        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.78rem", letterSpacing: 0.5, color: "text.secondary", pb: 0.5, mb: 1, mt: 2.25, borderBottom: "1px solid", borderColor: "divider" }}
        >
          Commercial
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              Unit price
            </Typography>
            <Typography variant="body2">
              {price != null ? `${price.toLocaleString("en-GB")} € / module` : "—"}
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">
              Manufacturer
            </Typography>
            <Typography variant="body2">{vendor || "—"}</Typography>
          </Grid>
        </Grid>
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", mt: 0.75, lineHeight: 1.4 }}
        >
          Used by auto-design to compare equipment options — and by the
          feasibility CAPEX: the hardware cost is this unit price × the
          installed count (the &quot;hybrid upfront cost&quot; term). The €/Wdc
          fields on the Economics page add installation/EPC on top; they do not
          replace this price. With PV Asset Status &quot;Existing&quot; the
          hardware is excluded from CAPEX but still anchors the % O&amp;M.
        </Typography>
      </Box>
    </Box>
  );
}


// ---------- INVERTER DIAGRAM ----------
// The conversion-model sets come from the backend in English; their Greek
// wording lives here, next to the dialog that shows them.
const INVERTER_SET_TEXT_EL = {
  sandia: ["Sandia (από καμπύλες απόδοσης)",
    "Προσαρμόζεται σε καμπύλες απόδοσης του φυλλαδίου σε τρεις τάσεις DC· αποδίδει την πτώση της απόδοσης σε χαμηλό φορτίο και τη μεταβολή της με την τάση DC."],
  pvwatts: ["PVWatts (τυπική καμπύλη απόδοσης)", "Η απόδοση μεταβάλλεται με το φορτίο βάσει της τυπικής καμπύλης PVWatts. Η ονομαστική απόδοση καθορίζει το επίπεδο της καμπύλης· δεν μοντελοποιείται εξάρτηση από την τάση DC."],
  ond_curve: ["Καμπύλη απόδοσης (αρχείο OND του PVsyst)",
    "Ανεβάστε το αρχείο OND του αντιστροφέα. Η καμπύλη απόδοσης, το κατώφλι ισχύος, το όριο AC, το παράθυρο MPPT και η νυχτερινή κατανάλωση χρησιμοποιούνται όπως ορίζονται, ώστε τα αποτελέσματα να συγκρίνονται γραμμή προς γραμμή με μελέτη PVsyst."],
};
const inverterSetLabel = (spec, greek) => (greek && INVERTER_SET_TEXT_EL[spec?.key]?.[0]) || (spec?.key === "pvwatts" ? "PVWatts (standard efficiency curve)" : spec?.label) || "";
const inverterSetHelp = (spec, greek) => (greek && INVERTER_SET_TEXT_EL[spec?.key]?.[1]) || (spec?.key === "pvwatts" ? "Efficiency varies with load following the standard PVWatts curve. Nominal efficiency sets the curve level; DC-voltage dependence is not modelled." : spec?.help) || "";
const inverterModelChip = (model) => (model === "ond_curve" ? "OND" : String(model || "").toUpperCase());

function InverterDiagram({ details }) {
  const greek = useIsGreek();
  if (!details) return null;
  const hasOnd = Boolean(details.characteristics?.ondModel);

  const chars = normalizeChars(details.characteristics);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const pac = toNum(chars.Paco);
  const pdco = toNum(chars.Pdco);
  // Fitted coefficients carry the full float from the regression; shown raw
  // they read as noise ("360941.1454009212 W"). Nobody sizes to the milliwatt.
  const watts = (v) =>
    v == null ? "—" : `${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })} W`;
  const vac = toNum(chars.Vac);
  const phases = toNum(chars.Phases);
  const vdcmax = toNum(chars.Vdcmax);
  const mpptLow = toNum(chars.Mppt_low);
  const mpptHigh = toNum(chars.Mppt_high);
  const effNom = toNum(chars.eff_nom);
  const effMax = toNum(chars.eff_max);
  const length = toNum(chars.Length);
  const width = toNum(chars.Width);
  // See the note in ModuleDiagram: this price drives auto-design comparison,
  // not feasibility CAPEX.
  const price = toNum(chars.Price);
  const vendor = chars.Vendor || chars.vendor;
  // Which AC model the engine will use for this inverter. system_simulation.py
  // picks 'sandia' as soon as C0/C1/C2 are present and otherwise falls back to
  // PVWatts, i.e. one flat efficiency for every operating point.
  const hasSandia =
    toNum(chars.C0) != null && toNum(chars.C1) != null && toNum(chars.C2) != null;
  const phaseLabel =
    phases === 1 ? "1-phase" : phases === 3 ? "3-phase" : phases != null ? `${phases}-phase` : null;
  const vacLabel =
    phases === 1 ? "Vac (phase voltage)" : phases === 3 ? "Vac (line-line)" : "Vac";
  const vacHelper =
    phases === 1
      ? "Single-phase inverter: Vac is the phase voltage."
      : phases === 3
      ? "Three-phase inverter: Vac is the line-to-line voltage."
      : null;

  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* Left card */}
      <Box
        sx={{
          width: 230,
          flexShrink: 0,
          borderRadius: 3,
          boxShadow: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: 1.4,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            INVERTER
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mt: 0.3, lineHeight: 1.2 }}
          >
            {details.name}
          </Typography>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 1.8,
            display: "flex",
            flexDirection: "column",
            gap: 1.4,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              P<sub>AC,nom</sub>
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {watts(pac)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Max DC input (Pdco)
            </Typography>
            <Typography variant="body2">
              {watts(pdco)}
            </Typography>
          </Box>

          <InverterMpptSummary chars={chars} />
        </Box>
      </Box>

      {/* Right specs */}
      <Box sx={{ flex: 1, minWidth: 260 }}>
        <Grid container spacing={1.5}>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Vdc max
            </Typography>
            <Typography variant="body2">
              {vdcmax != null ? `${vdcmax} V` : "—"}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              MPPT range
            </Typography>
            <Typography variant="body2">
              {mpptLow != null && mpptHigh != null
                ? `${mpptLow} – ${mpptHigh} V`
                : "—"}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              {vacLabel}
            </Typography>
            <Typography variant="body2">
              {vac != null ? `${vac} V` : "—"}
            </Typography>
            {vacHelper && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {vacHelper}
              </Typography>
            )}
          </Grid>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Phases
            </Typography>
            <Typography variant="body2">
              {phaseLabel || "—"}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Euro efficiency
            </Typography>
            <Typography variant="body2">
              {effNom != null ? `${effNom * 100 || effNom} %` : "—"}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Max efficiency
            </Typography>
            <Typography variant="body2">
              {effMax != null ? `${effMax * 100 || effMax} %` : "—"}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Width
            </Typography>
            <Typography variant="body2">
              {width != null ? `${width} m` : "—"}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Height
            </Typography>
            <Typography variant="body2">
              {length != null ? `${length} m` : "—"}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Unit price
            </Typography>
            <Typography variant="body2">
              {price != null ? `${price.toLocaleString("en-GB")} € / unit` : "—"}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Manufacturer
            </Typography>
            <Typography variant="body2">{vendor || "—"}</Typography>
          </Grid>
        </Grid>
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", mt: 0.75, lineHeight: 1.4 }}
        >
          Used by auto-design to compare equipment options — and by the
          feasibility CAPEX: the hardware cost is this unit price × the
          installed count (the &quot;hybrid upfront cost&quot; term). The €/Wdc
          fields on the Economics page add installation/EPC on top; they do not
          replace this price. With PV Asset Status &quot;Existing&quot; the
          hardware is excluded from CAPEX but still anchors the % O&amp;M.
        </Typography>

        <Divider sx={{ my: 1.25 }} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: "wrap" }}
          useFlexGap
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Conversion model
          </Typography>
          <Chip
            size="small"
            label={hasOnd
              ? (greek ? "Καμπύλη απόδοσης OND" : "OND efficiency curve")
              : hasSandia ? "Sandia (efficiency curves)" : (greek ? "PVWatts (τυπική καμπύλη απόδοσης)" : "PVWatts (standard efficiency curve)")}
            color={hasOnd || hasSandia ? "success" : "default"}
            variant="outlined"
            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
          />
        </Stack>
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", mt: 0.5, lineHeight: 1.4 }}
        >
          {hasOnd
            ? (greek
              ? "Η καμπύλη απόδοσης του κατασκευαστή από το αρχείο OND, με το κατώφλι ισχύος, το όριο AC και τη νυχτερινή κατανάλωση όπως τα ορίζει."
              : "The manufacturer's efficiency curve from the OND file, with the power threshold, AC limit and night consumption it states.")
            : hasSandia
            ? "Efficiency varies with load and DC voltage, fitted from measured curves."
            : "A single nominal efficiency is applied at every operating point. Adding datasheet efficiency curves fits the more detailed Sandia model."}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ConfigForm({ onSubmit, submitLabel = "Save", showTransformerErrors = false }) {
  const { config: ctxConfig } = useEngine();


  const history = useHistory();
  const roundTo = (x, dp = 2) => {
    const n = Number(x);
    if (!Number.isFinite(n)) return "";
    const p = 10 ** dp;
    return Math.round((n + Number.EPSILON) * p) / p; // returns NUMBER
  };

  React.useEffect(() => {
    const onKeyDown = (e) => {
      const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
      const mod = isMac ? e.metaKey : e.ctrlKey;

      if (!mod) return;

      // Layout-independent: e.code is the PHYSICAL key ("KeyZ"), unlike e.key
      // which follows the active layout (Greek z -> "ζ") and broke shortcuts.
      const code = e.code || "";
      const key = code.startsWith("Key")
        ? code.slice(3).toLowerCase()
        : (e.key || "").toLowerCase();

      // Don't steal shortcuts while typing in inputs/textareas/contenteditable
      const el = e.target;
      const tag = el?.tagName?.toLowerCase();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        el?.isContentEditable ||
        // MUI Autocomplete uses an input internally
        el?.getAttribute?.("role") === "combobox";

      // ctrl/cmd + z => undo
      if (key === "z" && !e.shiftKey) {
        if (!isTyping) {
          e.preventDefault();
          history.undo();
        }
        return;
      }

      // ctrl/cmd + shift + z => redo (common)
      if (key === "z" && e.shiftKey) {
        if (!isTyping) {
          e.preventDefault();
          history.redo();
        }
        return;
      }

      // ctrl/cmd + y => redo (Windows common)
      if (key === "y") {
        if (!isTyping) {
          e.preventDefault();
          history.redo();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [history]);


  // init history once (or when initialCfg changes from empty->loaded)
  useEffect(() => {
    if (history.present == null) {
      const base =
        ctxConfig && Object.keys(ctxConfig).length > 0
          ? { ...DEFAULT_CONFIG, ...ctxConfig }
          : DEFAULT_CONFIG;

      history.init(normalizeProjectConfig(base));
    }
  }, [history, ctxConfig]);

  // ✅ single source of truth
  const cfg = useMemo(
    () => normalizeProjectConfig(history.present ?? DEFAULT_CONFIG),
    [history.present]
  );
  const commitHistory = history.commit;

  const handleField = useCallback((path, value) => {
    const nextCfg = setPathInObject(cfg, path, value);
    commitHistory(nextCfg);
  }, [cfg, commitHistory]);

  // The installed-capacity field types into this, not into the config.
  //
  // Every keystroke used to call handleField, and handleField commits to the
  // undo history and re-renders the whole form -- including the layout panel,
  // which is always mounted and carries the 3D scene, the map and the string
  // grid. Deleting "1062.00" meant seven commits and seven of those renders,
  // and the field lagged behind the keyboard. It also left seven steps in the
  // undo history for one edit.
  //
  // Here the keystrokes are local and the config is committed once, on blur or
  // on Enter -- which is also when the value is normalised, so nothing else
  // changes. Clicking any button blurs the field first, so a typed figure is
  // committed before Auto-design or a report can read it.
  const [capacityText, setCapacityText] = useState(
    () => String(cfg?.pvInstalledCap?.kw ?? "")
  );
  const capacityFromConfig = String(cfg?.pvInstalledCap?.kw ?? "");
  useEffect(() => {
    // Auto-design, a saved layout and a project load all write this field from
    // outside; the box has to follow them.
    setCapacityText(capacityFromConfig);
  }, [capacityFromConfig]);
  const commitCapacity = () => {
    const n = roundTo(capacityText, 2);
    const next = n === "" ? "0.00" : n.toFixed(2);
    setCapacityText(next);
    if (next !== capacityFromConfig) handleField("pvInstalledCap.kw", next);
  };

  /**
   * Apply several config paths in ONE commit.
   *
   * handleField derives nextCfg from the `cfg` of the current render, so calling
   * it repeatedly in the same tick makes every call start from the same base and
   * only the last one survives. Anything that changes more than one field at once
   * must fold the updates first and commit once — which also keeps undo/redo to a
   * single step instead of one per field.
   */
  const handleFields = (entries) => {
    const nextCfg = (entries || []).reduce(
      (acc, [path, value]) => setPathInObject(acc, path, value),
      cfg
    );
    history.commit(nextCfg);
  };

  // How the array meets the ground or the building. It decides two separate
  // things the config used to run together: WHERE the tilt comes from, and how
  // hot the modules run.
  //
  // A flush roof array lies on the roof, so its tilt and azimuth ARE the roof's
  // pitch and facing. The primary stores them as roofPitch/roofAzimuth; later
  // faces store their own angles in their orientation records, not the terrain
  // slope out of the site info, which is the ground the building stands on. The
  // backend used to default the panel tilt to that terrain slope for every
  // project alike, which gave a ground mount on a hillside the hillside's angle
  // and a roof array something unrelated to its roof.
  //
  // The thermal half was never asked at all: everything was modelled on
  // open-rack cooling. On the golden year a roof array modelled that way reports
  // 4.3% high on a close mount and 6.0% high on a fully insulated back, with
  // mean cell temperature in the sunny hours moving from 37 to 52-57 C.
  // Whether a geometric model has already removed shading from the irradiance.
  // Mirrors pv_core/shading_allowance, because the engine now REFUSES a run that
  // charges the flat allowance on top of one -- the loss diagram stops adding up
  // otherwise, and non-additivity is the first thing a reviewing engineer
  // checks. Better to say so here than to fail a simulation the user has waited
  // for.
  const savedShadingMode = cfg?.pvLosses?.dc?.shadingMode;
  const flatShadingPercent = Number(cfg?.pvLosses?.dc?.shading ?? 0) || 0;
  const flatShadingDeclared = Boolean(
    cfg?.pvLosses?.dc?.shadingCoversUnmodelled || savedShadingMode === "geometry_plus_percentage"
  );
  const shadingMode = savedShadingMode === "percentage"
    ? "percentage"
    : savedShadingMode === "geometry_plus_percentage" || (flatShadingDeclared && flatShadingPercent > 0)
      ? "geometry_plus_percentage"
      : "geometry";
  const shadingChoiceSx = (choice) => ({
    m: 0,
    px: 1,
    py: 0.25,
    minHeight: 46,
    border: 1,
    borderColor: shadingMode === choice ? "primary.main" : "divider",
    bgcolor: shadingMode === choice ? "action.selected" : "transparent",
    borderRadius: 1.5,
  });
  const geometricShading = [
    (cfg?.panelOrientation?.nearShadingModel || "bypass") !== "none"
      ? "the hourly row-shading model"
      : null,
    cfg?.pv_design?.obstacleShading || cfg?.orientations?.some((plane) => plane?.obstacleShading)
      ? "the imported obstacle scene" : null,
    cfg?.panelOrientation?.horizonProfile &&
    horizonShadingEnabled(cfg)
      ? "the terrain horizon"
      : null,
  ].filter(Boolean);
  // Older projects can still hold a flat loss while the layout-only mode is
  // selected. The engine will use that stored value, so it must be resolved.
  const geometryHasFlatPercent = shadingMode === "geometry" && flatShadingPercent > 0;

  const installationType = cfg?.panelOrientation?.installationType || "ground";
  const isFlushRoof = installationType === "roof_flush";
  // The plane the engine will model, from the typed tilt and the way it is
  // measured. Identical to the typed numbers unless the tilt is "from the
  // ground" on a sloping site.
  const mountingPlane = mountingPlaneFor(cfg);
  // Several orientations: panelOrientation is the first; any further planes
  // live in cfg.orientations and each MPPT names the one it stands on.
  //
  // ONE form edits them all. The Panel Orientation fields below show the
  // plane selected in the strip above them, and every write goes through
  // setActiveOrientationField: the primary's values into panelOrientation,
  // where the layout, the 3D scene and auto-design read them, a further
  // plane's into its entry in the list.
  const orientationList = orientationsFromConfig(cfg);
  // For the few captions that carry a number and so cannot be a catalogue key.
  const isGreekScreen = useIsGreek();
  const translateText = useTranslateText();
  const [activeOrientationId, setActiveOrientationId] = useState(PRIMARY_ID);
  // null while every panel is closed; the form then keeps the primary's
  // values behind it, though nothing shows it until a panel is opened.
  const activeOrientation =
    orientationList.find((o) => o.id === activeOrientationId) || orientationList[0];
  const activeIsPrimary = activeOrientation.id === orientationList[0].id;
  const activeTilt = activeIsPrimary
    ? (isFlushRoof ? cfg?.panelOrientation?.roofPitch : cfg?.panelOrientation?.tilt)
    : activeOrientation.tilt;
  const activeAzim = activeIsPrimary
    ? (isFlushRoof ? cfg?.panelOrientation?.roofAzimuth : cfg?.panelOrientation?.azim)
    : activeOrientation.azim;
  const activeFrameRoofPitch = activeIsPrimary ? cfg?.panelOrientation?.roofPitch
    : (activeOrientation.roofPitch ?? activeOrientation.spacingInfo?.site_geometry?.roof?.pitchDeg ?? cfg?.panelOrientation?.roofPitch);
  const activeFrameRoofAzimuth = activeIsPrimary ? cfg?.panelOrientation?.roofAzimuth
    : (activeOrientation.roofAzimuth ?? activeOrientation.spacingInfo?.site_geometry?.roof?.azimuthDeg ?? cfg?.panelOrientation?.roofAzimuth);
  // A face drawn on the plan takes its angles from the roof drawing (its eave
  // and pitch, PhysicalLayoutDialog's roof model): here they are shown, not
  // typed, so the System tab and the layout hold one plane per face.
  const activeSpacing = activeIsPrimary ? (cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing) : activeOrientation.spacingInfo;
  const activeRoofDrawn = isRoofInstallation(installationType)
    && Array.isArray(activeSpacing?.site_geometry?.roof?.footprint) && activeSpacing.site_geometry.roof.footprint.length >= 3;
  const fromRoofNote = (el, en) => <>
    {isGreekScreen ? el : en}{" "}
    <Link component="button" type="button" underline="hover" sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
      onClick={() => { setLayoutOrientationId(activeOrientation.id); setPvTab("design"); setDesignStep("layout"); }}>
      {isGreekScreen ? "Άνοιγμα κάτοψης" : "Open the plan"}
    </Link>
  </>;
  const activeTiltReference = activeIsPrimary
    ? cfg?.panelOrientation?.tiltReference
    : activeOrientation.tiltReference;
  const activeMountingPlane = activeIsPrimary
    ? mountingPlane
    : mountingPlaneFor({
        ...cfg,
        panelOrientation: {
          ...(cfg?.panelOrientation || {}),
          tilt: activeTilt,
          azim: activeAzim,
          tiltReference: activeTiltReference,
          roofPitch: activeFrameRoofPitch,
          roofAzimuth: activeFrameRoofAzimuth,
        },
      }, activeOrientation.spacingInfo || null);
  // The plane the Physical layout step is laying out. Its own state: the
  // orientation form and the layout are on different tabs, and switching the
  // plane being edited above must not switch the layout being drawn below.
  const [layoutOrientationId, setLayoutOrientationId] = useState(PRIMARY_ID);
  const layoutOrientation =
    orientationList.find((o) => o.id === layoutOrientationId) || orientationList[0];
  const layoutIsPrimary = layoutOrientation.id === orientationList[0].id;
  const layoutIsMulti = orientationList.length > 1;
  const layoutPlane = layoutIsPrimary
    ? mountingPlane
    : mountingPlaneFor({
        ...cfg,
        panelOrientation: {
          ...(cfg?.panelOrientation || {}),
          tilt: layoutOrientation.tilt,
          azim: layoutOrientation.azim,
          tiltReference: layoutOrientation.tiltReference,
          roofPitch: layoutOrientation.roofPitch ?? layoutOrientation.spacingInfo?.site_geometry?.roof?.pitchDeg ?? cfg?.panelOrientation?.roofPitch,
          roofAzimuth: layoutOrientation.roofAzimuth ?? layoutOrientation.spacingInfo?.site_geometry?.roof?.azimuthDeg ?? cfg?.panelOrientation?.roofAzimuth,
        },
      }, layoutOrientation.spacingInfo || null);
  const layoutRoofPitch = isFlushRoof ? layoutPlane.tilt : layoutIsPrimary
    ? cfg?.panelOrientation?.roofPitch
    : (layoutOrientation.roofPitch ?? layoutOrientation.spacingInfo?.site_geometry?.roof?.pitchDeg ?? cfg?.panelOrientation?.roofPitch);
  const layoutRoofAzimuth = isFlushRoof ? layoutPlane.azimuth : layoutIsPrimary
    ? cfg?.panelOrientation?.roofAzimuth
    : (layoutOrientation.roofAzimuth ?? layoutOrientation.spacingInfo?.site_geometry?.roof?.azimuthDeg ?? cfg?.panelOrientation?.roofAzimuth);

  const setActiveOrientationField = (key, value) => {
    if (activeIsPrimary) {
      handleField(`panelOrientation.${key}`, value);
    } else {
      history.commit(updateOrientation(cfg, activeOrientation.id, { [key]: value }));
    }
  };
  const setActiveRoofFace = (field, value) => {
    history.commit(setFlushRoofFace(cfg, activeOrientation.id, field, value));
  };

  // A project saved before the installation type existed, with no panel tilt of
  // its own, runs on the TERRAIN slope -- the angle of the ground the array
  // stands on, not the plane the modules are mounted at. The engine preserves
  // that (deleting the fallback would change every old project's numbers), logs
  // it, and publishes `panelOrientationSource: "terrain_slope_fallback"`. Until
  // now nothing showed it, so a report could present a hillside's angle as a
  // design decision and no one on screen could tell.
  //
  // This is a DISPLAY hint, not a second copy of the rule: pv_core's
  // resolve_panel_orientation still decides, and it raises rather than
  // inheriting the moment a project names its installation type. The condition
  // here is only the shape that triggers it -- no type chosen, no tilt set, and
  // a terrain slope that is not flat.
  const tiltInheritedFromTerrain = useMemo(() => {
    const po = cfg?.panelOrientation || {};
    if (po.installationType) return false;          // explicit: the engine errors instead
    if (po.tiltOpt) return false;                   // the optimiser supplies one
    const tilt = Number(po.tilt);
    if (Number.isFinite(tilt) && tilt !== 0) return false;
    const slope = Number(cfg?.loc_info?.tilt);
    return Number.isFinite(slope) && slope !== 0;
  }, [cfg?.panelOrientation, cfg?.loc_info?.tilt]);
  const roofPitchValue = cfg?.panelOrientation?.roofPitch;
  const roofAzimuthValue = cfg?.panelOrientation?.roofAzimuth;
  const roofFieldsMissing =
    isFlushRoof &&
    (roofPitchValue == null ||
      roofPitchValue === "" ||
      roofAzimuthValue == null ||
      roofAzimuthValue === "");

  const orientationFieldsDisabled =
    cfg?.mountType === "single_axis" || cfg?.mountType === "dual_axis";
  const orientationTiltMissing =
    showTransformerErrors &&
    !orientationFieldsDisabled &&
    !isFlushRoof &&
    (cfg?.panelOrientation?.tilt == null ||
      cfg?.panelOrientation?.tilt === "" ||
      !Number.isFinite(Number(cfg?.panelOrientation?.tilt)));
  const orientationAzimMissing =
    showTransformerErrors &&
    !orientationFieldsDisabled &&
    !isFlushRoof &&
    (cfg?.panelOrientation?.azim == null ||
      cfg?.panelOrientation?.azim === "" ||
      !Number.isFinite(Number(cfg?.panelOrientation?.azim)));
  useEffect(() => {
    if (!isFlushRoof || roofFieldsMissing) return;
    const pitch = Number(roofPitchValue);
    const azimuth = Number(roofAzimuthValue);
    if (!Number.isFinite(pitch) || !Number.isFinite(azimuth)) return;
    if (
      Number(cfg?.panelOrientation?.tilt) === pitch &&
      Number(cfg?.panelOrientation?.azim) === azimuth
    ) {
      return;
    }
    const withTilt = setPathInObject(cfg, "panelOrientation.tilt", pitch);
    history.commit(setPathInObject(withTilt, "panelOrientation.azim", azimuth));
  }, [cfg, history, isFlushRoof, roofFieldsMissing, roofPitchValue, roofAzimuthValue]);

  useEffect(() => {
    if (!orientationFieldsDisabled) return;
    if (
      (cfg?.panelOrientation?.tilt ?? "") === "" &&
      (cfg?.panelOrientation?.azim ?? "") === ""
    ) {
      return;
    }

    const nextCfg = setPathInObject(cfg, "panelOrientation.tilt", "");
    const clearedCfg = setPathInObject(nextCfg, "panelOrientation.azim", "");
    history.commit(clearedCfg);
  }, [
    cfg,
    history,
    orientationFieldsDisabled,
    cfg?.panelOrientation?.tilt,
    cfg?.panelOrientation?.azim,
  ]);



  const parseNumericFieldValue = (rawValue) => {
    if (rawValue === "" || rawValue == null) return "";
    const value = Number(rawValue);
    return Number.isFinite(value) ? value : rawValue;
  };

  const handlePvLossField = (path, rawValue) => {
    handleField(path, parseNumericFieldValue(rawValue));
  };

  const pvLossInverterKeys = useMemo(() => {
    const systemKeys = Object.keys(cfg?.systemDesign || {});
    const acKeys = Object.keys(cfg?.pvLosses?.ac?.wiringDynamic || {}).filter(
      (key) => key !== "main"
    );
    const dcKeys = Object.keys(cfg?.pvLosses?.dc?.wiringDynamic || {});
    return [...new Set([...systemKeys, ...acKeys, ...dcKeys])];
  }, [cfg?.systemDesign, cfg?.pvLosses]);

  const pvLossMpptKeysByInverter = useMemo(() => {
    const result = {};
    pvLossInverterKeys.forEach((inverterKey) => {
      const fromSystemDesign = Array.isArray(cfg?.systemDesign?.[inverterKey])
        ? cfg.systemDesign[inverterKey].map((_, idx) => `MPPT${idx + 1}`)
        : [];
      const fromPvLosses = Object.keys(
        cfg?.pvLosses?.dc?.wiringDynamic?.[inverterKey] || {}
      )
        .filter((key) => /^MPPT\d+$/i.test(key))
        .sort(
          (a, b) =>
            Number(a.replace(/\D/g, "")) - Number(b.replace(/\D/g, ""))
        );
      result[inverterKey] = [...new Set([...fromSystemDesign, ...fromPvLosses])];
    });
    return result;
  }, [cfg?.systemDesign, cfg?.pvLosses, pvLossInverterKeys]);

  const acMaterialOptions = useMemo(
    () =>
      AC_WIRING_MATERIAL_OPTIONS.map((value) => ({
        value,
        label: value,
      })),
    []
  );
  const dcMaterialOptions = useMemo(
    () =>
      DC_WIRING_MATERIAL_OPTIONS.map((value) => ({
        value,
        label: value,
      })),
    []
  );
  const installationTypeOptions = useMemo(
    () =>
      AC_INSTALLATION_TYPE_OPTIONS.map((value) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
      })),
    []
  );
  const [activeDcLossInverter, setActiveDcLossInverter] = useState("");
  const [activeAcLossInverter, setActiveAcLossInverter] = useState("");

  useEffect(() => {
    if (!pvLossInverterKeys.length) {
      setActiveDcLossInverter("");
      setActiveAcLossInverter("");
      return;
    }
    setActiveDcLossInverter((prev) =>
      prev && pvLossInverterKeys.includes(prev) ? prev : pvLossInverterKeys[0]
    );
    setActiveAcLossInverter((prev) =>
      prev && pvLossInverterKeys.includes(prev) ? prev : pvLossInverterKeys[0]
    );
  }, [pvLossInverterKeys]);

  const applyDcLossSettingsToAll = useCallback(() => {
    if (!activeDcLossInverter || pvLossInverterKeys.length < 2) return;
    const source = cfg?.pvLosses?.dc?.wiringDynamic?.[activeDcLossInverter];
    if (!source) return;

    let nextCfg = cfg;
    pvLossInverterKeys.forEach((inverterKey) => {
      if (inverterKey === activeDcLossInverter) return;
      const target = cfg?.pvLosses?.dc?.wiringDynamic?.[inverterKey] || {};
      const nextBranch = {
        ...target,
        autoSize: source.autoSize,
        material: source.material,
        capPercent: source.capPercent,
      };
      const targetMpptKeys = pvLossMpptKeysByInverter[inverterKey] || [];
      const sourceMpptKeys = pvLossMpptKeysByInverter[activeDcLossInverter] || [];
      targetMpptKeys.forEach((mpptKey, index) => {
        const sourceMppt =
          source[mpptKey] ||
          source[sourceMpptKeys[index]] ||
          source[sourceMpptKeys[0]] ||
          {};
        nextBranch[mpptKey] = {
          ...(target[mpptKey] || {}),
          panelLinkLengthM: sourceMppt.panelLinkLengthM ?? "",
          panelCableSectionMm2: sourceMppt.panelCableSectionMm2 ?? null,
          stringHomerunLengthM: sourceMppt.stringHomerunLengthM ?? "",
          homerunCableSectionMm2: sourceMppt.homerunCableSectionMm2 ?? null,
        };
      });
      nextCfg = setPathInObject(nextCfg, `pvLosses.dc.wiringDynamic.${inverterKey}`, nextBranch);
    });
    history.commit(nextCfg);
  }, [activeDcLossInverter, cfg, history, pvLossInverterKeys, pvLossMpptKeysByInverter]);

  const applyAcLossSettingsToAll = useCallback(() => {
    if (!activeAcLossInverter || pvLossInverterKeys.length < 2) return;
    const source = cfg?.pvLosses?.ac?.wiringDynamic?.[activeAcLossInverter];
    if (!source) return;

    let nextCfg = cfg;
    pvLossInverterKeys.forEach((inverterKey) => {
      if (inverterKey === activeAcLossInverter) return;
      const target = cfg?.pvLosses?.ac?.wiringDynamic?.[inverterKey] || {};
      nextCfg = setPathInObject(nextCfg, `pvLosses.ac.wiringDynamic.${inverterKey}`, {
        ...target,
        lengthM: source.lengthM ?? "",
        autoSize: source.autoSize,
        material: source.material,
        capPercent: source.capPercent,
        powerFactor: source.powerFactor,
        parallelRuns: source.parallelRuns,
        cableSectionMm2: source.cableSectionMm2 ?? null,
        installationType: source.installationType,
        conductorTemperatureC: source.conductorTemperatureC,
      });
    });
    history.commit(nextCfg);
  }, [activeAcLossInverter, cfg, history, pvLossInverterKeys]);


  const { accessToken } = useAuth();
  const { currentProject, setSectionDirty, portfolio } = useFile();
  const navigate = useNavigate();
  const reportBranding = useMemo(() => portfolio?.project?.reportBranding || {}, [portfolio?.project?.reportBranding]);
  // ?design=<step> arrives from the feasibility summary's "Single-line
  // diagram" button. It used to set the step below while this tab stayed on
  // "equipment", so the link landed on the wrong page and looked broken.
  // `?design=` names a step inside the Electrical Design & Layout tab; `?pv=`
  // names one of these tabs outright, which is how Weather Data sends the
  // reader straight to the generation run.
  const [pvTab, setPvTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("pv");
      if (["equipment", "system", "design", "modelling", "losses", "generation", "knowledge"].includes(tab)) {
        return tab;
      }
      const wanted = params.get("design");
      return ["electrical", "layout", "sld"].includes(wanted) ? "design" : "equipment";
    } catch {
      return "equipment";
    }
  });
  const { catalogWriteAllowed, catalogWriteDisabledMessage, isAdminUser } = useCurrentUserAccess();
  const [addModuleOpen, setAddModuleOpen] = useState(false);
  const [addInvOpen, setAddInvOpen] = useState(false);
  const [catalogOrganizations, setCatalogOrganizations] = useState([]);
  const [catalogOrganizationsLoading, setCatalogOrganizationsLoading] = useState(false);
  const [catalogOrganizationsError, setCatalogOrganizationsError] = useState("");
  const [moduleVisibility, setModuleVisibility] = useState("global");
  const [moduleOrganizations, setModuleOrganizations] = useState([]);
  const [inverterVisibility, setInverterVisibility] = useState("global");
  const [inverterOrganizations, setInverterOrganizations] = useState([]);
  useEffect(() => {
    if (!isAdminUser || (!addModuleOpen && !addInvOpen)) return;
    let active = true;
    setCatalogOrganizationsLoading(true);
    getCatalogOrganizations(accessToken).then((data) => {
      if (active) {
        setCatalogOrganizations(data?.organizations || []);
        setCatalogOrganizationsError("");
      }
    }).catch(() => {
      if (active) setCatalogOrganizationsError(isGreekScreen
        ? "Δεν φορτώθηκαν οι οργανισμοί. Δοκιμάστε ξανά ανοίγοντας τη φόρμα."
        : "Organizations could not be loaded. Reopen the form to retry.");
    }).finally(() => { if (active) setCatalogOrganizationsLoading(false); });
    return () => { active = false; };
  }, [isAdminUser, addModuleOpen, addInvOpen, accessToken, isGreekScreen]);

  const scopePayload = (visibility, organizations) => isAdminUser
    ? { visibility, organizations: visibility === "selected" ? organizations : [] }
    : {};
  const scopeInvalid = (visibility, organizations) => isAdminUser
    && visibility === "selected" && (organizations.length === 0 || Boolean(catalogOrganizationsError));

  // --- Add custom module dialog ---
  const [moduleForm, setModuleForm] = useState(() => {
    const init = {};
    MODULE_FIELDS.forEach(f => (init[f.key] = ""));
    init.bifaciality = "0.7";
    return init;
  });
  const [moduleErrors, setModuleErrors] = useState({});
  const [moduleSaving, setModuleSaving] = useState(false);
  const [moduleSaveError, setModuleSaveError] = useState("");

  // Which parameter set the user is supplying. "datasheet" keeps the historical
  // form and its fit to CEC; the others let someone paste a manufacturer or lab
  // coefficient set, which the engine has always been able to run but the form
  // had no way to accept. Specs come from the backend so the two cannot drift.
  const [paramSets, setParamSets] = useState(null);
  const [moduleParamSet, setModuleParamSet] = useState("datasheet");
  const [moduleParamValues, setModuleParamValues] = useState({});
  const [modulePanImport, setModulePanImport] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCatalogParameterSets();
        if (!cancelled) setParamSets(data || null);
      } catch {
        if (!cancelled) setParamSets(null);   // advisory only; datasheet path still works
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const moduleParamSpec = useMemo(
    () => (paramSets?.modules?.sets || []).find((s) => s.key === moduleParamSet) || null,
    [paramSets, moduleParamSet]
  );
  const moduleParamMissing = useMemo(
    () =>
      moduleParamSet === "datasheet" || moduleParamSet === "pan"
        ? []
        : missingForSet(moduleParamSpec, moduleParamValues),
    [moduleParamSet, moduleParamSpec, moduleParamValues]
  );
  const [catalogToast, setCatalogToast] = useState({ open: false, msg: "", severity: "success" });

  // --- Add custom inverter dialog ---
  const [invForm, setInvForm] = useState(() => {
    const init = {};
    INVERTER_FIELDS.forEach(f => (init[f.key] = ""));
    init.pv_array_power_limit_kind = "required";
    return init;
  });
  const [invErrors, setInvErrors] = useState({});
  const [invSaving, setInvSaving] = useState(false);
  const [invSaveError, setInvSaveError] = useState("");
  const [invPerMppt, setInvPerMppt] = useState(false);
  const [invMpptRows, setInvMpptRows] = useState([]);
  // A short-circuit rating has been entered, in the general field or in any
  // per-MPPT row. Gates the Isc(STC) factor, which only qualifies such a rating.
  const invShortEntered = invPerMppt
    ? invMpptRows.some((row) => String(row?.short ?? "").trim() !== "")
    : String(invForm?.Iscmax_per_mppt ?? "").trim() !== "";
  // Fitted Sandia model staged while the add-inverter dialog is still open, so
  // the curves are saved with the initial POST instead of requiring a second
  // pass through the details dialog.
  const [invNewFitOpen, setInvNewFitOpen] = useState(false);
  const [invPendingFit, setInvPendingFit] = useState(null);
  const invPowerInputsReady = Number(invForm.Paco) > 0 && String(invForm.Pnt ?? "").trim() !== "" && Number(invForm.Pnt) >= 0;
  const invCurrentFit = currentSandiaFit(invPendingFit, invForm);
  const invFitEfficiencies = sandiaEfficiencies(invCurrentFit);
  const invCurveCharacteristics = useMemo(() => curveCharacteristicsForForm(invPendingFit, { Paco: invForm.Paco, Pnt: invForm.Pnt }),
    [invPendingFit, invForm.Paco, invForm.Pnt]);

  // Which AC parameter set the user is supplying, mirroring the module dialog.
  // The engine infers its inverter model from whichever coefficients are present
  // (C0/C1/C2 -> Sandia, else Pdco -> PVWatts; ADR is not offered, see
  // pv_core/parameter_sets.py), so this chooses what to provide.
  //
  // Defaults to Sandia rather than PVWatts. Sandia captures efficiency falling at
  // low load and shifting with DC voltage; PVWatts applies one flat efficiency at
  // every operating point, which overstates output in the part-load hours a plant
  // spends most of its time in. Defaulting to the better model means a user has to
  // opt INTO the cruder one rather than accidentally keep it.
  //
  // The cost is visible rather than hidden: Sandia is satisfied by a staged fit,
  // so the dialog opens asking for efficiency curves ("use Add efficiency
  // curves…") instead of presenting fields that can simply be typed. That prompt
  // is the point - it says what good data looks like.
  const [invParamSet, setInvParamSet] = useState("sandia");
  const [invEuro, setInvEuro] = useState({mode:"euro",euro:"",max:"",respectMax:true});
  const invEuroActive = invParamSet === "pvwatts" && invEuro.mode === "euro";
  const invEuroResult = useMemo(() => pvwattsFromEuro(invEuro, invForm.Paco), [invEuro, invForm.Paco]);
  // The OND route: a file, read on the server, saved with only a name and a price.
  const [invOndImport, setInvOndImport] = useState(null);

  const invParamSpec = useMemo(
    () => (paramSets?.inverters?.sets || []).find((s) => s.key === invParamSet) || null,
    [paramSets, invParamSet]
  );

  // Sandia is satisfied by a staged fit rather than by typed fields, so it is
  // checked separately from the spec's required list.
  const invParamMissing = useMemo(() => {
    if (!invParamSpec) return [];
    if (invParamSet === "ond_curve") return invOndImport ? [] : [isGreekScreen ? "αρχείο OND" : "OND file"];
    if (invParamSet === "sandia") {
      if (invPendingFit && !invCurrentFit) return [isGreekScreen
        ? "νέα προσαρμογή με τις τρέχουσες τιμές Paco/Pnt"
        : "a new fit with the current Paco/Pnt"];
      return invCurrentFit ? [] : [isGreekScreen
        ? "καμπύλες απόδοσης (πατήστε «Προσθέστε καμπύλες απόδοσης…»)"
        : "efficiency curves (use Add efficiency curves…)"];
    }
    if (invEuroActive && !invEuroResult.characteristics) return [isGreekScreen ? "έγκυρα Euro/Max και ονομαστική ισχύς AC" : "valid Euro/Max and rated AC power"];
    return (invParamSpec.required || [])
      .map((f) => f.key)
      .filter((k) => {
        const v = invEuroActive && ["Pdco", "eff_nom", "eff_max"].includes(k) ? invEuroResult.characteristics?.[k] : invForm[k];
        if (v === "" || v == null) return true;
        return !Number.isFinite(Number(v));
      });
  }, [invParamSpec, invParamSet, invForm, invCurrentFit, invPendingFit, invOndImport, isGreekScreen, invEuroActive, invEuroResult]);



  // --- module details dialog state ---
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleMonitorOpen, setModuleMonitorOpen] = useState(false);
  const [degradationOpen, setDegradationOpen] = useState(false);
  const [detailedModule, setDetailedModule] = useState(null);
  const [moduleDetails, setModuleDetails] = useState(null);
  const [moduleDialogLoading, setModuleDialogLoading] = useState(false);
  const [moduleDialogError, setModuleDialogError] = useState("");

  // Which model choices the selected module can actually back, for the
  // Advanced modeling panel. Separate from moduleDetails above, which is only
  // populated when the user opens the module details dialog.
  const [moduleModeling, setModuleModeling] = useState(null);
  const [moduleModelingLoading, setModuleModelingLoading] = useState(Boolean(cfg?.read_module));
  // Bifacial decides which physics path runs, so the modelling panel has to
  // know it. Same catalogue row the engine reads, from the call already made.
  const [moduleIsBifacial, setModuleIsBifacial] = useState(false);

  // Terrain (far-shading) profile, fetched from PVGIS and stored in the config.
  const [horizonBusy, setHorizonBusy] = useState(false);
  const [horizonError, setHorizonError] = useState("");

  const handleFetchHorizon = async () => {
    const projectId = currentProject?.id;
    if (!projectId) {
      setHorizonError("Open or save the project first, so the site coordinates are known.");
      return;
    }
    setHorizonBusy(true);
    setHorizonError("");
    try {
      const data = await fetchProjectHorizon(projectId, accessToken, {
        lat: cfg?.loc_info?.lat ?? cfg?.lat,
        lon: cfg?.loc_info?.lon ?? cfg?.lon,
      });
      // The coordinates travel with the profile, so a site that moves later is
      // detectable rather than silently inheriting the old terrain.
      handleFields([
        ["panelOrientation.horizonProfile", data?.profile || null],
        ["panelOrientation.horizonProfileSite", data?.profile ? {
          lat: data.latitude ?? (cfg?.loc_info?.lat ?? cfg?.lat),
          lon: data.longitude ?? (cfg?.loc_info?.lon ?? cfg?.lon),
          fetchedAt: new Date().toISOString(),
        } : null],
      ]);
      if (data?.is_flat) {
        setCatalogToast({
          open: true,
          msg: "Terrain around this site is level, so it will not change the result.",
          severity: "info",
        });
      }
    } catch (err) {
      setHorizonError(err?.message || "The terrain profile could not be retrieved.");
    } finally {
      setHorizonBusy(false);
    }
  };

  // Fetch the terrain profile without waiting to be asked -- for the site the
  // project is ACTUALLY at.
  //
  // The first version of this was worse than the button it replaced. The default
  // config ships Athens (loc_info 37.98, 23.73), so the effect fired immediately
  // on every new project, fetched ATHENS' horizon, stored it, and then its own
  // "already have a profile" guard stopped it ever looking again. Set the real
  // coordinates afterwards and the run kept a ridge profile from three hundred
  // kilometres away, with a confident chip on screen saying so. A wrong horizon
  // applied silently is a worse failure than no horizon, because nothing about
  // it looks unresolved.
  //
  // So the fetch is keyed on the COORDINATES, not on whether a profile exists,
  // and the profile records where it was measured. Move the site and it is
  // refetched; until it is, the panel says the stored profile belongs to a
  // different place rather than showing it as this site's terrain.
  const horizonAutoFetched = useRef(null);
  // The commit below happens after an await, so it must not build on the `cfg`
  // captured when the effect started -- that snapshot is stale by then, and
  // committing it would silently revert whatever the user typed while PVGIS was
  // answering.
  const cfgRef = useRef(cfg);
  const layoutSaveActiveRef = useRef(true);
  useEffect(() => {
    layoutSaveActiveRef.current = true;
    return () => { layoutSaveActiveRef.current = false; };
  }, []);
  useEffect(() => {
    cfgRef.current = cfg;
  });

  useEffect(() => {
    const projectId = currentProject?.id;
    if (!projectId || !accessToken) return;

    const lat = cfg?.loc_info?.lat ?? cfg?.lat;
    const lon = cfg?.loc_info?.lon ?? cfg?.lon;
    if (lat == null || lon == null) return;

    const key = `${projectId}:${Number(lat).toFixed(4)}:${Number(lon).toFixed(4)}`;
    if (horizonAutoFetched.current === key) return;

    // A profile already measured at these coordinates needs nothing.
    const meta = cfg?.panelOrientation?.horizonProfileSite;
    if (
      cfg?.panelOrientation?.horizonProfile &&
      meta &&
      Math.abs(Number(meta.lat) - Number(lat)) < 1e-4 &&
      Math.abs(Number(meta.lon) - Number(lon)) < 1e-4
    ) {
      horizonAutoFetched.current = key;
      return;
    }

    horizonAutoFetched.current = key;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchProjectHorizon(projectId, accessToken, { lat, lon });
        if (cancelled || !data?.profile) return;
        const base = cfgRef.current;
        const withProfile = setPathInObject(
          base, "panelOrientation.horizonProfile", data.profile
        );
        history.commit(
          setPathInObject(withProfile, "panelOrientation.horizonProfileSite", {
            lat: data.latitude ?? lat,
            lon: data.longitude ?? lon,
            fetchedAt: new Date().toISOString(),
          })
        );
      } catch {
        // Silent by design: an unreachable PVGIS is not worth interrupting the
        // user over, and the button is still there.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProject?.id,
    accessToken,
    cfg?.loc_info?.lat,
    cfg?.loc_info?.lon,
    cfg?.lat,
    cfg?.lon,
  ]);

  const handleClearHorizon = () => {
    setHorizonError("");
    handleFields([
      ["panelOrientation.horizonProfile", null],
      ["panelOrientation.horizonProfileSite", null],
    ]);
  };

  // Sandia inverter fit from datasheet efficiency curves.
  const [invFitOpen, setInvFitOpen] = useState(false);

  // Whether the selected inverter carries fitted Sandia coefficients, so the
  // Advanced modeling panel can say what the AC model will actually be.
  // undefined while unknown; the panel stays quiet rather than guessing.
  const [inverterHasCurves, setInverterHasCurves] = useState(undefined);
  // The same fetch already paid for, kept whole: the equipment tab shows the
  // inverter's limits beside the selector, and re-requesting the record to read
  // Mppt_low off it would be a second round trip for data already in hand.
  const [inverterChars, setInverterChars] = useState(null);
  const [ondConnectionOpen, setOndConnectionOpen] = useState(false);
  const [ondConnectionRevision, setOndConnectionRevision] = useState(0);
  const [inverterCharsLoading, setInverterCharsLoading] = useState(Boolean(cfg?.read_inverter));

  useEffect(() => {
    const name = cfg?.read_inverter;
    if (!name) {
      setInverterHasCurves(undefined);
      setInverterChars(null);
      setInverterCharsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setInverterCharsLoading(true);
    (async () => {
      try {
        const data = await getInverterDetails(name);
        const c = data?.characteristics || {};
        const ok = ["C0", "C1", "C2"].every((k) => Number.isFinite(Number(c[k])));
        if (!cancelled) {
          setInverterHasCurves(ok);
          setInverterChars(c);
        }
      } catch {
        if (!cancelled) {
          setInverterHasCurves(undefined);
          setInverterChars(null);
        }
      } finally {
        if (!cancelled) setInverterCharsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cfg?.read_inverter]);

  /**
   * The selected module's own figures, for the sizing voltages beside it.
   *
   * PVsyst puts Vmpp at the hot cell and Voc at the cold one on the module
   * panel, because they are what a string length is chosen from and reading
   * them off a datasheet by hand is arithmetic the tool should have done. The
   * maths mirrors pv_core/string_limits.py so the panel and the electrical
   * check can never quote different numbers.
   */
  const [moduleChars, setModuleChars] = useState(null);
  const [moduleCharsLoading, setModuleCharsLoading] = useState(Boolean(cfg?.read_module));

  useEffect(() => {
    const name = cfg?.read_module;
    if (!name) {
      setModuleChars(null);
      setModuleCharsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setModuleCharsLoading(true);
    (async () => {
      try {
        const data = await getModuleDetails(name);
        if (!cancelled) setModuleChars(data?.characteristics || null);
      } catch {
        if (!cancelled) setModuleChars(null);
      } finally {
        if (!cancelled) setModuleCharsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cfg?.read_module]);

  // --- inverter details dialog state ---
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [invDetails, setInvDetails] = useState(null);
  const [invDialogLoading, setInvDialogLoading] = useState(false);
  const [invDialogError, setInvDialogError] = useState("");
  // The weather the stored estimate ran on, and the weather selected right now.
  // Kept apart on purpose: the estimate's staleness check is a digest of the PV
  // design inputs, and the weather selection is not one of them -- it lives in
  // the project's TMY table, not in the config. So swapping the weather file
  // leaves an estimate looking current while its numbers came from a dataset
  // nobody is using any more. This pair is what notices that.
  const [estimateWeather, setEstimateWeather] = useState(null);
  const [selectedWeather, setSelectedWeather] = useState(null);
  // The project's standard UTC offset, only so the shading view can label its
  // time slider with a wall clock. Failure is not an error worth showing: the
  // view falls back to the zone meridian and says so.
  // Twelve month inputs will not fit beside eight other fields. While the
  // soiling override is open its cell takes the whole row and the rest wrap
  // below it, instead of the row splitting around a cell four times its height.
  const [soilingMonthlyOpen, setSoilingMonthlyOpen] = useState(false);
  const soilingMonthlyActive = Array.isArray(cfg.pvLosses?.dc?.soilingMonthly)
    && cfg.pvLosses.dc.soilingMonthly.length === 12;
  const [siteTimezone, setSiteTimezone] = useState(null);

  // True only when both sides are known AND they disagree. Every other case --
  // selection still loading, no estimate yet, or an estimate written before the
  // dataset id was recorded -- is "cannot tell", and says nothing rather than
  // accusing a run that may be perfectly current.
  const storedRunUsesOtherWeather = useMemo(() => {
    if (!estimateWeather || !selectedWeather) return false;
    const ranOn = estimateWeather.datasetId ?? null;
    const selected = selectedWeather.datasetId ?? null;
    // No id on the estimate means either platform weather or a run from before
    // the field existed. Only the first of those is knowable, and the provider
    // is what says so.
    if (ranOn === null && estimateWeather.provider !== "platform") return false;
    return ranOn !== selected;
  }, [estimateWeather, selectedWeather]);
  const [designReportError, setDesignReportError] = useState("");
  const [orientationLoading, setOrientationLoading] = useState(false);
  const [orientationError, setOrientationError] = useState("");
  const [orientationResult, setOrientationResult] = useState(null);
  // What the sweep maximises. Yield in all three cases -- the objective is
  // deliberately not revenue, so the recommendation stays a statement about the
  // plant rather than about a price curve that moves.
  const [orientationObjective, setOrientationObjective] = useState("year");
  // Tilt-only optimisation: the azimuth in the field is kept, and the sweep
  // looks for the best tilt at it.
  const [keepAzimuth, setKeepAzimuth] = useState(false);
  const savedOrientationOptimization = cfg?.orientationOptimization?.[activeOrientation.id];
  useEffect(() => {
    setOrientationObjective(savedOrientationOptimization?.result?.objective || "year");
    setKeepAzimuth(savedOrientationOptimization?.keepAzimuth === true);
  }, [savedOrientationOptimization?.result?.objective, savedOrientationOptimization?.keepAzimuth,
    activeOrientation.id, currentProject?.id]);
  // The curves are drawn from a COARSE sweep that runs on its own, so the panel
  // shows the trade-off before anyone presses a button -- which is how PVsyst's
  // dialog behaves, and the reason "where are the mini plots?" was a fair
  // question of a version that only drew them after Optimize.
  //
  // 5 deg / 10 deg is ~130 full-year evaluations, about 1.1 s, against ~2,800
  // and 22 s for the fine sweep behind Optimize -- and it lands within a couple
  // of degrees of it, which is inside the width of the peak either way.
  const [orientationPreview, setOrientationPreview] = useState(null);
  const [orientationPreviewBusy, setOrientationPreviewBusy] = useState(false);
  const [orientationSuccessOpen, setOrientationSuccessOpen] = useState(false);

  useEffect(() => {
    const lat = Number(cfg?.loc_info?.lat);
    const lon = Number(cfg?.loc_info?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setSiteTimezone(null);
      return undefined;
    }
    let cancelled = false;
    fetchUtcOffset(lat, lon, accessToken)
      .then((res) => {
        if (!cancelled) setSiteTimezone(res);
      })
      .catch(() => {
        if (!cancelled) setSiteTimezone(null);
      });
    return () => {
      cancelled = true;
    };
  }, [cfg?.loc_info?.lat, cfg?.loc_info?.lon, accessToken]);

  // The two TMY workbook downloads used to live here, each running the whole
  // annual simulation on its own. They are served from the stored estimate now,
  // by PvTmyEstimate - one run, both files. See api/routers/pv_tmy.py.

  const getPvLossTooltip = useCallback(
    (key) => translateText(PV_LOSS_TOOLTIPS[key] || ""),
    [translateText]
  );
  const materialDetailTooltip = useCallback(
    (material, mode = "dc") => {
      if (material === "Cu") {
        return mode === "ac"
          ? [
              "Copper conductor.",
              "rho20 = 1.724e-8 ohm·m at 20°C",
              "alpha = 0.00393",
              "rho_t = rho20 * (1 + alpha * (temp - 20.0))",
              "temp = Conductor Temperature",
            ]
          : "Copper conductor. DC model uses resistivity = 0.017 ohm·mm²/m.";
      }
      if (material === "Al") {
        return mode === "ac"
          ? [
              "Aluminum conductor.",
              "rho20 = 2.826e-8 ohm·m at 20°C",
              "alpha = 0.00403",
              "rho_t = rho20 * (1 + alpha * (temp - 20.0))",
              "temp = Conductor Temperature",
            ]
          : "Aluminum conductor. DC model uses resistivity = 0.02826 ohm·mm²/m.";
      }
      return [getPvLossTooltip(`material.${material}`)];
    },
    [getPvLossTooltip]
  );
  const installationTypeTooltip = useCallback(
    (installationType) => getPvLossTooltip(`installationType.${installationType}`),
    [getPvLossTooltip]
  );

  const [downloading, setDownloading] = React.useState(false);
  const [hasUnsavedAutoDesign, setHasUnsavedAutoDesign] = useState(false);
  const [hasUnsavedPhysicalLayout, setHasUnsavedPhysicalLayout] = useState(false);
  // Edits typed into the two tabs but not yet saved with their own Save button.
  // Reported by the tabs themselves; the flags above are about auto-design
  // drafts, and both count as "unsaved" for the asterisk and the chip.
  const [designFormDirty, setDesignFormDirty] = useState(false);
  const [layoutFormDirty, setLayoutFormDirty] = useState(false);
  const [planChecks, setPlanChecks] = useState({});
  const designUnsaved = hasUnsavedAutoDesign || designFormDirty;
  const layoutUnsaved = hasUnsavedPhysicalLayout || layoutFormDirty;
  // The project save refuses while either tab holds unsaved edits, and says
  // which one to save first.
  useEffect(() => {
    if (typeof setSectionDirty !== "function") return undefined;
    setSectionDirty("Electrical design", designUnsaved);
    setSectionDirty("Physical layout", layoutUnsaved);
    return () => {
      setSectionDirty("Electrical design", false);
      setSectionDirty("Physical layout", false);
    };
  }, [designUnsaved, layoutUnsaved, setSectionDirty]);
  const [physicalLayoutDraft, setPhysicalLayoutDraft] = useState(null);
  const [physicalLayoutModuleGeometry, setPhysicalLayoutModuleGeometry] = useState({
    length: null,
    width: null,
  });
  const [physicalLayoutModuleGeometryLoading, setPhysicalLayoutModuleGeometryLoading] = useState(Boolean(cfg?.read_module));
  const [designMeta, setDesignMeta] = useState(null); // { name, mppts: [2,2,...] }
  const [designDialogKey, setDesignDialogKey] = useState(0);
  const [autoDesignOpen, setAutoDesignOpen] = useState(false);
  const [focusAutoDesignTarget, setFocusAutoDesignTarget] = useState(false);
  // The layout tab hands its report builder up through this ref, and its
  // state up through the setter below -- a ref cannot re-render this button,
  // which is why the button used to show no sign that a report was building.
  const designReportRef = useRef(null);
  const [designReport, setDesignReport] = useState({
    busy: false, ready: false, error: "", reason: "",
  });
  // Which of the three design steps is showing. They used to be modals, so
  // there was nothing to remember; as tabs the choice has to live somewhere.
  // The step can be named in the URL (?design=sld) so another page can send
  // the reader straight to the diagram instead of to the first tab.
  const [designStep, setDesignStep] = useState(() => {
    try {
      const wanted = new URLSearchParams(window.location.search).get("design");
      return ["electrical", "layout", "sld"].includes(wanted) ? wanted : "electrical";
    } catch {
      return "electrical";
    }
  });
  const [autoDesignLoading, setAutoDesignLoading] = useState(false);
  const [autoDesignError, setAutoDesignError] = useState("");
  const [autoDesignResult, setAutoDesignResult] = useState(null);
  const [designSaveNotice, setDesignSaveNotice] = useState(null);
  useEffect(() => { setDesignSaveNotice(null); }, [currentProject?.id]);
  const [autoDesignLayout, setAutoDesignLayout] = useState(null);
  // Preview the current electrical proposal without saving it to the project.
  const layoutPreviewCfg = useMemo(() => layoutPreviewConfig(cfg, autoDesignLayout,
    autoDesignResult?.inverter || cfg?.read_inverter), [cfg, autoDesignLayout, autoDesignResult?.inverter]);
  // On a plant with several planes each layout covers the modules attached to
  // ITS plane, and the string map places only its MPPTs.
  const layoutPlaneSummary = layoutIsMulti
    ? orientationSummary(layoutPreviewCfg).find((s) => s.id === layoutOrientation.id) || null
    : null;
  const layoutSystemDesign = layoutIsMulti
    ? systemDesignForOrientation(layoutPreviewCfg?.systemDesign, orientationList, layoutOrientation.id)
    : (layoutPreviewCfg?.systemDesign || null);
  const layoutPlaneLabel = layoutIsMulti
    ? {
        index: orientationList.indexOf(layoutOrientation) + 1,
        count: orientationList.length,
        name: !layoutOrientation.autoName && layoutOrientation.name ? layoutOrientation.name : null,
        // Every plane of the plant, for the table of planes on each sheet.
        planes: planesForLayoutSheet(layoutPreviewCfg),
      }
    : null;

  // The knobs that produced the last SUCCESSFUL auto-design, captured from the
  // request that was actually sent rather than read back from the dialog at save
  // time. Without this, changing a knob after a run and then saving the layout
  // would record settings that never produced anything.
  const [autoDesignRunMeta, setAutoDesignRunMeta] = useState(null);
  const [autoDesignReportHistory, setAutoDesignReportHistory] = useState([]);
  const [autoDesignReportHistoryLoading, setAutoDesignReportHistoryLoading] = useState(false);
  const [autoDesignReportHistoryError, setAutoDesignReportHistoryError] = useState(false);
  const [autoDesignReportHistoryRevision, setAutoDesignReportHistoryRevision] = useState(0);
  useEffect(() => {
    let active = true;
    if (!currentProject?.id) {
      setAutoDesignReportHistory([]);
      setAutoDesignReportHistoryError(false);
      return undefined;
    }
    setAutoDesignReportHistory([]);
    setAutoDesignReportHistoryLoading(true);
    setAutoDesignReportHistoryError(false);
    listAutoDesignReports(currentProject.id, accessToken)
      .then((reports) => {
        if (active) setAutoDesignReportHistory(reports);
      })
      .catch(() => {
        if (active) setAutoDesignReportHistoryError(true);
      })
      .finally(() => {
        if (active) setAutoDesignReportHistoryLoading(false);
      });
    return () => { active = false; };
  }, [currentProject?.id, accessToken, autoDesignReportHistoryRevision]);
  // The auto-design knobs live in the project config, not in component state.
  // They decide which design is chosen, so a project has to carry them: held
  // locally, they reset to their defaults on every reload and nothing recorded
  // what produced the layout that was saved.
  //
  // Read through DEFAULT_CONFIG values so a project saved before these were
  // persisted still opens with the behaviour it always had.
  const AUTO_DESIGN_DEFAULTS = {
    wCost: 0.8,
    dcacMin: 1.0,
    dcacMax: 1.2,
    // Cell temperatures, not ambient: at the cold end there is no irradiance so
    // the two are the same, and naming the cell at the hot end avoids burying an
    // assumed temperature rise in a field that looks like weather. These are the
    // values the engine used to hardcode.
    minCellC: -10,
    maxCellC: 70,
    ...LEGACY_CAPACITY_RATIOS,
    // How the rows may be built. 1-3 high and least land is what the search
    // always did; the parcel fit and whole strings are opt-in.
    minTiers: 1,
    maxTiers: 3,
    fitToParcel: true,
    setbackM: 0,
    // Land on top of the rows for tracks and stations, when the design is
    // checked against the terrain area rather than laid in a drawn parcel.
    // Ground mounts only; 10% is what the search always assumed.
    landMarginPct: 10,
    wholeStringsPerRow: true,
    // Blank on purpose. This is the target DC size auto-design searches for,
    // and it has no sensible constant: left empty it falls back to the
    // project's installed capacity (see config/autoDesignTarget.js), which is
    // what every project saved before this field existed was designed against.
    targetCapacityKw: "",
    inverterPhases: "both",
    modulesOwnedOnly: false,
    invertersOwnedOnly: false,
    // Ranking among the valid designs (pv_core/design_capacity.py). The
    // sizing reads the same three from the project.
    ...RANKING_DEFAULTS,
  };
  const autoDesignParams = {
    ...AUTO_DESIGN_DEFAULTS,
    ...(cfg?.autoDesign && typeof cfg.autoDesign === "object" ? cfg.autoDesign : {}),
  };
  const setAutoDesignParam = (key, value) => handleField(`autoDesign.${key}`, value);

  // The fields hold raw strings so they stay editable while being typed in, so
  // the payload has to coerce. Falling back to the DEFAULT rather than to zero:
  // a cleared "DC/AC min" meant 0 before, which silently removed the lower
  // bound instead of keeping the one the user could still see on screen.
  const autoDesignNumber = (key) => {
    const raw = autoDesignParams[key];
    // Blank first, and deliberately: Number("") and Number(null) are both 0,
    // which is finite, so a numeric check alone lets an emptied field through as
    // a real zero. Zero is a legitimate cost weight but a meaningless DC/AC
    // bound, and it is never what clearing a box was meant to say.
    if (raw === "" || raw === null || raw === undefined) return AUTO_DESIGN_DEFAULTS[key];
    const value = Number(raw);
    return Number.isFinite(value) ? value : AUTO_DESIGN_DEFAULTS[key];
  };
  const modulePdcKwRef = useRef(null);
  const electricalModule = autoDesignResult?.module || cfg.read_module;
  const electricalInverter = autoDesignResult?.inverter || cfg.read_inverter;
  const electricalMinCell = autoDesignNumber("minCellC");
  const electricalMaxCell = autoDesignNumber("maxCellC");
  const loadElectricalContext = useCallback(async () => {
    const response = await validatePvDesign({
      module: electricalModule, inverter: electricalInverter, system_design: {},
      min_cell_c: electricalMinCell, max_cell_c: electricalMaxCell,
    }, accessToken);
    return response.context;
  // A saved connection changes limits without changing the selected model name.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electricalModule, electricalInverter, electricalMinCell, electricalMaxCell, accessToken, ondConnectionRevision]);

  const tryParseAutoDesignJson = (value) => {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) return undefined;
    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  };

  const stringifyAutoDesignValue = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  /**
   * The engine's own sentence about what to change.
   *
   * The diagnostics object is printed verbatim underneath, which is the right
   * thing for a support ticket and the wrong thing for a reader: "vac: 1" is a
   * count, not a cause. The engine now writes one sentence naming the part and
   * the number that failed, and that sentence belongs above the JSON, not
   * inside it.
   */
  const autoDesignSummary = (...sources) => {
    for (const source of sources) {
      const text =
        typeof source === "string"
          ? source
          : source && typeof source === "object"
          ? source.summary
          : null;
      if (typeof text === "string" && text.trim()) return text.trim();
    }
    return null;
  };

  const formatAutoDesignError = (err) => {
    const fallback = "Auto-design failed";
    if (!err) return { message: fallback, diagnostics: null, summary: null, status: null };

    const status = Number(
      err?.status || err?.data?.status || err?.data?.status_code || err?.data?.statusCode || 0
    );
    if (
      err &&
      typeof err === "object" &&
      typeof err.message === "string" &&
      (err.diagnostics != null || err.status != null || err.summary != null || err.rawResponse != null || err.requestContext != null) &&
      !err.data &&
      !err.responseText
    ) {
      return {
        message: err.message.trim() || fallback,
        diagnostics: err.diagnostics == null ? null : stringifyAutoDesignValue(err.diagnostics),
        summary: autoDesignSummary(err.summary, err.diagnostics),
        limitHit: err.limitHit || err.limit_hit || err.diagnostics?.limit_hit || null,
        candidateGroups: err.candidateGroups ?? err.candidate_groups ?? err.diagnostics?.candidate_groups ?? null,
        maxCandidateGroups: err.maxCandidateGroups ?? err.max_candidate_groups ?? err.diagnostics?.search_limits?.max_candidate_groups ?? null,
        status: status || null,
        rawResponse: err.rawResponse || null,
        requestContext: err.requestContext || null,
      };
    }
    const parsedMessage = tryParseAutoDesignJson(err?.message);
    const parsedResponse = tryParseAutoDesignJson(err?.responseText);
    const data =
      err?.data ||
      parsedResponse ||
      parsedMessage ||
      (typeof err === "string" ? tryParseAutoDesignJson(err) : null) ||
      (err && typeof err === "object" && !err.message ? err : null);

    if (data && typeof data === "object") {
      const detail = data.detail;
      const message =
        (detail && typeof detail === "object" && stringifyAutoDesignValue(detail.message)) ||
        (typeof detail === "string" && detail.trim()) ||
        stringifyAutoDesignValue(data.message) ||
        stringifyAutoDesignValue(data);
      const diagnosticsValue =
        detail && typeof detail === "object" ? detail.diagnostics : null;
      const diagnostics =
        diagnosticsValue != null ? stringifyAutoDesignValue(diagnosticsValue) : null;
      return {
        message: message || fallback,
        diagnostics,
        summary: autoDesignSummary(
          detail && typeof detail === "object" ? detail.summary : null,
          diagnosticsValue
        ),
        limitHit: detail && typeof detail === "object" ? detail.limit_hit || diagnosticsValue?.limit_hit || null : null,
        candidateGroups: detail && typeof detail === "object" ? detail.candidate_groups ?? diagnosticsValue?.candidate_groups ?? null : null,
        maxCandidateGroups: detail && typeof detail === "object" ? detail.max_candidate_groups ?? diagnosticsValue?.search_limits?.max_candidate_groups ?? null : null,
        status: status || Number(data.status || data.status_code || data.statusCode || 0) || null,
      };
    }

    if (typeof err === "string") {
      return { message: err.trim() || fallback, diagnostics: null, summary: null, status: status || null };
    }

    if (typeof err?.message === "string") {
      return { message: err.message.trim() || fallback, diagnostics: null, summary: null, status: status || null };
    }

    return {
      message: stringifyAutoDesignValue(err) || fallback,
      diagnostics: null,
      summary: null,
      status: status || null,
    };
  };

  // The selected inverter's MPPT metadata, kept in step with the selection.
  //
  // The same trap as the module dimensions: this was fetched by
  // openDesignDialog(), which nothing calls now that the design step is a tab.
  // A project that had never run auto-design and had no saved systemDesign
  // opened the electrical design panel with no metadata at all. The fallback to
  // metaFromSystemDesign hid it for saved projects, which is what made it easy
  // to miss.
  useEffect(() => {
    const inverterName = autoDesignResult?.inverter || cfg?.read_inverter;
    if (!inverterName) {
      setDesignMeta(null);
      return undefined;
    }
    let cancelled = false;
    getInverterDesign(inverterName, accessToken, { moduleName: electricalModule, maxCellC: electricalMaxCell })
      .then((data) => {
        if (!cancelled) setDesignMeta(data);
      })
      .catch((err) => {
        // Advisory: the panel falls back to the saved system design.
        console.error("Failed to load inverter design meta", err);
        if (!cancelled) setDesignMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [autoDesignResult?.inverter, cfg?.read_inverter, electricalModule, electricalMaxCell, accessToken, ondConnectionRevision]);

  async function loadDesignMetaByInverter(inverterName, moduleName = electricalModule) {
    if (!inverterName) return;
    try {
      const data = await getInverterDesign(inverterName, accessToken, { moduleName, maxCellC: electricalMaxCell });
      setDesignMeta(data);
    } catch (err) {
      console.error("Failed to load inverter design meta", err);
    }
  }

  const pvSetupSectionSx = {
    p: { xs: 1, md: 1.1 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(167,205,181,0.16)"
        : theme.palette.divider,
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, rgba(22,31,27,0.98) 0%, rgba(14,26,20,0.96) 100%)"
        : "linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%)",
  };

  /**
   * A row of key figures under a selector.
   *
   * Quiet by design: this is reference, not input. Tabular figures so the
   * numbers line up, the label above each in small caps, and the whole strip
   * set on the panel's own ground rather than in a card -- a card here would
   * compete with the selector it describes.
   */
  const pvSetupFigureStrip = ({ figures, note }) => (
    <Box sx={{ mt: 0.7 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: 2.2,
          rowGap: 0.6,
          py: 0.6,
          borderTop: "1px solid",
          borderBottom: note ? "none" : "1px solid",
          borderColor: "divider",
        }}
      >
        {figures.map((f) => (
          <Box key={f.label} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.63rem",
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "text.secondary",
                lineHeight: 1.3,
              }}
            >
              {f.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }}
            >
              {f.value}
            </Typography>
          </Box>
        ))}
      </Box>
      {note ? (
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", fontSize: "0.68rem", pt: 0.35 }}
        >
          {note}
        </Typography>
      ) : null}
    </Box>
  );

  const pvSetupFilterPanelSx = {
    borderRadius: 2,
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(76,175,132,0.22)"
        : "rgba(37,99,235,0.14)",
    // White, like the selector panel beside it: both are panels ON the
    // equipment card, and the card now carries the tint that separates them
    // from it. The old near-white tint made this one blend into that ground
    // instead. What still tells the two panels apart is this one's blue
    // border, which is also what marks it as the filters.
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(18,38,29,0.72)"
        : "rgba(255,255,255,0.96)",
    p: 0.9,
  };

  const pvSetupSelectorPanelSx = {
    borderRadius: 2,
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(167,205,181,0.14)"
        : "rgba(15,23,42,0.08)",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(14,26,20,0.86)"
        : "rgba(255,255,255,0.96)",
    p: 0.9,
    minHeight: 130,
  };
  // Autocomplete's floating label sits above the input border. Leave room
  // between it and the selector panel's heading on both equipment cards.
  // Panel titles ("Module selection", "Catalog filters") now match the blue
  // treatment every other heading at this level already uses - see baseLabelSx,
  // which styles "Degradation curve" and the shading-method label.
  //
  // The page previously had two looks for peer headings: blue 600 for some,
  // black 700 at a custom size for these. That inconsistency was what read as
  // unfinished, not the absence of decoration - so this aligns them with the
  // existing hierarchy rather than adding borders or extra weight, which would
  // only crowd an already dense panel.
  const pvSetupPanelTitleSx = {
    fontWeight: 600,
    color: "primary.main",
    letterSpacing: "0.02em",
  };

  const pvSetupSelectorTitleSx = {
    ...pvSetupPanelTitleSx,
    mb: 1.75,
  };

  const pvSetupEquipmentSelectorRowSx = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 1,
    width: "100%",
  };
  const pvSetupEquipmentAutocompleteSx = {
    flex: "1 1 430px",
    minWidth: { xs: "100%", sm: 320 },
    maxWidth: "100%",
    "& .MuiOutlinedInput-root": { minHeight: 48 },
    "& .MuiInputBase-input": { fontSize: "1rem" },
  };

  const pvSetupFilterStateBadgeSx = (active) => ({
    px: 0.8,
    py: 0.2,
    borderRadius: 999,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: active ? "primary.main" : "text.secondary",
    backgroundColor: (theme) =>
      active
        ? theme.palette.mode === "dark"
          ? "rgba(76,175,132,0.16)"
          : "rgba(37,99,235,0.10)"
        : theme.palette.mode === "dark"
          ? "rgba(167,205,181,0.10)"
          : "rgba(148,163,184,0.12)",
    border: "1px solid",
    borderColor: (theme) =>
      active
        ? theme.palette.mode === "dark"
          ? "rgba(76,175,132,0.30)"
          : "rgba(37,99,235,0.20)"
        : theme.palette.mode === "dark"
          ? "rgba(167,205,181,0.16)"
          : "rgba(148,163,184,0.18)",
  });

  /**
   * Equipment row: the selector, and the catalogue filters beside it.
   *
   * The column widths are FIXED, deliberately. An earlier version narrowed the
   * filter column while the filters were off, to give the width back to the
   * equipment -- and the cost was that switching the filters on shoved the
   * selector sideways. A control that moves its neighbour when you touch it is
   * worse than one that is a little wider than it needs to be. The panel earns
   * its space by growing DOWNWARDS when it opens; nothing moves horizontally.
   */
  const pvSetupEquipmentInnerGridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1.05fr) minmax(320px, 0.95fr)" },
    gap: 0.9,
    alignItems: "start",
  };

  /**
   * The Module card and the Inverter card.
   *
   * These two are the only places on the page where a card CONTAINS cards, and
   * that is what made them hard to read: the section ground faded to pure
   * white, the white selector panel sat on it, and the pale filter panel was a
   * third near-white. Three surfaces within one shade of each other, so the
   * eye had no edge to put the boundary on and the two equipment cards ran
   * together into one field.
   *
   * The fix is contrast between the LEVELS rather than decoration on any one
   * of them: the card keeps a flat tinted ground, and the panels inside stay
   * white, so the inner panels read as resting on the card instead of
   * dissolving into it. Scoped to these two rather than folded into
   * pvSetupSectionSx, which four single-level sections elsewhere also use and
   * which has nothing to separate itself from.
   */
  /*
   * The accent stripe marks a ZONE, which is the one job colour does well on a
   * form this long: after a few visits you stop reading the headings and go to
   * "the orange block" or "the green block". PVsyst does the same thing by
   * tinting the whole group box - its module group is blue and its inverter
   * group green - and it has decades of professional users behind that choice.
   *
   * Taken as a 3px edge rather than a filled ground for two reasons specific
   * to this page. These cards contain white panels, which PVsyst's group boxes
   * do not, and a tinted ground would compete with them for the boundary the
   * eye is looking for. And green is spoken for here: the status chips at the
   * top of the tab are green, so green on an equipment card pulls towards
   * "this section is fine" rather than "this section is the inverter" - which
   * is why the inverter is blue and not green. Sun for the modules, the
   * electrical blue already used across the page for the inverter.
   */
  const pvSetupEquipmentAccent = {
    module: { light: "#ed6c02", dark: "#ffa726" },
    inverter: { light: "#1976d2", dark: "#64b5f6" },
  };

  const pvSetupEquipmentCardSx = (kind) => ({
    ...pvSetupSectionSx,
    p: { xs: 1.15, md: 1.35 },
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(167,205,181,0.26)"
        : "rgba(15,23,42,0.14)",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(20,34,26,0.96)"
        : "rgba(241,245,249,0.82)",
    borderLeft: "3px solid",
    borderLeftColor: (theme) =>
      pvSetupEquipmentAccent[kind][theme.palette.mode === "dark" ? "dark" : "light"],
  });

  /**
   * ...and its title.
   *
   * It was 0.9rem/700 blue, against inner panel titles at essentially the same
   * size and the same blue - a hierarchy of one step expressed as no step at
   * all. Larger, darker, and closed off with a rule across the card, which is
   * what makes it read as the header OF something rather than as one more
   * label inside it.
   *
   * Not uppercased: MUI's default uppercase transform strips accents from
   * Greek, so "ΦΒ πλαίσιο" and "Αντιστροφέας" would lose them.
   */
  const pvSetupEquipmentCardTitleSx = {
    fontWeight: 700,
    fontSize: "1rem",
    letterSpacing: "0.01em",
    color: "text.primary",
    textTransform: "none",
    pb: 0.85,
    mb: 1.15,
    borderBottom: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(167,205,181,0.22)"
        : "rgba(15,23,42,0.12)",
  };

  // Other saved surfaces participate in both drawing and ray casting, using
  // either map coordinates or the common project frame (including heights).
  const roofWorkspaceRef = useRef({});
  const layoutSharedWorkspace = isRoofInstallation(installationType) || installationType === "ground";
  const roofSceneEditsRef = useRef({});
  const roofWorkspaceViewRef = useRef(null);
  const roofWorkspaceFrameRef = useRef({});
  const roofWorkspaceProjectRef = useRef(currentProject?.id);
  if (roofWorkspaceProjectRef.current !== currentProject?.id) {
    roofWorkspaceProjectRef.current = currentProject?.id;
    roofWorkspaceRef.current = {};
    roofSceneEditsRef.current = {};
    roofWorkspaceFrameRef.current = {};
    roofWorkspaceViewRef.current = null;
  }
  const roofFramePlanes = orientationList.map((o, i) => ({ id: o.id,
    spacing: i === 0 ? (cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing) : o.spacingInfo,
    totalModules: orientationSummary(layoutPreviewCfg).find((p) => p.id === o.id)?.modules || 0,
  }));
  const suggestedFrames = roofWorkspaceFrames(roofFramePlanes.map((p) => ({ ...p,
    spacing: { ...p.spacing, site_geometry: { ...p.spacing?.site_geometry,
      frame: p.spacing?.site_geometry?.frame || roofWorkspaceFrameRef.current[p.id] } },
  })));
  if (isRoofInstallation(installationType)) roofWorkspaceFrameRef.current = suggestedFrames;
  const layoutContextPlanes = layoutIsMulti
    ? orientationList
        .filter((o) => o.id !== layoutOrientation.id)
        .map((o) => {
          const i = orientationList.indexOf(o);
          const spacing = roofLayoutSource(i === 0
            ? (cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing || null)
            : (o.spacingInfo || null), installationType);
          const tables = Array.isArray(spacing?.parcel_tables) ? spacing.parcel_tables : [];
          if (!spacing && !isRoofInstallation(installationType) && !roofWorkspaceRef.current[o.id]?.spacing) return null;
          const plane = i === 0
            ? mountingPlane
            : mountingPlaneFor({
                ...cfg,
                panelOrientation: { ...(cfg?.panelOrientation || {}), tilt: o.tilt, azim: o.azim, tiltReference: o.tiltReference,
                  roofPitch: o.roofPitch ?? o.spacingInfo?.site_geometry?.roof?.pitchDeg ?? cfg?.panelOrientation?.roofPitch,
                  roofAzimuth: o.roofAzimuth ?? o.spacingInfo?.site_geometry?.roof?.azimuthDeg ?? cfg?.panelOrientation?.roofAzimuth },
              }, o.spacingInfo || null);
          const descriptor = {
            electricalDesignKey: physicalLayoutDesignKey(systemDesignForOrientation(layoutPreviewCfg.systemDesign, orientationList, o.id)),
            label: `${i + 1}`,
            id: o.id, spacing, primary: i === 0,
            siteGeometry: spacing?.site_geometry?.roof && (isFlushRoof || installationType === "roof_frame")
              ? { ...spacing.site_geometry, roof: { ...spacing.site_geometry.roof,
                  pitchDeg: isFlushRoof ? plane.tilt : (i === 0 ? cfg?.panelOrientation?.roofPitch : (o.roofPitch ?? spacing.site_geometry.roof.pitchDeg)),
                  azimuthDeg: isFlushRoof ? plane.azimuth : (i === 0 ? cfg?.panelOrientation?.roofAzimuth : (o.roofAzimuth ?? spacing.site_geometry.roof.azimuthDeg)) } }
              : spacing?.site_geometry,
            totalModules: mpptAssignments(layoutPreviewCfg.systemDesign, orientationList).filter((a) => a.orientation === o.id).reduce((n, a) => n + a.modules, 0),
            moduleWidth: cfg?.panelOrientation?.moduleOrientation === "landscape" ? physicalLayoutModuleGeometry.length : physicalLayoutModuleGeometry.width,
            slopeTilt: cfg?.loc_info?.tilt, slopeAzimuth: cfg?.loc_info?.azimuth,
            tilt: plane.tilt,
            azimuth: plane.azimuth,
            roofPitch: i === 0 ? cfg?.panelOrientation?.roofPitch : (o.roofPitch ?? spacing?.site_geometry?.roof?.pitchDeg ?? cfg?.panelOrientation?.roofPitch),
            roofAzimuth: i === 0 ? cfg?.panelOrientation?.roofAzimuth : (o.roofAzimuth ?? spacing?.site_geometry?.roof?.azimuthDeg ?? cfg?.panelOrientation?.roofAzimuth),
            tiers: Number(spacing?.row_vertical_modules) || 1,
            clearance: i === 0
              ? cfg?.panelOrientation?.groundClearanceM
              : (o.groundClearanceM ?? cfg?.panelOrientation?.groundClearanceM),
            moduleAlongSlope: cfg?.panelOrientation?.moduleOrientation === "landscape"
              ? physicalLayoutModuleGeometry.width
              : physicalLayoutModuleGeometry.length,
            tables,
          };
          const cached = roofWorkspaceRef.current[o.id];
          const draft = cached?.source === JSON.stringify(spacing || null) ? cached : null;
          if (!isRoofInstallation(installationType)) {
            const current = draft?.spacing || spacing;
            return { ...descriptor, spacing: current, tables: current?.parcel_tables || [],
              siteGeometry: current?.site_geometry || descriptor.siteGeometry,
              tiers: Number(current?.row_vertical_modules) || descriptor.tiers,
              clearance: current?.ground_clearance_m ?? descriptor.clearance,
              previewOnly: Boolean(draft?.dirty || !current?.module_mapping_valid) };
          }
          return roofWorkspacePlane(descriptor, { installationType,
            moduleLength: physicalLayoutModuleGeometry.length, moduleWidth: physicalLayoutModuleGeometry.width,
            portrait: cfg?.panelOrientation?.moduleOrientation !== "landscape",
            frame: suggestedFrames[o.id], draft,
          });
        })
        .filter(Boolean)
    : null;

  const isElectricalDesignSaved = !designUnsaved && hasPvSystemDesign(cfg);
  // On a plant with several planes the layout is saved when EVERY plane's is:
  // the primary's on pv_design, each further plane's on its own entry.
  const layoutPlanesSaved = orientationList.filter((o, i) =>
    isRoofInstallation(installationType)
      ? hasSavedRoofLayout(i === 0 ? (cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing) : o.spacingInfo,
          orientationSummary(layoutPreviewCfg).find((p) => p.id === o.id)?.modules || 0,
          physicalLayoutDesignKey(systemDesignForOrientation(layoutPreviewCfg.systemDesign, orientationList, o.id)))
      : (i === 0 ? Boolean(cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing) : Boolean(o.spacingInfo))
  ).length;
  const isPhysicalLayoutSaved = !layoutUnsaved && layoutPlanesSaved === orientationList.length;
  const planReconciliationIssues = orientationList.flatMap((plane, i) => {
    const check = planChecks[plane.id];
    if (!check?.inconsistent || check.projectId !== currentProject?.id) return [];
    return [isGreekScreen
      ? `Πλευρά ${i + 1}: η κάτοψη έχει ${check.physical} πλαίσια, ο σχεδιασμός ${check.electrical}. Ελλείποντα από strings: ${check.missing}, χωρίς string: ${check.unassigned}. Συμφιλιώστε κάτοψη και ηλεκτρολογικό σχεδιασμό πριν από την εκτίμηση.`
      : `Surface ${i + 1}: the plan has ${check.physical} modules, the design ${check.electrical}. Missing from strings: ${check.missing}; unassigned: ${check.unassigned}. Reconcile the plan and electrical design before estimating.`];
  });
  const sceneReadinessIssues = [...planReconciliationIssues, ...projectSceneReadiness(cfg, isGreekScreen, {
    detailed: cfg?.pvDetailedShading?.enabled === true,
    prepareShading: true,
  })];
  const areTmyExportsReady = isElectricalDesignSaved && isPhysicalLayoutSaved && sceneReadinessIssues.length === 0;
  const designStatusLabel = designUnsaved
    ? (isGreekScreen ? "Μη αποθηκευμένος ηλεκτρολογικός σχεδιασμός" : "Unsaved electrical design")
    : hasPvSystemDesign(cfg)
    ? (isGreekScreen ? "Αποθηκευμένος ηλεκτρολογικός σχεδιασμός" : "Electrical design saved")
    : (isGreekScreen ? "Χωρίς ηλεκτρολογικό σχεδιασμό" : "No electrical design");
  const layoutStatusLabel = layoutUnsaved
    ? (isGreekScreen ? "Μη αποθηκευμένη φυσική διάταξη" : "Unsaved physical layout")
    : layoutPlanesSaved === orientationList.length
    ? (isGreekScreen ? "Αποθηκευμένη φυσική διάταξη" : "Physical layout saved")
    : orientationList.length > 1 && layoutPlanesSaved > 0
    ? (isGreekScreen
        ? `Φυσική διάταξη: ${layoutPlanesSaved} από ${orientationList.length} προσανατολισμούς`
        : `Physical layout: ${layoutPlanesSaved} of ${orientationList.length} orientations`)
    : (isGreekScreen ? "Χωρίς φυσική διάταξη" : "No physical layout");

  const [moduleFiltersEnabled, setModuleFiltersEnabled] = useState(false);
  const [inverterFiltersEnabled, setInverterFiltersEnabled] = useState(false);

  const [moduleFilters, setModuleFilters] = useState({
    minPdc: "",    // W
    maxPdc: "",
    bifacialOnly: false,  // "bifacial only" switch
    ownedOnly: false,     // only this organisation's items, excluding shared ones
    vendor: "",           // exact match, chosen from the catalog's own values
    // Technology is stored as BOTH the display label and the raw values it
    // covers. The label drives the dropdown and the "is anything set" check; the
    // values are what the request sends, since one category can span several
    // (Multicrystalline = mc-Si + EFG mc-Si). Keeping the values here rather
    // than resolving the label at request time means loadCatalog does not have
    // to depend on the derived options list.
    technology: "",
    technologyValues: [],
  });

  const [inverterFilters, setInverterFilters] = useState({
    minPac: "",      // W
    maxPac: "",
    minVdcMax: "",   // V
    maxVdcMax: "",
    minVac: "",      // V, nominal AC grid voltage
    maxVac: "",
    phases: "",      // "" = any, "1" = single-phase, "3" = three-phase
    ownedOnly: false,
    vendor: "",
    model: "",       // conversion model key: "sandia" | "pvwatts" | "adr"
  });

  // Vendor and technology are offered as dropdowns rather than text boxes,
  // populated from /catalog/filter-facets. Free text would mean a single typo or
  // an unexpected spelling ("JinkoSolar" vs "Jinko") returns an empty catalogue
  // with nothing to indicate why, and there is no way for the user to know which
  // spellings the database actually holds.
  // `total` is the unfiltered, visibility-scoped catalog size, used as the
  // denominator in "showing N of M". null until known, so the UI shows a bare
  // count rather than "of 0" before the facets request lands.
  const [catalogFacets, setCatalogFacets] = useState({
    modules: { vendors: [], technologies: [], total: null },
    inverters: { vendors: [], models: [], total: null },
  });

  // Whether anything is actually set, which decides whether "Clear" is offered.
  // Toggling the enable switch off already resets these, but nothing tells the
  // user that, so it is not a discoverable way to clear five fields.
  const moduleFiltersDirty =
    Boolean(moduleFilters.minPdc) ||
    Boolean(moduleFilters.maxPdc) ||
    moduleFilters.bifacialOnly ||
    moduleFilters.ownedOnly ||
    Boolean(moduleFilters.vendor) ||
    Boolean(moduleFilters.technology);

  const inverterFiltersDirty =
    Boolean(inverterFilters.minPac) ||
    Boolean(inverterFilters.maxPac) ||
    Boolean(inverterFilters.minVdcMax) ||
    Boolean(inverterFilters.maxVdcMax) ||
    Boolean(inverterFilters.minVac) ||
    Boolean(inverterFilters.maxVac) ||
    Boolean(inverterFilters.phases) ||
    Boolean(inverterFilters.vendor) ||
    Boolean(inverterFilters.model) ||
    inverterFilters.ownedOnly;

  // Derived from whatever the catalog actually contains, so a category is only
  // offered when at least one module would come back for it.
  const technologyOptions = useMemo(
    () => buildTechnologyOptions(catalogFacets.modules.technologies),
    [catalogFacets.modules.technologies]
  );

  // Conversion-model options for the inverter filter.
  //
  // Labels come from /catalog/parameter-sets (pv_core.parameter_sets), the same
  // source the Add-inverter dialog uses for its "Conversion model" select, so the
  // two cannot describe the same model differently. Only models actually present
  // in the catalog are offered.
  //
  // A model with no entry there still appears, labelled with its raw key: ADR is
  // deliberately not an offered parameter set, but an imported inverter carrying
  // ADRCoefficients would be classified as adr, and hiding it would leave those
  // inverters unreachable through this filter.
  const inverterModelOptions = useMemo(() => {
    const specs = paramSets?.inverters?.sets || [];
    return (catalogFacets.inverters.models || []).map((key) => {
      const spec = specs.find((s) => s.key === key || s.resulting_model === key);
      return { key, label: spec ? inverterSetLabel(spec, isGreekScreen) : inverterModelChip(key) };
    });
  }, [catalogFacets.inverters.models, paramSets, isGreekScreen]);

  const clearModuleFilters = useCallback(() => {
    setModuleFilters({
      minPdc: "",
      maxPdc: "",
      bifacialOnly: false,
      ownedOnly: false,
      vendor: "",
      technology: "",
      technologyValues: [],
    });
  }, []);

  const clearInverterFilters = useCallback(() => {
    setInverterFilters({
      minPac: "",
      maxPac: "",
      minVdcMax: "",
      maxVdcMax: "",
      minVac: "",
      maxVac: "",
      phases: "",
      ownedOnly: false,
      vendor: "",
      model: "",
    });
  }, []);


  const BASE_URL = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");
  if (!BASE_URL) {
    console.error("❌ REACT_APP_API_BASE is not set");
  }
  const [moduleOptions, setModuleOptions] = useState([]);
  const [inverterOptions, setInverterOptions] = useState([]);
  const sortedModuleOptions = useMemo(
    () => sortCatalogOptionsByPower(moduleOptions, "module"), [moduleOptions]
  );
  const sortedInverterOptions = useMemo(
    () => sortCatalogOptionsByPower(inverterOptions, "inverter"), [inverterOptions]
  );
  const moduleLabelByName = useMemo(
    () => new Map(moduleOptions.map((item) => [
      item.name, equipmentOptionLabel(item.name, item, "module", isGreekScreen),
    ])),
    [moduleOptions, isGreekScreen]
  );
  const inverterLabelByName = useMemo(
    () => new Map(inverterOptions.map((item) => [
      item.name, equipmentOptionLabel(item.name, item, "inverter", isGreekScreen),
    ])),
    [inverterOptions, isGreekScreen]
  );
  const moduleOptionLabel = useCallback(
    (name) => moduleLabelByName.get(name) || equipmentOptionLabel(
      name,
      name === cfg.read_module ? { characteristics: moduleChars } : null,
      "module",
      isGreekScreen
    ),
    [moduleLabelByName, cfg.read_module, moduleChars, isGreekScreen]
  );
  const inverterOptionLabel = useCallback(
    (name) => inverterLabelByName.get(name) || equipmentOptionLabel(
      name,
      name === cfg.read_inverter ? { characteristics: inverterChars } : null,
      "inverter",
      isGreekScreen
    ),
    [inverterLabelByName, cfg.read_inverter, inverterChars, isGreekScreen]
  );
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [equipmentReady, setEquipmentReady] = useState(false);

  // Reveal the initial equipment fields together. Later selections and filter
  // refreshes keep the form visible so editing never loses its focus or state.
  useEffect(() => {
    if (!catalogLoading && !moduleCharsLoading && !inverterCharsLoading) {
      setEquipmentReady(true);
    }
  }, [catalogLoading, moduleCharsLoading, inverterCharsLoading]);

  // --- SLD preview dialog state ---
  const [sldOpen, setSldOpen] = useState(false);
  const [sldLoading, setSldLoading] = useState(false);
  const [sldError, setSldError] = useState("");
  const [sldPdfUrl, setSldPdfUrl] = useState("");

  const DEFAULT_SLD_OPTS = {
    use_generation_meter: true,
    use_spd: true,
    use_nvr: true,
    released_for: "CONSTRUCTION",
    designed_by: "METEN",
    drawn_by: "METEN",
    checked_by: "—",
    approved_by: "—",
    drawing_name: "SINGLE LINE DIAGRAM",
    project_title: "SOLAR POWER PLANT",

    // cables
    feeder_cable_label: "1R x 3.5C x 120 sq.mm (TBD)",
    client_panel_cable_label: "1R x 3.5C x 120 sq.mm (TBD)",

    // breakers
    main_breaker_rating: 630,
    main_breaker_voltage: 400,
    main_breaker_type: "MCCB",

    inverter_breaker_rating: 250,
    inverter_breaker_voltage: 400,
    inverter_breaker_type: "MCCB",

    // meter (only used if use_generation_meter)
    meter_accuracy_class: 0.5,
    meter_primary_current: 630,
    meter_secondary_current: 5,
    meter_burden_rating: 15,

    // contactor
    contactor_label: "CONTACTOR",
    contactor_rating: 630,

    // spd (only used if use_spd)
    spd_voltage: 400,
    spd_current_capacity: 40,
    spd_type: 2,
  };

  const [sldOpts, setSldOpts] = useState(DEFAULT_SLD_OPTS);
  const sldLoadingRef = useRef(false);

  const regenTimerRef = useRef(null);
  const handlePreviewSldRef = useRef(null);
  useEffect(() => {
    sldLoadingRef.current = sldLoading;
  }, [sldLoading]);

  // Load the selected module's modelling capability for the Advanced modeling
  // panel. Purely advisory: if it fails the panel just omits the module-specific
  // notes, so there is no error surfaced to the user. The cancelled flag stops a
  // slow response for a previous module overwriting a newer one.
  useEffect(() => {
    const name = cfg?.read_module;
    if (!name) {
      setDetailedModule(null);
      setModuleModeling(null);
      setModuleIsBifacial(false);
      setModuleModelingLoading(false);
      return undefined;
    }
    let cancelled = false;
    setModuleModelingLoading(true);
    setDetailedModule(null);
    (async () => {
      try {
        const data = await getModuleDetails(name);
        if (!cancelled) {
          setDetailedModule(data);
          setModuleModeling(data?.modeling || null);
          setModuleIsBifacial(
            Number(data?.characteristics?.Bifacial ?? 0) >= 0.5
          );
        }
      } catch {
        if (!cancelled) {
          setDetailedModule(null);
          setModuleModeling(null);
          setModuleIsBifacial(false);
        }
      } finally {
        if (!cancelled) setModuleModelingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cfg?.read_module]);

  useEffect(() => {
    if (!sldOpen) return;
    if (sldLoadingRef.current) return; // don't queue while in-flight

    if (regenTimerRef.current) clearTimeout(regenTimerRef.current);

    regenTimerRef.current = setTimeout(() => {
      handlePreviewSldRef.current?.();
    }, 450);

    return () => {
      if (regenTimerRef.current) clearTimeout(regenTimerRef.current);
    };
  }, [sldOpen, sldOpts]); // ✅ IMPORTANT: NO sldLoading here


  const pickFirstNum = (obj, keys, toNum) => {
    for (const k of keys) {
      const v = toNum(obj?.[k]);
      if (v != null) return v;
    }
    return undefined;
  };
  const countFromSystemDesign = (systemDesign) => {
    let modules = 0;
    let strings = 0;
    let inverters = 0;
    let mppts = 0;

    if (!systemDesign || typeof systemDesign !== "object") {
      return { modules, strings, inverters, mppts };
    }

    const invKeys = Object.keys(systemDesign);
    inverters = invKeys.length;

    invKeys.forEach((invKey) => {
      const mpptList = Array.isArray(systemDesign[invKey]) ? systemDesign[invKey] : [];
      mppts += mpptList.length;

      mpptList.forEach((mppt) => {
        const inputs = Array.isArray(mppt?.inputs) ? mppt.inputs : [];
        inputs.forEach((inp) => {
          const mps = Number(inp?.modules_per_string);
          if (Number.isFinite(mps) && mps > 0) {
            strings += 1;
            modules += mps;
          }
        });
      });
    });

    return { modules, strings, inverters, mppts };
  };


  async function handlePreviewSld() {
    setSldError("");
    setSldLoading(true);

    try {
      if (!BASE_URL) throw new Error("REACT_APP_API_BASE is not set.");
      if (!cfg?.systemDesign || !Object.keys(cfg.systemDesign || {}).length) {
        throw new Error("No PV design found. Please run 'Design PV system' and Save first.");
      }
      if (!cfg.read_module) throw new Error("Select a PV module first.");
      if (!cfg.read_inverter) throw new Error("Select an inverter first.");

      const toNum = (v) => {
        const n = Number(String(v ?? "").replace(",", "."));
        return Number.isFinite(n) ? n : undefined;
      };

      const modDets = await getModuleDetails(cfg.read_module);
      const invDets = await getInverterDetails(cfg.read_inverter);

      const modChars = normalizeChars(modDets?.characteristics);
      const invChars = normalizeChars(invDets?.characteristics);

      // ---------- MODULE ELECTRICAL ----------
      const moduleWp =
        pickFirstNum(modChars, ["STC", "Pmp0"], toNum) ??
        (toNum(modChars.Imp0 ?? modChars.Impo) != null && toNum(modChars.Vmp0 ?? modChars.Vmpo) != null
          ? toNum(modChars.Imp0 ?? modChars.Impo) * toNum(modChars.Vmp0 ?? modChars.Vmpo)
          : undefined);

      const moduleVmp  = toNum(modChars.V_mp_ref ?? modChars.Vmp0 ?? modChars.Vmpo);
      const moduleImp  = toNum(modChars.I_mp_ref ?? modChars.Imp0 ?? modChars.Impo);
      const moduleVoc  = toNum(modChars.V_oc_ref ?? modChars.Voc0 ?? modChars.Voco);
      const moduleIsc  = toNum(modChars.I_sc_ref ?? modChars.Isc0 ?? modChars.Isco);

      // ---------- MODULE PHYSICAL ----------
      const moduleType = modChars.Technology || modChars.Material || "";
      const moduleWidth = pickFirstNum(modChars, ["Width", "width"], toNum);
      const moduleLength = pickFirstNum(modChars, ["Length", "length"], toNum);

      // ---------- INVERTER ELECTRICAL ----------
      const pacW = pickFirstNum(invChars, ["Paco"], toNum);
      const inverterPowerKw = pacW != null ? pacW / 1000 : undefined;

      const vdcMax = pickFirstNum(invChars, ["Vdcmax", "Vdc_max", "VdcMax"], toNum);
      const mpptLow = pickFirstNum(invChars, ["Mppt_low", "MPPT_low"], toNum);
      const mpptHigh = pickFirstNum(invChars, ["Mppt_high", "MPPT_high"], toNum);

      const inputsPerMppt = pickFirstNum(invChars, ["Num Inputs per MPPT"], toNum);

      const phases = pickFirstNum(invChars, ["Phases"], toNum);

      // "Max DC per MPPT" is often NOT directly available.
      // If your DB has it, add its key here; otherwise we can only approximate from Idcmax / NumMPPTs.

      const moduleTilt = toNum(cfg?.panelOrientation?.tilt);
      const moduleAzimuth = toNum(cfg?.panelOrientation?.azim);

      const feederCableLabel = String(sldOpts.feeder_cable_label || "").trim() || "TBD";
      const clientPanelCableLabel = String(sldOpts.client_panel_cable_label || "").trim() || feederCableLabel;

      const plant = {
        inputsPerMppt: inputsPerMppt,
        feederCableLabel,
        moduleWp: moduleWp != null ? Math.round(moduleWp) : null,
        inverterKw: inverterPowerKw != null ? Number(inverterPowerKw.toFixed(3)) : null,

        // The plant's DC/AC/module figures are NOT sent: api/sld/renderer.py
        // derives every one of them from systemDesign and the catalog records,
        // so a second copy here could only ever disagree with the sheet it is
        // printed on. They used to be sent as plant.pv.*, which nothing read.
        //
        // Tilt, azimuth and the mount DID need to arrive - the title block
        // prints them - but the resolver looks for them under `project`, not
        // under `plant`. They are sent there now, below.

        module: {
          model: cfg.read_module,
          type: moduleType,
          dimensions_m: (moduleWidth != null && moduleLength != null)
            ? `${moduleWidth} x ${moduleLength}`
            : undefined,
          voc_v: moduleVoc,
          vmp_v: moduleVmp,
          isc_a: moduleIsc,
          imp_a: moduleImp,
        },

        inverter: {
          model: cfg.read_inverter,
          power_kw: inverterPowerKw,
          phases,
          vdc_max_v: vdcMax,
          mppt_range_v: (mpptLow != null && mpptHigh != null) ? [mpptLow, mpptHigh] : undefined,
        },

        // your existing blocks
        clientPanel: { label: "TO CLIENT PANEL", cable: clientPanelCableLabel },

        main_breaker: {
          rating: Number(sldOpts.main_breaker_rating) || 0,
          voltage: Number(sldOpts.main_breaker_voltage) || 0,
          type: sldOpts.main_breaker_type || "MCCB",
        },
        inverter_breaker: {
          rating: Number(sldOpts.inverter_breaker_rating) || 0,
          voltage: Number(sldOpts.inverter_breaker_voltage) || 0,
          type: sldOpts.inverter_breaker_type || "MCCB",
        },
        // include ONLY if enabled
        ...(sldOpts.use_generation_meter ? {
          meter: {
            accuracy_class: Number(sldOpts.meter_accuracy_class) || 0,
            primary_current: Number(sldOpts.meter_primary_current) || 0,
            secondary_current: Number(sldOpts.meter_secondary_current) || 0,
            burden_rating: Number(sldOpts.meter_burden_rating) || 0,
          }
        } : {}),

        ...(sldOpts.use_spd ? {
          spd: {
            voltage: Number(sldOpts.spd_voltage) || 0,
            current_capacity: Number(sldOpts.spd_current_capacity) || 0,
            type: Number(sldOpts.spd_type) || 2,
          }
        } : {}),

        contactor: {
          label: String(sldOpts.contactor_label || "CONTACTOR").trim(),
          rating: Number(sldOpts.contactor_rating) || 0,
        },

        use_generation_meter: Boolean(sldOpts.use_generation_meter),
        use_spd: Boolean(sldOpts.use_spd),
        use_nvr: Boolean(sldOpts.use_nvr),

        releasedFor: sldOpts.released_for || "",
        designedBy: sldOpts.designed_by || "",
        drawnBy: sldOpts.drawn_by || "",
        checkedBy: sldOpts.checked_by || "",
        approvedBy: sldOpts.approved_by || "",
        drawingName: sldOpts.drawing_name || "",
        projectTitle: sldOpts.project_title || "",

      };

      const res = await fetch(`${BASE_URL}/pv/sld.pdf`, {
        method: "POST",
        headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
        body: JSON.stringify({
          reportBranding,
          reportLanguage: getReportLanguage(cfg),
          inverterName: cfg.read_inverter || "",
          moduleName: cfg.read_module || "",
          systemDesign: cfg.systemDesign,
          plant,
          // The sheet's title block reads these from `project` and nowhere
          // else (api/sld/resolver.py). Sent under plant.pv.* they were
          // silently dropped, and the "Mount / tilt / azimuth" row printed
          // "TBD / ° / °" on every diagram the application has produced.
          //
          // Only the fields with a real source are sent. The resolver's own
          // "TBD" is the honest answer for the rest, and filling them with a
          // guess would put an invented drawing number on a construction
          // document.
          project: {
            name: sldOpts.project_title || currentProject?.name || undefined,
            mount_type: cfg?.mountType || undefined,
            tilt_deg: moduleTilt,
            azimuth_deg: moduleAzimuth,
            status: sldOpts.released_for || undefined,
            prepared_by: sldOpts.designed_by || undefined,
            checked_by: sldOpts.checked_by || undefined,
            approved_by: sldOpts.approved_by || undefined,
          },
        }),
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = String(j.detail);
        } catch {}
        throw new Error(msg);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setSldPdfUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } catch (e) {
      setSldError(e?.message || "Failed to generate SLD preview");
    } finally {
      setSldLoading(false);
    }
  }
  handlePreviewSldRef.current = handlePreviewSld;



  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(null);

    try {
      const modParams = new URLSearchParams();
      if (moduleFiltersEnabled) {
        if (moduleFilters.minPdc) modParams.append("min_pdc", moduleFilters.minPdc);
        if (moduleFilters.maxPdc) modParams.append("max_pdc", moduleFilters.maxPdc);
        if (moduleFilters.bifacialOnly) modParams.append("bifacial_only", "true");
        if (moduleFilters.ownedOnly) modParams.append("owned_only", "true");
        if (moduleFilters.vendor) modParams.append("vendor", moduleFilters.vendor);
        // One param per raw value in the chosen category. The backend matches on
        // any of them, so "Amorphous Silicon" sends both 2-a-Si and 3-a-Si.
        (moduleFilters.technologyValues || []).forEach((v) =>
          modParams.append("technology", v)
        );
      }

      const invParams = new URLSearchParams();
      if (inverterFiltersEnabled) {
        if (inverterFilters.minPac) invParams.append("min_pac", inverterFilters.minPac);
        if (inverterFilters.maxPac) invParams.append("max_pac", inverterFilters.maxPac);
        if (inverterFilters.minVdcMax) invParams.append("min_vdc_max", inverterFilters.minVdcMax);
        if (inverterFilters.maxVdcMax) invParams.append("max_vdc_max", inverterFilters.maxVdcMax);
        if (inverterFilters.minVac) invParams.append("min_vac", inverterFilters.minVac);
        if (inverterFilters.maxVac) invParams.append("max_vac", inverterFilters.maxVac);
        if (inverterFilters.phases) invParams.append("phases", inverterFilters.phases);
        if (inverterFilters.ownedOnly) invParams.append("owned_only", "true");
        if (inverterFilters.vendor) invParams.append("vendor", inverterFilters.vendor);
        if (inverterFilters.model) invParams.append("model", inverterFilters.model);
      }
      const [modsRes, invRes] = await Promise.all([
        fetch(`${BASE_URL}/catalog/modules?${modParams.toString()}`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }),
        fetch(`${BASE_URL}/catalog/inverters?${invParams.toString()}`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }),
      ]);

      if (!modsRes.ok || !invRes.ok) throw new Error("Catalog HTTP error");

      const [modsJson, invJson] = await Promise.all([modsRes.json(), invRes.json()]);
      setModuleOptions(dedupeCatalogOptions(modsJson));
      setInverterOptions(dedupeCatalogOptions(invJson));
    } catch (err) {
      console.error("Failed to load PV catalog:", err);
      setCatalogError("Could not load PV catalog from server.");
    } finally {
      setCatalogLoading(false);
    }
  }, [moduleFiltersEnabled, inverterFiltersEnabled, moduleFilters, inverterFilters, accessToken, BASE_URL]);

  // NOTE ON PLACEMENT: this block must stay BELOW loadCatalog and the
  // moduleOptions / inverterOptions state. Its dependency arrays are evaluated
  // as this line runs, so declaring it earlier threw
  //   Uncaught ReferenceError: Cannot access 'moduleOptions' before initialization
  // and rendered the whole PV System page blank - a temporal dead zone error, not
  // a logic one, so it parsed and linted cleanly.
  // ---------------- Catalogue deletion ----------------
  //
  // Ownership comes from the catalogue response (`owned` / `shared` per item), so
  // the UI never has to know or guess organisation names. The server re-checks
  // every delete against the caller's profile; these flags only decide whether a
  // control is worth showing.
  const [catalogDelete, setCatalogDelete] = useState(null); // { kind, name } | null
  const [catalogDeleteBusy, setCatalogDeleteBusy] = useState(false);
  const [catalogDeleteError, setCatalogDeleteError] = useState("");

  const findCatalogEntry = useCallback(
    (kind, name) => {
      if (!name) return null;
      const list = kind === "module" ? moduleOptions : inverterOptions;
      return (list || []).find((item) => item?.name === name) || null;
    },
    [moduleOptions, inverterOptions]
  );

  const canDeleteSelected = useCallback(
    (kind) => {
      const name = kind === "module" ? cfg.read_module : cfg.read_inverter;
      const entry = findCatalogEntry(kind, name);
      if (!entry) return false;
      if (entry.owned) return true;
      // Shared library items are administrator-only, matching the backend rule.
      return Boolean(entry.shared && isAdminUser);
    },
    [cfg.read_module, cfg.read_inverter, findCatalogEntry, isAdminUser]
  );

  const requestCatalogDelete = useCallback((kind, name) => {
    setCatalogDeleteError("");
    setCatalogDelete({ kind, name });
  }, []);

  const confirmCatalogDelete = useCallback(async () => {
    if (!catalogDelete) return;
    const { kind, name } = catalogDelete;
    setCatalogDeleteBusy(true);
    setCatalogDeleteError("");
    try {
      if (kind === "module") await deleteModule(name, accessToken);
      else await deleteInverter(name, accessToken);

      // Clear the selection: leaving a deleted name in the config would save a
      // project that cannot run, which is the failure this whole change is meant
      // to make visible rather than latent.
      if (kind === "module" && cfg.read_module === name) handleField("read_module", "");
      if (kind === "inverter" && cfg.read_inverter === name) handleField("read_inverter", "");

      setCatalogDelete(null);
      await loadCatalog();
    } catch (err) {
      // Show the server's own explanation - it distinguishes "shared item, admin
      // required" from "not found" - rather than a generic failure.
      setCatalogDeleteError(String(err?.message || err) || "Could not delete the item.");
    } finally {
      setCatalogDeleteBusy(false);
    }
  }, [catalogDelete, accessToken, cfg.read_module, cfg.read_inverter, handleField, loadCatalog]);


  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Facets are the full set of vendors/technologies in the catalogue, so they are
  // loaded once and NOT refetched when a filter changes. Deriving them from the
  // filtered results instead would collapse the dropdown to whatever is currently
  // selected, leaving no way to switch to another vendor without clearing first.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/catalog/filter-facets`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!active) return;
        const asCount = (v) => (Number.isFinite(v) && v >= 0 ? v : null);
        setCatalogFacets({
          modules: {
            vendors: Array.isArray(json?.modules?.vendors) ? json.modules.vendors : [],
            technologies: Array.isArray(json?.modules?.technologies) ? json.modules.technologies : [],
            total: asCount(json?.modules?.total),
          },
          inverters: {
            vendors: Array.isArray(json?.inverters?.vendors) ? json.inverters.vendors : [],
            models: Array.isArray(json?.inverters?.models) ? json.inverters.models : [],
            total: asCount(json?.inverters?.total),
          },
        });
      } catch (err) {
        // Non-fatal on purpose: the numeric filters and the catalogue itself still
        // work. The vendor/technology dropdowns simply render empty and disabled
        // rather than blocking equipment selection.
        console.error("Failed to load catalog filter facets:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, BASE_URL]);

  // Two effects stood here, both mirroring PV capacity into the auto-design
  // knobs. The first spread `prev` and changed nothing at all; the second wrote
  // a `pvCapacityKw` field that no reader ever consumed -- the payload takes the
  // capacity straight from cfg.pvInstalledCap.kw. Removed rather than carried
  // over: with the knobs now living in the project config, a phantom field would
  // have been persisted into every project and shown up as an unrecognised key.

  /**
   * Auto-design provenance to store with the design being saved.
   *
   * Both save paths need it and for opposite reasons: the electrical-design save
   * rebuilds pv_design from scratch and would otherwise DROP an existing stamp,
   * while the physical-layout save spreads the previous design and would other-
   * wise KEEP a stale one after a fresh run. A run in this session wins; failing
   * that, whatever the project already recorded is carried forward.
   *
   * Note this records the run, not the final geometry: if the pitch was edited
   * by hand afterwards, spacingInfo.mode says so.
   */
  const autoDesignStamp = () =>
    (autoDesignRunMeta?.projectId === currentProject?.id ? autoDesignRunMeta : null) ||
    structuredPvDesign?.autoDesign ||
    persistedPvDesign?.autoDesign ||
    null;

  const requiredAutoInputs = useCallback(() => {
    const missing = [];
    if (cfg?.loc_info?.lat == null && cfg?.lat == null) missing.push("Latitude");
    if (cfg?.loc_info?.lon == null && cfg?.lon == null) missing.push("Longitude");
    if (cfg?.roof_area == null || cfg.roof_area === "") missing.push("Roof area");
    // A flush array lies on the roof: its plane IS the roof's pitch and facing.
    const tiltKey = isFlushRoof ? "roofPitch" : "tilt";
    const azimuthKey = isFlushRoof ? "roofAzimuth" : "azim";
    if (cfg?.panelOrientation?.[tiltKey] == null || cfg.panelOrientation[tiltKey] === "")
      missing.push(isFlushRoof ? "Roof pitch" : "Tilt");
    if (cfg?.panelOrientation?.[azimuthKey] == null || cfg.panelOrientation[azimuthKey] === "")
      missing.push(isFlushRoof ? "Roof facing" : "Azimuth");
    // Module and inverter are optional for auto-design; engine can choose if missing
    // The TARGET, which falls back to the installed capacity on projects saved
    // before the target field existed. Naming the field the user has to fill,
    // not the one it happens to fall back to.
    if (resolveTargetKw(cfg) === null) missing.push("Target DC size");
    return missing;
  }, [cfg, isFlushRoof]);

  // The coarse sweep behind the curves. Keyed on the things that change the
  // SURFACE -- location, ground, objective, and the models the sweep shares with
  // the simulation -- and deliberately NOT on tilt or azimuth: those move the
  // slice and the marker, which is done in the browser from the grid below.
  const orientationPinnedGcr = orientationGcr(activeSpacing, autoDesignLayout, activeIsPrimary);
  const orientationResultKey = orientationOptimizationKey(cfg, orientationObjective, orientationPinnedGcr);
  useEffect(() => {
    const lat = Number(cfg?.loc_info?.lat ?? cfg?.lat);
    const lon = Number(cfg?.loc_info?.lon ?? cfg?.lon);
    const restored = restoredOrientationResult(savedOrientationOptimization, orientationResultKey);
    setOrientationResult(restored);
    setOrientationPreview(null);
    if (restored || isFlushRoof || !Number.isFinite(lat) || !Number.isFinite(lon) || !cfg?.surfaceType) {
      setOrientationPreviewBusy(false);
      return undefined;
    }
    let cancelled = false;
    setOrientationPreviewBusy(true);
    const timer = setTimeout(async () => {
      try {
        const po = cfg?.panelOrientation || {};
        const res = await optimizeOrientation(
          {
            lat,
            lon,
            surface_type: cfg.surfaceType,
            transposition_model: po.transpositionModel,
            aoi_model: po.aoiModel,
            thermal_model: po.thermalModel,
            thermal_settings: po.thermalSettings,
            installation_type: po.installationType,
            mounting_config: po.mountingConfig,
            array_geometry: po.arrayGeometry,
            horizon_profile: po.horizonProfile,
            objective: orientationObjective,
            tilt_step: 5,
            azimuth_step: 10,
            include_grid: true,
            ...(Number.isFinite(orientationPinnedGcr) && orientationPinnedGcr > 0 && orientationPinnedGcr < 1
              ? { gcr: orientationPinnedGcr }
              : {}),
          },
          accessToken
        );
        if (!cancelled) setOrientationPreview(res);
      } catch {
        // A chart that could not be drawn must never interrupt the form.
        if (!cancelled) setOrientationPreview(null);
      } finally {
        if (!cancelled) setOrientationPreviewBusy(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedOrientationOptimization, orientationResultKey, activeOrientation.id, currentProject?.id,
    cfg?.loc_info?.lat, cfg?.loc_info?.lon, cfg?.surfaceType,
    orientationObjective, isFlushRoof,
    cfg?.panelOrientation?.transpositionModel,
    cfg?.panelOrientation?.aoiModel,
    cfg?.panelOrientation?.thermalModel,
    cfg?.panelOrientation?.thermalSettings,
    cfg?.panelOrientation?.installationType,
    cfg?.panelOrientation?.mountingConfig,
    cfg?.panelOrientation?.arrayGeometry,
    cfg?.panelOrientation?.horizonProfile,
    orientationPinnedGcr,
    accessToken,
  ]);

  // What the two charts draw. The fine sweep wins when one has been run;
  // otherwise the coarse preview. Either way the slices are cut HERE, from the
  // grid, at whatever tilt and azimuth are in the fields right now -- so editing
  // either one moves the curve and the marker without touching the server.
  /**
   * The two mini-plots, re-sliced from the grid as the user types.
   *
   * ONE RULE, IN TWO LANGUAGES -- AND A TEST THAT PINS THEM TOGETHER.
   * The backend returns `profiles` and `current` computed exactly this way; this
   * repeats the arithmetic only so the curves follow the tilt field without a
   * round trip on every keystroke. `orientationInterpolation.js` holds the rule
   * and pv/orientation.test.jsx checks it against the backend's own answers, so
   * the two cannot drift the way the loss ladder did.
   *
   * The first version SNAPPED to the nearest grid point. On the quick 5 degree /
   * 10 degree preview that drew a project at 28 deg / 177 deg as 30 / 175 and
   * reported it as 0.00% below the optimum -- because the rounding had moved it
   * onto the optimum. The number was measuring the rounding.
   */
  const orientationChartCapacityKw = orientationList.length === 1
    ? Number(cfg?.pvInstalledCap?.kw)
    : NaN;
  const orientationChart = useMemo(() => {
    const source = orientationResult?.grid ? orientationResult : orientationPreview;
    const grid = source?.grid;
    if (!grid || grid.length === 0) return null;

    const tilt = toFiniteNumber(activeTilt);
    const azimuth = toFiniteNumber(activeAzim);
    const sliceAzimuth = azimuth ?? source.optimal_azimuth;
    const sliceTilt = tilt ?? source.optimal_tilt;

    const sliced = sliceOrientationGrid(grid, {
      tilt: sliceTilt,
      azimuth: sliceAzimuth,
    });
    if (!sliced) return null;

    const here = tilt != null && azimuth != null ? sliced.current : null;
    const globalBest = Math.max(...grid.map((g) => g.kwh_per_kwdc));
    // Tilt-only mode reads the best tilt off the slice at the azimuth in the
    // field, so the caption and the loss answer the question that was asked.
    const fixed = keepAzimuth && azimuth != null;
    const tiltPeak = sliced.tiltPoints.reduce(
      (best, p) => (p.y != null && (!best || p.y > best.y) ? p : best),
      null
    );
    const bestScore = fixed && tiltPeak ? tiltPeak.y : globalBest;

    const capacityKw = Number.isFinite(orientationChartCapacityKw) && orientationChartCapacityKw > 0
      ? orientationChartCapacityKw : null;
    const scale = capacityKw || 1;
    const period = (source.objective || orientationObjective) === "year";
    return {
      objective: source.objective || orientationObjective,
      axisLabel: capacityKw
        ? (isGreekScreen ? `Κατακόρυφος άξονας: ${period ? "ετήσια" : "εποχική"} παραγωγή DC (kWh)` : `Y-axis: ${period ? "annual" : "seasonal"} DC production (kWh)`)
        : (isGreekScreen ? `Κατακόρυφος άξονας: ειδική ${period ? "ετήσια" : "εποχική"} παραγωγή DC (kWh/kWp)` : `Y-axis: specific ${period ? "annual" : "seasonal"} DC yield (kWh/kWp)`),
      capacityKw,
      optimalTilt: fixed && tiltPeak ? tiltPeak.x : source.optimal_tilt,
      optimalAzimuth: fixed ? azimuth : source.optimal_azimuth,
      fixed,
      // What letting the azimuth move as well would add, in tilt-only mode.
      freeGainPct: fixed && tiltPeak && tiltPeak.y
        ? (100 * (globalBest - tiltPeak.y)) / tiltPeak.y
        : null,
      atAzimuth: Math.round(sliceAzimuth * 10) / 10,
      atTilt: Math.round(sliceTilt * 10) / 10,
      tiltPoints: sliced.tiltPoints.map((point) => ({ ...point, y: point.y == null ? null : point.y * scale })),
      azimuthPoints: sliced.azimuthPoints.map((point) => ({ ...point, y: point.y == null ? null : point.y * scale })),
      current: here != null ? { tilt: sliceTilt, azimuth: sliceAzimuth, y: here * scale } : null,
      lossPct: here != null && bestScore ? (100 * (here - bestScore)) / bestScore : null,
      // A coarse preview is not the recommendation, and the caption says so
      // rather than letting a 5 degree grid read as a precise answer.
      coarse: !orientationResult?.grid,
    };
  }, [orientationResult, orientationPreview, orientationObjective, activeTilt, activeAzim, keepAzimuth, orientationChartCapacityKw, isGreekScreen]);

  const handleOptimizeOrientation = async () => {
    const lat = cfg?.loc_info?.lat ?? cfg?.lat;
    const lon = cfg?.loc_info?.lon ?? cfg?.lon;
    const surfaceType = cfg?.surfaceType;

    if (lat == null || lat === "" || lon == null || lon === "") {
      setOrientationSuccessOpen(false);
      setOrientationError("Latitude and longitude are required to optimize panel orientation");
      return;
    }
    if (!surfaceType) {
      setOrientationSuccessOpen(false);
      setOrientationError("Surface type is required to optimize panel orientation");
      return;
    }

    setOrientationError("");
    setOrientationSuccessOpen(false);
    setOrientationLoading(true);

    try {
      // The modelling choices the sweep must share with the simulation, sent
      // from the live form rather than the saved project so the answer reflects
      // what is on screen, unsaved edits included. Mount type is deliberately
      // not sent: a tilt search is a fixed-array question, and a tracker
      // computes its tilt hour by hour.
      //
      // Row spacing IS sent, but only once a layout exists. Before that the
      // pitch is derived from the tilt being searched for, so sending it would
      // be circular -- and the sweep self-corrects anyway, because the spacing
      // rule loosens the rows as the tilt steepens. Once a pitch is pinned that
      // self-correction is gone, and a dense layout optimised free-standing can
      // be off by many degrees.
      const po = cfg?.panelOrientation || {};
      const fixedAzimuth = keepAzimuth ? toFiniteNumber(activeAzim) : null;
      const res = await optimizeOrientation(
        {
          lat,
          lon,
          surface_type: surfaceType,
          transposition_model: po.transpositionModel,
          aoi_model: po.aoiModel,
          thermal_model: po.thermalModel,
            thermal_settings: po.thermalSettings,
            installation_type: po.installationType,
          mounting_config: po.mountingConfig,
          array_geometry: po.arrayGeometry,
          horizon_profile: po.horizonProfile,
          objective: orientationObjective,
          // Sent so the profile curves are sliced where the project SITS, and
          // so the sweep can report what the current orientation costs against
          // the best it found.
          current_tilt: toFiniteNumber(activeTilt),
          current_azimuth: toFiniteNumber(activeAzim),
          include_grid: true,
          ...(fixedAzimuth != null ? { fixed_azimuth: fixedAzimuth } : {}),
          ...(Number.isFinite(orientationPinnedGcr) && orientationPinnedGcr > 0 && orientationPinnedGcr < 1
            ? { gcr: orientationPinnedGcr }
            : {}),
        },
        accessToken
      );

      const optimalTilt = Number(res?.optimal_tilt);
      // Tilt-only: the azimuth field is left exactly as typed.
      const optimalAzimuth = fixedAzimuth != null ? fixedAzimuth : Number(res?.optimal_azimuth);

      if (!Number.isFinite(optimalTilt) || !Number.isFinite(optimalAzimuth)) {
        throw new Error("Orientation optimization returned invalid tilt/azimuth");
      }

      // Written to the plane on screen: the primary's fields, or the
      // selected further orientation.
      const nextWithAzimuth = activeIsPrimary
        ? setPathInObject(
            setPathInObject(cfg, "panelOrientation.tilt", optimalTilt),
            "panelOrientation.azim",
            optimalAzimuth
          )
        : updateOrientation(cfg, activeOrientation.id, { tilt: optimalTilt, azim: optimalAzimuth });
      const nextWithSurfaceType = res?.surface_type
        ? { ...nextWithAzimuth, surfaceType: res.surface_type }
        : nextWithAzimuth;

      const result = {
        orientationId: activeOrientation.id,
        optimal_tilt: optimalTilt,
        optimal_azimuth: optimalAzimuth,
        annual_kwh_per_kwdc: res?.annual_kwh_per_kwdc ?? null,
        surface_type: res?.surface_type || surfaceType,
        objective: res?.objective || orientationObjective,
        season_months: res?.season_months || null,
        profiles: res?.profiles || null,
        // The orientation the project had when the sweep ran. Kept as it was
        // SENT, not re-read afterwards: the tilt and azimuth fields are about
        // to be overwritten with the optimum, and a "now" marker that moves to
        // sit on the peak would tell the reader nothing.
        current: res?.current || null,
        grid: res?.grid || null,
      };
      history.commit({
        ...nextWithSurfaceType,
        orientationOptimization: {
          ...nextWithSurfaceType.orientationOptimization,
          [activeOrientation.id]: {
            key: orientationOptimizationKey(nextWithSurfaceType, orientationObjective, orientationPinnedGcr),
            keepAzimuth,
            result,
          },
        },
      });
      setOrientationResult(result);
      setOrientationError("");
      setOrientationSuccessOpen(true);
    } catch (err) {
      setOrientationSuccessOpen(false);
      setOrientationError(formatAutoDesignError(err).message);
    } finally {
      setOrientationLoading(false);
    }
  };

  useEffect(() => {
    if (!orientationSuccessOpen) return undefined;
    const timeout = window.setTimeout(() => {
      setOrientationSuccessOpen(false);
    }, 3000);
    return () => window.clearTimeout(timeout);
  }, [orientationSuccessOpen]);

  const getModulePdcKw = useCallback(async (moduleName = cfg?.read_module) => {
    if (!moduleName) return null;
    const useSavedModuleCache = moduleName === cfg?.read_module;
    if (useSavedModuleCache && modulePdcKwRef.current != null) return modulePdcKwRef.current;
    try {
      const details = await getModuleDetails(moduleName, accessToken);
      const chars = normalizeChars(details?.characteristics);
      const pdcW =
        chars?.STC ??
        chars?.Pmp0 ??
        ((chars?.Imp0 ?? chars?.Impo) != null && (chars?.Vmp0 ?? chars?.Vmpo) != null
          ? Number(chars?.Imp0 ?? chars?.Impo) * Number(chars?.Vmp0 ?? chars?.Vmpo)
          : undefined);
      const kw = Number.isFinite(Number(pdcW)) && Number(pdcW) > 0 ? Number(pdcW) / 1000 : null;
      if (useSavedModuleCache) modulePdcKwRef.current = kw;
      return kw;
    } catch {
      return null;
    }
  }, [cfg?.read_module, accessToken]);

  const getSelectedModuleGeometry = useCallback(async (moduleName) => {
    const targetModule = moduleName || cfg?.read_module;
    if (!targetModule) return null;
    try {
      const details = await getModuleDetails(targetModule, accessToken);
      const chars = normalizeChars(details?.characteristics);
      const rawLength = chars?.Length ?? chars?.length;
      const rawWidth = chars?.Width ?? chars?.width;
      const length = rawLength == null || rawLength === "" ? null : Number(rawLength);
      const width = rawWidth == null || rawWidth === "" ? null : Number(rawWidth);
      return {
        length: Number.isFinite(length) && length > 0 ? length : null,
        width: Number.isFinite(width) && width > 0 ? width : null,
      };
    } catch {
      return null;
    }
  }, [accessToken, cfg?.read_module]);

  // The module's physical size, kept in step with whichever module the layout
  // is actually about.
  //
  // This used to be fetched by openPhysicalLayoutDialog(), which was the only
  // way in while the layout was a modal. Turning the design steps into tabs
  // removed that call and left nothing loading the dimensions: the panel opened
  // with length 0, so GCR came out 0 and the geometry was rejected as invalid
  // even though a perfectly good module was selected. An effect keyed on the
  // module name cannot be bypassed by a new way of reaching the panel.
  const layoutModuleName =
    physicalLayoutDraft?.module ||
    (hasUnsavedPhysicalLayout ? autoDesignResult?.module : null) ||
    cfg?.read_module ||
    null;
  const layoutInverterName =
    physicalLayoutDraft?.inverter ||
    (hasUnsavedPhysicalLayout ? autoDesignResult?.inverter : null) ||
    cfg?.read_inverter ||
    null;

  useEffect(() => {
    if (!layoutModuleName) {
      setPhysicalLayoutModuleGeometry({ length: null, width: null });
      setPhysicalLayoutModuleGeometryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPhysicalLayoutModuleGeometry({ length: null, width: null });
    setPhysicalLayoutModuleGeometryLoading(true);
    getSelectedModuleGeometry(layoutModuleName).then((geometry) => {
      if (!cancelled) {
        setPhysicalLayoutModuleGeometry(geometry || { length: null, width: null });
      }
    }).finally(() => {
      if (!cancelled) setPhysicalLayoutModuleGeometryLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [layoutModuleName, getSelectedModuleGeometry]);

  const computeCapacityFromLayout = useCallback((layoutInput, moduleKw) => {
    if (!moduleKw || !layoutInput) return null;

    const normalizeToArray = (input) => {
      if (!input) return null;
      if (Array.isArray(input)) return input;
      if (Array.isArray(input?.layout)) return input.layout;
      if (input.layoutByInverter && typeof input.layoutByInverter === "object") {
        return Object.values(input.layoutByInverter);
      }
      if (typeof input === "object") {
        return Object.values(input);
      }
      return null;
    };

    const layout = normalizeToArray(layoutInput);
    if (!layout || !Array.isArray(layout)) return null;

    let modules = 0;
    layout.forEach((inv) => {
      (inv || []).forEach((mppt) => {
        const mps = Number(mppt?.modulesPerString) || 0;
        const used = (mppt?.inputs || []).filter((i) => i?.used).length;
        modules += mps * used;
      });
    });
    const kw = modules * moduleKw;
    return Number.isFinite(kw) ? kw : null;
  }, []);

  const normalizeSystemDesign = (systemDesign) => {
    if (!systemDesign || typeof systemDesign !== "object") return null;

    const inverterKeys = Object.keys(systemDesign);
    if (!inverterKeys.length) return null;

    const layoutByInverter = {};
    const metaByInverter = {};
    const inverterOrder = [...inverterKeys];

    inverterKeys.forEach((key) => {
      const mpptList = Array.isArray(systemDesign[key]) ? systemDesign[key] : [];

      const mpptStates = mpptList.map((mppt) => {
        const inputsRaw = Array.isArray(mppt?.inputs) ? mppt.inputs : [];
        const modulesValue =
          mppt?.modules_per_string != null
            ? mppt.modules_per_string
            : inputsRaw.find((inp) => inp?.modules_per_string != null)?.modules_per_string;
        const modulesPerString = modulesValue == null ? "" : String(modulesValue);
        const maxInputIdx = inputsRaw.reduce((max, inp) => {
          const idx = Number(inp?.index ?? inp?.inputIndex);
          return Number.isFinite(idx) && idx > max ? idx : max;
        }, 0);
        const inputsCount = Math.max(maxInputIdx, inputsRaw.length);
        const hasIndexedInputs = inputsRaw.some((inp) =>
          Number.isFinite(Number(inp?.index ?? inp?.inputIndex))
        );

        const inputs = Array.from({ length: inputsCount || 0 }, (_, i) => {
          const match = hasIndexedInputs
            ? inputsRaw.find(
                (inp) => Number(inp?.index ?? inp?.inputIndex) === i + 1
              )
            : inputsRaw[i];
          const matchIndex = match?.index ?? match?.inputIndex;
          return {
            index: Number(matchIndex) || i + 1,
            used: Boolean(
              match &&
                (match.modules_per_string != null ||
                  match.modulesPerString != null ||
                  match.used === true)
            ),
            modules_per_string: match?.modules_per_string ?? match?.modulesPerString,
          };
        });

        return {
          modulesPerString,
          inputs,
          // The plane this MPPT stands on, when it is not the primary.
          ...(mppt?.orientation ? { orientation: mppt.orientation } : {}),
        };
      });

      layoutByInverter[key] = mpptStates;
      metaByInverter[key] = { mppts: mpptStates.map((m) => m.inputs.length) };
    });

    return {
      inverterOrder,
      layoutByInverter,
      metaByInverter,
    };
  };


  const designFromSystemDesign = useMemo(() => {
    const normalized = normalizeSystemDesign(cfg?.systemDesign);
    if (!normalized) return null;
    return {
      nInverters: normalized.inverterOrder.length,
      inverterOrder: normalized.inverterOrder,
      layoutByInverter: normalized.layoutByInverter,
      layout: normalized.inverterOrder.map((k) => normalized.layoutByInverter[k]),
    };
  }, [cfg?.systemDesign]);

  const metaFromSystemDesign = useMemo(() => {
    const normalized = normalizeSystemDesign(cfg?.systemDesign);
    if (!normalized) return null;
    return {
      byInverter: normalized.metaByInverter,
      inverterOrder: normalized.inverterOrder,
    };
  }, [cfg?.systemDesign]);

  const countModulesFromStructuredDesign = useCallback((design) => {
    const layout = Array.isArray(design?.layout)
      ? design.layout
      : design?.layoutByInverter
      ? Object.values(design.layoutByInverter)
      : null;
    if (!Array.isArray(layout)) return 0;

    let modules = 0;
    layout.forEach((inv) => {
      (inv || []).forEach((mppt) => {
        const modulesPerString = Number(mppt?.modulesPerString) || 0;
        const usedInputs = (mppt?.inputs || []).filter((input) => input?.used).length;
        modules += modulesPerString * usedInputs;
      });
    });
    return modules;
  }, []);

  const persistedPvDesign = useMemo(() => {
    return cfg?.pv_design && typeof cfg.pv_design === "object" ? cfg.pv_design : null;
  }, [cfg?.pv_design]);

  const structuredPvDesign = useMemo(() => {
    const persistedStructured =
      persistedPvDesign &&
      (Array.isArray(persistedPvDesign?.layout) ||
        Boolean(persistedPvDesign?.layoutByInverter) ||
        Array.isArray(persistedPvDesign?.inverterOrder))
        ? persistedPvDesign
        : null;
    return autoDesignLayout || persistedStructured || designFromSystemDesign || null;
  }, [autoDesignLayout, persistedPvDesign, designFromSystemDesign]);

  const physicalLayoutTotalModules = useMemo(() => {
    const structuredCount = countModulesFromStructuredDesign(structuredPvDesign);
    if (structuredCount > 0) return structuredCount;
    const draftCount = countModulesFromStructuredDesign(physicalLayoutDraft?.design);
    if (draftCount > 0) return draftCount;
    return countFromSystemDesign(cfg?.systemDesign).modules;
  }, [cfg?.systemDesign, countModulesFromStructuredDesign, physicalLayoutDraft?.design, structuredPvDesign]);

  // The project as "Save layout" would leave it: the open face's layout, the
  // other faces' unsaved drafts and the shared scene's moves, in one config.
  // The Save commits it; the shading calculation of every face works on it,
  // so the tables it gives hold for exactly what the Save then stores.
  // `light` skips the deep copy of the saved design (its pictures included),
  // for when only the geometry is read.
  const composeLayoutSave = (spacingInfo, frames, sceneLayoutsFromScene, { light = false } = {}) => {
    // One Save for the whole building: the other faces' unsaved drafts (edits
    // typed or drawn while they were the face being edited) are written with
    // this one. Moves made in the shared scene are newer than the draft they
    // were made over, so they win. A draft its own validation refuses stays a
    // draft.
    let sceneLayouts = sceneLayoutsFromScene;
    let draftIds = [];
    if (layoutSharedWorkspace) {
      const drafts = Object.entries(roofWorkspaceRef.current)
        .filter(([id, draft]) => id !== layoutOrientation.id && draft.dirty && !draft.invalid
          && draft.spacing && orientationList.some((o) => o.id === id)
          && !layoutDesignChanged(draft.spacing,
            orientationSummary(layoutPreviewCfg).find((p) => p.id === id)?.modules || 0,
            physicalLayoutDesignKey(systemDesignForOrientation(layoutPreviewCfg.systemDesign, orientationList, id))));
      if (drafts.length) {
        sceneLayouts = { ...Object.fromEntries(drafts.map(([id, draft]) => [id, draft.spacing])), ...(sceneLayoutsFromScene || {}) };
        draftIds = drafts.map(([id]) => id);
      }
    }
    // Ground clearance is typed in the spacing panel but it is not spacing:
    // the engine reads it from panelOrientation, so it is split out here
    // rather than stored twice under two names that could drift apart.
    const { ground_clearance_m: clearanceM, scene_views: sceneViews, ...spacingOnly } = spacingInfo || {};
    const clearance = Number.isFinite(Number(clearanceM)) && Number(clearanceM) >= 0 && Number(clearanceM) <= 10
      ? Number(clearanceM) : null;
    if (!layoutIsPrimary) {
      // A further plane's layout lives on its entry, in the shape the engine
      // reads (spacingInfo) beside its own clearance.
      const patch = { spacingInfo: spacingOnly };
      if (Array.isArray(sceneViews)) patch.sceneViews = sceneViews;
      if (clearance != null) patch.groundClearanceM = clearance;
      return { next: applySceneLayouts(applySceneFrames(updateOrientation(cfg, layoutOrientation.id, patch), frames), sceneLayouts),
        draftIds, spacingOnly };
    }
    const design = {
      ...(structuredPvDesign && typeof structuredPvDesign === "object" ? structuredPvDesign : {}),
      module: structuredPvDesign?.module || autoDesignResult?.module || cfg.read_module || null,
      inverter: structuredPvDesign?.inverter || autoDesignResult?.inverter || cfg.read_inverter || null,
      spacingInfo: spacingOnly,
      obstacleShading: persistedPvDesign?.obstacleShading || null,
      // Pictures of the field at the spacing-critical hours, for the report.
      // Kept apart from spacingInfo, which the engine reads.
      sceneViews: Array.isArray(sceneViews) ? sceneViews : [],
      ...(autoDesignStamp() ? { autoDesign: autoDesignStamp() } : {}),
    };
    let nextCfg = setPathInObject(cfg, "pv_design", light ? { ...design, sceneViews: [] } : JSON.parse(JSON.stringify(design)));
    if (clearance != null) nextCfg = setPathInObject(nextCfg, "panelOrientation.groundClearanceM", clearance);
    return { next: applySceneLayouts(applySceneFrames(nextCfg, frames), sceneLayouts), draftIds, spacingOnly };
  };

  // The shading of EVERY face, calculated in one go from the layout and kept
  // until "Save layout" stores it with that layout, in the same step: one
  // face's shading depends on all the others, so they are calculated, kept
  // and saved together. { key, tables: { planeId: evidence } }.
  const [sceneShadingDraft, setSceneShadingDraft] = useState(null);
  const layoutShadingKey = (spacingInfo, frames, sceneLayoutsFromScene, placement) =>
    sceneShadingKey(composeLayoutSave(spacingInfo, frames, sceneLayoutsFromScene, { light: true }).next,
      placement !== undefined ? { [layoutOrientation.id]: placement } : {});
  const calculateLayoutShading = async (spacingInfo, frames, sceneLayoutsFromScene, placement, { onProgress, shouldCancel } = {}) => {
    let draft = composeLayoutSave(spacingInfo, frames, sceneLayoutsFromScene, { light: true }).next;
    const key = sceneShadingKey(draft, placement !== undefined ? { [layoutOrientation.id]: placement } : {});
    if (placement !== undefined) {
      // The imported model on this face as the open layout has it: placed (or
      // moved) and not saved yet -- read from this machine's cache at this
      // placement -- or removed.
      const plane = orientationsFromConfig(draft).find((o) => o.id === layoutOrientation.id);
      const { placement: ignored, ...evidence } = (plane?.primary ? draft.pv_design : plane)?.obstacleShading || {};
      draft = saveOrientationShading(draft, layoutOrientation.id, { ...evidence, ...(placement ? { placement } : {}) });
    }
    // Calculated whatever the study's shading mode: the design report needs it too.
    draft = { ...draft, pvLosses: { ...draft.pvLosses, dc: { ...draft.pvLosses?.dc, shadingMode: "geometry" } } };
    const module = await getModuleDetails(cfg.read_module, accessToken);
    let prepared;
    try {
      prepared = await prepareSceneInBackground(draft, { module, projectId: currentProject?.id, onProgress, shouldCancel });
    } catch (error) {
      throw Object.assign(new Error(scenePreparationMessage(error, isGreekScreen)), scenePreparationIssue(error),
        { cancelled: isSceneCalculationCancelled(error) });
    }
    const tables = {};
    orientationsFromConfig(prepared).forEach((plane) => {
      const evidence = plane.primary ? prepared.pv_design?.obstacleShading : plane.obstacleShading;
      if (evidence?.table && !evidence.stale) tables[plane.id] = evidence;
    });
    setSceneShadingDraft({ key, tables });
  };

  // "Calculate shading" offered by the estimate when the layout's shading is
  // missing or out of date: every face, from the saved layout, stored with it
  // as the layout's own calculation is. A project changed meanwhile is not
  // overwritten with a calculation made on the one before. Returns the project
  // as stored, which the estimate then runs on.
  const calculateProjectShading = async ({ onProgress, shouldCancel } = {}) => {
    const start = cfgRef.current;
    const module = await getModuleDetails(start.read_module, accessToken);
    let next;
    try {
      next = await calculateSavedSceneShading(start, { module, projectId: currentProject?.id, onProgress, shouldCancel });
    } catch (error) {
      throw Object.assign(new Error(scenePreparationMessage(error, isGreekScreen)), scenePreparationIssue(error),
        { cancelled: isSceneCalculationCancelled(error) });
    }
    if (cfgRef.current !== start) {
      throw new Error(isGreekScreen
        ? "Οι παράμετροι άλλαξαν κατά τον υπολογισμό. Υπολογίστε ξανά τις σκιάσεις."
        : "Parameters changed during calculation. Calculate the shading again.");
    }
    history.commit(next);
    return next;
  };

  const ensureOptionPresent = useCallback(
    async (name, options, fetchFn, setOptions) => {
      if (!name) return false;
      const exists = (options || []).some((opt) => opt?.name === name);
      if (exists) return true;
      try {
        const detail = await fetchFn(name);
        if (detail && detail.name) {
          setOptions((prev = []) => {
            const already = prev.some((opt) => opt?.name === detail.name);
            return already ? prev : [...prev, detail];
          });
          return true;
        }
      } catch (err) {
        console.warn(`Could not fetch catalog item for ${name}`, err);
      }
      return false;
    },
    []
  );

  const applyAutoDesignResult = async (res) => {
    if (!res || typeof res !== "object") return;
    const resultProjectId = currentProject?.id;

    const mod = res.module || res.module_name || res.moduleName;
    const inv = res.inverter || res.inverter_name || res.inverterName;
    const pvCap =
      res.pv_capacity_kw ??
      res.pv_capacity ??
      res.capacity_kw ??
      res.capacity ??
      (res.actual_capacity_w != null ? Number(res.actual_capacity_w) / 1000 : undefined);

    // Do NOT persist selections to the config here; just keep them for the dialog/result.
    // We fetch options if missing so the dialog can show names, but avoid writing read_module/read_inverter.
    if (mod) {
      await ensureOptionPresent(mod, moduleOptions, getModuleDetails, setModuleOptions);
    }
    if (inv) {
      await ensureOptionPresent(inv, inverterOptions, getInverterDetails, setInverterOptions);
    }
    if (roofWorkspaceProjectRef.current !== resultProjectId || !layoutSaveActiveRef.current) return false;

    let systemDesign = res.systemDesign || res.auto_system_design;
    if (systemDesign && typeof systemDesign === "object" && systemDesign.systemDesign) {
      systemDesign = systemDesign.systemDesign;
    }
    if (typeof systemDesign === "string") {
      try {
        systemDesign = JSON.parse(systemDesign);
      } catch (err) {
        console.warn("Could not parse systemDesign string from auto-design", err);
      }
    }
    const designFromSystem = (() => {
      const normalized = normalizeSystemDesign(systemDesign);
      if (!normalized) return null;

      setDesignMeta({
        byInverter: normalized.metaByInverter,
        inverterOrder: normalized.inverterOrder,
      });

      const layoutArray = normalized.inverterOrder.map(
        (name) => normalized.layoutByInverter[name]
      );

      return {
        nInverters: normalized.inverterOrder.length,
        inverterOrder: normalized.inverterOrder,
        layoutByInverter: normalized.layoutByInverter,
        layout: layoutArray,
      };
    })();

    const manualRoofLayout = isRoofInstallation(cfgRef.current?.panelOrientation?.installationType);
    const savedSpacing = cfgRef.current?.pv_design?.spacingInfo || cfgRef.current?.pv_design?.spacing || null;
    const generatedSpacing = (
      res?.spacingInfo ||
      res?.spacing ||
      res?.pv_design?.spacingInfo ||
      res?.pv_design?.spacing ||
      res?.design?.spacingInfo ||
      res?.design?.spacing ||
      res?.layout?.spacingInfo ||
      res?.layout?.spacing ||
      null);

    const design =
      designFromSystem ||
      res.systemDesign ||
      res.auto_system_design ||
      res.design_dict ||
      res.pv_design ||
      res.design ||
      res.layout ||
      res.auto_design ||
      res.autodesign;
    let structuredDesign = withInstallationLayout(
      design,
      cfgRef.current?.panelOrientation?.installationType, generatedSpacing, savedSpacing
    );
    const candidateSpacing = manualRoofLayout ? structuredDesign?.spacingInfo || {} : generatedSpacing;
    const simpleLayout = canSaveSimpleAutoLayout(cfgRef.current, candidateSpacing, roofWorkspaceRef.current, roofSceneEditsRef.current);
    const spacingInfo = simpleLayout ? candidateSpacing : savedSpacing;
    if (!simpleLayout && structuredDesign && typeof structuredDesign === "object") {
      structuredDesign = { ...structuredDesign, spacingInfo: savedSpacing, spacing: undefined };
    }
    // On a plant with several planes the result's MPPTs are shared out
    // across them by the dialog's shares, in whole inverters where possible.
    // Read from the config at the moment of the result, which is what the
    // dialog was showing when Run was pressed.
    const planeList = orientationsFromConfig(cfg);
    const distributed = planeList.length > 1 && structuredDesign
      ? distributeDesignAcrossOrientations(structuredDesign, planeList, orientationShares(cfg))
      : structuredDesign;
    setAutoDesignLayout(distributed || null);
    setAutoDesignResult({
      inverterReplacement: res.inverterReplacement || null,
      module: mod || null,
      inverter: inv || null,
      pvCapacityKw: pvCap || null,
      reportId: res.report_id || null,
      reportSaved: res.report_saved ?? null,
      reportRetentionOk: res.report_retention_ok ?? null,
      actualCapacityKw:
        res.actual_capacity_w != null && Number.isFinite(Number(res.actual_capacity_w))
          ? Number(res.actual_capacity_w) / 1000
          : null,
      spacingInfo: spacingInfo || null,
    });
    setHasUnsavedAutoDesign(true);
    setHasUnsavedPhysicalLayout(true);
    if (simpleLayout) setPhysicalLayoutDraft(
      spacingInfo || manualRoofLayout
        ? {
            spacingInfo,
            module: mod || cfg?.read_module || null,
            inverter: inv || cfg?.read_inverter || null,
            design: distributed || null,
          }
        : null
    );
    return true;
  };

  /**
   * The selected module's rating, and the installed capacity that follows from it.
   *
   * Once a design is saved, the plant's size is a FACT: module count times the
   * module's STC. The capacity field stops being a target the auto-designer
   * aims at and becomes a statement about the design. Swapping the module
   * without re-running the layout used to leave that statement behind,
   * describing the previous module -- an 1,800-module plant kept reading
   * 1,062 kWp after a 590 W module became a 500 W one, when it was 900 kWp.
   *
   * Nothing downstream caught it, because the engine sizes from the design and
   * never reads the field. The stale number surfaced only on screen, in the
   * estimate fingerprint, and in monitoring -- which falls back to it as the DC
   * rating behind every performance ratio it computes.
   *
   * Deliberately scoped to the case where a design exists. With no design the
   * field IS the target the auto-designer sizes to, and a user who types
   * "1,000 kWp" before designing anything must not have it overwritten.
   *
   * The rating fetch and the capacity live in ONE effect on purpose: the ref
   * below is the fetch's cache, and a second effect reading it would race the
   * reset and fetch the same record twice on every module change.
   */
  useEffect(() => {
    modulePdcKwRef.current = null;
    if (!cfg?.read_module) return undefined;
    let cancelled = false;
    (async () => {
      const moduleKw = await getModulePdcKw();
      if (cancelled) return;
      if (!capacityNeedsUpdate(cfg?.pvInstalledCap?.kw, physicalLayoutTotalModules, moduleKw)) {
        return;
      }
      const next = formatCapacityKw(
        capacityFromDesign(physicalLayoutTotalModules, moduleKw)
      );
      if (next !== null) handleField("pvInstalledCap.kw", next);
    })();
    return () => {
      cancelled = true;
    };
    // handleField is rebuilt on every render and would re-fire this on each
    // one; the capacity depends on the module and the design alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.read_module, physicalLayoutTotalModules, getModulePdcKw]);

  const handleRunAutoDesign = async () => {
    const projectId = currentProject?.id;
    if (!projectId) {
      setAutoDesignError("Open or save a project before running auto-design");
      setAutoDesignResult(null);
      setHasUnsavedAutoDesign(false);
      setHasUnsavedPhysicalLayout(false);
      setPhysicalLayoutDraft(null);
      return false;
    }

    const missing = requiredAutoInputs();
    if (missing.length) {
      setAutoDesignError(`${isGreekScreen ? "Λείπουν στοιχεία" : "Missing inputs"}: ${missing.map((m) => autoDesignInputName(m, isGreekScreen)).join(", ")}`);
      setAutoDesignResult(null);
      setHasUnsavedAutoDesign(false);
      setHasUnsavedPhysicalLayout(false);
      setPhysicalLayoutDraft(null);
      return false;
    }

    if (!validCapacityRatios(Number(autoDesignParams.undersizeRatio), Number(autoDesignParams.oversizeRatio))) {
      setAutoDesignError(isGreekScreen
        ? "Η επιτρεπόμενη μείωση πρέπει να είναι από 0% έως λιγότερο από 100% και η υπέρβαση τουλάχιστον 0%."
        : "Allowed reduction must be at least 0% and below 100%; allowed excess must be at least 0%.");
      return false;
    }

    setAutoDesignError("");
    setDesignSaveNotice(null);
    setAutoDesignResult(null);
    setHasUnsavedAutoDesign(false);
    setHasUnsavedPhysicalLayout(false);
    setPhysicalLayoutDraft(null);
    setAutoDesignLoading(true);

    const payload = {
      saved_report_id: persistedPvDesign?.autoDesign?.reportId || undefined,
      lat: cfg?.loc_info?.lat ?? cfg?.lat ?? null,
      lon: cfg?.loc_info?.lon ?? cfg?.lon ?? null,
      roof_area: cfg?.roof_area ?? null,
      avg_monthly_consumption: 10000, // hardcoded default
      size_mode: "manual", // hardcoded manual
      slope_unit: "deg",
      slope_tilt: cfg?.loc_info?.tilt ?? null,
      slope_azimuth: cfg?.loc_info?.azimuth ?? null,
      // An explicit equipment selection is fixed. Candidate-pool filters for
      // that kind are omitted below because they cannot change the selection.
      module: cfg?.read_module || undefined,
      inverter: cfg?.read_inverter || undefined,
      tilt: (isFlushRoof ? cfg?.panelOrientation?.roofPitch : cfg?.panelOrientation?.tilt) ?? null,
      azimuth: (isFlushRoof ? cfg?.panelOrientation?.roofAzimuth : cfg?.panelOrientation?.azim) ?? null,
      tilt_opt: false,
      azimuth_opt: false,
      // How the array is installed. A flush roof array has no rows to space:
      // the engine then needs only the modules' own area, and takes the plane
      // from the roof, not from the terrain the building stands on.
      installation_type: installationType,
      roof_pitch: cfg?.panelOrientation?.roofPitch ?? undefined,
      roof_azimuth: cfg?.panelOrientation?.roofAzimuth ?? undefined,
      // The mount, which the layout depends on: it sets the row height, the
      // spacing rule and the ground clearance. These were hardcoded to a fixed
      // array server-side, so a tracked plant was being laid out as if it did
      // not move.
      mount_type: cfg?.mountType ?? undefined,
      axis_orientation: cfg?.axisOrientation ?? undefined,
      max_angle: cfg?.maxAngle ?? undefined,
      backtrack: cfg?.backTrack ?? undefined,
      // The TARGET, from the auto-design dialog -- not the installed-capacity
      // field, which describes the plant that already exists. Reading the
      // latter made every re-run aim at whatever the last design happened to
      // produce, so the search could never be asked for a different size
      // without first mis-stating the current one.
      pv_capacity_kw: resolveTargetKw(cfg),
      surface_type: cfg?.surfaceType || undefined,
      w_cost: autoDesignNumber("wCost"),
      dcac_min: autoDesignNumber("dcacMin"),
      dcac_max: autoDesignNumber("dcacMax"),
      min_cell_c: autoDesignNumber("minCellC"),
      max_cell_c: autoDesignNumber("maxCellC"),
      oversize_ratio: autoDesignNumber("oversizeRatio"),
      undersize_ratio: autoDesignNumber("undersizeRatio"),
      prefer_nearest: true,
      ranking: {
        strict_nearest: autoDesignParams.preferNearestStrict !== false,
        fewer_inverters: autoDesignParams.preferFewerInverters !== false,
        within_current_ratings: autoDesignParams.withinCurrentRatings === true,
      },
      ...autoDesignCandidateFilters(autoDesignParams, cfg?.read_module, cfg?.read_inverter),
      layout: isRoofInstallation(installationType) ? {
        min_tiers: 1, max_tiers: 1, fit_to_parcel: false, whole_strings_per_row: false,
      } : {
        min_tiers: autoDesignNumber("minTiers"),
        max_tiers: autoDesignNumber("maxTiers"),
        // The parcel only counts when one has been drawn; the checkbox alone
        // does nothing without it.
        fit_to_parcel: Boolean(autoDesignParams.fitToParcel) && Boolean(cfg?.loc_info?.parcel),
        parcel: cfg?.loc_info?.parcel || null,
        setback_m: autoDesignNumber("setbackM"),
        land_margin_pct: autoDesignNumber("landMarginPct"),
        whole_strings_per_row: Boolean(autoDesignParams.wholeStringsPerRow),
      },
    };

    try {
      // Only try to prefetch module data if a module was selected; optional otherwise
      if (payload.module) {
        await getModulePdcKw();
      } else {
        modulePdcKwRef.current = null;
      }
      const res = await runWithInverterFallback(body => runAutoDesign(projectId, body, accessToken), payload, autoDesignParams);
      if (roofWorkspaceProjectRef.current !== projectId) return false;
      if (await applyAutoDesignResult(res) === false) return false;
      // Taken from `payload`, not from the knobs: those are the coerced numbers
      // the backend actually scored against. Set after the call returns, so a
      // failed run leaves the previous stamp in place rather than claiming a
      // design it did not produce.
      setAutoDesignRunMeta({
        projectId,
        reportId: res.report_id || null,
        reportSaved: res.report_saved ?? null,
        reportRetentionOk: res.report_retention_ok ?? null,
        wCost: payload.w_cost,
        dcacMin: payload.dcac_min,
        dcacMax: payload.dcac_max,
        minCellC: payload.min_cell_c,
        maxCellC: payload.max_cell_c,
        oversizeRatio: payload.oversize_ratio,
        undersizeRatio: payload.undersize_ratio,
        moduleTechnologies: payload.module_technologies,
        modulesOwnedOnly: payload.modules_owned_only,
        inverterConversionModel: payload.inverter_conversion_model || "",
        inverterPhases: payload.inverter_phases,
        invertersOwnedOnly: payload.inverters_owned_only,
        targetCapacityKw: payload.pv_capacity_kw,
        ranAt: new Date().toISOString(),
      });
      setAutoDesignReportHistoryRevision((revision) => revision + 1);
      const invName =
        res?.inverter || res?.inverter_name || res?.inverterName || cfg.read_inverter;
      await loadDesignMetaByInverter(invName, res?.module || res?.module_name || cfg.read_module);
      return true;
    } catch (err) {
      setAutoDesignError({
        ...formatAutoDesignError(err),
        rawResponse: err?.responseText || null,
        requestContext: {
          time: new Date().toISOString(),
          target_dc_kw: payload.pv_capacity_kw,
          selected_module: payload.module || null,
          selected_inverter: payload.inverter || null,
          inverter_conversion_model: payload.inverter_conversion_model || null,
          inverter_phases: payload.inverter_phases,
          dcac_min: payload.dcac_min,
          dcac_max: payload.dcac_max,
        },
      });
      return false;
    } finally {
      setAutoDesignLoading(false);
    }
  };

  // The quick study's «Δημιουργία και εκτέλεση», step 2 (components/quickStudyRun.js):
  // auto-design as the button runs it, then the design dialog saves the result
  // through its own save. A failure stops here with its message on this page.
  const quickDesignStep = quickStep(typeof window === "undefined" ? null : window.location) === "design";
  const quickDesignStartedRef = useRef(false);
  const [quickDesignSaveRequest, setQuickDesignSaveRequest] = useState(0);
  const stopQuickStudy = useCallback(
    () => navigate(withoutQuick(window.location), { replace: true }),
    [navigate]
  );
  // The page loads several things of its own when it opens (horizon, time
  // zone, orientation curves) and some of them update the project. The design
  // save refuses when the project changed while it was saving, so the quick
  // flow waits for the page to settle, and retries a save that met such a
  // change; a design the electrical check rejects is not retried.
  const quickDesignAttemptsRef = useRef(0);
  const latestRunAutoDesignRef = useRef(null);
  latestRunAutoDesignRef.current = handleRunAutoDesign;
  useEffect(() => {
    if (!quickDesignStep || quickDesignStartedRef.current || !currentProject?.id || designStep !== "electrical") return undefined;
    quickDesignStartedRef.current = true;
    const stillOnStep = () => quickStep(window.location) === "design";
    setTimeout(async () => {
      if (!stillOnStep()) return;  // stopped meanwhile
      const ok = await latestRunAutoDesignRef.current();
      if (!stillOnStep()) return;
      if (!ok) { stopQuickStudy(); return; }
      setQuickDesignSaveRequest(Date.now());
    }, 3000);
    return undefined;
    // The timer is not cleared: the flow is started once per visit (the ref).
  }, [quickDesignStep, currentProject?.id, designStep]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleQuickDesignSaved = useCallback((result) => {
    if (quickStep(window.location) !== "design") return;  // stopped meanwhile
    if (result === "saved") { navigate(QUICK_STUDY_PATHS.run); return; }
    if ((result === "error" || result === "changed" || result === "busy") && quickDesignAttemptsRef.current < 3) {
      quickDesignAttemptsRef.current += 1;
      setTimeout(() => setQuickDesignSaveRequest(Date.now()), 3000);
      return;
    }
    stopQuickStudy();
  }, [navigate, stopQuickStudy]);




  const handleOpenModuleDialog = async () => {
    if (!cfg.read_module) return;
    setModuleDialogOpen(true);
    setModuleDialogLoading(true);
    setModuleDialogError("");
    setModuleDetails(null);

    try {
      const data = await getModuleDetails(cfg.read_module);
      setModuleDetails(data);
    } catch (err) {
      setModuleDialogError(
        err?.message || "Failed to load module details."
      );
    } finally {
      setModuleDialogLoading(false);
    }
  };

  // Read out of the stored TABLE, never out of the two numbers it was filled
  // from: a row edited by hand afterwards would otherwise be described on the
  // card by numbers the engine is no longer using.
  const degradationRetention = cfg.pvDegradation?.retentionPct;
  const degradationSummary = describeDegradation(degradationRetention);

  const handleSaveDegradation = (retentionPct) => {
    setDegradationOpen(false);
    if (!retentionPct) return;
    // annualDegFactor is written alongside and deliberately: it is a REQUIRED
    // config key that a dozen places in the feasibility engine still read, and
    // every project saved before this existed carries it. Keeping it in step
    // with the curve's average annual slope means an older code path reading it
    // gets the right order of magnitude rather than a stale number.
    const summary = describeDegradation(retentionPct);
    handleFields([
      ["pvDegradation", { retentionPct }],
      ["pvLosses.dc.lid", Number(Math.max(0, 100 - retentionPct[0]).toFixed(4))],
      ["pvLosses.dc.age", 0],
      ...(summary ? [["annualDegFactor", Number(summary.annualPct.toFixed(4))]] : []),
    ]);
  };

  const handleOpenModuleMonitor = () => {
    if (!cfg.read_module) return;
    setModuleMonitorOpen(true);
  };

  const handleDetailedSettings = (settings) => {
    // Choosing the method and editing its topology are project inputs, not a
    // simulation. Prepare and validate geometry when saving/calculating the
    // physical layout or starting a run, so configuration works before layout.
    handleField("pvDetailedShading", { ...settings, scene: undefined });
  };

  /**
   * Save a re-fitted inverter model as a NEW catalogue entry.
   *
   * Never updates the inverter being viewed. Simulations resolve equipment by
   * name at run time (BESS/DB/db_pg.py: inverter_by_name) and no run stores its
   * own copy of the characteristics, so overwriting would silently change the
   * results of every project already using that inverter — including studies
   * already issued. A new name keeps history reproducible.
   *
   * The measurement arrays travel with the coefficients so the backend can refit
   * from them later, instead of leaving eight opaque numbers nobody can audit.
   */
  // The fitted coefficients ARE the model: without Pdco/Pso/Vdco/C0-C3 stored
  // alongside the measurements, the engine cannot recognise a Sandia inverter
  // and silently converts at a single flat efficiency instead - which is a
  // whole-plant yield error, not a rounding one. They are merged into the
  // saved characteristics here, where the catalogue entry is written.
  const handleApplyInverterFit = async ({ characteristics, coefficients, saveAsName }) => {
    const source = invDetails?.name || cfg.read_inverter;
    const target = String(saveAsName || "").trim();
    if (!target || target === source) {
      setInvDialogError(
        "A new, different inverter name is required; the stored inverter is not modified."
      );
      return;
    }
    setInvDialogError("");
    try {
      const response = await createInverter({
        inverter_name: target,
        characteristics: {
          ...(invDetails?.characteristics || {}),
          ...characteristics,
          ...(coefficients || {}),
        },
      });
      const savedName = String(response?.name || target).trim();
      await loadCatalog();
      handleField("read_inverter", savedName);
      setInvDialogOpen(false);
      setCatalogToast({
        open: true,
        msg: `Saved "${savedName}" and selected it. "${source}" was left unchanged.`,
        severity: "success",
      });
    } catch (err) {
      setInvDialogError(
        err?.message || "The fitted model could not be saved as a new inverter."
      );
    }
  };

  /**
   * The efficiency model, reachable from the row that owns the inverter.
   *
   * The module row carries an I-V / P-V Monitor button; the inverter row
   * carried only an info icon, so the curve dialog -- the richest thing in the
   * tab -- sat two clicks deep behind "Inverter details". The dialog reads the
   * stored curves off invDetails, so the details are fetched here too rather
   * than opening it blank.
   */
  const handleOpenInverterCurves = async () => {
    if (!cfg.read_inverter) return;
    setInvFitOpen(true);
    if (invDetails?.name === cfg.read_inverter) return;
    try {
      setInvDetails(await getInverterDetails(cfg.read_inverter));
    } catch {
      // The dialog still opens on the name alone and can fit fresh curves.
    }
  };

  const handleOpenInverterDialog = async () => {
    if (!cfg.read_inverter) return;
    setInvDialogOpen(true);
    setInvDialogLoading(true);
    setInvDialogError("");
    setInvDetails(null);

    try {
      const data = await getInverterDetails(cfg.read_inverter);
      setInvDetails(data);
    } catch (err) {
      setInvDialogError(
        err?.message || "Failed to load inverter details."
      );
    } finally {
      setInvDialogLoading(false);
    }
  };



  const renderField = (f, values, setValues, errors, setErrors) => {
    if (typeof f.showWhen === "function" && !f.showWhen(values)) {
      return null;
    }

    const value = values[f.key] ?? "";
    const errMsg = errors[f.key] || "";
    const gridMd = f.md ?? 4;

    const commonProps = {
      fullWidth: true,
      size: "small",
      label: (isGreekScreen && f.labelEl ? f.labelEl : isGreekScreen && f.key === "mppt_isc_stc_multiplier"
        ? "Συντελεστής σχεδιασμού Isc(STC)"
        : isGreekScreen && f.key === "Idcmax_per_input" ? "Μέγιστο ρεύμα ανά φυσική είσοδο" : translateText(f.label)) + (f.required ? " *" : ""),
      value,
      error: Boolean(errMsg),
      helperText: (f.displayPercent && errMsg ? translateText("Enter an efficiency between 0 and 100%.") : translateText(errMsg)) || (isGreekScreen && f.key === "mppt_isc_stc_multiplier"
        ? "Μόνο αν το απαιτεί ο κατασκευαστής. Με όρια βραχυκύκλωσης ανά MPPT, ελέγχεται το μεγαλύτερο από Isc στη θερμή κυψέλη και συντελεστής × Isc(STC). Αλλιώς αφήστε το κενό."
        : isGreekScreen && f.key === "Pdcmax"
          ? "Μέγιστη/συνιστώμενη ονομαστική ισχύς Φ/Β συστοιχίας, εφόσον δηλώνεται από τον κατασκευαστή. Αφήστε το κενό αν δεν αναφέρεται."
          : isGreekScreen && f.key === "Idcmax_per_input"
            ? "Όριο ρεύματος λειτουργίας του κατασκευαστή ανά βύσμα: μία σειρά (string) ανά είσοδο σε αυτή τη διάταξη. Η ίδια τιμή ισχύει για κάθε είσοδο, ανεξάρτητα από το συνολικό όριο του MPPT. Αν είναι άγνωστο, αφήστε το κενό· δεν υπολογίζεται με διαίρεση του ορίου MPPT. Με αναλυτικά όρια ανά MPPT περιορίζει και την ωριαία ισχύ εισόδου DC."
          : (isGreekScreen && f.helperEl ? f.helperEl : translateText(f.helper))) || " ",
    };

    if (f.displayPercent) {
      return <Grid item xs={12} md={gridMd} key={f.key}>
        <EquipmentPercentageField {...commonProps} fraction={value}
          onFractionChange={raw => {
            setValues(previous => ({ ...previous, [f.key]: raw }));
            setErrors(previous => ({ ...previous, [f.key]: validateField(f, raw) || undefined }));
          }} onBlur={() => setErrors(previous => ({ ...previous, [f.key]: validateField(f, values[f.key]) || undefined }))} />
      </Grid>;
    }
    if (f.kind === "number") {
      return (
        <Grid item xs={12} md={gridMd} key={f.key}>
          <TextField
            {...commonProps}
            type="text"
            inputMode="decimal"
            onChange={(e) => {
              const raw = e.target.value.replace(",", ".");
              if (!NUM_RE.test(raw)) return; // block non-numeric keystrokes

              setValues((p) => {
                const next = { ...p, [f.key]: raw };
                const msg = validateField(f, raw);
                setErrors((prev) => ({ ...prev, [f.key]: msg || undefined }));
                return next;
              });
            }}
            onBlur={() => {
              setErrors((prev) => {
                const msg = validateField(f, values[f.key]);
                return { ...prev, [f.key]: msg || undefined };
              });
            }}
            InputProps={{
              endAdornment: f.unit ? (
                <InputAdornment position="end">{f.unit}</InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
      );
    }

    if (f.kind === "select") {
      return (
        <Grid
          item
          xs={12}
          md={gridMd}
          key={f.key}
          sx={{
            flexBasis: { xs: "100%", md: `calc(${(gridMd / 12) * 100}% - 16px)` },
            maxWidth: { xs: "100%", md: `calc(${(gridMd / 12) * 100}% - 16px)` },
          }}
        >
          <AccessibleSelectTextField
            {...commonProps}
            select
            sx={{ width: "100%", minWidth: 0 }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              const raw = e.target.value;
              setValues((p) => {
                const next = { ...p, [f.key]: raw };
                if (f.key === "Bifacial" && raw === "1" && isEmpty(next.bifaciality)) {
                  next.bifaciality = "0.7";
                }
                const msg = validateField(f, raw);
                setErrors((prev) => ({ ...prev, [f.key]: msg || undefined }));
                return next;
              });
            }}
          >
            {!f.required ? <MenuItem value="">None</MenuItem> : null}
            {f.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {isGreekScreen && option.labelEl ? option.labelEl : translateText(option.label)}
              </MenuItem>
            ))}
          </AccessibleSelectTextField>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} md={gridMd} key={f.key}>
        <TextField
          {...commonProps}
          type="text"
          onChange={(e) => {
            const raw = e.target.value;
            setValues((p) => {
              const next = { ...p, [f.key]: raw };
              const msg = validateField(f, raw);
              setErrors((prev) => ({ ...prev, [f.key]: msg || undefined }));
              return next;
            });
          }}
        />
      </Grid>
    );
  };



  const handleSaveModule = async () => {
    setModuleSaveError("");
    if (scopeInvalid(moduleVisibility, moduleOrganizations)) {
      setModuleSaveError(isGreekScreen ? "Επιλέξτε τουλάχιστον έναν διαθέσιμο οργανισμό." : "Select at least one available organization.");
      return;
    }
    if (!catalogWriteAllowed) {
      setModuleSaveError(catalogWriteDisabledMessage);
      return;
    }
    if (moduleParamSet === "pan") {
      const errs = validateAll(MODULE_FIELDS.filter((f) => ["module_name", "Price"].includes(f.key)), moduleForm);
      setModuleErrors(errs);
      if (!modulePanImport || Object.keys(errs).length) return;
      setModuleSaving(true);
      try {
        const response = await createModule({
          module_name: String(moduleForm.module_name).trim(),
          ...scopePayload(moduleVisibility, moduleOrganizations),
          characteristics: {
            ...modulePanImport.characteristics,
            Price: Number(String(moduleForm.Price).replace(",", ".")),
          },
        }, accessToken);
        await loadCatalog();
        const savedName = String(response?.name || moduleForm.module_name).trim();
        handleField("read_module", savedName);
        setAddModuleOpen(false);
        setCatalogToast({ open: true, msg: `Saved "${savedName}" with its PVsyst model and selected it.`, severity: "success" });
      } catch (err) {
        setModuleSaveError(err?.message || "Failed to save the imported module.");
      } finally {
        setModuleSaving(false);
      }
      return;
    }
    // Coefficient route: validate against the declared set and post the raw
    // coefficients. The datasheet fields are not required here — they are what
    // the backend fits FROM, and a coefficient set needs no fitting.
    if (moduleParamSet !== "datasheet") {
      if (!String(moduleForm.module_name || "").trim()) {
        setModuleSaveError("A module name is required.");
        return;
      }
      if (moduleParamMissing.length) {
        setModuleSaveError(
          `The ${moduleParamSpec?.label} set is incomplete; missing: ${moduleParamMissing.join(", ")}.`
        );
        return;
      }
      const chars = { parameterSet: moduleParamSet };
      for (const [k, v] of Object.entries(moduleParamValues)) {
        if (isEmpty(v)) continue;
        const n = Number(String(v).replace(",", "."));
        chars[k] = Number.isFinite(n) ? n : String(v).trim();
      }
      // Geometry and price are used by auto-design and cost ranking regardless
      // of which physics model runs, so they are collected on every route.
      for (const key of ["Length", "Width", "Price", "Vendor", "Technology"]) {
        const raw = moduleForm[key] ?? moduleForm[key.toLowerCase()];
        if (isEmpty(raw)) continue;
        const n = Number(String(raw).replace(",", "."));
        chars[key] = Number.isFinite(n) && key !== "Vendor" && key !== "Technology"
          ? n
          : String(raw).trim();
      }
      setModuleSaving(true);
      try {
        const response = await createModule(
          // `chars`, not the shorthand: `characteristics` is declared further
          // down for the datasheet route, so the shorthand resolved to that
          // const in its temporal dead zone and threw at runtime.
          { module_name: String(moduleForm.module_name).trim(), characteristics: chars, ...scopePayload(moduleVisibility, moduleOrganizations) },
          accessToken
        );
        await loadCatalog();
        const savedName = String(response?.name || moduleForm.module_name).trim();
        handleField("read_module", savedName);
        setAddModuleOpen(false);
        setCatalogToast({
          open: true,
          msg: `Saved "${savedName}" — will run as ${String(moduleParamSpec?.resulting_model).toUpperCase()}.`,
          severity: "success",
        });
      } catch (err) {
        setModuleSaveError(err?.message || "Failed to create module");
      } finally {
        setModuleSaving(false);
      }
      return;
    }

    const errs = validateAll(MODULE_FIELDS, moduleForm);
    setModuleErrors(errs);
    if (Object.keys(errs).length) return;

    // Build characteristics object (exclude module_name)
    const characteristics = {};
    for (const f of MODULE_FIELDS) {
      if (f.key === "module_name") continue;
      if (f.key === "bifaciality" && String(moduleForm.Bifacial ?? "") !== "1") continue;
      const raw = moduleForm[f.key];

      if (isEmpty(raw)) continue;

      if (f.kind === "number" || f.parseAs === "number") {
        characteristics[f.key] = Number(String(raw).replace(",", "."));
      }
      else characteristics[f.key] = String(raw).trim();
    }

    // Optional: infer Area if missing and L/W exist
    if (characteristics.area == null) {
      const L = characteristics.length;
      const W = characteristics.width;
      if (Number.isFinite(L) && Number.isFinite(W)) characteristics.area = L * W;
    }

    setModuleSaving(true);
    try {
      const response = await createModule(
        { module_name: String(moduleForm.module_name).trim(), characteristics, ...scopePayload(moduleVisibility, moduleOrganizations) },
        accessToken
      );

      await loadCatalog();
      const savedName = String(response?.name || moduleForm.module_name).trim();
      handleField("read_module", savedName);
      setCatalogToast({
        open: true,
        msg: formatCatalogUploadSuccessMessage("Module", response, moduleForm.module_name, isGreekScreen),
        severity: "success",
      });

      setAddModuleOpen(false);
    } catch (e) {
      setModuleSaveError(formatApiError(e, "Failed to create module."));
    } finally {
      setModuleSaving(false);
    }
  };

  const handleSaveInverter = async () => {
    setInvSaveError("");
    if (scopeInvalid(inverterVisibility, inverterOrganizations)) {
      setInvSaveError(isGreekScreen ? "Επιλέξτε τουλάχιστον έναν διαθέσιμο οργανισμό." : "Select at least one available organization.");
      return;
    }
    if (!catalogWriteAllowed) {
      setInvSaveError(catalogWriteDisabledMessage);
      return;
    }
    // OND route: everything comes from the file (the backend rebuilds it from
    // the stored text on save); the user supplies the name and the price.
    if (invParamSet === "ond_curve") {
      const errs = validateAll(INVERTER_FIELDS.filter((f) => f.key === "inverter_name"), invForm);
      const price = Number(String(invForm.Price ?? "").replace(",", "."));
      if (String(invForm.Price ?? "").trim() === "" || !Number.isFinite(price) || price < 0) {
        errs.Price = isGreekScreen ? "Συμπληρώστε τιμή (≥ 0)." : "Enter a price (0 or more).";
      }
      setInvErrors(errs);
      if (!invOndImport || Object.keys(errs).length) return;
      setInvSaving(true);
      try {
        const response = await createInverter({
          inverter_name: String(invForm.inverter_name).trim(),
          ...scopePayload(inverterVisibility, inverterOrganizations),
          characteristics: { ...invOndImport.characteristics, Price: price },
        }, accessToken);
        await loadCatalog();
        const savedName = String(response?.name || invForm.inverter_name).trim();
        handleField("read_inverter", savedName);
        setCatalogToast({
          open: true,
          msg: isGreekScreen
            ? `Αποθηκεύτηκε το "${savedName}" με την καμπύλη απόδοσης του OND και επιλέχθηκε.`
            : `Saved "${savedName}" with its OND efficiency curve and selected it.`,
          severity: "success",
        });
        setAddInvOpen(false);
      } catch (e) {
        setInvSaveError(formatApiError(e, "Failed to create inverter."));
      } finally {
        setInvSaving(false);
      }
      return;
    }
    const mpptCount = Number(invForm["Num MPPTs"]);
    let mpptInputs = null;
    let mpptCurrent = null;
    let mpptShort = null;
    if (invPerMppt) {
      if (!Number.isInteger(mpptCount) || mpptCount < 1 || mpptCount > 32) {
        setInvSaveError(isGreekScreen ? "Ο αριθμός MPPT πρέπει να είναι ακέραιος από 1 έως 32." : "MPPT count must be an integer from 1 to 32.");
        return;
      }
      const rows = Array.from({ length: mpptCount }, (_, index) => invMpptRows[index] || {});
      mpptInputs = rows.map((row) => Number(row.inputs));
      mpptCurrent = rows.map((row) => Number(String(row.current ?? "").replace(",", ".")));
      if (mpptInputs.some((value) => !Number.isInteger(value) || value < 1)
          || mpptCurrent.some((value) => !Number.isFinite(value) || value <= 0)) {
        setInvSaveError(isGreekScreen ? "Συμπλήρωσε θετικό ακέραιο αριθμό εισόδων και θετικό όριο λειτουργίας (A) για κάθε MPPT." : "Enter a positive whole number of inputs and a positive operating-current limit (A) for every MPPT.");
        return;
      }
      const hasAnyShort = rows.some((row) => String(row.short ?? "").trim() !== "");
      if (hasAnyShort) {
        mpptShort = rows.map((row) => Number(String(row.short ?? "").replace(",", ".")));
        if (mpptShort.some((value) => !Number.isFinite(value) || value <= 0)) {
          setInvSaveError(isGreekScreen ? "Αν δηλωθεί όριο βραχυκύκλωσης, απαιτείται θετική τιμή για κάθε MPPT." : "If short-circuit limits are supplied, enter a positive value for every MPPT.");
          return;
        }
        if (mpptShort.some((value, index) => value < mpptCurrent[index])) {
          setInvSaveError(isGreekScreen ? "Το όριο βραχυκύκλωσης δεν μπορεί να είναι μικρότερο από το όριο λειτουργίας του ίδιου MPPT." : "A short-circuit rating cannot be below the operating-current rating of the same MPPT.");
          return;
        }
      }
      const total = Number(String(invForm.Idcmax ?? "").replace(",", "."));
      if (Number.isFinite(total) && total < Math.max(...mpptCurrent)) {
        setInvSaveError(isGreekScreen ? "Το συνολικό Idcmax δεν μπορεί να είναι μικρότερο από το όριο ενός MPPT." : "Total Idcmax cannot be below a single MPPT limit.");
        return;
      }
    }
    // One short-circuit rating for all trackers. Written below as the same
    // per-MPPT arrays the separate rows would produce, so the record gets the
    // per-tracker check: Isc against this rating, Imp against the operating
    // one. That check needs the tracker's operating rating as published - it
    // is never derived by dividing the inverter total.
    const generalShortRaw = String(invForm.Iscmax_per_mppt ?? "").trim();
    let generalLimits = null;
    if (!invPerMppt && generalShortRaw !== "") {
      const short = Number(generalShortRaw.replace(",", "."));
      const operating = Number(String(invForm.Idcmax_per_mppt ?? "").replace(",", "."));
      const inputs = Number(invForm["Num Inputs per MPPT"]);
      if (!Number.isInteger(mpptCount) || mpptCount < 1 || mpptCount > 32) {
        setInvSaveError(isGreekScreen ? "Ο αριθμός MPPT πρέπει να είναι ακέραιος από 1 έως 32." : "MPPT count must be an integer from 1 to 32.");
        return;
      }
      if (!Number.isFinite(short) || short <= 0) {
        setInvSaveError(isGreekScreen ? "Το μέγιστο ρεύμα βραχυκύκλωσης ανά MPPT πρέπει να είναι θετικός αριθμός." : "The maximum short-circuit current per MPPT must be a positive number.");
        return;
      }
      if (!Number.isFinite(operating) || operating <= 0) {
        setInvSaveError(isGreekScreen ? "Με όριο βραχυκύκλωσης ανά MPPT, συμπλήρωσε και το Idcmax ανά MPPT όπως δίνεται στο datasheet· δεν υπολογίζεται με διαίρεση του συνολικού Idcmax." : "With a short-circuit rating per MPPT, also enter Idcmax per MPPT as given in the datasheet; it is not derived by dividing the total Idcmax.");
        return;
      }
      if (short < operating) {
        setInvSaveError(isGreekScreen ? "Το όριο βραχυκύκλωσης δεν μπορεί να είναι μικρότερο από το όριο λειτουργίας του MPPT." : "A short-circuit rating cannot be below the MPPT operating-current rating.");
        return;
      }
      const total = Number(String(invForm.Idcmax ?? "").replace(",", "."));
      if (Number.isFinite(total) && total < operating) {
        setInvSaveError(isGreekScreen ? "Το συνολικό Idcmax δεν μπορεί να είναι μικρότερο από το όριο ενός MPPT." : "Total Idcmax cannot be below a single MPPT limit.");
        return;
      }
      if (!Number.isInteger(inputs) || inputs < 1) {
        setInvSaveError(isGreekScreen ? "Συμπλήρωσε θετικό ακέραιο αριθμό εισόδων ανά MPPT." : "Enter a positive whole number of inputs per MPPT.");
        return;
      }
      generalLimits = { inputs: Array(mpptCount).fill(inputs), current: Array(mpptCount).fill(operating), short: Array(mpptCount).fill(short) };
    }
    if (invParamSet === "sandia" && !invCurrentFit) {
      setInvSaveError(isGreekScreen ? "Ολοκληρώστε την προσαρμογή με τις τρέχουσες τιμές Paco και Pnt πριν την αποθήκευση." : "Complete the fit with the current Paco and Pnt before saving.");
      return;
    }
    if (invParamSet === "sandia" && !invFitEfficiencies) {
      setInvSaveError(isGreekScreen ? "Η προσαρμογή δεν δίνει φυσικά αποδεκτές αποδόσεις. Ελέγξτε τις καμπύλες." : "The fit does not yield physically valid efficiencies. Check the curves.");
      return;
    }
    if (invEuroActive && !invEuroResult.characteristics) {
      setInvSaveError(isGreekScreen ? "Ελέγξτε τα Euro/Max και την ισχύ AC." : "Check Euro/Max and AC power.");
      return;
    }
    const effectiveInvForm = { ...invForm, ...(invEuroActive ? invEuroResult.characteristics : {}), ...(invParamSet === "sandia" ? { ...invCurrentFit, ...invFitEfficiencies } : {}),
      ...(invPerMppt ? { "Num Inputs per MPPT": Math.min(...mpptInputs), Idcmax_per_mppt: Math.min(...mpptCurrent), Iscmax_per_mppt: "" } : {}),
      // Without a short-circuit rating the factor has nothing to qualify; its
      // field is hidden then, and a value left in it is not saved.
      ...((invPerMppt ? mpptShort : generalLimits) ? {} : { mppt_isc_stc_multiplier: "" }) };
    const errs = validateAll(INVERTER_FIELDS, effectiveInvForm);
    setInvErrors(errs);
    if (Object.keys(errs).length) return;

    const characteristics = {};
    for (const f of INVERTER_FIELDS) {
      if (f.key === "inverter_name") continue;
      if (f.key === "Pnt" && invParamSet !== "sandia") continue;
      if (f.key === "pv_array_power_limit_kind" && isEmpty(effectiveInvForm.Pdcmax)) continue;
      const raw = effectiveInvForm[f.key];

      if (isEmpty(raw)) continue;

      if (f.kind === "number" || f.key === "Phases") characteristics[f.key] = Number(String(raw).replace(",", "."));
      else characteristics[f.key] = String(raw).trim();
    }
    if (invPerMppt) {
      characteristics.mppt_strings_per_tracker = mpptInputs;
      characteristics.mppt_max_input_current_a = mpptCurrent;
      if (mpptShort) characteristics.mppt_max_short_circuit_current_a = mpptShort;
    } else if (generalLimits) {
      characteristics.mppt_strings_per_tracker = generalLimits.inputs;
      characteristics.mppt_max_input_current_a = generalLimits.current;
      characteristics.mppt_max_short_circuit_current_a = generalLimits.short;
    }

    // Merge any staged Sandia fit last so its Paco/Pnt and the four cec_*
    // measurement arrays win. The backend refits from those arrays on save, so
    // storing them (not just the coefficients) keeps the fit reproducible.
    if (invParamSet === "sandia" && invCurrentFit) Object.assign(characteristics, invCurrentFit, invFitEfficiencies);

    if (invEuroActive) Object.assign(characteristics, invEuroResult.characteristics);

    setInvSaving(true);
    try {
      const response = await createInverter(
        { inverter_name: String(invForm.inverter_name).trim(), characteristics, ...scopePayload(inverterVisibility, inverterOrganizations) },
        accessToken
      );

      await loadCatalog();
      const savedName = String(response?.name || invForm.inverter_name).trim();
      handleField("read_inverter", savedName);
      setCatalogToast({
        open: true,
        msg: formatCatalogUploadSuccessMessage("Inverter", response, invForm.inverter_name, isGreekScreen),
        severity: "success",
      });

      setAddInvOpen(false);
    } catch (e) {
      setInvSaveError(formatApiError(e, "Failed to create inverter."));
    } finally {
      setInvSaving(false);
    }
  };


  // The electrical design, saved. Lifted out of the editor's onSave so the
  // physical layout can call it too: fitting the strings to a plan edited
  // by hand is an electrical change, and it goes through the same door as
  // one typed in the editor - same validation, same roof sync, same history.
  const saveElectricalDesign = async (designSummary, { systemDesignOverride, plan } = {}) => {
    setDesignSaveNotice(null);
    const generated = autoDesignResult?.spacingInfo;
    const joinedSave = !plan && Boolean(autoDesignResult) && canSaveSimpleAutoLayout(cfg, generated, roofWorkspaceRef.current, roofSceneEditsRef.current);
    const preservePhysical = !plan && Boolean(autoDesignResult) && !joinedSave;
    const selectedModule = designSummary?.module || autoDesignResult?.module || cfg.read_module;
    const selectedInverter = designSummary?.inverter || autoDesignResult?.inverter || cfg.read_inverter;
    // Always persist the capacity shown in the dialog (fallback to inferred if missing)
    const parsedCap =
      designSummary?.capacityKw != null && Number.isFinite(Number(designSummary.capacityKw))
        ? Number(designSummary.capacityKw)
        : null;
    let finalCap = parsedCap;

    if (finalCap == null) {
      const moduleKw = await getModulePdcKw(selectedModule);
      const inferredCap = computeCapacityFromLayout(
        designSummary?.layout,
        moduleKw
      );
      if (inferredCap != null && Number.isFinite(Number(inferredCap))) {
        finalCap = Number(inferredCap);
      }
    }

    const moduleOk =
      selectedModule &&
      ((moduleOptions || []).some((opt) => opt?.name === selectedModule) ||
        (await ensureOptionPresent(
          selectedModule,
          moduleOptions,
          getModuleDetails,
          setModuleOptions
        )));

    const inverterOk =
      selectedInverter &&
      ((inverterOptions || []).some((opt) => opt?.name === selectedInverter) ||
        (await ensureOptionPresent(
          selectedInverter,
          inverterOptions,
          getInverterDetails,
          setInverterOptions
        )));

    if (!moduleOk || !inverterOk) {
      throw electricalSaveError(isGreekScreen
        ? "Ο εξοπλισμός του σχεδίου δεν είναι πλέον διαθέσιμος στον κατάλογο. Επιλέξτε ξανά πλαίσιο και αντιστροφέα."
        : "Selected design equipment is no longer available. Select the module and inverter again.");
    }

    const persistedSpacing = preservePhysical ? persistedPvDesign?.spacingInfo || persistedPvDesign?.spacing || null :
      structuredPvDesign?.spacingInfo ||
      structuredPvDesign?.spacing ||
      persistedPvDesign?.spacingInfo ||
      persistedPvDesign?.spacing ||
      null;

    const designToSave = JSON.parse(
      JSON.stringify({
        ...persistedPvDesign,
        ...designSummary,
        ...(persistedSpacing
          ? {
              spacingInfo: persistedSpacing,
            }
          : {}),
        ...(finalCap != null ? { capacityKw: finalCap } : {}),
        ...(autoDesignStamp() ? { autoDesign: autoDesignStamp() } : {}),
      })
    );
    const systemDesignForCfg = systemDesignOverride || buildSystemDesignFromLayout(
      designToSave,
      selectedInverter
    );

    // Commit both pv_design and pvInstalledCap in one snapshot to avoid overwriting
    let nextCfg = setPathInObject(cfg, "pv_design", designToSave);
    if (systemDesignForCfg) {
      nextCfg = setPathInObject(nextCfg, "systemDesign", systemDesignForCfg);
    }
    if (finalCap != null) {
      // nextCfg = setPathInObject(nextCfg, "pvInstalledCap.kw", finalCap);
      const capStr = Number(finalCap).toFixed(2);
      nextCfg = setPathInObject(nextCfg, "pvInstalledCap.kw", capStr);
    }
    if (moduleOk) {
      nextCfg = setPathInObject(nextCfg, "read_module", selectedModule);
    }
    if (inverterOk) {
      nextCfg = setPathInObject(nextCfg, "read_inverter", selectedInverter);
    }
    if (preservePhysical) nextCfg = preserveAutoDesignLayout(cfg, nextCfg);
    const layoutGeometry = isRoofInstallation(installationType)
      ? await getSelectedModuleGeometry(selectedModule) : null;
    // The module being replaced, so the roof rows keep their clear gap for
    // the new module length instead of keeping a spacing sized for the old one.
    const previousGeometry = isRoofInstallation(installationType) && cfg?.read_module && cfg.read_module !== selectedModule
      ? await getSelectedModuleGeometry(cfg.read_module) : null;
    const roofSync = joinedSave || preservePhysical
      ? { config: nextCfg, changedIds: [], pendingIds: preservePhysical ? orientationList.map(o => o.id) : [] }
      : plan && !isRoofInstallation(installationType)
      ? await applyGroundPlanDesign(nextCfg, plan, body => fetchStringLayout(body, accessToken), isGreekScreen)
      : await syncRoofLayoutsAfterDesign(cfg, nextCfg, {
      moduleLength: layoutGeometry?.length, moduleWidth: layoutGeometry?.width,
      previousModuleLength: previousGeometry?.length, previousModuleWidth: previousGeometry?.width,
      portrait: nextCfg.panelOrientation?.moduleOrientation !== "landscape",
      drafts: plan ? { ...roofWorkspaceRef.current, [plan.id]: {
        source: JSON.stringify(plan.id === orientationList[0].id
          ? cfg.pv_design?.spacingInfo || cfg.pv_design?.spacing || null
          : orientationList.find(o => o.id === plan.id)?.spacingInfo || null), spacing: plan.spacing,
      } } : roofWorkspaceRef.current,
      sceneEdits: plan ? Object.fromEntries(Object.entries(roofSceneEditsRef.current).filter(([id]) => id !== plan.id))
        : roofSceneEditsRef.current, preservePlanIds: plan ? [plan.id] : [],
      fetchMapping: (body) => fetchStringLayout(body, accessToken),
    });
    nextCfg = roofSync.config;
    let joinedResult = null;
    if (joinedSave) {
      joinedResult = await saveSimpleAutoLayout(cfg, nextCfg, generated, {
        module: await getModuleDetails(selectedModule, accessToken),
        fetchMapping: body => fetchStringLayout(body, accessToken), greek: isGreekScreen,
        projectId: currentProject?.id,
        shouldCancel: () => roofWorkspaceProjectRef.current !== currentProject?.id || !layoutSaveActiveRef.current,
      });
      nextCfg = joinedResult.config;
    }
    if (plan) {
      const applied = plan.id === orientationList[0].id ? nextCfg.pv_design?.spacingInfo
        : orientationsFromConfig(nextCfg).find(o => o.id === plan.id)?.spacingInfo;
      if (applied?.module_positions?.length !== physicalLayoutCount(plan.spacing)) throw electricalSaveError(isGreekScreen
        ? "Η αντιστοίχιση των νέων strings δεν ολοκληρώθηκε. Δοκιμάστε ξανά· δεν εφαρμόστηκε αλλαγή."
        : "The new string mapping could not be completed. Retry; no change was applied.");
    }
    if (roofWorkspaceProjectRef.current !== currentProject?.id) throw electricalSaveConflict(isGreekScreen);
    nextCfg = mergeElectricalSaveSnapshot(cfg, nextCfg, cfgRef.current, isGreekScreen);
    roofSync.changedIds.forEach((id) => {
      delete roofWorkspaceRef.current[id];
      delete roofSceneEditsRef.current[id];
    });
    setPlanChecks(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => !roofSync.changedIds.includes(id))));
    history.commit(nextCfg);
    if (joinedSave) setDesignSaveNotice({ severity: joinedResult.shadingError ? "warning" : "success", message:
      joinedResult.shadingError ? scenePreparationMessage(joinedResult.shadingIssue, isGreekScreen, { saved: true }) :
        (isGreekScreen ? "Αποθηκεύτηκαν το ηλεκτρολογικό σχέδιο και η φυσική διάταξη." : "Electrical design and physical layout saved.") });
    else if (preservePhysical) setDesignSaveNotice({ severity: "info", message: isGreekScreen
      ? "Το ηλεκτρολογικό σχέδιο αποθηκεύτηκε. Η υπάρχουσα χωροθέτηση διατηρήθηκε. Ελέγξτε και αποθηκεύστε τη «Φυσική διάταξη» για ενημέρωση των σκιάσεων."
      : "Electrical design saved. Existing placement was preserved. Review and save Physical layout to update shading." });

    setHasUnsavedAutoDesign(false);
    if (joinedSave) {
      setPhysicalLayoutDraft(null);
      setSceneShadingDraft(null);
      setHasUnsavedPhysicalLayout(false);
    } else if (preservePhysical) {
      setHasUnsavedPhysicalLayout(true);
    } else if (isRoofInstallation(installationType)) {
      setPhysicalLayoutDraft(null);
      setSceneShadingDraft(null);
      setHasUnsavedPhysicalLayout(roofSync.pendingIds.length > 0 ||
        Object.values(roofWorkspaceRef.current).some((draft) => draft.dirty));
    } else if (plan) {
      setPhysicalLayoutDraft(null);
      setSceneShadingDraft(null);
      setHasUnsavedPhysicalLayout(false);
    } else if (hasUnsavedPhysicalLayout) {
      setPhysicalLayoutDraft((prev) =>
        prev && typeof prev === "object"
          ? {
              ...prev,
              spacingInfo: prev.spacingInfo || prev.spacing || null,
              module: selectedModule || prev.module || null,
              inverter: selectedInverter || prev.inverter || null,
              design: designToSave,
            }
          : prev
      );
    } else {
      setPhysicalLayoutDraft(null);
    }
    setAutoDesignLayout(null);
    setAutoDesignResult(null);
  };

  // ------------------------------------------------------------
  // 4️⃣ UI
  // ------------------------------------------------------------
  return (
    <Paper elevation={1} sx={{ p: 2, mb: 1.6, borderRadius: 2 }}>
      <SetupPageHeader
        title="Photovoltaic System Setup"
        description="Configure the photovoltaic system design, site assumptions, and supporting electrical parameters for project modeling."
        sx={{ mb: 2 }}
      />

      <Tabs
        value={pvTab}
        onChange={(e, v) => setPvTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1.6, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { minWidth: "auto", px: 1 } }}
      >
        <Tab value="equipment" label="Equipment" />
        <Tab value="system" label="System & Orientation" />
        <Tab value="design" label="Electrical Design & Layout" />
        <Tab value="modelling" label={isGreekScreen ? "Μοντελοποίηση" : "Modelling"} />
        <Tab value="losses" label="PV Losses" />
        <Tab value="generation" label="Generation" />
        <Tab value="knowledge" label="Knowledge" />
      </Tabs>

      {/* ===== TAB: System & Orientation ===== */}
      <Box sx={{ display: pvTab === "system" ? "block" : "none" }}>
          {/* PV sizing fields (capacity, degradation) moved to the Electrical Design & Layout tab */}

          <Grid container spacing={2.25} sx={{ mt: 1.2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <MaybeTooltip
                title="How the modules are held: at one fixed angle, or on a tracker that follows the sun."
                arrow
                placement="top-start"
              >
              <FormControl fullWidth>
                <InputLabel id="pv-mount-type-label"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  Mount Type
                </InputLabel>
                <Select id="pv-mount-type" labelId="pv-mount-type-label"
                  label="Mount Type"
                  value={cfg.mountType}
                  onChange={(e) => handleField("mountType", e.target.value)}
                  renderValue={(v) => optionLabel(MOUNT_TYPE_OPTIONS, v)}
                >
                  {MOUNT_TYPE_OPTIONS.map(optionMenuItem)}
                </Select>
                <FormHelperText sx={{ lineHeight: 1.3, mt: 0.45 }}>
                  • Fixed tilt → constant angle<br />
                  • Single axis → rotates around one axis<br />
                  • Dual axis → rotates around two axes<br />
                </FormHelperText>
              </FormControl>
              </MaybeTooltip>
            </Grid>

            {/* Installation type also sets the thermal model. For a flush roof,
                every orientation card below owns one roof face's angles. */}
            <Grid size={{ xs: 12, md: 4 }}>
              <MaybeTooltip
                title="Where the array sits. It sets how hot the modules run and fills in the thermal model below; with modules flush to a roof it also supplies the tilt and azimuth."
                arrow
                placement="top-start"
              >
              <FormControl fullWidth id="field-pv-installation">
                <InputLabel id="pv-installation-label"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    "&.Mui-focused": { color: "primary.main" },
                  }}
                >
                  Installation
                </InputLabel>
                <Select id="pv-installation" labelId="pv-installation-label"
                  label="Installation"
                  value={installationType}
                  renderValue={(v) => optionLabel(INSTALLATION_OPTIONS, v)}
                  onChange={(e) => {
                    const next = e.target.value;
                    const withType = setPathInObject(
                      cfg,
                      "panelOrientation.installationType",
                      next
                    );
                    // Preserve saved/manual thermal choices. Explicit automatic
                    // settings resolve from installation in the thermal model.
                    history.commit(withType);
                  }}
                >
                  {INSTALLATION_OPTIONS.map(optionMenuItem)}
                </Select>
                <FormHelperText sx={{ lineHeight: 1.3, mt: 0.45 }}>
                  {isFlushRoof ? (
                    <>
                      {isGreekScreen
                        ? "Κάθε πλευρά στέγης ορίζεται χωριστά στην κάρτα της, με δική της κλίση και φορά."
                        : "Set the pitch and facing of each roof face in its own orientation card below."}
                    </>
                  ) : (
                    <>
                      {isGreekScreen
                        ? "Ορίζει τη γεωμετρία εγκατάστασης. Οι θερμικές παραδοχές εμφανίζονται στη Μοντελοποίηση."
                        : "Defines installation geometry. Thermal assumptions are shown on the Modelling tab."}
                    </>
                  )}
                </FormHelperText>
              </FormControl>
              </MaybeTooltip>
            </Grid>

            {tiltInheritedFromTerrain && (
              <Grid size={12}>
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
                >
                  <b>The panel tilt is being inherited from the terrain slope</b>{" "}
                  ({Number(cfg?.loc_info?.tilt)}°). That is the angle of the
                  ground, not the plane the modules sit at, and it is carried
                  over only because this project predates the installation-type
                  field. Choose an installation above and set a panel tilt — or
                  turn the tilt optimiser on — so the report states a design
                  decision rather than a hillside.
                </Alert>
              </Grid>
            )}

            {(cfg.mountType === "single_axis" ||
              cfg.mountType === "dual_axis") && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Target ground coverage (GCR)"
                  type="number"
                  value={cfg?.pvDesign?.spacingInfo?.targetGcr ?? ""}
                  inputProps={{ min: 0.05, max: 0.95, step: 0.01 }}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleField(
                      "pvDesign.spacingInfo.targetGcr",
                      v === "" ? "" : Math.min(0.95, Math.max(0.05, Number(v)))
                    );
                  }}
                  helperText={
                    <>
                      Leave blank for 0.35. A tracker never clears its own shadow
                      — it backtracks — so a shadow-free spacing date is the wrong
                      criterion and GCR is the number vendors quote.<br />
                      Denser costs energy: about −1.3% of plane irradiance at
                      0.40 and −4.6% at 0.50, against 0.35.
                    </>
                  }
                  FormHelperTextProps={{ sx: { mt: 0.45, lineHeight: 1.3 } }}
                  InputLabelProps={{
                    sx: {
                      color: "primary.main",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      "&.Mui-focused": { color: "primary.main" },
                    },
                  }}
                />
              </Grid>
            )}

            {cfg.mountType === "single_axis" && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="pv-axis-orientation-label"
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  >
                    Axis Orientation
                  </InputLabel>
                  <Select id="pv-axis-orientation" labelId="pv-axis-orientation-label"
                    label="Axis Orientation"
                    value={cfg.axisOrientation}
                    onChange={(e) =>
                      handleField("axisOrientation", e.target.value)
                    }
                  >
                    <MenuItem value="North-South">North-South</MenuItem>
                    <MenuItem value="East-West">East-West</MenuItem>
                  </Select>
                  <FormHelperText sx={{ lineHeight: 1.3, mt: 0.45 }}>
                    • North-South axis tracks the sun East-West<br />
                    • East-West axis tracks the sun North-South<br />
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {cfg.mountType === "single_axis" && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Max Angle (deg)"
                  type="number"
                  error={orientationTiltMissing}
                  value={cfg.maxAngle || ""}
                  inputProps={{ min: 0, max: 90 }}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    handleField("maxAngle", Number.isNaN(val) ? "" : val);
                  }}
                  helperText={
                    <>
                      Limits how far panels can rotate toward sunrise/sunset..<br />
                      Typical 0–90°, most sites use 50–60°.
                    </>
                  }
                  InputLabelProps={{
                    sx: {
                      color: "primary.main",           // normal state
                      fontWeight: 600,
                      letterSpacing: "0.02em",

                      "&.Mui-focused": {
                        color: "primary.main",         // when focused
                      },
                    },
                  }}
                />
              </Grid>
            )}


            {cfg.mountType === "single_axis" && (
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledSwitchWithHint
                  label="Backtrack"
                  checked={cfg.backTrack}
                  onChange={(e) =>
                    handleField("backTrack", e.target.checked)
                  }
                  hint={
                    <>
                      Controls whether the tracker has the capability to "backtrack"
                      to avoid row-to-row shading.
                    </>
                  }
                />
              </Grid>
            )}

          </Grid>


          <Divider sx={{ my: 1.15 }} />



          {/* -------------------- Panel Orientation -------------------- */}
          <Paper elevation={0} sx={{ ...pvSetupSectionSx, mb: 1.1 }}>
          {/* One panel per plane, the selected one open with the form below
              in it; a single-plane project shows the form as it always did. */}
          <OrientationPanels
            cfg={cfg}
            activeId={activeOrientationId}
            onSelect={setActiveOrientationId}
            onChange={(next) => history.commit(next)}
            plantKwp={Number(cfg?.pvInstalledCap?.kw) || null}
            onOpenElectricalDesign={() => {
              setPvTab("design");
              setDesignStep("electrical");
            }}
            heading={
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontSize: "0.85rem",
                  color: "text.primary",
                }}
              >
                {isFlushRoof
                  ? (isGreekScreen ? "Πλευρές στέγης" : "Roof faces")
                  : "Panel Orientation"}
              </Typography>
            }
          >
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" },
                     gap: 2.5, alignItems: "stretch", mt: 0 }}>

            <Box sx={{ flex: { xs: "1 1 auto", md: isFlushRoof ? "1 1 auto" : "0 0 440px" }, width: { xs: "100%", md: isFlushRoof ? "100%" : 440 },
                       display: "flex", flexDirection: "column", gap: 1.5 }}>
              {installationType === "roof_frame" && <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <TextField size="small" type="number" label={isGreekScreen ? "Κλίση στέγης (°)" : "Roof pitch (°)"}
                  value={activeFrameRoofPitch ?? ""} inputProps={{ min: 0, max: 74.9, step: 1, readOnly: activeRoofDrawn }}
                  error={activeFrameRoofPitch == null || activeFrameRoofPitch === "" || Number(activeFrameRoofPitch) < 0 || Number(activeFrameRoofPitch) >= 75}
                  onChange={activeRoofDrawn ? undefined : (e) => setActiveOrientationField("roofPitch", e.target.value === "" ? "" : Number(e.target.value))}
                  helperText={activeRoofDrawn
                    ? fromRoofNote("Από τη στέγη: η κλίση της πλευράς όπως ορίστηκε στην κάτοψη.", "From the roof: the face's pitch as set on the plan.")
                    : (isGreekScreen ? "Κλίση της στέγης αυτής της πλευράς· τα πλαίσια στις βάσεις έχουν δική τους κλίση." : "Slope of this roof face; framed modules have their own tilt.")}
                  sx={{ flex: "1 1 170px" }} />
                <TextField size="small" type="number" label={isGreekScreen ? "Φορά στέγης (°)" : "Roof bearing (°)"}
                  value={activeFrameRoofAzimuth ?? ""} inputProps={{ min: 0, max: 359.9, step: 1, readOnly: activeRoofDrawn }}
                  error={activeFrameRoofAzimuth == null || activeFrameRoofAzimuth === "" || Number(activeFrameRoofAzimuth) < 0 || Number(activeFrameRoofAzimuth) >= 360}
                  onChange={activeRoofDrawn ? undefined : (e) => setActiveOrientationField("roofAzimuth", e.target.value === "" ? "" : Number(e.target.value))}
                  helperText={activeRoofDrawn
                    ? fromRoofNote("Από τη φορά της πλευράς στην κάτοψη· μπορεί να οριστεί ανεξάρτητα από την ακμή αναφοράς.", "From the face's bearing on the plan; it can be set independently of the reference edge.")
                    : (isGreekScreen ? "Δεξιόστροφα από τον Βορρά: 0° Β, 90° Α, 180° Ν, 270° Δ." : "Clockwise from north: 0° N, 90° E, 180° S, 270° W.")}
                  sx={{ flex: "1 1 170px" }} />
              </Box>}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }} id="field-pv-tilt">
                <TextField
                  fullWidth
                  label={isFlushRoof ? (isGreekScreen ? "Κλίση πλευράς στέγης (°)" : "Roof face pitch (°)") : "Tilt (deg)"}
                  type="number"
                  disabled={orientationFieldsDisabled}
                  error={isFlushRoof ? (activeTilt === null || activeTilt === undefined || activeTilt === "") : activeIsPrimary ? orientationTiltMissing : activeOrientation.tilt === null}
                  value={activeTilt ?? ""}
                  inputProps={{ min: 0, max: 90, step: 1, readOnly: isFlushRoof && activeRoofDrawn }}
                  onChange={isFlushRoof && activeRoofDrawn ? undefined : (e) => {
                    // 0-90 is the only meaningful range: 0 is flat, 90 is vertical,
                    // beyond that the modules would face the ground. Clamp rather
                    // than accept a value the engine cannot use.
                    const raw = e.target.value;
                    (isFlushRoof ? setActiveRoofFace : setActiveOrientationField)(
                      "tilt",
                      raw === "" ? "" : Math.min(90, Math.max(0, Number(raw)))
                    );
                  }}
                  helperText={
                    isFlushRoof && activeRoofDrawn
                      ? fromRoofNote("Από τη στέγη: η κλίση της πλευράς όπως ορίστηκε στην κάτοψη· είναι και η κλίση των πλαισίων.", "From the roof: the face's pitch as set on the plan; it is also the modules' tilt.")
                    : isFlushRoof ? (
                      isGreekScreen
                        ? "Η κλίση αυτής της πλευράς είναι και η κλίση των συνεπίπεδων πλαισίων της."
                        : "The pitch of this roof face is also the tilt of its flush-mounted modules."
                    ) : (
                      <>
                        Panel tilt measured from horizontal (0° = flat, 90° = vertical).<br />
                        Typical values: 10–35° depending on latitude.
                      </>
                    )
                  }
                  FormHelperTextProps={{ sx: { mt: 0.45, lineHeight: 1.3 } }}
                  InputLabelProps={{
                    sx: {
                      color: "primary.main",           // normal state
                      fontWeight: 600,
                      letterSpacing: "0.02em",

                      "&.Mui-focused": {
                        color: "primary.main",         // when focused
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }} id="field-pv-azimuth">
                <TextField
                  fullWidth
                  label={isFlushRoof ? (isGreekScreen ? "Φορά πλευράς στέγης (°)" : "Roof face facing (°)") : "Azimuth (deg)"}
                  type="number"
                  disabled={orientationFieldsDisabled}
                  error={isFlushRoof ? (activeAzim === null || activeAzim === undefined || activeAzim === "") : activeIsPrimary ? orientationAzimMissing : activeOrientation.azim === null}
                  value={activeAzim ?? ""}
                  inputProps={{ min: 0, max: 360, step: 1, readOnly: isFlushRoof && activeRoofDrawn }}
                  onChange={isFlushRoof && activeRoofDrawn ? undefined : (e) => {
                    const raw = e.target.value;
                    (isFlushRoof ? setActiveRoofFace : setActiveOrientationField)(
                      "azim",
                      raw === "" ? "" : Math.min(360, Math.max(0, Number(raw)))
                    );
                  }}
                  helperText={
                    isFlushRoof && activeRoofDrawn
                      ? fromRoofNote("Από τη φορά της πλευράς στην κάτοψη (0° Β, 90° Α, 180° Ν, 270° Δ).", "From the face's bearing on the plan (0° N, 90° E, 180° S, 270° W).")
                    : isFlushRoof ? (
                      isGreekScreen
                        ? "Αζιμούθιο αυτής της πλευράς, δεξιόστροφα από τον Βορρά: 0° Β, 90° Α, 180° Ν, 270° Δ."
                        : "Azimuth of this roof face, clockwise from north: 0° N, 90° E, 180° S, 270° W."
                    ) : (
                      <>
                        Measured clockwise from north.<br />
                        0°=N, 90°=E, 180°=S, 270°=W.
                      </>
                    )
                  }
                  FormHelperTextProps={{ sx: { mt: 0.45, lineHeight: 1.3 } }}
                  InputLabelProps={{
                    sx: {
                      color: "primary.main",           // normal state
                      fontWeight: 600,
                      letterSpacing: "0.02em",

                      "&.Mui-focused": {
                        color: "primary.main",         // when focused
                      },
                    },
                  }}
                />
              </Box>
            </Box>

              {!isFlushRoof && String(cfg?.mountType || "fixed") === "fixed" && (
                <MaybeTooltip
                  title="Which plane the tilt above is measured from. On level ground both give the same result."
                  arrow
                  placement="top-start"
                >
                <AccessibleSelectTextField
                  select
                  SelectProps={{ renderValue: (v) => optionLabel(TILT_REFERENCE_OPTIONS, v) }}
                  fullWidth
                  size="small"
                  label="Tilt measured from"
                  disabled={orientationFieldsDisabled}
                  value={activeTiltReference === "ground" ? "ground" : "horizontal"}
                  onChange={(e) => setActiveOrientationField("tiltReference", e.target.value)}
                  helperText={
                    activeMountingPlane.changed
                      ? describeMountingPlane(activeMountingPlane)
                      : activeTiltReference === "ground"
                        ? "The racking follows the terrain. The site is level (or its slope is not set on the Project page), so this is the same plane."
                        : "The usual convention: the angle between the modules and the horizontal, posts cut to the slope. Choose \"the ground\" for equal-pile racking that follows the terrain."
                  }
                  FormHelperTextProps={{ sx: { mt: 0.45, lineHeight: 1.3 } }}
                  InputLabelProps={{ sx: { color: "primary.main", fontWeight: 600, letterSpacing: "0.02em", "&.Mui-focused": { color: "primary.main" } } }}
                >
                  {TILT_REFERENCE_OPTIONS.map(optionMenuItem)}
                </AccessibleSelectTextField>
                </MaybeTooltip>
              )}

            </Box>

            {/* The sweep's own grid, sliced two ways.
                Drawn from a coarse sweep that runs on its own, so the trade-off
                is on screen before anyone presses Optimize -- and re-sliced in
                the browser as the tilt and azimuth fields are edited, so the
                marker follows what is typed. Pressing Optimize replaces it with
                the fine sweep. */}
            {/* PVsyst puts the two profile plots beside the angle fields, and
                that is the right shape: the fields are the subject, the curves
                are what the fields do. Left: what you set. Right: what it costs. */}
            {!isFlushRoof && <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Box
                  sx={{
                    border: "1px solid", borderColor: "divider", borderRadius: 1,
                    p: 1.5, bgcolor: "action.hover", height: "100%",
                  }}
                >
                  {/* The sweep's settings and the button that runs it, in one
                      row above the curves they produce. In the 440px column
                      beside the angle fields they were squeezed until the
                      button text spilled out of its outline. */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1.25,
                      mb: 1.5,
                      "& .MuiButton-root": { whiteSpace: "nowrap", flexShrink: 0 },
                    }}
                  >
                {/* What the sweep maximises. Yield in every case: a season is a
                    different set of hours, not a different objective. */}
                <MaybeTooltip
                  title="Which hours the Optimize button maximises. The target is always energy yield."
                  arrow
                  placement="top-start"
                >
                <AccessibleSelectTextField
                  select
                  size="small"
                  label="Optimise for"
                  value={orientationObjective}
                  onChange={(e) => setOrientationObjective(e.target.value)}
                  disabled={orientationFieldsDisabled || orientationLoading}
                  sx={{ width: { xs: "100%", sm: 240 } }}
                  SelectProps={{ renderValue: (v) => optionLabel(ORIENTATION_OBJECTIVE_OPTIONS, v) }}
                >
                  {ORIENTATION_OBJECTIVE_OPTIONS.map(optionMenuItem)}
                </AccessibleSelectTextField>
                </MaybeTooltip>
                {/* Tilt-only search, for a facing the site decides (a roof
                    edge, a road, the parcel's long side) rather than the sun. */}
                <MaybeTooltip
                  title="Search the tilt only and keep the azimuth as it is: for a facing set by the site (a roof edge, a road, the shape of the parcel) rather than by the sun."
                  arrow
                  placement="top-start"
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={keepAzimuth}
                        onChange={(e) => setKeepAzimuth(e.target.checked)}
                        disabled={
                          orientationFieldsDisabled ||
                          orientationLoading ||
                          toFiniteNumber(activeAzim) == null
                        }
                      />
                    }
                    label={<Typography variant="body2">Keep the azimuth (tilt only)</Typography>}
                  />
                </MaybeTooltip>
                <MaybeTooltip title={isGreekScreen ? ORIENTATION_METHODOLOGY_EL : ORIENTATION_METHODOLOGY} arrow>
                  <IconButton size="small" tabIndex={-1}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </MaybeTooltip>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOptimizeOrientation}
                  disabled={
                    orientationFieldsDisabled ||
                    orientationLoading ||
                    cfg?.loc_info?.lat == null ||
                    cfg?.loc_info?.lat === "" ||
                    cfg?.loc_info?.lon == null ||
                    cfg?.loc_info?.lon === "" ||
                    !cfg?.surfaceType
                  }
                  startIcon={
                    orientationLoading ? <CircularProgress size={16} thickness={5} /> : null
                  }
                >
                  {orientationLoading ? "Optimizing..." : "Optimize"}
                </Button>
                {orientationSuccessOpen && (
                  <Alert
                    severity="success"
                    variant="filled"
                    icon={<CheckCircleOutlineIcon fontSize="small" />}
                    sx={{
                      py: 0,
                      alignItems: "center",
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        py: "4px",
                      },
                    }}
                  >
                    Orientation optimized
                  </Alert>
                )}
                  </Box>
                  {orientationError && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>{orientationError}</Alert>
                  )}
                  {savedOrientationOptimization && !restoredOrientationResult(savedOrientationOptimization, orientationResultKey) && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {isGreekScreen
                        ? "Η προηγούμενη βελτιστοποίηση έχει αποθηκευτεί, αλλά δεν αντιστοιχεί στις τρέχουσες παραμέτρους υπολογισμού ή δεν περιέχει πλήρες γράφημα. Οι επιλεγμένες γωνίες διατηρούνται. Το παρακάτω γράφημα είναι νέα πρόχειρη εκτίμηση· εκτελέστε ξανά Βελτιστοποίηση για ενημερωμένο αποτέλεσμα."
                        : "The previous optimization is saved, but does not match the current calculation settings or has no complete chart. Your selected angles are retained. The chart below is a new preliminary estimate; run Optimize again for an updated result."}
                    </Alert>
                  )}
                  {orientationChart ? (
                    <>
                      {/* Two across, sharing the panel's width. They were capped
                          at 340px each and left half the panel empty beside and
                          below them. */}
                      <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 3,
                      }}>
                        <OrientationProfileChart
                          title="Yield against tilt"
                          yAxisLabel={orientationChart.axisLabel}
                          locale={isGreekScreen ? "el-GR" : "en-GB"}
                          points={orientationChart.tiltPoints}
                          current={
                            orientationChart.current
                              ? { x: orientationChart.current.tilt, y: orientationChart.current.y }
                              : null
                          }
                          optimumX={orientationChart.optimalTilt}
                          caption={isGreekScreen
                            ? `Τομή στο αζιμούθιο ${String(orientationChart.atAzimuth)}°`
                            : `Sliced at azimuth ${orientationChart.atAzimuth}°`}
                        />
                        <OrientationProfileChart
                          title="Yield against azimuth"
                          yAxisLabel={orientationChart.axisLabel}
                          locale={isGreekScreen ? "el-GR" : "en-GB"}
                          points={orientationChart.azimuthPoints}
                          current={
                            orientationChart.current
                              ? { x: orientationChart.current.azimuth, y: orientationChart.current.y }
                              : null
                          }
                          optimumX={orientationChart.fixed ? null : orientationChart.optimalAzimuth}
                          caption={isGreekScreen
                            ? `Τομή στην κλίση ${String(orientationChart.atTilt)}°`
                            : `Sliced at tilt ${orientationChart.atTilt}°`}
                        />
                      </Box>
                      {orientationChart.capacityKw && (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                          {isGreekScreen
                            ? `Οι kWh προκύπτουν από την ειδική απόδοση × ${orientationChart.capacityKw.toLocaleString("en-GB")} kWp εγκατεστημένης ισχύος. Είναι εκτίμηση DC, πριν από τις απώλειες και την αντιστροφή DC/AC.`
                            : `kWh = specific yield × ${orientationChart.capacityKw.toLocaleString("en-GB")} kWp installed capacity. This is DC energy before system losses and inverter conversion.`}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", mt: 1 }}
                      >
                        {orientationChart.fixed ? (
                          isGreekScreen ? (
                            <span data-no-translate>
                              Καλύτερη κλίση για{" "}
                              <b>
                                {orientationChart.objective === "summer"
                                  ? "το καλοκαίρι (Απρ–Σεπ)"
                                  : orientationChart.objective === "winter"
                                    ? "τον χειμώνα (Οκτ–Μαρ)"
                                    : "το έτος"}
                              </b>{" "}
                              με σταθερό αζιμούθιο {String(orientationChart.optimalAzimuth)}°:{" "}
                              <b>{orientationChart.optimalTilt}°</b>.
                              {orientationChart.lossPct != null && (
                                <> Η τρέχουσα επιλογή είναι <b>{Math.abs(orientationChart.lossPct).toFixed(2)}%</b> κάτω από αυτό.</>
                              )}
                              {orientationChart.freeGainPct != null && orientationChart.freeGainPct > 0.005 && (
                                <> Αν άλλαζε και το αζιμούθιο, η απόδοση θα ανέβαινε ακόμη <b>{orientationChart.freeGainPct.toFixed(2)}%</b>.</>
                              )}
                              {orientationChart.coarse ? " Σχεδιασμένο από μια γρήγορη σάρωση 5° — πατήστε Βελτιστοποίηση για την ακριβή απάντηση." : ""}
                            </span>
                          ) : (
                            <>
                              Best tilt for{" "}
                              <b>
                                {orientationChart.objective === "summer"
                                  ? "summer (Apr–Sep)"
                                  : orientationChart.objective === "winter"
                                    ? "winter (Oct–Mar)"
                                    : "the year"}
                              </b>{" "}
                              with the azimuth kept at {orientationChart.optimalAzimuth}°:{" "}
                              <b>{orientationChart.optimalTilt}°</b>.
                              {orientationChart.lossPct != null && (
                                <> Where you are now is <b>{Math.abs(orientationChart.lossPct).toFixed(2)}%</b> below it.</>
                              )}
                              {orientationChart.freeGainPct != null && orientationChart.freeGainPct > 0.005 && (
                                <> Letting the azimuth move as well would add <b>{orientationChart.freeGainPct.toFixed(2)}%</b>.</>
                              )}
                              {orientationChart.coarse ? " Drawn from a quick 5° sweep — press Optimize for the precise answer." : ""}
                            </>
                          )
                        ) : (
                        <>
                        Best for{" "}
                        <b>
                          {orientationChart.objective === "summer"
                            ? "summer (Apr–Sep)"
                            : orientationChart.objective === "winter"
                              ? "winter (Oct–Mar)"
                              : "the year"}
                        </b>
                        : tilt <b>{orientationChart.optimalTilt}°</b>, azimuth{" "}
                        <b>{orientationChart.optimalAzimuth}°</b>.
                        {orientationChart.lossPct != null && (
                          <>
                            {" "}Where you are now is{" "}
                            <b>{Math.abs(orientationChart.lossPct).toFixed(2)}%</b> below it.
                          </>
                        )}
                        {orientationChart.coarse
                          ? " Drawn from a quick 5° sweep — press Optimize for the precise answer."
                          : ""}
                        </>
                        )}{" "}
                        The curves are usually flat near the peak: a few degrees either way
                        normally costs a fraction of a percent, and the land, the row pitch
                        and the structure often decide it instead.
                      </Typography>
                    </>
                  ) : orientationPreviewBusy ? (
                    <PvLoadingRegion minHeight={120} isGreek={isGreekScreen} />
                  ) : null}
                </Box>
            </Box>}
          </Box>

          {/* Live geometry preview of the two angles above. Deliberately a plain
              Box sibling of the Grid container: on MUI v7 the legacy Grid "item"
              and "xs" props size nothing, so as a grid child this rendered as an
              auto-width flex item alongside the fields instead of beneath them. */}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <PvOrientationDiagram
              tilt={activeTilt}
              azimuth={activeAzim}
              latitude={cfg?.loc_info?.lat}
              mountType={cfg?.mountType}
            />
          </Box>
          </OrientationPanels>

          {/* The model choices moved to their own Modelling tab: they decide
              the physics of every number in the report, which is not something
              to find by opening a collapsed panel under the tilt fields. */}
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
            Sky, incidence-angle, thermal, shading and conversion models are set
            under <b>Modelling</b>.
          </Typography>
          <OrientationStatus cfg={cfg} />
          </Paper>


          <Divider sx={{ my: 1.15 }} />

          </Box>{/* end System & Orientation tab */}


          {/* ===== TAB: Modelling ===== */}
          <Box sx={{ display: pvTab === "modelling" ? "block" : "none" }}>
          <PvLoadingRegion loading={moduleModelingLoading || inverterCharsLoading} isGreek={isGreekScreen}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {isGreekScreen ? "Μοντελοποίηση" : "Modelling"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5, maxWidth: 820 }}>
            {isGreekScreen
              ? "Ρυθμίσεις μετατροπής των καιρικών δεδομένων σε παραγωγή. Ελέγξτε τα μοντέλα και τις ενεργές παραμέτρους πριν από νέα εκτέλεση."
              : "Settings that turn weather data into generation. Review models and active parameters before running a new calculation."}
          </Typography>
          {/* Optional physics model choices. Collapsed by default: the values
              in here are already the recommended ones, so a user who never
              opens it gets the same result as one who does. */}
          {pvTab === "modelling" && <PvAdvancedModeling
            transpositionModel={cfg.panelOrientation?.transpositionModel}
            aoiModel={cfg.panelOrientation?.aoiModel}
            onChange={(key, value) =>
              handleField(`panelOrientation.${key}`, value)
            }
            // Reset touches four fields at once, so it must go through a single
            // commit; four handleField calls would collapse into just the last.
            onReset={(entries) =>
              handleFields(
                entries.map(([key, value]) => [`panelOrientation.${key}`, value])
              )
            }
            thermalModel={cfg.panelOrientation?.thermalModel}
            spectralModel={cfg.panelOrientation?.spectralModel}
            acModel={cfg.panelOrientation?.acModel}
            arrayGeometry={cfg.panelOrientation?.arrayGeometry}
            nearShadingModel={cfg.panelOrientation?.nearShadingModel}
            nearShadingElectricalEffect={
              cfg.panelOrientation?.nearShadingElectricalEffect
            }
            moduleOrientation={cfg.panelOrientation?.moduleOrientation}
            horizonProfile={cfg.panelOrientation?.horizonProfile}
            siteLat={cfg?.loc_info?.lat ?? cfg?.lat}
            siteLon={cfg?.loc_info?.lon ?? cfg?.lon}
            onFetchHorizon={handleFetchHorizon}
            onClearHorizon={handleClearHorizon}
            horizonBusy={horizonBusy}
            horizonError={horizonError}
            capability={moduleModeling}
            isBifacial={moduleIsBifacial}
            detailedShadingEnabled={cfg?.pvDetailedShading?.enabled === true}
            defaultExpanded
            moduleName={cfg?.read_module}
            mountingConfig={cfg.panelOrientation?.mountingConfig}
            installationType={cfg.panelOrientation?.installationType}
            thermalSettings={cfg.panelOrientation?.thermalSettings}
            surfaceType={cfg?.surfaceType}
            inverterName={cfg?.read_inverter}
            inverterHasCurves={inverterHasCurves}
            inverterHasOnd={Boolean(inverterChars?.ondModel)}
            // The layout's shading holds a fixed array's own rows: "Not
            // modelled" could not take their shadow out of it.
            layoutShadingIncludesRows={shadingMode !== "percentage"
              && !(cfg?.pvDetailedShading?.enabled === true)
              && (cfg?.mountType || "fixed") === "fixed"
              && (cfg?.panelOrientation?.installationType || "ground") !== "roof_flush"
              && [cfg?.pv_design, ...(cfg?.orientations || []).slice(1)]
                .some((record) => record?.spacingInfo?.scene_review_required)}
          />}
          </PvLoadingRegion>
          </Box>{/* end Modelling tab */}

          {/* ===== TAB: Equipment ===== */}
          <Box sx={{ display: pvTab === "equipment" ? "block" : "none" }}>
          <PvLoadingRegion loading={!equipmentReady} isGreek={isGreekScreen}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1, maxWidth: 860, lineHeight: 1.35 }}>
            Select the photovoltaic module and inverter for the plant. Electrical design and
            layout are configured in the Electrical Design &amp; Layout tab.
          </Typography>

          {/* Two cards, so two card-widths of air between them. At 1.05 they
              shared an edge region narrower than the padding inside either
              one, which reads as a single divided card rather than as two. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.9 }}>
            {/* ===== MODULE SIDE ===== */}
            <Paper
              elevation={0}
              sx={pvSetupEquipmentCardSx("module")}
            >
              <Typography
                variant="subtitle2"
                sx={pvSetupEquipmentCardTitleSx}
              >
                Module
              </Typography>

              <Box
                sx={pvSetupEquipmentInnerGridSx}
              >
                <Box sx={pvSetupSelectorPanelSx} id="field-pv-module">
                  <Typography variant="body2" sx={pvSetupSelectorTitleSx}>
                    Module selection
                  </Typography>
                  <Box sx={pvSetupEquipmentSelectorRowSx}>
                    <Autocomplete
                      disabled={catalogLoading}
                      options={sortedModuleOptions.map((m) => m.name)}
                      getOptionLabel={moduleOptionLabel}
                      filterOptions={catalogFilterOptions}
                      value={cfg.read_module || null}
                      onChange={(_, newValue) => {
                        handleField("read_module", newValue || "");
                      }}
                      sx={pvSetupEquipmentAutocompleteSx}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="PV module"
                          placeholder={
                            catalogLoading
                              ? "Loading modules..."
                              : "Start typing module name..."
                          }
                          error={Boolean(catalogError)}
                          size="small"
                          InputLabelProps={{
                            sx: {
                              color: "primary.main",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              "&.Mui-focused": {
                                color: "primary.main",
                              },
                            },
                          }}
                        />
                      )}
                      clearOnBlur={false}
                      autoSelect
                      autoHighlight
                      size="small"
                    />
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                      {/* A labelled control, not a bare glyph. An "i" reads as
                          a footnote; this opens the full datasheet, which is a
                          different promise and deserves the word for it. */}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SearchIcon fontSize="small" />}
                        disabled={!cfg.read_module}
                        onClick={handleOpenModuleDialog}
                        sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0 }}
                      >
                        Details
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!cfg.read_module}
                        onClick={handleOpenModuleMonitor}
                        sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0 }}
                      >
                        I-V / P-V Monitor
                      </Button>
                      {/* Only rendered when this organisation may actually remove the
                          selected item: its own equipment always, a shared library
                          item only for an administrator. The server enforces the same
                          rule independently - hiding the control is courtesy, not
                          protection. */}
                      {canDeleteSelected("module") ? (
                        <MaybeTooltip title="Remove from equipment catalogue">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => requestCatalogDelete("module", cfg.read_module)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </MaybeTooltip>
                      ) : null}
                    </Stack>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.45, color: catalogError ? "error.main" : "text.secondary", lineHeight: 1.45 }}
                  >
                    {/* A match count rather than a list of the active filter names.
                        The previous wording named the filters explicitly ("power and
                        bifaciality") and went stale the moment vendor and technology
                        were added. The count cannot rot, and it answers the question
                        the user actually has — did that filter do what I expected —
                        including the case that matters most, zero matches. */}
                    {catalogError
                      ? catalogError
                      : moduleFiltersEnabled
                      ? catalogFilterSummary("module", moduleOptions.length, catalogFacets.modules.total, isGreekScreen)
                      : "Choose from the full module catalog or start typing a module name directly."}
                  </Typography>
                  {/\bCPV\b|concentrat/i.test(`${cfg.read_module || ""} ${moduleChars?.Technology || ""}`) ? (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
                      {isGreekScreen
                        ? `Το επιλεγμένο πλαίσιο φαίνεται να είναι συγκεντρωτικό Φ/Β (CPV), όχι συμβατικό επίπεδο πλαίσιο. Επιβεβαιώστε ότι αυτός είναι ο πραγματικός εξοπλισμός, ότι υπάρχουν οι απαιτούμενες οπτικές/παρακολούθηση και ότι ο τύπος εγκατάστασης (${installationType}) είναι σωστός πριν από την εκτίμηση παραγωγής.`
                        : `The selected module appears to be concentrated PV (CPV), not a conventional flat-plate module. Confirm that it matches the installed equipment, that the required optics/tracking are present, and that the installation type (${installationType}) is correct before estimating generation.`}
                    </Alert>
                  ) : null}
                  {/* The two figures a string length is chosen from, computed
                      at this project's own design temperatures so the panel and
                      the electrical design tab can never disagree. */}
                  {(() => {
                    if (!cfg.read_module || !moduleChars) return null;
                    const v = sizingVoltages(moduleChars, {
                      minCellC: cfg?.autoDesign?.minCellC,
                      maxCellC: cfg?.autoDesign?.maxCellC,
                    });
                    if (v.vocCold == null && v.vmpHot == null) return null;
                    const wp = Number(moduleChars.STC ?? moduleChars.Pmp0);
                    return pvSetupFigureStrip({
                      figures: [
                        ...(Number.isFinite(wp) ? [{ label: "Nominal Power", value: `${wp.toFixed(0)} Wp` }] : []),
                        {
                          label: `Voc at ${v.coldC}\u00a0°C`,
                          value: v.vocCold == null ? "—" : `${v.vocCold.toFixed(1)} V`,
                        },
                        {
                          label: `Vmpp at ${v.hotC}\u00a0°C`,
                          value: v.vmpHot == null ? "—" : `${v.vmpHot.toFixed(1)} V`,
                        },
                      ],
                      note:
                        "Sizing voltages at the cell temperatures this project checks strings at" +
                        (v.coeffSource === "approximated from the power coefficient"
                          ? " · Vmpp coefficient approximated from the power coefficient"
                          : ""),
                    });
                  })()}
                  <Box sx={{ mt: 0.15 }}>
                    {catalogWriteAllowed ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setModuleSaveError("");
                          setModuleErrors({});
                          setModuleForm((prev) => ({ ...prev, module_name: "" }));
                          setModulePanImport(null);
                          setAddModuleOpen(true);
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        + Add custom module
                      </Button>
                    ) : null}
                  </Box>
                </Box>

                <Box sx={{ ...pvSetupFilterPanelSx, ...(moduleFiltersEnabled ? { minHeight: 130 } : null) }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.35, gap: 1 }}>
                    <MaybeTooltip title="Narrow the catalog by module power range, manufacturer and cell technology, and optionally keep only bifacial modules, before selecting equipment.">
                      <Typography variant="body2" sx={{ ...pvSetupPanelTitleSx, cursor: "help" }}>
                        Catalog filters
                      </Typography>
                    </MaybeTooltip>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Only when something is set: a permanent "Clear" implies
                          there is state to clear even when there is none. */}
                      {moduleFiltersEnabled && moduleFiltersDirty ? (
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={clearModuleFilters}
                          sx={{ fontWeight: 600 }}
                        >
                          Clear
                        </Link>
                      ) : null}
                      <Box component="span" sx={pvSetupFilterStateBadgeSx(moduleFiltersEnabled)}>
                        {moduleFiltersEnabled ? "Filters enabled" : "Filters disabled"}
                      </Box>
                    </Box>
                  </Box>

                  <FormControlLabel
                    sx={{ m: 0, mb: moduleFiltersEnabled ? 0.6 : 0.2 }}
                    control={
                      <Switch
                        size="small"
                        checked={moduleFiltersEnabled}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setModuleFiltersEnabled(on);

                          if (!on) {
                            setModuleFilters({
                              minPdc: "",
                              maxPdc: "",
                              bifacialOnly: false,
                              ownedOnly: false,
                              vendor: "",
                              technology: "",
                              technologyValues: [],
                            });
                          }
                        }}
                      />
                    }
                    label="Enable module filters"
                  />

                  {moduleFiltersEnabled ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                        gap: 0.75,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Min Pdc (W)"
                        type="number"
                        value={moduleFilters.minPdc}
                        onChange={(e) =>
                          setModuleFilters((f) => ({ ...f, minPdc: e.target.value }))
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Pdc (W)"
                        type="number"
                        value={moduleFilters.maxPdc}
                        onChange={(e) =>
                          setModuleFilters((f) => ({ ...f, maxPdc: e.target.value }))
                        }
                      />
                      <Autocomplete
                        size="small"
                        options={catalogFacets.modules.vendors}
                        value={moduleFilters.vendor || null}
                        onChange={(_e, v) =>
                          setModuleFilters((f) => ({ ...f, vendor: v || "" }))
                        }
                        filterOptions={catalogFilterOptions}
                        // Not freeSolo: every option comes from the catalogue, so a
                        // typed value that matches nothing can only ever produce an
                        // empty list with no explanation.
                        disabled={catalogFacets.modules.vendors.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Manufacturer"
                            placeholder={
                              catalogFacets.modules.vendors.length ? "Any" : "None recorded"
                            }
                          />
                        )}
                      />
                      {/* Grouped labels, not the raw database strings. The
                          options carry the raw values they cover, so selecting
                          one stores both. */}
                      <Autocomplete
                        size="small"
                        options={technologyOptions}
                        getOptionLabel={(o) => (typeof o === "string" ? o : o?.label || "")}
                        isOptionEqualToValue={(o, v) => o.label === v.label}
                        value={
                          technologyOptions.find((o) => o.label === moduleFilters.technology) || null
                        }
                        onChange={(_e, v) =>
                          setModuleFilters((f) => ({
                            ...f,
                            technology: v?.label || "",
                            technologyValues: v?.values || [],
                          }))
                        }
                        disabled={technologyOptions.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Technology"
                            placeholder={technologyOptions.length ? "Any" : "None recorded"}
                          />
                        )}
                      />
                      <Box
                        sx={{
                          gridColumn: { xs: "1 / -1", sm: "1 / -1" },
                          display: "flex",
                          alignItems: "center",
                          minHeight: 36,
                          px: 0.15,
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={moduleFilters.bifacialOnly}
                              onChange={(e) =>
                                setModuleFilters((f) => ({ ...f, bifacialOnly: e.target.checked }))
                              }
                            />
                          }
                          label="Bifacial only"
                        />
                      </Box>
                      {/* Restricts to equipment this organisation added. A checkbox
                          rather than a switch: it is a scope, not a mode. The shared
                          library is excluded by the server, so "my organisation" never
                          silently includes the global catalogue. */}
                      <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", px: 0.15 }}>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={moduleFilters.ownedOnly}
                              onChange={(e) =>
                                setModuleFilters((f) => ({ ...f, ownedOnly: e.target.checked }))
                              }
                            />
                          }
                          label="My organization's modules only"
                        />
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
              {/* Cell construction. Two datasheet facts, kept here with the
                  module rather than under Advanced modeling because that is
                  what they are — not modelling preferences. They set how a row
                  shadow maps onto bypass diodes, and getting half-cut wrong
                  doubles the modelled shading loss. */}
              <DetailedShadingControls config={cfg} module={detailedModule}
                onChange={handleDetailedSettings} standardControls={(disabled) => (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
                sx={{ mb: 1.25, flexWrap: "wrap" }}
                useFlexGap
              >
                <Box sx={{ width: { xs: "100%", sm: 320 } }}>
                  <PvLossFieldLabel
                    label="Cell layout"
                    id="pv-cell-layout-label"
                    tooltip="Half-cut modules wire two halves in parallel, so a shadow along the bottom edge takes only the lower half and the top keeps producing. Full-cell modules lose the whole panel to the same shadow. Almost every module made in recent years is half-cut — check the datasheet."
                  />
                  <AccessibleSelectTextField
                    id="pv-cell-layout"
                    disabled={disabled}
                    SelectProps={{ labelId: "pv-cell-layout-label" }}
                    select
                    fullWidth
                    size="small"
                    value={cfg.panelOrientation?.moduleHalfCut === false ? "full" : "half"}
                    onChange={(e) =>
                      handleField(
                        "panelOrientation.moduleHalfCut",
                        e.target.value === "half"
                      )
                    }
                  >
                    <MenuItem value="half">Half-cut</MenuItem>
                    <MenuItem value="full">Full cell</MenuItem>
                  </AccessibleSelectTextField>
                </Box>
                <Box sx={{ width: { xs: "100%", sm: 165 } }}>
                  <PvLossFieldLabel
                    label="Bypass diodes"
                    htmlFor="pv-bypass-diodes"
                    tooltip="Diodes per module: 3 on a classic full-cell module, usually 6 on a half-cut one (three per half). Only affects landscape mounting, where the diodes set how many horizontal bands a rising shadow crosses."
                  />
                  <TextField
                    id="pv-bypass-diodes"
                    disabled={disabled}
                    fullWidth
                    size="small"
                    type="number"
                    value={
                      cfg.panelOrientation?.moduleBypassDiodes ??
                      (cfg.panelOrientation?.moduleHalfCut === false ? 3 : 6)
                    }
                    onChange={(e) =>
                      handleField(
                        "panelOrientation.moduleBypassDiodes",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    inputProps={{ min: 1, max: 24, step: 1 }}
                  />
                </Box>
              </Stack>
              )} />

              {/* Degradation, which is a property of the MODULE and belongs
                  beside it.

                  It used to be a single "0.2 %/year" on the feasibility page,
                  doing two jobs badly: standing for the first year -- which is
                  not ageing at all but light-induced degradation, a one-off
                  drop in the first few hundred hours -- and for every year
                  after, which really is a slow linear decline. Every datasheet
                  warrants the two separately.

                  The curve is the warranty table itself, and the two halves go
                  to different places: the first year into the hourly
                  simulation as pvLosses.dc.lid, the rest into the year-by-year
                  projection. Feasibility now shows it read-only, because a
                  module fact edited on a financial page is a module fact
                  nobody will think to check. */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mb: 1.25, flexWrap: "wrap" }}
                useFlexGap
                id="field-pv-degradation"
              >
                <Box sx={{ minWidth: { xs: "100%", sm: 240 } }}>
                  <PvLossFieldLabel
                    label="Degradation curve"
                    tooltip="Retention warranted by the manufacturer, year by year. The first year is light-induced degradation and is applied inside the hourly simulation; every year after scales the year-by-year projection. Entered as the warranty table so a datasheet can be transcribed rather than reduced to one number."
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                    {degradationSummary
                      ? (degradationSummary.uniform
                          ? `${degradationSummary.firstYearPct.toFixed(2)}% first year, then ${degradationSummary.annualPct.toFixed(2)}% / yr`
                          : "Entered year by year")
                      : "Not set"}
                  </Typography>
                  {degradationSummary ? (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {`${degradationSummary.finalRetentionPct.toFixed(1)}% of nameplate at year ${degradationSummary.years}`}
                    </Typography>
                  ) : null}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SearchIcon fontSize="small" />}
                  onClick={() => setDegradationOpen(true)}
                  sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0 }}
                >
                  Degradation curve
                </Button>
              </Stack>

              </Box>
            </Paper>

            {/* ===== INVERTER SIDE ===== */}
            <Paper
              elevation={0}
              sx={pvSetupEquipmentCardSx("inverter")}
            >
              <Typography
                variant="subtitle2"
                sx={pvSetupEquipmentCardTitleSx}
              >
                Inverter
              </Typography>

              <Box
                sx={pvSetupEquipmentInnerGridSx}
              >
                <Box sx={pvSetupSelectorPanelSx} id="field-pv-inverter">
                  <Typography variant="body2" sx={pvSetupSelectorTitleSx}>
                    Inverter selection
                  </Typography>
                  <Box sx={pvSetupEquipmentSelectorRowSx}>
                    <Autocomplete
                      disabled={catalogLoading}
                      options={sortedInverterOptions.map((inv) => inv.name)}
                      getOptionLabel={inverterOptionLabel}
                      filterOptions={catalogFilterOptions}
                      value={cfg.read_inverter || null}
                      onChange={(_, newValue) => {
                        handleField("read_inverter", newValue || "");
                      }}
                      sx={pvSetupEquipmentAutocompleteSx}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Inverter"
                          placeholder={
                            catalogLoading
                              ? "Loading inverters..."
                              : "Start typing inverter name..."
                          }
                          error={Boolean(catalogError)}
                          size="small"
                          InputLabelProps={{
                            sx: {
                              color: "primary.main",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              "&.Mui-focused": {
                                color: "primary.main",
                              },
                            },
                          }}
                        />
                      )}
                      clearOnBlur={false}
                      autoSelect
                      autoHighlight
                      size="small"
                    />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SearchIcon fontSize="small" />}
                        disabled={!cfg.read_inverter}
                        onClick={handleOpenInverterDialog}
                        sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0 }}
                    >
                      Details
                    </Button>
                    {/* The counterpart to the module's I-V / P-V Monitor. The
                        two blocks are laid out as a matched pair, and the
                        inverter half was missing its one first-class control. */}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!cfg.read_inverter}
                      onClick={handleOpenInverterCurves}
                      sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0 }}
                    >
                      Efficiency model
                    </Button>
                    {canDeleteSelected("inverter") ? (
                      <MaybeTooltip title="Remove from equipment catalogue">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => requestCatalogDelete("inverter", cfg.read_inverter)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </MaybeTooltip>
                    ) : null}
                    </Box>
                  </Box>
                  {/* Which AC model this record can actually run.
                      Sandia needs fitted C0/C1/C2; without them the engine uses
                      one flat efficiency for every operating point, and nothing
                      on this tab said so -- which is how an inverter ends up
                      named "... correct sandia". inverterHasCurves is already
                      resolved for the Advanced modeling panel. */}
                  {cfg.read_inverter && inverterChars?.ondModel ? (
                    <Box sx={{ mt: 0.6 }}>
                      <Button size="small" onClick={() => setOndConnectionOpen(true)} sx={{ mr: 1 }}>
                        {isGreekScreen ? "Σύνδεση εισόδων DC" : "DC input connection"}
                      </Button>
                      {!ondConnectionComplete(inverterChars) && <Chip size="small" color="warning" variant="outlined" sx={{ mr: 1 }}
                        label={isGreekScreen ? "Μη επαληθευμένη σύνδεση DC" : "DC connection not verified"} />}
                      <MaybeTooltip
                        title={isGreekScreen
                          ? "Καμπύλη απόδοσης από το αρχείο OND: η προσομοίωση χρησιμοποιεί απευθείας τα σημεία του κατασκευαστή, με κατώφλι, όριο ισχύος AC και νυχτερινή κατανάλωση όπως τα ορίζει το αρχείο."
                          : "Efficiency curve from the OND file: the simulation converts on the manufacturer's points, with the threshold, AC limit and night consumption the file states."}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label="OND"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, cursor: "help" }}
                        />
                      </MaybeTooltip>
                    </Box>
                  ) : cfg.read_inverter && inverterHasCurves !== undefined ? (
                    <Box sx={{ mt: 0.6 }}>
                      <MaybeTooltip
                        title={
                          inverterHasCurves
                            ? "Sandia: efficiency follows load and DC voltage, fitted from the datasheet curves stored with this inverter."
                            : "PVWatts: efficiency varies with load along a standard curve, without DC-voltage dependence. Open Efficiency model and enter curves at three DC voltages to fit Sandia."
                        }
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color={inverterHasCurves ? "success" : "warning"}
                          label={inverterHasCurves ? "SANDIA" : "PVWATTS"}
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, cursor: "help" }}
                        />
                      </MaybeTooltip>
                    </Box>
                  ) : null}
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.45, color: catalogError ? "error.main" : "text.secondary", lineHeight: 1.45 }}
                  >
                    {/* Match count, not a filter list — see the note on the module
                        side. This wording previously omitted the vendor filter. */}
                    {catalogError
                      ? catalogError
                      : inverterFiltersEnabled
                      ? catalogFilterSummary("inverter", inverterOptions.length, catalogFacets.inverters.total, isGreekScreen)
                      : "Choose from the full inverter catalog or start typing an inverter name directly."}
                  </Typography>
                  {/* The limits the module figures above have to fit inside.
                      Put side by side, a reader can see the string window
                      without opening either dialog. */}
                  {(() => {
                    if (!cfg.read_inverter || !inverterChars) return null;
                    const n = (v) => {
                      const out = Number(String(v ?? "").replace(",", "."));
                      return Number.isFinite(out) ? out : null;
                    };
                    const paco = n(inverterChars.Paco);
                    const lo = n(inverterChars.Mppt_low);
                    const hi = n(inverterChars.Mppt_high);
                    const vdcmax = n(inverterChars.Vdcmax);
                    const mppts = n(inverterChars["Num MPPTs"] ?? inverterChars.numMPPT);
                    const inputs = n(inverterChars["Num Inputs per MPPT"] ?? inverterChars.numInptMPPT);
                    const perMpptInputs = Array.isArray(inverterChars.mppt_strings_per_tracker)
                      ? inverterChars.mppt_strings_per_tracker : null;
                    // OND central inverters use a synthetic string capacity for
                    // sizing. Display the file's physical DC inputs instead.
                    const ondInputs = Number(inverterChars.ondModel?.n_inputs);
                    const hasOndInputs = Number.isInteger(ondInputs) && ondInputs > 0;
                    const figures = [];
                    if (paco != null) figures.push({ label: "Rated AC", value: `${(paco / 1000).toFixed(0)} kW` });
                    if (lo != null && hi != null)
                      figures.push({ label: "MPPT window", value: `${lo.toFixed(0)}–${hi.toFixed(0)} V` });
                    if (vdcmax != null) figures.push({ label: "Max DC", value: `${vdcmax.toFixed(0)} V` });
                    if (mppts != null)
                      figures.push({
                        label: "MPPT trackers",
                        value: hasOndInputs
                          ? `${mppts.toFixed(0)} MPPT · ${ondInputs} ${isGreekScreen
                            ? (ondInputs === 1 ? "είσοδος DC" : "είσοδοι DC")
                            : (ondInputs === 1 ? "DC input" : "DC inputs")}`
                          : perMpptInputs
                          ? `${mppts.toFixed(0)} MPPT · ${perMpptInputs.join("+")} inputs`
                          : inputs != null ? `${mppts.toFixed(0)} × ${inputs.toFixed(0)} ${Math.round(inputs) === 1 ? "input" : "inputs"}` : mppts.toFixed(0),
                      });
                    if (figures.length === 0) return null;
                    return pvSetupFigureStrip({
                      figures,
                      note: "A string must stay under Max DC when cold and above the MPPT floor when hot.",
                    });
                  })()}
                  <Box sx={{ mt: 0.15 }}>
                    {catalogWriteAllowed ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setInvSaveError("");
                          setInvErrors({});
                          setInvForm((prev) => ({ ...prev, inverter_name: "" }));
                          setInvPerMppt(false);
                          setInvMpptRows([]);
                          // Clear any fit staged from a previous visit, so it
                          // cannot be attached to a different inverter.
                          setInvPendingFit(null);
                          setInvEuro({mode:"euro",euro:"",max:"",respectMax:true});
                          setInvOndImport(null);
                          setAddInvOpen(true);
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        + {translateText("Add custom inverter")}
                      </Button>
                    ) : null}
                  </Box>
                </Box>

                <Box sx={{ ...pvSetupFilterPanelSx, ...(inverterFiltersEnabled ? { minHeight: 130 } : null) }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.35, gap: 1 }}>
                    <MaybeTooltip title="Narrow the catalog by inverter AC power, DC voltage range and manufacturer before choosing the unit used for electrical design.">
                      <Typography variant="body2" sx={{ ...pvSetupPanelTitleSx, cursor: "help" }}>
                        Catalog filters
                      </Typography>
                    </MaybeTooltip>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {inverterFiltersEnabled && inverterFiltersDirty ? (
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={clearInverterFilters}
                          sx={{ fontWeight: 600 }}
                        >
                          Clear
                        </Link>
                      ) : null}
                      <Box component="span" sx={pvSetupFilterStateBadgeSx(inverterFiltersEnabled)}>
                        {inverterFiltersEnabled ? "Filters enabled" : "Filters disabled"}
                      </Box>
                    </Box>
                  </Box>

                  <FormControlLabel
                    sx={{ m: 0, mb: inverterFiltersEnabled ? 0.6 : 0.2 }}
                    control={
                      <Switch
                        size="small"
                        checked={inverterFiltersEnabled}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setInverterFiltersEnabled(on);

                          if (!on) {
                            setInverterFilters({
                              minPac: "",
                              maxPac: "",
                              minVdcMax: "",
                              maxVdcMax: "",
                              minVac: "",
                              maxVac: "",
                              phases: "",
                              ownedOnly: false,
                              vendor: "",
                              model: "",
                            });
                          }
                        }}
                      />
                    }
                    label="Enable inverter filters"
                  />

                  {inverterFiltersEnabled ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                        gap: 0.75,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Min Pac (W)"
                        type="number"
                        value={inverterFilters.minPac}
                        onChange={(e) =>
                          setInverterFilters((f) => ({ ...f, minPac: e.target.value }))
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Pac (W)"
                        type="number"
                        value={inverterFilters.maxPac}
                        onChange={(e) =>
                          setInverterFilters((f) => ({ ...f, maxPac: e.target.value }))
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Min Vdc_max (V)"
                        type="number"
                        value={inverterFilters.minVdcMax}
                        onChange={(e) =>
                          setInverterFilters((f) => ({ ...f, minVdcMax: e.target.value }))
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Vdc_max (V)"
                        type="number"
                        value={inverterFilters.maxVdcMax}
                        onChange={(e) =>
                          setInverterFilters((f) => ({ ...f, maxVdcMax: e.target.value }))
                        }
                      />
                      {/* Nominal AC voltage: line-to-line on three-phase units,
                          phase voltage on single-phase ones -- the same value
                          the inverter card shows as Vac. */}
                      <MaybeTooltip
                        title="Nominal AC grid voltage. Three-phase inverters list the line-to-line voltage (e.g. 400 V), single-phase ones the phase voltage (e.g. 230 V)."
                        arrow
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label="Min Vac (V)"
                          type="number"
                          value={inverterFilters.minVac}
                          onChange={(e) =>
                            setInverterFilters((f) => ({ ...f, minVac: e.target.value }))
                          }
                        />
                      </MaybeTooltip>
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Vac (V)"
                        type="number"
                        value={inverterFilters.maxVac}
                        onChange={(e) =>
                          setInverterFilters((f) => ({ ...f, maxVac: e.target.value }))
                        }
                      />
                      <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", px: 0.15 }}>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={inverterFilters.ownedOnly}
                              onChange={(e) =>
                                setInverterFilters((f) => ({ ...f, ownedOnly: e.target.checked }))
                              }
                            />
                          }
                          label="My organization's inverters only"
                        />
                      </Box>
                      <MaybeTooltip
                        title="Read from the catalogue's phase field. Where a catalogue entry does not state it, 380 V and above counts as three-phase and 220–277 V as single-phase; entries with any other voltage are left out when this filter is set."
                        arrow
                      >
                        <AccessibleSelectTextField
                          select
                          fullWidth
                          size="small"
                          label="Phases"
                          value={inverterFilters.phases}
                          onChange={(e) =>
                            setInverterFilters((f) => ({ ...f, phases: e.target.value }))
                          }
                        >
                          <MenuItem value="">Any</MenuItem>
                          <MenuItem value="1">Single-phase</MenuItem>
                          <MenuItem value="3">Three-phase</MenuItem>
                        </AccessibleSelectTextField>
                      </MaybeTooltip>
                      {/* Conversion model. Sits beside Phases so the two
                          derived filters share a row, the way Min/Max pairs do;
                          Vendor takes the full row below. */}
                      <Autocomplete
                        size="small"
                        options={inverterModelOptions}
                        getOptionLabel={(o) => (typeof o === "string" ? o : o?.label || "")}
                        isOptionEqualToValue={(o, v) => o.key === v.key}
                        value={
                          inverterModelOptions.find((o) => o.key === inverterFilters.model) || null
                        }
                        onChange={(_e, v) =>
                          setInverterFilters((f) => ({ ...f, model: v?.key || "" }))
                        }
                        disabled={inverterModelOptions.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Conversion model"
                            placeholder={inverterModelOptions.length ? "Any" : "None recorded"}
                          />
                        )}
                      />
                      <Autocomplete
                        size="small"
                        sx={{ gridColumn: "1 / -1" }}
                        options={catalogFacets.inverters.vendors}
                        value={inverterFilters.vendor || null}
                        onChange={(_e, v) =>
                          setInverterFilters((f) => ({ ...f, vendor: v || "" }))
                        }
                        filterOptions={catalogFilterOptions}
                        disabled={catalogFacets.inverters.vendors.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Manufacturer"
                            placeholder={
                              catalogFacets.inverters.vendors.length ? "Any" : "None recorded"
                            }
                          />
                        )}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          </Box>
          </PvLoadingRegion>
          </Box>{/* end Equipment tab */}

          {/* ===== TAB: Electrical Design & Layout ===== */}
          <Box sx={{ display: pvTab === "design" ? "block" : "none" }}>
          {/* ---------- PV sizing (moved from System) ---------- */}
          <Paper elevation={0} sx={{ ...pvSetupSectionSx, mb: 1.1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 360px))" }, gap: 1.5, alignItems: "start" }}>
            <Box sx={{ minWidth: 0 }}>
              <TextField
                fullWidth
                label="PV Installed Capacity (kWp)"
                type="text"                 // ✅ no arrows + no native number quirks
                inputMode="decimal"
                sx={{
                  maxWidth: { xs: "100%", md: 360 },
                  "& .MuiOutlinedInput-notchedOutline legend": { fontSize: "0.84rem" },
                  "& .MuiFormHelperText-root": {
                    maxWidth: 320,
                    lineHeight: 1.3,
                    mt: 0.45,
                  },
                }}
                value={capacityText}
                // Read-only, not disabled, once a design exists: from that
                // moment the capacity is not an opinion, it is the module
                // count times the module's rating. Greying it out would say
                // "unavailable" about a number that is perfectly available and
                // the one the reader came for -- readOnly keeps it legible and
                // selectable while refusing the edit.
                //
                // It used to be typeable at all times AND overwritten by every
                // design save, so the two quietly fought: a figure typed here
                // was lost on the next save, and a module swapped elsewhere
                // left this one behind describing the previous plant. What the
                // field was really being used for, a size to aim at, now has
                // its own box in the auto-design dialog.
                InputProps={{
                  readOnly: capacityIsDerived(physicalLayoutTotalModules),
                  // The figure is what the reader came for: bold, a touch larger.
                  sx: { "& .MuiInputBase-input": { fontWeight: 700, fontSize: "1.12rem" } },
                  // Where the figure comes from, behind an (i): a whole sentence
                  // with no number in it, so the Greek catalogue matches it.
                  endAdornment: (
                    <InputAdornment position="end">
                      {capacityIsDerived(physicalLayoutTotalModules) && (
                        <MaybeTooltip title={isGreekScreen ? "Ορισμός ισχύος στόχου στον αυτόματο σχεδιασμό" : "Set target capacity in auto-design"} arrow>
                          <IconButton
                            size="small"
                            edge="end"
                            disabled={autoDesignLoading}
                            aria-label={isGreekScreen ? "Ορισμός ισχύος στόχου στον αυτόματο σχεδιασμό" : "Set target capacity in auto-design"}
                            onClick={() => {
                              setFocusAutoDesignTarget(true);
                              setAutoDesignOpen(true);
                            }}
                            sx={{ mr: 0.25, color: "primary.main" }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                              <path d="M12 4H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-7" />
                              <path d="m16 3 5 5M9 15l1-5 8-8 5 5-8 8z" />
                            </svg>
                          </IconButton>
                        </MaybeTooltip>
                      )}
                      <MaybeTooltip
                        title={capacityIsDerived(physicalLayoutTotalModules)
                          ? "Derived from the saved design; change the design to change it. The auto-design target is set in its own dialog."
                          : "Inferred from the layout once you save a design."}
                        arrow
                      >
                        <span style={{ display: "inline-flex" }} aria-label={isGreekScreen ? "Πληροφορίες: Εγκατεστημένη ισχύς Φ/Β" : "Information: PV Installed Capacity"}>
                          <InfoOutlinedIcon sx={{ fontSize: 17, opacity: 0.7 }} />
                        </span>
                      </MaybeTooltip>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  // Empty is allowed while editing; digits and one dot only.
                  if (/^\d*\.?\d*$/.test(v)) setCapacityText(v);
                }}
                onBlur={commitCapacity}
                onKeyDown={(e) => {
                  // Enter finishes the edit as leaving the field does.
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                // One line under the box; the provenance sits behind the (i).
                helperText={capacityIsDerived(physicalLayoutTotalModules)
                  ? "Derived from the saved design."
                  : "Total DC nameplate capacity of the PV plant."}
                InputLabelProps={{
                  sx: {
                    color: "primary.main",
                    fontWeight: 700,
                    // A size up from the other labels: this is the headline
                    // figure of the tab. The legend below keeps the notch as
                    // wide as the larger label, so the title does not sit on
                    // the border.
                    fontSize: "1.12rem",
                    letterSpacing: "0.02em",
                    "&.Mui-focused": { color: "primary.main" },
                  },
                }}
              />
              {/* BTM PV projects only; renders nothing otherwise. */}
              <PvSizingEntryLink cfg={cfg} variant="capacity" sx={{ mt: 0.5 }} />
            </Box>

            {/* PV asset status: "existing" marks the PV plant as a sunk asset
                (retrofit / storage-addition studies). The backend then excludes
                module + inverter hardware and their installation from project
                CAPEX, keeps PV O&M in the cash flows, and the report discloses
                the treatment in Basis of Preparation. Default "new" = current
                behaviour, byte-identical results. */}
            <Box sx={{ minWidth: 0 }}>
              <FormControl fullWidth sx={{ maxWidth: { xs: "100%", md: 360 } }}>
                <InputLabel id="pv-asset-status-label"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    "&.Mui-focused": { color: "primary.main" },
                  }}
                >
                  PV Asset Status
                </InputLabel>
                <Select id="pv-asset-status" labelId="pv-asset-status-label"
                  label="PV Asset Status"
                  value={cfg.pvAssetStatus || "new"}
                  onChange={(e) => handleField("pvAssetStatus", e.target.value)}
                  // What the choice does to the CAPEX, behind an (i) beside the arrow.
                  endAdornment={
                    <InputAdornment position="end" sx={{ mr: 2.5 }}>
                      <MaybeTooltip
                        title={String(cfg.pvAssetStatus || "new") === "existing"
                          ? "Retrofit study: module & inverter hardware and their installation are excluded from CAPEX (sunk asset); PV O&M stays in the cash flows. Metrics measure the return on the new (storage) capital. Disclosed in the report."
                          : "The PV plant is part of the investment: module & inverter hardware (catalog prices) and installation are included in project CAPEX."}
                        arrow
                      >
                        <span style={{ display: "inline-flex" }} aria-label={isGreekScreen ? "Πληροφορίες: Καθεστώς Φ/Β εγκατάστασης" : "Information: PV Asset Status"}>
                          <InfoOutlinedIcon sx={{ fontSize: 17, opacity: 0.7 }} />
                        </span>
                      </MaybeTooltip>
                    </InputAdornment>
                  }
                >
                  <MenuItem value="new">New build (PV CAPEX included)</MenuItem>
                  <MenuItem value="existing">Existing plant — sunk asset (retrofit)</MenuItem>
                </Select>
              </FormControl>
            </Box>


            {/* PV Losses block */}
          </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...pvSetupSectionSx,
              mt: 0.5,
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(180deg, rgba(22,31,27,0.98) 0%, rgba(14,26,20,0.96) 100%)"
                  : "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.92) 100%)",
            }}
          >
          <Box>
            <Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.2 }}>
                  Design actions
                </Typography>
                {/* Two nodes, not one sentence: each is a whole static line the
                    text translator can match. The second used to sit beside the
                    buttons -- AFTER the report button, so it read as a
                    description of the report, which it never was. */}
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 620, lineHeight: 1.35 }}>
                  <span>Work through the three steps below. Each one saves on its own.</span>{" "}
                  <span>Auto-design proposes a module, an inverter, the strings and the rows for the target capacity.</span>
                </Typography>
              </Box>
<Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 0.85, alignItems: "center" }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.75,
                    flex: "1 1 240px",
                    maxWidth: { xs: "100%", md: 340 },
                    minWidth: 0,
                    minHeight: 52,
                    boxSizing: "border-box",
                    borderRadius: 2,
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, rgba(18,38,29,0.78) 0%, rgba(14,26,20,0.92) 100%)"
                        : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(244,249,255,0.94) 100%)",
                    border: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(167,205,181,0.16)"
                        : "rgba(148,163,184,0.18)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      mb: 0.25,
                      fontWeight: 400,
                    }}
                  >
                    Electrical design
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: (theme) =>
                        isElectricalDesignSaved
                          ? theme.palette.mode === "dark"
                            ? "#74d7a2"
                            : "#0f766e"
                          : designUnsaved
                          ? theme.palette.mode === "dark"
                            ? "#fbbf24"
                            : "#9a3412"
                          : theme.palette.text.primary,
                    }}
                  >
                    {designStatusLabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.75,
                    flex: "1 1 240px",
                    maxWidth: { xs: "100%", md: 340 },
                    minWidth: 0,
                    minHeight: 52,
                    boxSizing: "border-box",
                    borderRadius: 2,
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, rgba(18,38,29,0.78) 0%, rgba(14,26,20,0.92) 100%)"
                        : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(244,249,255,0.94) 100%)",
                    border: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(167,205,181,0.16)"
                        : "rgba(148,163,184,0.18)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      mb: 0.25,
                      fontWeight: 400,
                    }}
                  >
                    Physical layout
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: (theme) =>
                        isPhysicalLayoutSaved
                          ? theme.palette.mode === "dark"
                            ? "#74d7a2"
                            : "#0f766e"
                          : layoutUnsaved
                          ? theme.palette.mode === "dark"
                            ? "#fbbf24"
                            : "#9a3412"
                          : theme.palette.text.primary,
                    }}
                  >
                    {layoutStatusLabel}
                  </Typography>
                </Box>

                {/* Auto-design writes to BOTH steps -- the strings and the rows --
                    so it belongs up here with their status, not inside the
                    electrical tab where it looked like a step-one tool. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: 0, md: 1 }, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setFocusAutoDesignTarget(false);
                      setAutoDesignOpen(true);
                    }}
                    disabled={autoDesignLoading}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {autoDesignLoading ? "Designing…" : "Auto-design"}
                  </Button>
                  {/* The design as a document: site, rows, strings on the rows,
                      the electrical checks and the sun path. It describes both
                      steps, so it is offered here with them rather than inside
                      the layout tab. The layout tab holds the pieces (the 3D
                      scene, the fitted rows) and does the building. */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => designReportRef.current?.download?.()}
                    disabled={!designReport.ready || designReport.busy}
                    startIcon={designReport.busy ? <CircularProgress size={14} thickness={5} /> : null}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {designReport.busy ? "Building…" : layoutIsMulti
                      ? (isGreekScreen ? "ΑΝΑΦΟΡΑ ΟΛΩΝ ΤΩΝ ΠΡΟΣΑΝΑΤΟΛΙΣΜΩΝ (.PDF)" : "All orientations report (.pdf)")
                      : "Design report (.pdf)"}
                  </Button>
                  {designReport.error || designReport.reason ? (
                    <Typography
                      variant="caption"
                      sx={{ color: designReport.error ? "error.main" : "text.secondary" }}
                    >
                      {designReport.error || designReport.reason}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>

            {/* The three steps were buttons opening modals. As tabs the sequence
                is visible and you can move between steps without dismissing
                anything -- a modal you must close to see the next step hides the
                very order this panel exists to communicate. */}
            <Tabs
              value={designStep}
              onChange={(e, v) => setDesignStep(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mt: 1.25, mb: 1.5, borderBottom: 1, borderColor: "divider", minHeight: 40 }}
            >
              <Tab value="electrical" label="Electrical design" sx={{ minHeight: 40 }} />
              <Tab value="layout" label="Physical layout" sx={{ minHeight: 40 }} />
              <Tab value="sld" label="Single-line diagram" sx={{ minHeight: 40 }} />
            </Tabs>
          </Box>

                    {/* step: electrical */}
          <Box sx={{ display: designStep === "electrical" ? "block" : "none" }}>
            {designSaveNotice && <Alert severity={designSaveNotice.severity} sx={{ mb: 1 }} onClose={() => setDesignSaveNotice(null)}>
              {designSaveNotice.message}
            </Alert>}
            {autoDesignResult?.inverterReplacement && <Alert severity="info" sx={{ mb: 1 }}>
              {inverterReplacementMessage(autoDesignResult.inverterReplacement, isGreekScreen)}
            </Alert>}
<DesignSystemDialog
            inline
            key={`design-${designDialogKey}`}
            open={designStep === "electrical"}
            onDiscard={() => {
              setHasUnsavedAutoDesign(false);
              setAutoDesignLayout(null);
              setAutoDesignResult(null);
              setAutoDesignRunMeta(null);
              if (isRoofInstallation(installationType)) setPhysicalLayoutDraft(null);
            }}
            inverterName={autoDesignResult?.inverter || cfg.read_inverter}
            moduleName={autoDesignResult?.module || cfg.read_module}
            meta={designMeta || metaFromSystemDesign}
            orientations={orientationList}
            showAutoDesignBanner={Boolean(autoDesignLayout)}
            showUnsavedMarker={designUnsaved}
            onDirtyChange={setDesignFormDirty}
            canDownloadReport={Boolean(
              currentProject?.id &&
              (autoDesignResult || autoDesignStamp())?.reportSaved !== false &&
              ((autoDesignResult || autoDesignStamp())?.reportId ||
                (cfg.read_module && cfg.read_inverter))
            )}
            reportSaved={(autoDesignResult || autoDesignStamp())?.reportSaved ?? null}
            reportRetentionOk={(autoDesignResult || autoDesignStamp())?.reportRetentionOk ?? null}
            reportHistory={autoDesignReportHistory}
            savedReportId={persistedPvDesign?.autoDesign?.reportId || null}
            reportHistoryLoading={autoDesignReportHistoryLoading}
            reportHistoryError={autoDesignReportHistoryError}
            downloadReportLoading={downloading}
            onDownloadReport={async (reportId = undefined) => {
              setDesignReportError("");
              if (!currentProject?.id) {
                setDesignReportError("Open or save a project before downloading the auto-design report");
                return;
              }
              setDownloading(true);
              try {
                const { blob, filename } = await downloadAutoDesignReportExcel(
                  currentProject.id,
                  accessToken,
                  reportId === undefined
                    ? (autoDesignResult || autoDesignStamp())?.reportId || null
                    : reportId
                );
                downloadBlob(blob, filename);
              } catch (err) {
                setDesignReportError(
                  formatAutoDesignError(err).message || "Design report download failed."
                );
              } finally {
                setDownloading(false);
              }
            }}
            initialDesign={
              structuredPvDesign ||
              cfg.systemDesign ||
              null
            }
            designCapacityKw={
              computeCapacityFromLayout(
                structuredPvDesign || cfg.systemDesign || null,
                modulePdcKwRef.current
              ) ??
              autoDesignResult?.actualCapacityKw ??
              cfg?.pvInstalledCap?.kw ??
              null
            }
            moduleKw={modulePdcKwRef.current}
            validationKey={JSON.stringify([cfg?.autoDesign, orientationList.map((o) => o.id)])}
            onLoadValidationContext={electricalModule && electricalInverter ? loadElectricalContext : null}
            designSettings={{ dcacMin: autoDesignNumber("dcacMin"), dcacMax: autoDesignNumber("dcacMax") }}
            onValidate={(designSummary) => validatePvDesign({
              module: designSummary.module,
              inverter: designSummary.inverter,
              system_design: buildSystemDesignFromLayout(designSummary, designSummary.inverter),
              min_cell_c: autoDesignNumber("minCellC"),
              max_cell_c: autoDesignNumber("maxCellC"),
              dcac_min: autoDesignNumber("dcacMin"),
              dcac_max: autoDesignNumber("dcacMax"),
              orientation_ids: orientationList.map((o) => o.id),
            }, accessToken)}
            onSave={saveElectricalDesign}
            autoSaveRequest={quickDesignSaveRequest}
            onAutoSaveDone={handleQuickDesignSaved}
          />
          </Box>

                    {/* step: sld */}
          <Box sx={{ display: designStep === "sld" ? "block" : "none" }}>
            <SldPreviewDialog
              reportBranding={reportBranding}
              reportLanguage={getReportLanguage(cfg)}
              inline
              open={designStep === "sld"}
              onClose={() => {}}
              config={cfg}
              currentProject={currentProject}
              accessToken={accessToken}
            />
          </Box>

                    {/* step: layout */}
          <Box sx={{ display: designStep === "layout" ? "block" : "none" }}>
          {sceneReadinessIssues.length > 0 && <Alert severity="info" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {isGreekScreen ? "Εκκρεμότητες φυσικής διάταξης" : "Physical layout checks pending"}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {sceneReadinessIssues.map((issue) => <li key={issue}>{issue}</li>)}
            </Box>
          </Alert>}
          <LayoutOrientationPicker
            cfg={cfg}
            previewSystemDesign={layoutPreviewCfg.systemDesign}
            activeId={layoutOrientation.id}
            onSelect={setLayoutOrientationId}
          />
<PhysicalLayoutDialog
            key={`layout-${layoutOrientation.id}`}
            workspaceId={layoutSharedWorkspace ? layoutOrientation.id : null}
            workspaceDraftsRef={layoutSharedWorkspace ? roofWorkspaceRef : null}
            workspaceSceneEditsRef={layoutSharedWorkspace ? roofSceneEditsRef : null}
            workspaceViewRef={layoutSharedWorkspace ? roofWorkspaceViewRef : null}
            defaultSceneFrame={isRoofInstallation(installationType) ? suggestedFrames[layoutOrientation.id] : null}
            orientationLabel={layoutPlaneLabel}
            contextPlanes={layoutContextPlanes}
            reportBranding={reportBranding}
            reportLanguage={getReportLanguage(cfg)}
            reportRef={designReportRef}
            reportLayouts={orientationReportLayouts(cfg)}
            onReportState={setDesignReport}
            inline
            open={designStep === "layout"}
            onDiscard={() => {
              setHasUnsavedPhysicalLayout(false);
              setPhysicalLayoutDraft(null);
              setSceneShadingDraft(null);
            }}
            initialSpacingInfo={
              !layoutIsPrimary
                ? (layoutOrientation.spacingInfo || null)
                : physicalLayoutDraft?.spacingInfo ||
                  structuredPvDesign?.spacingInfo ||
                  structuredPvDesign?.spacing ||
                  persistedPvDesign?.spacingInfo ||
                  persistedPvDesign?.spacing ||
                  (hasUnsavedPhysicalLayout ? autoDesignResult?.spacingInfo : null) ||
                  null
            }
            moduleName={layoutModuleName}
            inverterName={layoutInverterName}
            surfaceType={cfg.surfaceType}
            moduleLength={physicalLayoutModuleGeometry.length}
            moduleWidth={physicalLayoutModuleGeometry.width}
            moduleGeometryLoading={physicalLayoutModuleGeometryLoading}
            totalModules={layoutIsMulti ? (layoutPlaneSummary?.modules || 0) : physicalLayoutTotalModules}
            incrementFactor={isRoofInstallation(installationType) ? 1.1 : 1 + autoDesignNumber("landMarginPct") / 100}
            // The rows stand on the ground along the racking's own heading, so
            // the layout keeps the design azimuth; the tilt is the plane's from
            // the horizontal, which is what the engine models.
            surfaceTilt={layoutPlane.tilt}
            latitude={cfg?.loc_info?.lat}
            mountType={cfg?.mountType}
            installationType={cfg?.panelOrientation?.installationType || "ground"}
            roofPitch={layoutRoofPitch ?? 0}
            roofAzimuth={layoutRoofAzimuth ?? 180}
            // From the plan's arrow or an edge click. On a flush roof the
            // plane IS the roof, so the orientation's azimuth moves with it;
            // on racking only the roof's own azimuth does.
            onRoofAzimuthChange={(deg) => {
              if (isFlushRoof) history.commit(setFlushRoofFace(cfg, layoutOrientation.id, "azim", deg));
              else if (layoutIsPrimary) handleField("panelOrientation.roofAzimuth", deg);
              else history.commit(updateOrientation(cfg, layoutOrientation.id, { roofAzimuth: deg }));
            }}
            // The roof drawing sets every face's angles: its pitch as typed,
            // its azimuth from its eave. Each PV face hands them to its
            // orientation (planeId null: the one being edited), in one step.
            onFaceAnglesChange={(list) => {
              let next = cfg;
              (list || []).forEach(({ planeId, pitchDeg, azimuthDeg }) => {
                const id = planeId ?? layoutOrientation.id;
                const target = orientationsFromConfig(next).find((o) => o.id === id);
                if (!target) return;
                const pitch = Number.isFinite(Number(pitchDeg)) && pitchDeg !== "" && pitchDeg != null ? Number(pitchDeg) : null;
                const azimuth = Number.isFinite(Number(azimuthDeg)) && azimuthDeg !== "" && azimuthDeg != null ? Number(azimuthDeg) : null;
                if (isFlushRoof) {
                  if (pitch != null) next = setFlushRoofFace(next, id, "tilt", pitch);
                  if (azimuth != null) next = setFlushRoofFace(next, id, "azim", azimuth);
                } else if (target.primary || id === orientationList[0].id) {
                  next = { ...next, panelOrientation: { ...(next.panelOrientation || {}),
                    ...(pitch != null ? { roofPitch: pitch } : {}), ...(azimuth != null ? { roofAzimuth: azimuth } : {}) } };
                } else {
                  next = updateOrientation(next, id, { ...(pitch != null ? { roofPitch: pitch } : {}), ...(azimuth != null ? { roofAzimuth: azimuth } : {}) });
                }
              });
              if (next !== cfg) history.commit(next);
            }}
            // On a roof the orientations follow the faces: a face drawn without
            // PV becomes a plane when it gets some, and a plane becomes a face
            // of the scene when it loses them.
            onPromoteRoofFace={isRoofInstallation(installationType) ? (obstacle, frame) => {
              const { cfg: next, id } = promoteRoofFace(cfg, layoutOrientation.id, obstacle, frame, { installationType });
              if (id) history.commit(next);
              return id;
            } : null}
            onDemoteRoofFace={isRoofInstallation(installationType) ? (planeId, frame) => {
              const { cfg: next, obstacle, carried } = demoteRoofFace(cfg, layoutOrientation.id, planeId, frame, { installationType, isGreek: isGreekScreen });
              if (next !== cfg) history.commit(next);
              // The face and whatever stood on it, for the editor's draft of this plane.
              return obstacle ? [obstacle, ...(carried || [])] : null;
            } : null}
            // A PV face deleted goes with its orientation (planeId null: the
            // one being edited); its MPPTs return to the first. The first
            // orientation always stays: it only loses its face.
            onRemoveFace={isRoofInstallation(installationType) ? (planeId) => {
              const id = planeId ?? layoutOrientation.id;
              const { cfg: next } = removeOrientation(cfg, id);
              if (next === cfg) return false;
              delete roofWorkspaceRef.current[id];
              delete roofSceneEditsRef.current[id];
              if (id === layoutOrientation.id) setLayoutOrientationId(orientationList[0].id);
              history.commit(next);
              return true;
            } : null}
            onSelectPlane={setLayoutOrientationId}
            longitude={cfg?.loc_info?.lon}
            surfaceAzimuth={layoutIsPrimary ? cfg?.panelOrientation?.azim : layoutOrientation.azim}
            mountingPlane={layoutPlane}
            groundClearance={
              layoutIsPrimary
                ? cfg?.panelOrientation?.groundClearanceM
                : (layoutOrientation.groundClearanceM ?? cfg?.panelOrientation?.groundClearanceM)
            }
            slopeTilt={cfg?.loc_info?.tilt}
            slopeAzimuth={cfg?.loc_info?.azimuth}
            modulePortrait={cfg?.panelOrientation?.moduleOrientation !== "landscape"}
            moduleHalfCut={cfg?.panelOrientation?.moduleHalfCut !== false}
            moduleBypassDiodes={cfg?.panelOrientation?.moduleBypassDiodes}
            moduleTopology={cfg?.pvDetailedShading?.topology?.sourceModule === cfg.read_module
              ? cfg.pvDetailedShading.topology : null}
            detailedShadingEnabled={cfg?.pvDetailedShading?.enabled === true}
            detailedSceneReady={cfg?.pvDetailedShading?.enabled === true && Boolean(cfg.pvDetailedShading.scene?.sourceInputs) &&
              JSON.stringify(cfg.pvDetailedShading.scene.sourceInputs) === JSON.stringify(detailedSceneInputs(cfg))}
            nearShadingModel={cfg?.panelOrientation?.nearShadingModel}
            nearShadingElectricalEffect={cfg?.panelOrientation?.nearShadingElectricalEffect}
            axisOrientation={cfg?.axisOrientation}
            maxAngle={cfg?.maxAngle}
            backTrack={cfg?.backTrack}
            utcOffsetHours={siteTimezone?.utcOffsetHours}
            timeZoneLabel={siteTimezone?.timeZone}
            horizonProfile={cfg?.panelOrientation?.horizonProfile}
            projectName={currentProject?.name || currentProject?.project_name || null}
            elevationM={cfg?.loc_info?.elevation_m}
            parcel={layoutParcel(cfg)}
            systemDesign={layoutSystemDesign}
            designCellTemperatures={{ minCellC: cfg?.autoDesign?.minCellC, maxCellC: cfg?.autoDesign?.maxCellC }}
            onFitStringsToPlan={async (proposal, spacing) => {
              if (Number(proposal.modules) !== physicalLayoutCount(spacing) || Number(proposal.shortfall) > 0) {
                throw new Error(isGreekScreen ? "Η πρόταση δεν καλύπτει ακριβώς τα πάνελ της κάτοψης." : "The proposal does not match the plan exactly.");
              }
              // The plan may preview an unsaved auto-design. Merge into that
              // exact topology, which is also the one sent for string fitting.
              let merged;
              try {
                merged = mergePlanDesign(layoutPreviewCfg.systemDesign, proposal.system_design, orientationList, layoutOrientation.id);
              } catch {
                throw new Error(isGreekScreen
                  ? "Η πρόταση δεν αντιστοιχεί πλέον στο σχέδιο που εμφανίζεται. Ζητήστε νέα προσαρμογή strings."
                  : "The proposal no longer matches the displayed design. Request a new string fit.");
              }
              if (orientationSummary({ ...layoutPreviewCfg, systemDesign: merged }).find(o => o.id === layoutOrientation.id)?.modules !== physicalLayoutCount(spacing)) {
                throw new Error(isGreekScreen ? "Τα προτεινόμενα strings δεν καλύπτουν ακριβώς την κάτοψη." : "The proposed strings do not match the plan exactly.");
              }
              const checked = await validatePvDesign({ module: layoutModuleName, inverter: layoutInverterName,
                system_design: merged, min_cell_c: autoDesignNumber("minCellC"), max_cell_c: autoDesignNumber("maxCellC"),
                dcac_min: autoDesignNumber("dcacMin"), dcac_max: autoDesignNumber("dcacMax"),
                orientation_ids: orientationList.map(o => o.id) }, accessToken);
              if (checked?.valid !== true || checked.errors?.length) throw new Error(
                checked?.errors?.map(e => e.message?.[isGreekScreen ? "el" : "en"]).filter(Boolean).join(" · ") ||
                (isGreekScreen ? "Ο ηλεκτρολογικός έλεγχος απέτυχε." : "Electrical validation failed."));
              await saveElectricalDesign(structuredPlanDesign(merged, structuredPvDesign, layoutModuleName, layoutInverterName), {
                systemDesignOverride: merged, plan: { id: layoutOrientation.id, spacing },
              });
            }}
            capacityDcKw={
              layoutIsMulti
                ? (layoutPlaneSummary?.share != null && Number(cfg?.pvInstalledCap?.kw) > 0
                    ? Number(cfg.pvInstalledCap.kw) * layoutPlaneSummary.share
                    : null)
                : cfg?.pvInstalledCap?.kw
            }
            designTemps={{ minCellC: cfg?.autoDesign?.minCellC, maxCellC: cfg?.autoDesign?.maxCellC }}
            accessToken={accessToken}
            horizonProfileSite={cfg?.panelOrientation?.horizonProfileSite}
            siteLat={cfg?.loc_info?.lat ?? cfg?.lat}
            siteLon={cfg?.loc_info?.lon ?? cfg?.lon}
            horizonEnabled={horizonShadingEnabled(cfg)}
            onHorizonEnabledChange={(next) => {
              // Committed straight away rather than waiting for the layout's
              // Save button: it is a switch, and a switch that needs saving
              // somewhere else is a switch nobody trusts.
              history.commit(
                withHorizonShading(cfg, next)
              );
            }}
            obstacleShading={
              layoutIsPrimary
                ? (persistedPvDesign?.obstacleShading || null)
                : (layoutOrientation.obstacleShading || null)
            }
            projectId={currentProject?.id
              ? (layoutIsPrimary ? currentProject.id : `${currentProject.id}:orientation:${layoutOrientation.id}`)
              : null}
            // "Calculate shading" works out every face at once (one, or all
            // the faces of a shared scene) from the layout as it stands, and
            // the layout's Save stores it with that layout. Saving never
            // calculates: a layout saved without it is marked out of date.
            onCalculateShadingAll={calculateLayoutShading}
            sceneShadingKeyFor={layoutShadingKey}
            sceneShadingDraft={sceneShadingDraft
              ? { key: sceneShadingDraft.key, own: sceneShadingDraft.tables[layoutOrientation.id] || null,
                  count: Object.keys(sceneShadingDraft.tables).length }
              : null}
            onDiscardSceneShading={() => setSceneShadingDraft(null)}
            shadingFaceCount={orientationList.length}
            showUnsavedMarker={layoutUnsaved}
            unsavedElsewhere={layoutSharedWorkspace && !hasUnsavedPhysicalLayout
              ? Object.entries(roofWorkspaceRef.current)
                .filter(([id, draft]) => id !== layoutOrientation.id && (draft.dirty || draft.shadingDirty))
                .map(([id]) => orientationList.findIndex((o) => o.id === id) + 1)
                .filter((n) => n > 0)
              : []}
            onDirtyChange={(dirty, check) => {
              setLayoutFormDirty(dirty || (layoutSharedWorkspace && Object.entries(roofWorkspaceRef.current)
                .some(([id, draft]) => id !== layoutOrientation.id && (draft.dirty || draft.shadingDirty))));
              if (check) setPlanChecks(previous => {
                const next = { ...check, projectId: currentProject?.id };
                return JSON.stringify(previous[check.id]) === JSON.stringify(next) ? previous : { ...previous, [check.id]: next };
              });
            }}
            // `shading`: the obstacle shading calculated on this layout, saved
            // with it in the same step -- undefined: none calculated, keep what
            // is stored; null: cleared; { sceneKey }: every face's, calculated
            // together (sceneShadingDraft) on exactly what this Save stores.
            // In the same commit as the layout, so it is not marked out of date
            // by the change it was made for.
            automaticShading
            onSave={async (spacingInfo, frames, sceneLayoutsFromScene, shading, { placement, shouldCancel } = {}) => {
              // A shortened inactive ground group must not be silently omitted
              // from the shared save while the current group reports success.
              const unresolvedGroundDrafts = installationType === "ground" ? Object.entries(roofWorkspaceRef.current)
                .filter(([id, draft]) => id !== layoutOrientation.id && draft.dirty && orientationList.some(o => o.id === id)
                  && (draft.invalid || layoutDesignChanged(draft.spacing,
                    orientationSummary(layoutPreviewCfg).find(p => p.id === id)?.modules || 0,
                    physicalLayoutDesignKey(systemDesignForOrientation(layoutPreviewCfg.systemDesign, orientationList, id))))) : [];
              if (unresolvedGroundDrafts.length) {
                const groups = unresolvedGroundDrafts.map(([id]) => orientationList.findIndex(o => o.id === id) + 1).join(", ");
                throw new Error(isGreekScreen
                  ? `Οι ομάδες ${groups} έχουν μη έγκυρες αλλαγές διάταξης ή απόκλιση από τα strings. Επιστρέψτε σε αυτές και διορθώστε τη διάταξη/τον ηλεκτρολογικό σχεδιασμό πριν αποθηκεύσετε. Οι αλλαγές παραμένουν στο πρόχειρο.`
                  : `Groups ${groups} have invalid layout changes or a string mismatch. Return to these groups and correct the layout/electrical design before saving. Your edits remain in the draft.`);
              }
              const { next: composed, draftIds, spacingOnly } = composeLayoutSave(spacingInfo, frames, sceneLayoutsFromScene);
              let next = composed;
              if (shading?.sceneKey) {
                if (sceneShadingDraft?.key === shading.sceneKey) {
                  Object.entries(sceneShadingDraft.tables).forEach(([id, evidence]) => {
                    next = saveOrientationShading(next, id, evidence);
                  });
                }
              } else if (shading !== undefined) {
                next = saveOrientationShading(next, layoutOrientation.id, shading);
              }
              const saveCancelled = () => !layoutSaveActiveRef.current || shouldCancel?.() ||
                cfgRef.current !== cfg || roofWorkspaceProjectRef.current !== currentProject?.id;
              const result = await prepareLayoutShading(cfg, next, {
                calculate: true,
                orientationId: layoutOrientation.id, placement, projectId: currentProject?.id,
                getModule: () => getModuleDetails(cfg.read_module, accessToken),
                shouldCancel: saveCancelled,
              });
              if (saveCancelled()) {
                throw new Error(isGreekScreen
                  ? "Οι παράμετροι άλλαξαν κατά τον υπολογισμό. Αποθηκεύστε ξανά τη διάταξη."
                  : "Parameters changed during calculation. Save the layout again.");
              }
              history.commit(result.config);
              draftIds.forEach((id) => { delete roofWorkspaceRef.current[id]; });
              setSceneShadingDraft(null);
              if (layoutIsPrimary && autoDesignLayout && typeof autoDesignLayout === "object") {
                setAutoDesignLayout((prev) =>
                  prev && typeof prev === "object"
                    ? { ...prev, spacingInfo: spacingOnly }
                    : prev
                );
              }
              setHasUnsavedPhysicalLayout(false);
              setLayoutFormDirty(false);
              setPhysicalLayoutDraft(null);
              return { shadingError: result.shadingError
                ? (isGreekScreen ? translateLegacyText(result.shadingError) : result.shadingError) : null,
                shadingIssue: result.shadingIssue || null,
                shadingUpdated: result.shadingUpdated || false,
                // Saved without a calculation that matches it: the Save says so.
                shadingPending: sceneShadingPending(result.config).length > 0 };
            }}
          />
          </Box>
          </Paper>

        </Box>{/* end Electrical Design & Layout tab */}

      {/* The single-line diagram now renders as step 3 inside the
          Electrical Design tab, not as a top-level dialog. */}

      <Dialog
        open={sldOpen}
        onClose={() => {
          setSldOpen(false);
          setSldPdfUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return "";
          });
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Single-line diagram (preview)</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 500 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* LEFT: options */}
            <Paper variant="outlined" sx={{ width: 280, p: 2, borderRadius: 2, height: "70vh", overflow: "auto" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                SLD options
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={sldOpts.use_generation_meter}
                    onChange={(e) => {
                      setSldOpts((p) => ({ ...p, use_generation_meter: e.target.checked }));
                    }}
                  />
                }
                label="Generation Meter"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={sldOpts.use_spd}
                    onChange={(e) => {
                      setSldOpts((p) => ({ ...p, use_spd: e.target.checked }));
                    }}
                  />
                }
                label="SPD"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={sldOpts.use_nvr}
                    onChange={(e) => {
                      setSldOpts((p) => ({ ...p, use_nvr: e.target.checked }));
                    }}
                  />
                }
                label="NVR"
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Cables</Typography>

              <TextField
                fullWidth
                size="small"
                label="Feeder cable label"
                value={sldOpts.feeder_cable_label}
                onChange={(e) => setSldOpts(p => ({ ...p, feeder_cable_label: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth
                size="small"
                label="To client panel cable label"
                value={sldOpts.client_panel_cable_label}
                onChange={(e) => setSldOpts(p => ({ ...p, client_panel_cable_label: e.target.value }))}
                helperText="Cable from ACDB to client panel"
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Breakers</Typography>

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Main breaker rating (A)"
                    value={sldOpts.main_breaker_rating}
                    onChange={(e) => setSldOpts(p => ({ ...p, main_breaker_rating: Number(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Main breaker voltage (V)"
                    value={sldOpts.main_breaker_voltage}
                    onChange={(e) => setSldOpts(p => ({ ...p, main_breaker_voltage: Number(e.target.value) }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Main breaker type"
                    value={sldOpts.main_breaker_type}
                    onChange={(e) => setSldOpts(p => ({ ...p, main_breaker_type: e.target.value }))}
                    helperText='e.g. "MCCB", "MCB"'
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Inv breaker rating (A)"
                    value={sldOpts.inverter_breaker_rating}
                    onChange={(e) => setSldOpts(p => ({ ...p, inverter_breaker_rating: Number(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Inv breaker voltage (V)"
                    value={sldOpts.inverter_breaker_voltage}
                    onChange={(e) => setSldOpts(p => ({ ...p, inverter_breaker_voltage: Number(e.target.value) }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Inv breaker type"
                    value={sldOpts.inverter_breaker_type}
                    onChange={(e) => setSldOpts(p => ({ ...p, inverter_breaker_type: e.target.value }))}
                  />
                </Grid>
              </Grid>

              {sldOpts.use_generation_meter && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Meter</Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="Accuracy class"
                        value={sldOpts.meter_accuracy_class}
                        onChange={(e) => setSldOpts(p => ({ ...p, meter_accuracy_class: Number(e.target.value) }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="Primary current (A)"
                        value={sldOpts.meter_primary_current}
                        onChange={(e) => setSldOpts(p => ({ ...p, meter_primary_current: Number(e.target.value) }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="Secondary current (A)"
                        value={sldOpts.meter_secondary_current}
                        onChange={(e) => setSldOpts(p => ({ ...p, meter_secondary_current: Number(e.target.value) }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="Burden (VA)"
                        value={sldOpts.meter_burden_rating}
                        onChange={(e) => setSldOpts(p => ({ ...p, meter_burden_rating: Number(e.target.value) }))}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {sldOpts.use_spd && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>SPD</Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="SPD voltage (V)"
                        value={sldOpts.spd_voltage}
                        onChange={(e) => setSldOpts(p => ({ ...p, spd_voltage: Number(e.target.value) }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="Current capacity (kA)"
                        value={sldOpts.spd_current_capacity}
                        onChange={(e) => setSldOpts(p => ({ ...p, spd_current_capacity: Number(e.target.value) }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small" type="number"
                        label="SPD type"
                        value={sldOpts.spd_type}
                        onChange={(e) => setSldOpts(p => ({ ...p, spd_type: Number(e.target.value) }))}
                        helperText="Usually Type 2"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Contactor</Typography>

              <TextField
                fullWidth
                size="small"
                label="Label"
                value={sldOpts.contactor_label}
                onChange={(e) => setSldOpts(p => ({ ...p, contactor_label: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Rating (A)"
                value={sldOpts.contactor_rating}
                onChange={(e) => setSldOpts(p => ({ ...p, contactor_rating: Number(e.target.value) }))}
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Title Block
              </Typography>

              <TextField
                fullWidth size="small"
                label="Released for"
                value={sldOpts.released_for}
                onChange={(e) => setSldOpts(p => ({ ...p, released_for: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth size="small"
                label="Project title"
                value={sldOpts.project_title}
                onChange={(e) => setSldOpts(p => ({ ...p, project_title: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth size="small"
                label="Drawing name"
                value={sldOpts.drawing_name}
                onChange={(e) => setSldOpts(p => ({ ...p, drawing_name: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small"
                    label="Designed by"
                    value={sldOpts.designed_by}
                    onChange={(e) => setSldOpts(p => ({ ...p, designed_by: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small"
                    label="Drawn by"
                    value={sldOpts.drawn_by}
                    onChange={(e) => setSldOpts(p => ({ ...p, drawn_by: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small"
                    label="Checked by"
                    value={sldOpts.checked_by}
                    onChange={(e) => setSldOpts(p => ({ ...p, checked_by: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small"
                    label="Approved by"
                    value={sldOpts.approved_by}
                    onChange={(e) => setSldOpts(p => ({ ...p, approved_by: e.target.value }))}
                  />
                </Grid>
              </Grid>



            </Paper>

            {/* RIGHT: preview */}
            <Box sx={{ flex: 1, height: "70vh" }}>
              {sldLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Generating…</Typography>
                </Box>
              )}

              {sldError && <Alert severity="error" sx={{ mt: 2 }}>{sldError}</Alert>}

              {!sldLoading && !sldError && sldPdfUrl && (
                <iframe title="SLD PDF Preview" src={sldPdfUrl} style={{ width: "100%", height: "100%", border: "none" }} />
              )}

              {!sldLoading && !sldError && !sldPdfUrl && (
                <Typography variant="body2" color="text.secondary">No SLD data.</Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            disabled={!sldPdfUrl || sldLoading}
            onClick={() => downloadBlobUrl(sldPdfUrl, "Single Line Diagram.pdf")}
          >
            Download PDF
          </Button>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setSldOpen(false);
              setSldPdfUrl((old) => {
                if (old) URL.revokeObjectURL(old);
                return "";
              });
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoDesignOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
          >
            <DialogTitle>Auto-design PV layout</DialogTitle>
            <DialogContent dividers>
              {/* Two columns from md up: what the search does, what it needs
                  and what it is asked for on the left; the tuning dials on
                  the right. In one narrow column the dialog ran to several
                  screens of scrolling. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 5fr) minmax(0, 7fr)" },
                  columnGap: 3,
                  rowGap: 1,
                  alignItems: "start",
                }}
              >
              <Box>
              {isFlushRoof ? (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {isGreekScreen
                    ? "Προτιμά την πλησιέστερη συμβατή ισχύ μέσα στις ανοχές που ορίζετε. Για εξίσου κοντινές λύσεις αξιολογεί κόστος και απόδοση. Τα πλαίσια τοποθετούνται συνεπίπεδα στη στέγη και πρέπει να χωρούν στη διαθέσιμη επιφάνεια."
                    : "Prefers the closest compatible capacity within your tolerances. Equally close options are ranked by cost and efficiency. Modules lie flush on the roof and must fit the available area."}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {isGreekScreen
                    ? "Προτιμά την πλησιέστερη συμβατή ισχύ μέσα στις ανοχές που ορίζετε. Για εξίσου κοντινές λύσεις αξιολογεί κόστος και απόδοση. Προτείνει επίσης διάταξη σειρών με αποστάσεις για τον περιορισμό της σκίασης."
                    : "Prefers the closest compatible capacity within your tolerances. Equally close options are ranked by cost and efficiency. Also proposes a row layout with spacing to limit shading."}
                </Typography>
              )}
              {isFlushRoof ? (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  The modules take the roof&apos;s own pitch and facing, as set on the
                  System &amp; Orientation tab. Neither is optimised.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Tilt and azimuth are taken as given, not optimised. Set them on the
                  System &amp; Orientation tab first &mdash; Optimize there finds the
                  annual best pair for the site.
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Inputs used:
              </Typography>
              <Box
                component="ul"
                sx={{ pl: 3, mt: 0, mb: 2, color: "text.secondary" }}
              >
                <li>Latitude / Longitude</li>
                {isFlushRoof ? (
                  <>
                    <li>Available roof area (the project&apos;s area field)</li>
                    <li>Roof pitch / facing</li>
                  </>
                ) : (
                  <>
                    <li>{cfg?.loc_info?.parcel ? "Parcel boundary (or terrain area, if the parcel is not used)" : "Terrain area"}</li>
                    <li>Tilt / Azimuth</li>
                  </>
                )}
                <li>Selected module and inverter (optional)</li>
                <li>Target DC size (below)</li>
                <li>Surface type</li>
              </Box>

              {requiredAutoInputs().length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {isGreekScreen ? "Λείπουν στοιχεία" : "Missing inputs"}: {requiredAutoInputs().map((m) => autoDesignInputName(m, isGreekScreen)).join(", ")}
                </Alert>
          )}

              {/* The target, above Tuning and in the same shape as the groups
                  inside it: an uppercase caption, one line saying what it is,
                  and the field. It is not a dial -- everything under Tuning
                  shapes HOW the search runs, while this is WHAT it is asked
                  for -- but it does not need a panel of its own to say so.

                  The fallback figure lives in the field's placeholder rather
                  than in the sentence beside it. A number interpolated into a
                  sentence cannot be translated: the Greek catalogue matches
                  whole strings, so the text would print in English the moment
                  a capacity appeared in it. */}
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary" }}>
                    Target
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    The DC size the search aims at.
                  </Typography>
                </Stack>
                <MaybeTooltip
                  title={isGreekScreen
                    ? "Οι υποψήφιοι σχεδιασμοί μπορούν να υπερβαίνουν αυτή την ισχύ έως τον καθορισμένο λόγο υπερδιαστασιολόγησης. Αν το πεδίο μείνει κενό, χρησιμοποιείται η εγκατεστημένη ισχύς του έργου."
                    : "Candidates are accepted up to the oversize ratio above this size, so the design that comes back is usually a little larger. Left blank, the search uses the plant's installed capacity."}
                  arrow
                >
                  <TextField
                    size="small"
                    type="number"
                    label="Target DC size (kWp)"
                    autoFocus={focusAutoDesignTarget}
                    value={autoDesignParams.targetCapacityKw ?? ""}
                    onChange={(e) => setAutoDesignParam("targetCapacityKw", e.target.value)}
                    inputProps={{ step: 1, min: 0 }}
                    sx={{ width: { xs: "100%", sm: 260 } }}
                    placeholder={
                      targetSource(cfg) === "capacity"
                        ? String(cfg?.pvInstalledCap?.kw ?? "")
                        : undefined
                    }
                    helperText={
                      storedTargetKw(cfg) === null && targetSource(cfg) === "capacity"
                        ? "Blank: the plant's installed capacity is used."
                        : undefined
                    }
                  />
                </MaybeTooltip>
              </Box>

              <Box sx={{ mt: 2, color: "text.primary" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {isGreekScreen ? "Παραδοχές" : "Assumptions"}
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0, fontSize: 14, lineHeight: 1.6 }}>
                  <li>{isGreekScreen
                    ? "Τα φίλτρα υποψηφίων εφαρμόζονται μόνο όταν δεν έχει ήδη επιλεγεί το αντίστοιχο πλαίσιο ή αντιστροφέας στον Εξοπλισμό."
                    : "Candidate filters apply only when the corresponding module or inverter has not already been selected in Equipment."}</li>
                  <li>{isGreekScreen
                    ? "Αν έχει επιλεγεί πλαίσιο, ο σχεδιαστής το διατηρεί και αναζητά συμβατό αντιστροφέα."
                    : "If a module is selected, the designer keeps it and searches for a compatible inverter."}</li>
                  <li>{isGreekScreen
                    ? "Αν δεν έχει επιλεγεί πλαίσιο, αναζητά και πλαίσιο και αντιστροφέα. Το φίλτρο τεχνολογίας πλαισίων επιτρέπει νέα επιλογή πλαισίου."
                    : "If no module is selected, it searches for both module and inverter. The module technology filter allows a new module choice."}</li>
                </Box>
              </Box>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  mt: 0,
                  mb: 1,
                  p: 2,
                  backgroundColor: (t) =>
                    t.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Tuning
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                    {isGreekScreen ? "Υποψήφιος εξοπλισμός" : "Candidate equipment"}
                  </Typography>
                  {cfg.read_inverter && <FormControlLabel
                    control={<Checkbox size="small" checked={Boolean(autoDesignParams.keepInverterModel)}
                      onChange={event => setAutoDesignParam("keepInverterModel", event.target.checked)} />}
                    label={isGreekScreen ? "Διατήρηση μοντέλου αντιστροφέα" : "Keep inverter model"}
                  />}
                  <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                    {isGreekScreen
                      ? "Το επιλεγμένο πλαίσιο διατηρείται. Ο επιλεγμένος αντιστροφέας δοκιμάζεται πρώτα· αν δεν βρεθεί συμβατή λύση, προτείνεται άλλος, εκτός αν ενεργοποιήσετε τη «Διατήρηση μοντέλου αντιστροφέα». Η αναζήτηση καταλόγου τηρεί τα παρακάτω φίλτρα."
                      : "The selected module is retained. The selected inverter is tried first; if no compatible design is found, another is proposed unless Keep inverter model is enabled. Catalogue searches respect the filters below."}
                  </Typography>
                  {(cfg?.read_module || cfg?.read_inverter) && <Stack direction="row" gap={0.6} flexWrap="wrap" sx={{ mb: 1 }}>
                    {cfg?.read_module && <Chip size="small" variant="outlined" label={`${isGreekScreen ? "Πλαίσιο" : "Module"}: ${cfg.read_module}`} />}
                    {cfg?.read_inverter && <Chip size="small" variant="outlined" label={`${isGreekScreen ? "Αντιστροφέας" : "Inverter"}: ${cfg.read_inverter}`} />}
                  </Stack>}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.25, sm: 2 }} sx={{ mb: 1.5 }}>
                    <FormControlLabel
                      disabled={Boolean(cfg?.read_module)}
                      control={<Switch
                        size="small"
                        checked={Boolean(autoDesignParams.modulesOwnedOnly)}
                        onChange={(event) => setAutoDesignParam("modulesOwnedOnly", event.target.checked)}
                      />}
                      label={isGreekScreen ? "Μόνο πλαίσια του οργανισμού μου" : "My organization's modules only"}
                    />
                    <FormControlLabel
                      disabled={Boolean(cfg?.read_inverter)}
                      control={<Switch
                        size="small"
                        checked={Boolean(autoDesignParams.invertersOwnedOnly)}
                        onChange={(event) => setAutoDesignParam("invertersOwnedOnly", event.target.checked)}
                      />}
                      label={isGreekScreen ? "Μόνο αντιστροφείς του οργανισμού μου" : "My organization's inverters only"}
                    />
                  </Stack>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      disabled={Boolean(cfg?.read_module)}
                      size="small"
                      options={technologyOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.label === value.label}
                      value={technologyOptions.filter((option) =>
                        option.values.some((value) => (autoDesignParams.moduleTechnologies || []).includes(value))
                      )}
                      onChange={(_event, options) => setAutoDesignParam(
                        "moduleTechnologies",
                        [...new Set(options.flatMap((option) => option.values))]
                      )}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            sx={{ mr: 1, "&.Mui-checked": { color: "primary.main" } }}
                          />
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} label={isGreekScreen ? "Τεχνολογίες Φ/Β πλαισίων" : "PV module technologies"} placeholder={isGreekScreen ? "Όλες" : "Any"} />}
                    />
                    <AccessibleSelectTextField
                      select
                      size="small"
                      disabled={Boolean(cfg?.read_inverter)}
                      label={isGreekScreen ? "Μοντέλο απόδοσης αντιστροφέα" : "Inverter conversion model"}
                      value={autoDesignParams.inverterConversionModel || ""}
                      onChange={(event) => setAutoDesignParam("inverterConversionModel", event.target.value)}
                      helperText={isGreekScreen ? "Περιορίζει τους υποψήφιους αντιστροφείς του σχεδιασμού." : "Restricts candidate inverters for this design run."}
                    >
                      <MenuItem value="">{isGreekScreen ? "Οποιοδήποτε" : "Any"}</MenuItem>
                      {inverterModelOptions.filter((option) => ["sandia", "pvwatts"].includes(option.key)).map((option) => (
                        <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                      ))}
                    </AccessibleSelectTextField>
                    <AccessibleSelectTextField
                      select
                      size="small"
                      disabled={Boolean(cfg?.read_inverter)}
                      label={isGreekScreen ? "Φάσεις υποψήφιων αντιστροφέων" : "Candidate inverter phases"}
                      value={["one", "three", "both"].includes(autoDesignParams.inverterPhases) ? autoDesignParams.inverterPhases : "both"}
                      onChange={(event) => setAutoDesignParam("inverterPhases", event.target.value)}
                      helperText={isGreekScreen
                        ? "Πάνω από 5 kW εξετάζονται μόνο τριφασικοί αντιστροφείς."
                        : "Above 5 kW, only three-phase inverters are considered."}
                    >
                      <MenuItem value="one">{isGreekScreen ? "Μόνο μονοφασικοί" : "Single-phase only"}</MenuItem>
                      <MenuItem value="three">{isGreekScreen ? "Μόνο τριφασικοί" : "Three-phase only"}</MenuItem>
                      <MenuItem value="both">{isGreekScreen ? "Και οι δύο" : "Both"}</MenuItem>
                    </AccessibleSelectTextField>
                  </Box>
                </Box>

                {/* Two groups, because the dials answer two different questions:
                    how the electrical design is chosen and scored, and how the
                    rows may be built. Laid out on a CSS grid: MUI 7's Grid ignores
                    the legacy item/xs props, which is why these fields used to
                    arrange themselves by content width. */}
                {[
                  {
                    title: "Electrical design",
                    lede: "Power tolerance, DC/AC ratio and the allowed string voltages.",
                    fields: [
                      { key: "undersizeRatio", label: isGreekScreen ? "Επιτρεπόμενη μείωση ισχύος (%)" : "Allowed power reduction (%)", step: 1, min: 0, max: 99, percent: true, lower: true,
                        tip: isGreekScreen ? "Πόσο κάτω από την ισχύ-στόχο μπορεί να είναι η πρόταση. Νέα έργα: 10%. Τα υπάρχοντα κρατούν την προηγούμενη ανοχή. Προτιμάται η πλησιέστερη συμβατή ισχύς." : "How far below the target a proposal may be. New projects: 10%; existing projects retain their previous allowance. The closest compatible capacity is preferred." },
                      { key: "oversizeRatio", label: isGreekScreen ? "Επιτρεπόμενη υπέρβαση ισχύος (%)" : "Allowed power excess (%)", step: 1, min: 0, percent: true,
                        tip: isGreekScreen ? "Πόσο πάνω από την ισχύ-στόχο μπορεί να είναι η πρόταση. Νέα έργα: 10%. Δεν αλλάζει τα ηλεκτρολογικά όρια του εξοπλισμού." : "How far above the target a proposal may be. New projects: 10%. Equipment electrical limits remain unchanged." },
                      { key: "dcacMin", label: "DC/AC min", step: 0.05, min: 0.5,
                        tip: isGreekScreen ? "Κατώτατο όριο του λόγου ισχύος Φ/Β DC προς ισχύ αντιστροφέα AC. Προεπιλογή: 1,0. Κάτω από το 1, η ισχύς του αντιστροφέα υπερβαίνει την ισχύ των πλαισίων." : "Lower bound on DC/AC (array DC over inverter AC). Default 1.0 — below 1 the design carries more inverter than array." },
                      { key: "dcacMax", label: "DC/AC max", step: 0.05, min: 0.5,
                        tip: isGreekScreen ? "Ανώτατο όριο του λόγου ισχύος Φ/Β DC προς ισχύ αντιστροφέα AC. Προεπιλογή: 1,2." : "Upper bound on DC/AC. Default 1.2." },
                      { key: "minCellC", label: "Coldest cell (°C)", step: 1,
                        tip: isGreekScreen ? "Ελάχιστη θερμοκρασία κυψέλης για τον έλεγχο του string. Όσο ψύχεται η κυψέλη, αυξάνεται η τάση ανοικτού κυκλώματος (Voc). Η τιμή αυτή καθορίζει τη μέγιστη τάση DC, ιδίως πριν ξεκινήσει ο αντιστροφέας το πρωί. Προεπιλογή: −10 °C." : "Coldest cell temperature the string is checked at. Voc rises as the cell cools and the inverter is not yet running at dawn, so this sets the highest voltage the DC system ever sees. Default −10 °C." },
                      { key: "maxCellC", label: "Hottest cell (°C)", step: 1,
                        tip: isGreekScreen ? "Μέγιστη θερμοκρασία κυψέλης για τον έλεγχο του string. Όσο θερμαίνεται η κυψέλη, μειώνεται η τάση στο σημείο μέγιστης ισχύος (Vmp). Έτσι προσδιορίζεται το ελάχιστο μήκος string που παραμένει εντός της περιοχής MPPT του αντιστροφέα. Προεπιλογή: 70 °C." : "Hottest cell temperature the string is checked at. Vmp falls as the cell heats, so this decides the shortest string that stays above the inverter's MPPT floor on a summer afternoon. Default 70 °C." },
                    ],
                  },
                  {
                    title: "Ranking",
                    lede: "How the proposed design is chosen among the valid ones.",
                    fields: [
                      { key: "wCost", label: isGreekScreen ? "Βάρος κόστους (0–1)" : "Cost weight (0–1)", step: 0.05, min: 0, max: 1, clamp: true,
                        tip: isGreekScreen
                          ? "Όταν περισσότερες λύσεις απέχουν το ίδιο από την ισχύ-στόχο: 0 = προτιμάται η αποδοτικότερη, 1 = προτιμάται η φθηνότερη ανά W. Προεπιλογή: 0,8."
                          : "When several designs are equally close to the target capacity: 0 = the most efficient is preferred, 1 = the cheapest per W. Default 0.8." },
                    ],
                    toggles: [
                      { key: "preferNearestStrict", label: isGreekScreen ? "Προτεραιότητα στην πλησιέστερη ισχύ" : "Nearest capacity first",
                        checked: autoDesignParams.preferNearestStrict !== false,
                        tip: isGreekScreen
                          ? "Ενεργό: από τις λύσεις μέσα στην επιτρεπόμενη μείωση/υπέρβαση ισχύος προτιμάται πάντα αυτή που απέχει λιγότερο από την ισχύ-στόχο, και μόνο μεταξύ ισοδύναμων αποφασίζουν οι υπόλοιπες ρυθμίσεις. Ανενεργό: όλες οι λύσεις μέσα στα όρια συγκρίνονται μόνο με τα υπόλοιπα κριτήρια (αντιστροφείς, κόστος, απόδοση), οπότε η πρόταση μπορεί να απέχει περισσότερο από τον στόχο. Ισχύει και στη Διαστασιολόγηση. Προεπιλογή: ενεργό."
                          : "On: among the designs within the allowed power reduction/excess, the one closest to the target always wins, and the other settings decide only between equals. Off: every design within the limits is compared on the other criteria alone (inverters, cost, efficiency), so the proposal may sit further from the target. Also applies to PV sizing. Default: on." },
                      { key: "preferFewerInverters", label: isGreekScreen ? "Προτίμηση λιγότερων αντιστροφέων" : "Prefer fewer inverters",
                        checked: autoDesignParams.preferFewerInverters !== false,
                        tip: isGreekScreen
                          ? "Ενεργό: ανάμεσα σε λύσεις ίδιας ισχύος προτιμάται αυτή με τους λιγότερους αντιστροφείς, πριν από το κόστος και την απόδοση (λιγότερος εξοπλισμός, συντήρηση και χώρος). Ανενεργό: ο αριθμός αντιστροφέων μετρά μόνο ως μικρό δευτερεύον κριτήριο. Ισχύει και στη Διαστασιολόγηση. Προεπιλογή: ενεργό."
                          : "On: among designs of the same capacity the one with the fewest inverters wins, before cost and efficiency (less equipment, maintenance and space). Off: the inverter count is only a small secondary criterion. Also applies to PV sizing. Default: on." },
                      { key: "withinCurrentRatings", label: isGreekScreen ? "Μόνο εντός ονομαστικού ρεύματος MPPT" : "Only within MPPT current ratings",
                        checked: autoDesignParams.withinCurrentRatings === true,
                        tip: isGreekScreen
                          ? "Ενεργό: απορρίπτονται οι λύσεις όπου το ρεύμα των strings ενός MPPT ξεπερνά το ονομαστικό ρεύμα λειτουργίας του αντιστροφέα (πιο συντηρητικό). Ανενεργό: επιτρέπονται με μικρή ποινή στη βαθμολογία, αφού ο αντιστροφέας περιορίζει το ρεύμα και η προσομοίωση υπολογίζει την απώλεια· το όριο βραχυκύκλωσης ελέγχεται πάντα. Ισχύει και στη Διαστασιολόγηση. Προεπιλογή: ανενεργό."
                          : "On: designs whose strings drive an MPPT above the inverter's operating current rating are rejected (more conservative). Off: they are allowed with a small score penalty, since the inverter limits the current and the simulation counts the loss; the short-circuit limit is always enforced. Also applies to PV sizing. Default: off." },
                    ],
                  },
                  {
                    title: "Row layout",
                    lede: "How the rows may be built, and where.",
                    fields: [
                      { key: "minTiers", label: "Min stacked per row", step: 1, min: 1, max: 6,
                        tip: isGreekScreen ? "Ελάχιστος αριθμός πλαισίων καθ' ύψος σε κάθε σειρά που μπορεί να προτείνει ο σχεδιασμός. Προεπιλογή: 1." : "Fewest modules stacked in a row the search may propose. Default 1." },
                      { key: "maxTiers", label: "Max stacked per row", step: 1, min: 1, max: 6,
                        tip: isGreekScreen ? "Μέγιστος αριθμός πλαισίων καθ' ύψος σε κάθε σειρά που μπορεί να προτείνει ο σχεδιασμός. Προεπιλογή: 3." : "Most modules stacked in a row the search may propose — the structure the client has priced. Default 3." },
                      { key: "setbackM", label: "Setback (m)", step: 0.5, min: 0,
                        disabled: !cfg?.loc_info?.parcel || !autoDesignParams.fitToParcel,
                        tip: isGreekScreen ? "Απόσταση από το όριο του γηπέδου που μένει ελεύθερη από πλαίσια, π.χ. για περίφραξη, πρόσβαση ή απαιτούμενη υποχώρηση." : "Distance from the boundary kept clear of modules: fence, access track, planning offset." },
                      { key: "landMarginPct", label: "Land margin (%)", step: 1, min: 0, max: 100,
                        disabled: Boolean(cfg?.loc_info?.parcel) && Boolean(autoDesignParams.fitToParcel),
                        tip: isGreekScreen ? "Επιπλέον έκταση πάνω από τις σειρές για διαδρόμους πρόσβασης, θέσεις αντιστροφέων και υποσταθμό, όταν ο σχεδιασμός ελέγχεται με την Επιφάνεια εδάφους. Όταν οι σειρές τοποθετούνται μέσα στο σχεδιασμένο γήπεδο, ισχύει η απόσταση από τα όρια. Προεπιλογή: 10%." : "Extra land on top of the rows for access tracks, inverter and transformer stations, when the design is checked against the terrain area. When the rows are laid inside the drawn parcel, the setback applies instead. Default 10%." },
                    ],
                    toggles: [
                      { key: "fitToParcel", label: "Fit rows to the parcel",
                        disabled: !cfg?.loc_info?.parcel,
                        checked: Boolean(autoDesignParams.fitToParcel) && Boolean(cfg?.loc_info?.parcel),
                        tip: cfg?.loc_info?.parcel
                          ? (isGreekScreen ? "Τοποθετεί τις σειρές κάθε υποψήφιου σχεδιασμού μέσα στο περίγραμμα του γηπέδου και απορρίπτει όσους δεν χωρούν. Οι επιλεγμένες σειρές μεταφέρονται στη φυσική διάταξη." : "Lay each candidate's rows inside the parcel drawn on the site map, and reject any that cannot hold the design there. The chosen rows arrive in the layout tab table by table.")
                          : (isGreekScreen ? "Σχεδιάστε πρώτα το όριο του γηπέδου στον χάρτη της καρτέλας Έργο." : "Draw the site boundary on the map under Project setup to enable this.") },
                      { key: "wholeStringsPerRow", label: "Whole strings per row",
                        checked: Boolean(autoDesignParams.wholeStringsPerRow),
                        tip: isGreekScreen ? "Προτείνει μόνο μήκη string που είναι πολλαπλάσια των πλαισίων καθ' ύψος, ώστε κάθε string να γεμίζει ολόκληρες στήλες. Για παράδειγμα, string 25 πλαισίων σε σειρά ύψους 3 αφήνει δύο κενές θέσεις στην τελευταία στήλη." : "Only propose string lengths that are a multiple of the stacking, so every string fills whole columns. A 25-module string on a 3-high row leaves two positions empty in its last column." },
                    ],
                  },
                ].filter((group) => !isRoofInstallation(installationType) || group.title !== "Row layout").map((group, gi) => (
                  <Box key={group.title} sx={{ mt: gi ? 2.25 : 0 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary" }}>
                        {group.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {group.lede}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                        gap: 1.5,
                      }}
                    >
                      {group.fields.map((f) => (
                        <MaybeTooltip key={f.key} title={f.tip} arrow>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={f.label}
                            value={f.percent ? tolerancePercent(autoDesignParams[f.key], f.lower) : autoDesignParams[f.key]}
                            disabled={Boolean(f.disabled)}
                            onChange={(e) => setAutoDesignParam(f.key, f.percent ? toleranceRatio(e.target.value, f.lower)
                              : f.clamp ? clampedInput(e.target.value, f.min, f.max) : e.target.value)}
                            inputProps={{ step: f.step, ...(f.min != null ? { min: f.min } : {}), ...(f.max != null ? { max: f.max } : {}) }}
                          />
                        </MaybeTooltip>
                      ))}
                    </Box>
                    {group.toggles ? (
                      <Stack direction="row" sx={{ mt: 0.75, flexWrap: "wrap", columnGap: 2, rowGap: 0.25 }}>
                        {group.toggles.map((t) => (
                          <MaybeTooltip key={t.key} title={t.tip} arrow>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={t.checked}
                                  disabled={Boolean(t.disabled)}
                                  onChange={(e) => setAutoDesignParam(t.key, e.target.checked)}
                                />
                              }
                              label={<Typography variant="body2">{t.label}</Typography>}
                            />
                          </MaybeTooltip>
                        ))}
                      </Stack>
                    ) : null}
                  </Box>
                ))}
                <AutoDesignShares
                  cfg={cfg}
                  onChange={(shares) => setAutoDesignParam("orientationShares", shares)}
                />
              </Paper>
              </Box>

          {autoDesignResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">Auto-design applied</Typography>
              {autoDesignResult.inverterReplacement && <Typography variant="body2" sx={{ my: 1 }}>
                {inverterReplacementMessage(autoDesignResult.inverterReplacement, isGreekScreen)}
              </Typography>}
              <Typography variant="body2">
                {autoDesignResult.reportSaved === true
                  ? (isGreekScreen ? "Η αναφορά αποθηκεύτηκε." : "The report was saved.")
                  : autoDesignResult.reportSaved === false
                  ? (isGreekScreen
                      ? "Η αναφορά δεν αποθηκεύτηκε. Εκτελέστε ξανά τον σχεδιασμό για νέα αναφορά."
                      : "The report was not saved. Run auto-design again to create a new report.")
                  : null}
              </Typography>
              {autoDesignResult.reportRetentionOk === false && (
                <Typography variant="body2" color="warning.main">
                  {isGreekScreen
                    ? "Η αναφορά αποθηκεύτηκε, αλλά δεν ολοκληρώθηκε η διατήρηση μόνο των 5 τελευταίων."
                    : "The report was saved, but older reports could not be removed."}
                </Typography>
              )}
              {autoDesignResult.module && (
                <Typography variant="body2">• Module: {autoDesignResult.module}</Typography>
              )}
              {autoDesignResult.inverter && (
                <Typography variant="body2">• Inverter: {autoDesignResult.inverter}</Typography>
              )}
              {autoDesignResult.pvCapacityKw && (
                <Typography variant="body2">• PV size: {autoDesignResult.pvCapacityKw} kW</Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // The design step is a tab now, not a dialog. This used to
                    // call setDesignOpen(true), which nothing has read since the
                    // modals became tabs - so "See results" quietly did nothing.
                    // The key bump still forces the panel to remount, so it
                    // reads the design that was just generated.
                    setAutoDesignOpen(false);
                    setDesignDialogKey((k) => k + 1);
                    setDesignStep("electrical");
                  }}
                >
                  See results
                </Button>
              </Box>
            </Alert>
          )}
          {autoDesignError && (() => {
            const parsedError = formatAutoDesignError(autoDesignError);
            const explanation = describeAutoDesignFailure(parsedError, isGreekScreen);
            return (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{explanation.title}</Typography>
                {explanation.cause && <Typography variant="body2" sx={{ mt: 0.5 }}>{explanation.cause}</Typography>}
                {explanation.action && <Typography variant="body2" sx={{ mt: 0.5 }}>{explanation.action}</Typography>}
                {isAdminUser && (
                  <Box component="details" sx={{ mt: 1.25 }}>
                    <Typography component="summary" variant="caption" sx={{ cursor: "pointer", fontWeight: 700 }}>
                      {isGreekScreen ? "Διαγνωστικά εκτέλεσης και απάντηση διακομιστή" : "Run diagnostics and server response"}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "rgba(0,0,0,0.08)",
                        color: "text.primary",
                        fontSize: 12,
                        lineHeight: 1.5,
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {JSON.stringify({
                        status: parsedError.status,
                        request: parsedError.requestContext,
                        message: parsedError.message,
                        summary: parsedError.summary,
                        diagnostics: parsedError.diagnostics ? tryParseAutoDesignJson(parsedError.diagnostics) || parsedError.diagnostics : null,
                        server_response: parsedError.rawResponse ? tryParseAutoDesignJson(parsedError.rawResponse) || parsedError.rawResponse : null,
                      }, null, 2)}
                    </Box>
                  </Box>
                )}
              </Alert>
            );
          })()}
        </DialogContent>
        <DialogActions>
              {/* Why the run button is greyed out: the user is told what to do and
                  where, instead of facing a disabled button with no reason. */}
              {(() => {
                if (autoDesignLoading) return null;
                const reasons = autoDesignBlockers({
                  projectSaved: Boolean(currentProject?.id),
                  missing: requiredAutoInputs(),
                  sharesInvalid: orientationList.length > 1 && !sharesTotal(orientationShares(cfg)).valid,
                  isGreek: isGreekScreen,
                });
                if (!reasons.length) return null;
                return (
                  <Box data-testid="auto-design-blockers" sx={{ mr: "auto", pl: 1, maxWidth: 560 }}>
                    {reasons.map((r) => (
                      <Typography key={r} variant="body2" sx={{ color: "warning.dark", lineHeight: 1.4 }}>{r}</Typography>
                    ))}
                  </Box>
                );
              })()}
              <Button onClick={() => setAutoDesignOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleRunAutoDesign}
                disabled={
                  autoDesignLoading || !currentProject?.id || requiredAutoInputs().length > 0 ||
                  (orientationList.length > 1 && !sharesTotal(orientationShares(cfg)).valid)
                }
                startIcon={
                  autoDesignLoading ? <CircularProgress size={16} thickness={5} /> : null
                }
              >
                {autoDesignLoading ? "Designing..." : "Run auto-design"}
              </Button>
        </DialogActions>
      </Dialog>


      {/* ===== TAB: PV Losses ===== */}
      <Box sx={{ display: pvTab === "losses" ? "block" : "none" }}>
      <PvLoadingRegion loading={moduleModelingLoading} isGreek={isGreekScreen}>
      {/* --------------------  PV LOSSES  -------------------- */}
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 44,
            "& .MuiAccordionSummary-content": {
              my: 0,
            },
            "&.Mui-expanded": {
              minHeight: 44,
            },
            "&.Mui-expanded .MuiAccordionSummary-content": {
              my: 0,
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.85rem",
              color: "text.primary",
            }}
          >
            PV Losses
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          {/* Six numbered steps of onboarding, permanently occupying a third
              of the panel. Collapsed: useful the first time, noise every time
              after, and the fields below are what people come here for. */}
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(96, 165, 250, 0.3)"
                  : "rgba(37, 99, 235, 0.18)",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(59, 130, 246, 0.08)"
                  : "rgba(37, 99, 235, 0.05)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Recommended setup flow
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box
                component="ol"
                sx={{ m: 0, pl: 2.5, color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.7 }}
              >
                <li>Choose how wiring losses are modelled — a fixed percentage, or calculated from cable parameters — for both DC and AC.</li>
                <li>Enter the DC non-wiring losses. These apply before inverter conversion.</li>
                <li>Configure DC wiring. In dynamic mode, set the global target first, then the inverter and MPPT branch details.</li>
                <li>Configure AC branch wiring, the same way.</li>
                <li>Add the main AC cable if the plant-level collector run should be included.</li>
                <li>Check the inverter and MPPT structure matches the system design before saving.</li>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                DC Losses
              </Typography>

              <Box id="field-pv-loss-shading" sx={{ mb: 1.5, p: 1.5, maxWidth: 1120, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {isGreekScreen ? "Τρόπος υπολογισμού σκίασης" : "Shading calculation method"}
                </Typography>
                <RadioGroup
                  row
                  value={shadingMode}
                  onChange={(event) => {
                    const next = event.target.value;
                    handleFields(next === "geometry"
                      ? [
                          ["pvLosses.dc.shadingMode", "geometry"],
                          ["pvLosses.dc.shading", 0],
                          ["pvLosses.dc.shadingCoversUnmodelled", false],
                        ]
                      : [
                          ["pvLosses.dc.shadingMode", next],
                          ["pvLosses.dc.shadingCoversUnmodelled", next === "geometry_plus_percentage"],
                        ]);
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    gap: 1,
                    mt: 1,
                    mb: 1.25,
                  }}
                >
                  <FormControlLabel
                    value="geometry"
                    sx={shadingChoiceSx("geometry")}
                    control={<Radio size="small" />}
                    label={isGreekScreen ? "Από τη φυσική διάταξη" : "From the physical layout"}
                  />
                  <FormControlLabel
                    value="percentage"
                    sx={shadingChoiceSx("percentage")}
                    control={<Radio size="small" />}
                    label={isGreekScreen ? "Με ετήσιο ποσοστό" : "Annual percentage"}
                  />
                  <FormControlLabel
                    value="geometry_plus_percentage"
                    sx={shadingChoiceSx("geometry_plus_percentage")}
                    control={<Radio size="small" />}
                    label={isGreekScreen ? "Φυσική διάταξη + πρόσθετο ποσοστό" : "Physical layout + additional percentage"}
                  />
                </RadioGroup>
                <HorizonShadingControl config={cfg} greek={isGreekScreen}
                  onChange={(enabled) => history.commit(withHorizonShading(cfg, enabled))}
                  onFetch={handleFetchHorizon} busy={horizonBusy} error={horizonError} />
                {shadingMode !== "geometry" && <PvLossNumberField
                  label={isGreekScreen
                    ? (shadingMode === "percentage" ? "Ετήσια σκίαση" : "Πρόσθετη σκίαση")
                    : (shadingMode === "percentage" ? "Annual shading" : "Additional shading")}
                  tooltip={shadingMode === "percentage"
                    ? (isGreekScreen
                        ? "Ετήσια παραδοχή απώλειας λόγω σκίασης από σειρές και κοντινά εμπόδια, χωρίς υπολογισμό της φυσικής διάταξης. Όταν είναι ενεργός ο ορίζοντας, μην τον συμπεριλαμβάνετε και στο ποσοστό."
                        : "Annual allowance for row and nearby-obstacle shading without layout calculations. When the horizon is enabled, do not include it again in the percentage.")
                    : (isGreekScreen
                        ? "Πρόσθετη ποσοστιαία απώλεια μετά τη γεωμετρική σκίαση. Χρησιμοποιήστε την μόνο για σκιές που δεν υπάρχουν στη φυσική διάταξη, ώστε να μην υπολογιστούν δύο φορές."
                        : "Additional percentage loss after geometric shading. Use only for shadows absent from the physical layout to avoid double counting.")}
                  value={cfg.pvLosses.dc.shading}
                  onChange={(e) => handlePvLossField("pvLosses.dc.shading", e.target.value)}
                  helperText={shadingMode === "percentage"
                    ? (horizonShadingEnabled(cfg)
                        ? (isGreekScreen ? "Πρόσθετες σκιάσεις, χωρίς τον ορίζοντα" : "Additional shading, excluding the horizon")
                        : (isGreekScreen ? "Μοναδική παραδοχή σκίασης" : "Only shading allowance"))
                    : (isGreekScreen ? "Επιπλέον, μετά τη γεωμετρική σκίαση" : "Additional, after geometric shading")}
                  adornment="%"
                  width={220}
                />}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, maxWidth: 900 }}>
                  {shadingMode === "percentage"
                    ? (isGreekScreen
                        ? "Το ποσοστό εφαρμόζεται στην παραγωγή που απομένει μετά τον ορίζοντα, εφόσον είναι ενεργός. Η γεωμετρία σειρών και κοντινών εμποδίων παραμένει αποθηκευμένη, αλλά δεν χρησιμοποιείται."
                        : "The percentage applies to generation remaining after the horizon, when enabled. Row and nearby-obstacle geometry remains saved but is not used.")
                    : shadingMode === "geometry_plus_percentage"
                      ? (isGreekScreen
                          ? "Πρώτα υπολογίζονται οι σκιές της φυσικής διάταξης. Το ποσοστό «Πρόσθετη σκίαση» αφαιρείται έπειτα από την παραγωγή που απομένει και πρέπει να καλύπτει μόνο σκιάσεις που δεν περιέχει το γεωμετρικό μοντέλο."
                          : "Layout shadows are calculated first. The shading percentage is then deducted from the remaining generation and must cover only shadows absent from the geometric model.")
                    : geometryHasFlatPercent
                      ? (isGreekScreen
                          ? "Για υπολογισμό μόνο από τη φυσική διάταξη, μηδενίστε το αποθηκευμένο ποσοστό σκίασης."
                          : "To use only the physical layout, clear the saved shading percentage.")
                      : (isGreekScreen
                          ? "Οι σκιές υπολογίζονται από τη διάταξη και τα εμπόδια, μαζί με τον ορίζοντα εφόσον είναι ενεργός. Δεν προστίθεται ποσοστό σκίασης."
                          : "Shadows are calculated from the layout and obstacles, plus the horizon when enabled. No shading percentage is added.")}
                </Typography>
                {moduleIsBifacial && !isFlushRoof && shadingMode === "percentage" && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {isGreekScreen
                      ? "Για τα πλαίσια διπλής όψης δεν υπολογίζονται εμπόδια και σκιάσεις σειρών. Ο ενεργός ορίζοντας επηρεάζει την ακτινοβολία και το ποσοστό εφαρμόζεται έπειτα στη συνολική παραγωγή των δύο όψεων."
                      : "For bifacial modules, row and obstacle shading are not calculated. An enabled horizon affects irradiance; the percentage then applies to the combined production of both faces."}
                  </Typography>
                )}
                {geometryHasFlatPercent && (
                  <Alert severity="warning" sx={{ mt: 1.25, py: 0.25, maxWidth: 900 }}>
                    <Typography variant="body2" sx={{ mb: 0.75 }}>
                      {isGreekScreen
                        ? `Έχει απομείνει αποθηκευμένο ποσοστό σκίασης ${flatShadingPercent}%. ${geometricShading.length > 0 ? "Η εκτέλεση θα σταματήσει για να αποφευχθεί πιθανή διπλή μέτρηση." : "Αυτό το ποσοστό θα εφαρμοστεί, παρότι έχει επιλεγεί μόνο η φυσική διάταξη."} Επιλέξτε πώς θα χρησιμοποιηθεί.`
                        : `A saved shading loss of ${flatShadingPercent}% remains. ${geometricShading.length > 0 ? "The run will stop to prevent possible double counting." : "This percentage would still be applied despite the physical-layout-only selection."} Choose how to use it.`}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Button size="small" variant="outlined" onClick={() => handleFields([
                        ["pvLosses.dc.shading", 0],
                        ["pvLosses.dc.shadingCoversUnmodelled", false],
                      ])}>
                        {isGreekScreen ? "Μηδενισμός ποσοστού" : "Clear percentage"}
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleFields([
                        ["pvLosses.dc.shadingMode", "geometry_plus_percentage"],
                        ["pvLosses.dc.shadingCoversUnmodelled", true],
                      ])}>
                        {isGreekScreen ? `Χρήση ως πρόσθετου ${flatShadingPercent}%` : `Use as additional ${flatShadingPercent}%`}
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </Box>

              <Grid
                container
                spacing={0}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                  gap: 1.5,
                  maxWidth: 1120,
                  alignItems: "start",
                  "& > .MuiGrid-root": { minWidth: 0, width: "100%", maxWidth: "none" },
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={soilingMonthlyOpen ? 12 : 3}
                  sx={soilingMonthlyOpen ? { gridColumn: "1 / -1" } : undefined}
                  id="field-pv-loss-soiling"
                >
                  <PvLossNumberField
                    label={soilingMonthlyActive
                      ? (isGreekScreen ? "Ρύπανση — ετήσια τιμή αναφοράς" : "Soiling — annual reference value")
                      : "Soiling"}
                    width={soilingMonthlyOpen ? 240 : "100%"}
                    tooltip={getPvLossTooltip("pvLosses.dc.soiling")}
                    value={cfg.pvLosses.dc.soiling}
                    disabled={soilingMonthlyActive}
                    onChange={(e) => handlePvLossField("pvLosses.dc.soiling", e.target.value)}
                    adornment="%"
                  />
                  {soilingMonthlyActive && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      {isGreekScreen
                        ? "Στους υπολογισμούς χρησιμοποιούνται οι 12 μηνιαίες τιμές. Η ετήσια τιμή αναφοράς δεν εφαρμόζεται. Πατήστε «Επαναφορά στην ετήσια τιμή» για να τη χρησιμοποιήσετε ξανά."
                        : "Calculations use the 12 monthly values. The annual reference value is not applied. Select ‘Reset to annual figure’ to use it again."}
                    </Typography>
                  )}
                  {/* Attached to the field it overrides. It first went in beside
                      the static wiring percentage, where it read as part of the
                      wiring block -- a monthly grid belongs to its own number,
                      not to whatever happened to be next in the layout. */}
                  <Box sx={{ mt: 0.75 }}>
                    <MonthlyValueGrid
                      label="Soiling"
                      values={cfg.pvLosses.dc.soilingMonthly}
                      onChange={(next) => handleField("pvLosses.dc.soilingMonthly", next)}
                      seedValue={Number(cfg.pvLosses.dc.soiling) || 0}
                      seedLabel="annual figure"
                      step={0.1}
                      min={0}
                      max={100}
                      unit="%"
                      decimals={1}
                      helper="Worth setting where the site has a real dry season."
                      onOpenChange={setSoilingMonthlyOpen}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <PvLossNumberField
                    label="Snow"
                    width="100%"
                    tooltip={getPvLossTooltip("pvLosses.dc.snow")}
                    value={cfg.pvLosses.dc.snow}
                    onChange={(e) => handlePvLossField("pvLosses.dc.snow", e.target.value)}
                    adornment="%"
                  />
                </Grid>
                <Grid item xs={12} md={3} id="field-pv-loss-other">
                  <PvLossNumberField
                    label="Mismatch"
                    width="100%"
                    tooltip={getPvLossTooltip("pvLosses.dc.mismatch")}
                    value={cfg.pvLosses.dc.mismatch}
                    onChange={(e) => handlePvLossField("pvLosses.dc.mismatch", e.target.value)}
                    adornment="%"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PvLossNumberField
                    label="Connections"
                    width="100%"
                    tooltip={getPvLossTooltip("pvLosses.dc.connections")}
                    value={cfg.pvLosses.dc.connections}
                    onChange={(e) => handlePvLossField("pvLosses.dc.connections", e.target.value)}
                    adornment="%"
                  />
                </Grid>
                {/* LID, derived from the module's degradation curve when the
                    project has one.

                    Light-induced degradation and a warranty's first-year drop
                    are the same physical effect under two names, so the engine
                    takes the curve's first year and uses it HERE. Leaving the
                    field editable meant the screen could say 1.50% while the
                    run used 1.00% -- the report named the figure it actually
                    applied, but this is the page someone opens to check, and a
                    number you can type that the engine then ignores is worse
                    than no number at all. */}
                <Grid item xs={12} md={3}>
                  <PvLossNumberField
                    label="LID"
                    width="100%"
                    tooltip={
                      degradationSummary
                        ? "Set by the module's degradation curve: its first-year loss IS light-induced degradation, and the engine applies it here, every hour of the simulated year. Edit the curve on the Equipment tab to change it."
                        : getPvLossTooltip("pvLosses.dc.lid")
                    }
                    value={
                      degradationSummary
                        ? Number(degradationSummary.firstYearPct.toFixed(2))
                        : cfg.pvLosses.dc.lid
                    }
                    onChange={(e) => handlePvLossField("pvLosses.dc.lid", e.target.value)}
                    disabled={Boolean(degradationSummary)}
                    helperText={degradationSummary ? "from the degradation curve" : undefined}
                    adornment="%"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PvLossNumberField
                    label="Nameplate Rating"
                    width="100%"
                    tooltip={getPvLossTooltip("pvLosses.dc.nameplate_rating")}
                    value={cfg.pvLosses.dc.nameplate_rating}
                    onChange={(e) => handlePvLossField("pvLosses.dc.nameplate_rating", e.target.value)}
                    adornment="%"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PvLossNumberField
                    label={isGreekScreen ? "Προϋπάρχουσα φθορά" : "Pre-existing module wear"}
                    width="100%"
                    tooltip={degradationSummary
                      ? (isGreekScreen
                        ? "Η καμπύλη γήρανσης καλύπτει ήδη αυτή την απώλεια. Δεν προστίθεται δεύτερη ποσοστιαία μείωση."
                        : "The degradation curve already covers module ageing. No second percentage loss is added.")
                      : (isGreekScreen
                        ? "Εφάπαξ απώλεια από φθορά που έχει ήδη υποστεί μια υφιστάμενη εγκατάσταση πριν από το έτος αναφοράς. Για νέο σύστημα αφήστε 0%."
                        : getPvLossTooltip("pvLosses.dc.age"))}
                    value={degradationSummary ? 0 : cfg.pvLosses.dc.age}
                    onChange={(e) => handlePvLossField("pvLosses.dc.age", e.target.value)}
                    disabled={Boolean(degradationSummary)}
                    helperText={degradationSummary
                      ? (isGreekScreen ? "Καλύπτεται από την καμπύλη γήρανσης" : "Covered by the degradation curve")
                      : undefined}
                    adornment="%"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PvLossNumberField
                    label="Availability"
                    width="100%"
                    tooltip={getPvLossTooltip("pvLosses.dc.availability")}
                    value={cfg.pvLosses.dc.availability}
                    onChange={(e) => handlePvLossField("pvLosses.dc.availability", e.target.value)}
                    adornment="%"
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={0}
                sx={{ display: "flex", flexWrap: "wrap", alignItems: "end", gap: 2, maxWidth: 1120, mt: 1.5 }}
              >
                <Grid item xs={12} md={6}>
                  <Box>
                    <PvLossFieldLabel
                      label="Wiring Mode"
                      tooltip={getPvLossTooltip("pvLosses.dc.wiringMode")}
                    />
                    <FormControl>
                      <RadioGroup
                        row
                        value={cfg.pvLosses.dc.wiringMode}
                        onChange={(e) => handleField("pvLosses.dc.wiringMode", e.target.value)}
                      >
                        <FormControlLabel value="static" control={<Radio />} label="Static" />
                        <FormControlLabel value="dynamic" control={<Radio />} label="Dynamic" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ maxWidth: 220 }}>
                    <PvLossNumberField
                      label="Static Wiring Loss"
                      tooltip={getPvLossTooltip("pvLosses.dc.wiringStaticPercent")}
                      value={cfg.pvLosses.dc.wiringStaticPercent ?? ""}
                      onChange={(e) => handlePvLossField("pvLosses.dc.wiringStaticPercent", e.target.value)}
                      disabled={cfg.pvLosses.dc.wiringMode === "dynamic"}
                      helperText={
                        cfg.pvLosses.dc.wiringMode === "dynamic"
                          ? "Disabled in dynamic mode"
                          : undefined
                      }
                      adornment="%"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  DC Dynamic Wiring
                </Typography>
                {cfg.pvLosses.dc.wiringMode === "dynamic" ? (
                  <Box sx={{ width: { xs: "100%", md: 220 } }}>
                    <PvLossNumberField
                      label="Voltage Drop Target"
                      tooltip={getPvLossTooltip("pvLosses.dc.voltageDropPercent")}
                      value={cfg.pvLosses.dc.voltageDropPercent}
                      onChange={(e) => handlePvLossField("pvLosses.dc.voltageDropPercent", e.target.value)}
                      adornment="%"
                    />
                  </Box>
                ) : null}
              </Box>
              {cfg.pvLosses.dc.wiringMode !== "dynamic" ? (
                <Alert severity="info">
                  Switch DC wiring mode to dynamic to configure inverter and MPPT branch wiring.
                </Alert>
              ) : pvLossInverterKeys.length === 0 ? (
                <Alert severity="info">
                  Save or design the PV system first to generate inverter and MPPT-specific DC wiring rows.
                </Alert>
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Tabs
                      value={activeDcLossInverter}
                      onChange={(_, value) => setActiveDcLossInverter(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ minHeight: 40 }}
                    >
                      {pvLossInverterKeys.map((inverterKey) => (
                        <Tab key={`dc-tab-${inverterKey}`} value={inverterKey} label={inverterKey} sx={{ minHeight: 40 }} />
                      ))}
                    </Tabs>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <MaybeTooltip
                      title="Copies the settings of the selected inverter to all remaining inverters."
                      arrow
                    >
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={applyDcLossSettingsToAll}
                          disabled={pvLossInverterKeys.length < 2 || !activeDcLossInverter}
                        >
                          Apply To All
                        </Button>
                      </span>
                    </MaybeTooltip>
                  </Box>
                  {activeDcLossInverter ? (
                    (() => {
                      const inverterKey = activeDcLossInverter;
                    const dcBranch = cfg.pvLosses.dc.wiringDynamic?.[inverterKey] || {};
                    const mpptKeys = pvLossMpptKeysByInverter[inverterKey] || [];
                    return (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {inverterKey}
                            </Typography>
                            <MaybeTooltip title={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV]")} arrow>
                              <span style={{ display: "inline-flex" }}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              </span>
                            </MaybeTooltip>
                          </Box>

                          <Grid container spacing={1.25}>
                            <Grid item xs={12} md={4}>
                              <PvLossSelectField
                                label="Material"
                                tooltip={
                                  <Box sx={{ minWidth: 420 }}>
                                    <Box sx={{ mb: 0.75 }}>
                                      {getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].material")}
                                    </Box>
                                    <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.5 }}>
                                      {[]
                                        .concat(materialDetailTooltip(dcBranch.material, "dc"))
                                        .map((line) => (
                                          <li
                                            key={`dc-material-tip-${dcBranch.material}-${line}`}
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            {translateText(line)}
                                          </li>
                                        ))}
                                    </Box>
                                  </Box>
                                }
                                value={dcBranch.material}
                                onChange={(e) =>
                                  handleField(`pvLosses.dc.wiringDynamic.${inverterKey}.material`, e.target.value)
                                }
                                options={dcMaterialOptions}
                                helperText={getPvLossTooltip(`material.${dcBranch.material}`)}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ maxWidth: 180 }}>
                                <PvLossNumberField
                                  label="Loss Cap"
                                  tooltip={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].capPercent")}
                                  value={dcBranch.capPercent}
                                  onChange={(e) =>
                                    handlePvLossField(`pvLosses.dc.wiringDynamic.${inverterKey}.capPercent`, e.target.value)
                                  }
                                  adornment="%"
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <LabeledSwitchWithHint
                                label="Auto Size"
                                checked={Boolean(dcBranch.autoSize)}
                                onChange={(e) =>
                                  handleField(`pvLosses.dc.wiringDynamic.${inverterKey}.autoSize`, e.target.checked)
                                }
                                hint={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].autoSize")}
                              />
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          <Grid container spacing={1.25}>
                            {mpptKeys.map((mpptKey) => {
                              const mpptCfg = dcBranch[mpptKey] || {};
                              return (
                                <Grid item xs={12} key={`${inverterKey}-${mpptKey}`}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: "divider",
                                      backgroundColor: "background.default",
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                      {mpptKey}
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={3}>
                                        <PvLossNumberField
                                          label="Panel Link Length"
                                          tooltip={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].MPPTx.panelLinkLengthM")}
                                          value={mpptCfg.panelLinkLengthM}
                                          onChange={(e) =>
                                            handlePvLossField(`pvLosses.dc.wiringDynamic.${inverterKey}.${mpptKey}.panelLinkLengthM`, e.target.value)
                                          }
                                          adornment="m"
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={3}>
                                        <PvLossNumberField
                                          label="String Homerun Length"
                                          tooltip={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].MPPTx.stringHomerunLengthM")}
                                          value={mpptCfg.stringHomerunLengthM}
                                          onChange={(e) =>
                                            handlePvLossField(`pvLosses.dc.wiringDynamic.${inverterKey}.${mpptKey}.stringHomerunLengthM`, e.target.value)
                                          }
                                          adornment="m"
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={3}>
                                        <PvLossNumberField
                                          label="Panel Cable Section"
                                          tooltip={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].MPPTx.panelCableSectionMm2")}
                                          value={mpptCfg.panelCableSectionMm2 ?? ""}
                                          onChange={(e) =>
                                            handlePvLossField(`pvLosses.dc.wiringDynamic.${inverterKey}.${mpptKey}.panelCableSectionMm2`, e.target.value)
                                          }
                                          disabled={Boolean(dcBranch.autoSize)}
                                          helperText={dcBranch.autoSize ? "Auto-sized when enabled" : undefined}
                                          adornment="mm²"
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={3}>
                                        <PvLossNumberField
                                          label="Homerun Cable Section"
                                          tooltip={getPvLossTooltip("pvLosses.dc.wiringDynamic[INV].MPPTx.homerunCableSectionMm2")}
                                          value={mpptCfg.homerunCableSectionMm2 ?? ""}
                                          onChange={(e) =>
                                            handlePvLossField(`pvLosses.dc.wiringDynamic.${inverterKey}.${mpptKey}.homerunCableSectionMm2`, e.target.value)
                                          }
                                          disabled={Boolean(dcBranch.autoSize)}
                                          helperText={dcBranch.autoSize ? "Auto-sized when enabled" : undefined}
                                          adornment="mm²"
                                        />
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                      </Paper>
                    );
                    })()
                  ) : null}
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                AC Losses
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.65 }}
              >
                These settings refer only to AC wiring losses. Inverter losses are inferred internally during simulation from the inverter model and its characteristics, while transformer losses are calculated separately.
              </Typography>
              <Grid container spacing={1.25}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <PvLossFieldLabel
                      label="Wiring Mode"
                      tooltip={getPvLossTooltip("pvLosses.ac.wiringMode")}
                    />
                    <FormControl>
                      <RadioGroup
                        row
                        value={cfg.pvLosses.ac.wiringMode}
                        onChange={(e) => handleField("pvLosses.ac.wiringMode", e.target.value)}
                      >
                        <FormControlLabel value="static" control={<Radio />} label="Static" />
                        <FormControlLabel value="dynamic" control={<Radio />} label="Dynamic" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
                {cfg.pvLosses.ac.wiringMode === "dynamic" ? (
                  <Grid item xs={12} md={3}>
                    <PvLossNumberField
                      label="Voltage Drop Target"
                      tooltip={getPvLossTooltip("pvLosses.ac.voltageDropPercent")}
                      value={cfg.pvLosses.ac.voltageDropPercent}
                      onChange={(e) => handlePvLossField("pvLosses.ac.voltageDropPercent", e.target.value)}
                      adornment="%"
                    />
                  </Grid>
                ) : null}
                {cfg.pvLosses.ac.wiringMode === "static" ? (
                  <Grid item xs={12} md={3}>
                    <PvLossNumberField
                      label="Static Wiring Loss"
                      tooltip={getPvLossTooltip("pvLosses.ac.wiringStaticPercent")}
                      value={cfg.pvLosses.ac.wiringStaticPercent ?? ""}
                      onChange={(e) => handlePvLossField("pvLosses.ac.wiringStaticPercent", e.target.value)}
                      adornment="%"
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                AC Dynamic Wiring
              </Typography>
              {cfg.pvLosses.ac.wiringMode !== "dynamic" ? (
                <Alert severity="info">
                  Switch AC wiring mode to dynamic to configure branch cables and the main collector cable.
                </Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Main AC Cable
                      </Typography>
                      <MaybeTooltip title={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.enabled")} arrow>
                        <span style={{ display: "inline-flex" }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                        </span>
                      </MaybeTooltip>
                    </Box>

                    <Grid container spacing={1.25}>
                      <Grid item xs={12} md={4}>
                        <LabeledSwitchWithHint
                          label="Enabled"
                          checked={Boolean(cfg.pvLosses.ac.wiringDynamic?.main?.enabled)}
                          onChange={(e) => handleField("pvLosses.ac.wiringDynamic.main.enabled", e.target.checked)}
                          hint={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.enabled")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <PvLossSelectField
                          label="Material"
                          tooltip={
                            <Box sx={{ minWidth: 460 }}>
                              <Box sx={{ mb: 0.75 }}>
                                {getPvLossTooltip("pvLosses.ac.wiringDynamic.main.material")}
                              </Box>
                              <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.5 }}>
                                {[]
                                  .concat(materialDetailTooltip(cfg.pvLosses.ac.wiringDynamic?.main?.material, "ac"))
                                  .map((line) => (
                                    <li
                                      key={`ac-main-material-tip-${cfg.pvLosses.ac.wiringDynamic?.main?.material}-${line}`}
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      {translateText(line)}
                                    </li>
                                  ))}
                              </Box>
                            </Box>
                          }
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.material}
                          onChange={(e) => handleField("pvLosses.ac.wiringDynamic.main.material", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                          options={acMaterialOptions}
                          helperText={getPvLossTooltip(`material.${cfg.pvLosses.ac.wiringDynamic?.main?.material}`)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <PvLossSelectField
                          label="Installation Type"
                          tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.installationType")}
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.installationType}
                          onChange={(e) => handleField("pvLosses.ac.wiringDynamic.main.installationType", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                          options={installationTypeOptions}
                          helperText={installationTypeTooltip(cfg.pvLosses.ac.wiringDynamic?.main?.installationType)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <PvLossNumberField
                          label="Length"
                          tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.lengthM")}
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.lengthM}
                          onChange={(e) => handlePvLossField("pvLosses.ac.wiringDynamic.main.lengthM", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                          adornment="m"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <PvLossNumberField
                          label="Power Factor"
                          tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.powerFactor")}
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.powerFactor}
                          onChange={(e) => handlePvLossField("pvLosses.ac.wiringDynamic.main.powerFactor", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <PvLossNumberField
                          label="Conductor Temperature"
                          tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.conductorTemperatureC")}
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.conductorTemperatureC}
                          onChange={(e) => handlePvLossField("pvLosses.ac.wiringDynamic.main.conductorTemperatureC", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                          adornment="°C"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ maxWidth: 180 }}>
                          <PvLossNumberField
                            label="Loss Cap"
                            tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.capPercent")}
                            value={cfg.pvLosses.ac.wiringDynamic?.main?.capPercent}
                            onChange={(e) => handlePvLossField("pvLosses.ac.wiringDynamic.main.capPercent", e.target.value)}
                            disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                            adornment="%"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <PvLossNumberField
                          label="Parallel Runs"
                          tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.parallelRuns")}
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.parallelRuns}
                          onChange={(e) => handlePvLossField("pvLosses.ac.wiringDynamic.main.parallelRuns", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                          min={1}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <LabeledSwitchWithHint
                          label="Auto Size"
                          checked={Boolean(cfg.pvLosses.ac.wiringDynamic?.main?.autoSize)}
                          onChange={(e) => handleField("pvLosses.ac.wiringDynamic.main.autoSize", e.target.checked)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled}
                          hint={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.autoSize")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <PvLossNumberField
                          label="Cable Section"
                          tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic.main.cableSectionMm2")}
                          value={cfg.pvLosses.ac.wiringDynamic?.main?.cableSectionMm2 ?? ""}
                          onChange={(e) => handlePvLossField("pvLosses.ac.wiringDynamic.main.cableSectionMm2", e.target.value)}
                          disabled={!cfg.pvLosses.ac.wiringDynamic?.main?.enabled || Boolean(cfg.pvLosses.ac.wiringDynamic?.main?.autoSize)}
                          helperText={cfg.pvLosses.ac.wiringDynamic?.main?.autoSize ? "Auto-sized when enabled" : undefined}
                          adornment="mm²"
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {pvLossInverterKeys.length === 0 ? (
                    <Alert severity="info">
                      Save or design the PV system first to generate inverter-specific AC branch wiring rows.
                    </Alert>
                  ) : (
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Tabs
                          value={activeAcLossInverter}
                          onChange={(_, value) => setActiveAcLossInverter(value)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ minHeight: 40 }}
                        >
                          {pvLossInverterKeys.map((inverterKey) => (
                            <Tab key={`ac-tab-${inverterKey}`} value={inverterKey} label={inverterKey} sx={{ minHeight: 40 }} />
                          ))}
                        </Tabs>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <MaybeTooltip
                          title="Copies the settings of the selected inverter to all remaining inverters."
                          arrow
                        >
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={applyAcLossSettingsToAll}
                              disabled={pvLossInverterKeys.length < 2 || !activeAcLossInverter}
                            >
                              Apply To All
                            </Button>
                          </span>
                        </MaybeTooltip>
                      </Box>
                      {activeAcLossInverter ? (
                        (() => {
                          const inverterKey = activeAcLossInverter;
                        const acBranch = cfg.pvLosses.ac.wiringDynamic?.[inverterKey] || {};
                        return (
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {inverterKey}
                                </Typography>
                                <MaybeTooltip title={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV]")} arrow>
                                  <span style={{ display: "inline-flex" }}>
                                    <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                  </span>
                                </MaybeTooltip>
                              </Box>
                              <Grid container spacing={1.25}>
                                <Grid item xs={12} md={4}>
                                  <PvLossNumberField
                                    label="Length"
                                    tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].lengthM")}
                                    value={acBranch.lengthM}
                                    onChange={(e) => handlePvLossField(`pvLosses.ac.wiringDynamic.${inverterKey}.lengthM`, e.target.value)}
                                    adornment="m"
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <PvLossSelectField
                                    label="Material"
                                    tooltip={
                                      <Box sx={{ minWidth: 460 }}>
                                        <Box sx={{ mb: 0.75 }}>
                                          {getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].material")}
                                        </Box>
                                        <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.5 }}>
                                          {[]
                                            .concat(materialDetailTooltip(acBranch.material, "ac"))
                                            .map((line) => (
                                              <li
                                                key={`ac-branch-material-tip-${acBranch.material}-${line}`}
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {translateText(line)}
                                              </li>
                                            ))}
                                        </Box>
                                      </Box>
                                    }
                                    value={acBranch.material}
                                    onChange={(e) => handleField(`pvLosses.ac.wiringDynamic.${inverterKey}.material`, e.target.value)}
                                    options={acMaterialOptions}
                                    helperText={getPvLossTooltip(`material.${acBranch.material}`)}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <PvLossSelectField
                                    label="Installation Type"
                                    tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].installationType")}
                                    value={acBranch.installationType}
                                    onChange={(e) => handleField(`pvLosses.ac.wiringDynamic.${inverterKey}.installationType`, e.target.value)}
                                    options={installationTypeOptions}
                                    helperText={installationTypeTooltip(acBranch.installationType)}
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <PvLossNumberField
                                    label="Power Factor"
                                    tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].powerFactor")}
                                    value={acBranch.powerFactor}
                                    onChange={(e) => handlePvLossField(`pvLosses.ac.wiringDynamic.${inverterKey}.powerFactor`, e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <PvLossNumberField
                                    label="Conductor Temperature"
                                    tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].conductorTemperatureC")}
                                    value={acBranch.conductorTemperatureC}
                                    onChange={(e) => handlePvLossField(`pvLosses.ac.wiringDynamic.${inverterKey}.conductorTemperatureC`, e.target.value)}
                                    adornment="°C"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <Box sx={{ maxWidth: 180 }}>
                                    <PvLossNumberField
                                      label="Loss Cap"
                                      tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].capPercent")}
                                      value={acBranch.capPercent}
                                      onChange={(e) => handlePvLossField(`pvLosses.ac.wiringDynamic.${inverterKey}.capPercent`, e.target.value)}
                                      adornment="%"
                                    />
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <PvLossNumberField
                                    label="Parallel Runs"
                                    tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].parallelRuns")}
                                    value={acBranch.parallelRuns}
                                    onChange={(e) => handlePvLossField(`pvLosses.ac.wiringDynamic.${inverterKey}.parallelRuns`, e.target.value)}
                                    min={1}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <LabeledSwitchWithHint
                                    label="Auto Size"
                                    checked={Boolean(acBranch.autoSize)}
                                    onChange={(e) => handleField(`pvLosses.ac.wiringDynamic.${inverterKey}.autoSize`, e.target.checked)}
                                    hint={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].autoSize")}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <PvLossNumberField
                                    label="Cable Section"
                                    tooltip={getPvLossTooltip("pvLosses.ac.wiringDynamic[INV].cableSectionMm2")}
                                    value={acBranch.cableSectionMm2 ?? ""}
                                    onChange={(e) => handlePvLossField(`pvLosses.ac.wiringDynamic.${inverterKey}.cableSectionMm2`, e.target.value)}
                                    disabled={Boolean(acBranch.autoSize)}
                                    helperText={acBranch.autoSize ? "Auto-sized when enabled" : undefined}
                                    adornment="mm²"
                                  />
                                </Grid>
                              </Grid>
                          </Paper>
                        );
                        })()
                      ) : null}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      </PvLoadingRegion>
      </Box>{/* end PV Losses tab */}

      {/* Transformer & POI chain settings moved to the dedicated
          Grid & Interconnection page (/Grid) — see TransformerPanel.jsx. */}



      {/* MODULE DETAILS DIALOG */}
      <Dialog
        open={moduleDialogOpen}
        onClose={() => setModuleDialogOpen(false)}
        maxWidth="md"
        // 680px, not the 900 of "md": the tab strip is four items and the
        // tables are two columns of short rows, so "md" was mostly margin.
        // Tried at 560 with the tables stacked into one column, which does fit
        // -- but the dialog then reads as a tall narrow slot. 680 is the
        // balance point: two columns hold, and no label wraps.
        PaperProps={{ sx: { maxWidth: 680 } }}
        fullWidth
      >
        <DialogTitle>
          Module details – {moduleDetails?.name || cfg.read_module || ""}
        </DialogTitle>
        <DialogContent dividers>
          {moduleDialogLoading && (
            <PvLoadingRegion isGreek={isGreekScreen} />
          )}
          {moduleDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {moduleDialogError}
            </Alert>
          )}
          {!moduleDialogLoading && !moduleDialogError && moduleDetails && (
            <EquipmentSpecTabs
              kind="module"
              chars={moduleDetails.characteristics}
              catalogAccess={moduleDetails.catalog_access}
              // No action button here: the I-V / P-V Monitor is already on the
              // module row that opened this dialog.
              overview={<ModuleDiagram details={moduleDetails} />}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DegradationCurveDialog
        open={degradationOpen}
        onClose={() => setDegradationOpen(false)}
        retention={degradationRetention}
        lifetimeYears={Number(cfg.settlementPeriod) || 30}
        moduleName={cfg.read_module || ""}
        onSave={handleSaveDegradation}
      />

      <Dialog
        open={moduleMonitorOpen}
        onClose={() => setModuleMonitorOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Module performance – {cfg.read_module || ""}
        </DialogTitle>
        <DialogContent dividers>
          {moduleMonitorOpen ? (
            <ModulePerformanceDialog
              moduleName={cfg.read_module}
              accessToken={accessToken}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleMonitorOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* INVERTER DETAILS DIALOG */}
      <Dialog
        open={invDialogOpen}
        onClose={() => setInvDialogOpen(false)}
        maxWidth="md"
        // 680px, not the 900 of "md": the tab strip is four items and the
        // tables are two columns of short rows, so "md" was mostly margin.
        // Tried at 560 with the tables stacked into one column, which does fit
        // -- but the dialog then reads as a tall narrow slot. 680 is the
        // balance point: two columns hold, and no label wraps.
        PaperProps={{ sx: { maxWidth: 680 } }}
        fullWidth
      >
        <DialogTitle>
          Inverter details – {invDetails?.name || cfg.read_inverter || ""}
        </DialogTitle>
        <DialogContent dividers>
          {invDialogLoading && (
            <PvLoadingRegion isGreek={isGreekScreen} />
          )}
          {invDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {invDialogError}
            </Alert>
          )}
          {!invDialogLoading && !invDialogError && invDetails && (
            <EquipmentSpecTabs
              kind="inverter"
              chars={invDetails.characteristics}
              catalogAccess={invDetails.catalog_access}
              overview={<InverterDiagram details={invDetails} />}
            />
          )}
        </DialogContent>
        <DialogActions>
          {/* The re-fit entry point lives on the inverter row (Efficiency model),
              next to the SANDIA/PVWATTS chip that says why you would want it.
              Repeating it here only made the dialog look like it did two jobs. */}
          <Button onClick={() => setInvDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <OndConnectionDialog open={ondConnectionOpen} onClose={() => setOndConnectionOpen(false)}
        name={cfg.read_inverter} characteristics={inverterChars} accessToken={accessToken} greek={isGreekScreen}
        onSaved={(chars) => {
          setInverterChars(chars);
          setInvDetails(null);
          setOndConnectionRevision((value) => value + 1);
        }} />
      <InverterCurveFitDialog
        open={invFitOpen}
        onClose={() => setInvFitOpen(false)}
        inverterName={invDetails?.name || cfg.read_inverter || ""}
        initialPaco={invDetails?.characteristics?.Paco ?? ""}
        initialPnt={invDetails?.characteristics?.Pnt ?? ""}
        // Lets the dialog read back the curves already stored for this inverter
        // instead of opening blank and silently replacing them on save.
        characteristics={invDetails?.characteristics || null}
        // Non-empty => read-only mode: view and re-fit, but only ever save to a
        // new name, so an inverter other projects depend on is never mutated.
        existingName={invDetails?.name || cfg.read_inverter || ""}
        onApply={handleApplyInverterFit}
      />



      {/* Removal confirmation.
          Deliberately states the consequence for OTHER people's work: projects
          reference equipment by name and resolve it at run time, so a colleague's
          saved project can be left unrunnable by this action. The user chose a
          warning over a hard block, so the warning has to be specific enough to
          be a real decision rather than a reflex click-through. */}
      <Dialog
        open={Boolean(catalogDelete)}
        onClose={() => (catalogDeleteBusy ? null : setCatalogDelete(null))}
        maxWidth="sm"
        fullWidth
        // MUI gives Autocomplete poppers the same zIndex as a Dialog
        // (theme.zIndex.modal), and a popper portaled later in the DOM wins.
        // These delete buttons sit outside the dropdown so there is no clash
        // today, but this makes the dialog unhideable if that ever changes.
        sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
      >
        <DialogTitle>
          {/* One sentence, so it translates whole. */}
          {catalogDelete?.kind === "module" ? "Remove module from catalogue?" : "Remove inverter from catalogue?"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {catalogDelete?.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will permanently remove the equipment from your organization's
            catalogue. This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Active projects within your organization may reference this equipment.
            Simulations for those projects will not be able to run until an
            alternative is selected.
          </Alert>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            We recommend confirming that no ongoing studies depend on this item
            before proceeding.
          </Typography>
          {catalogDeleteError ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {catalogDeleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogDelete(null)} disabled={catalogDeleteBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmCatalogDelete}
            disabled={catalogDeleteBusy}
          >
            {catalogDeleteBusy ? "Removing…" : "Remove permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={catalogWriteAllowed && addModuleOpen} onClose={() => { if (!moduleSaving) setAddModuleOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Add custom module</DialogTitle>
        <DialogContent dividers>
          {moduleSaveError && <Alert severity="error" sx={{ mb: 2 }}>{moduleSaveError}</Alert>}
          {isAdminUser && <CatalogVisibilityFields visibility={moduleVisibility} setVisibility={setModuleVisibility}
            selected={moduleOrganizations} setSelected={setModuleOrganizations}
            organizations={catalogOrganizations} loading={catalogOrganizationsLoading}
            error={catalogOrganizationsError} greek={isGreekScreen} />}

          {/* PAN import remains available even when coefficient specs fail to load. */}
          {
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="pv-module-parameter-source-label" shrink sx={{ color: "primary.main", fontWeight: 600 }}>
                Parameter source
              </InputLabel>
              <Select
                id="pv-module-parameter-source"
                labelId="pv-module-parameter-source-label"
                notched
                label="Parameter source"
                value={moduleParamSet}
                disabled={moduleSaving}
                onChange={(e) => {
                  setModuleParamSet(e.target.value);
                  setModuleParamValues({});
                  setModuleSaveError("");
                  setModulePanImport(null);
                  setModuleErrors({});
                }}
              >
                {(paramSets?.modules?.sets || [{ key: "datasheet", label: "Datasheet values (recommended)", fitted: true, resulting_model: "cec" }]).map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{s.label}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          s.fitted
                            ? `${translateText("Fitted to")} ${String(s.resulting_model).toUpperCase()}`
                            : String(s.resulting_model).toUpperCase()
                        }
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    </Stack>
                  </MenuItem>
                ))}
                <MenuItem value="pan">Upload PVsyst PAN file</MenuItem>
              </Select>
              <FormHelperText>
                Choose the data you actually hold. Datasheet values are fitted for
                you; a coefficient set is used as supplied.
              </FormHelperText>
            </FormControl>
          }

          {moduleParamSet === "datasheet" ? (
            <Grid container spacing={2}>
              {MODULE_FIELDS.map((f) => renderField(f, moduleForm, setModuleForm, moduleErrors, setModuleErrors))}
            </Grid>
          ) : moduleParamSet === "pan" ? (
            <>
              <PanModuleImport value={modulePanImport} accessToken={accessToken} disabled={moduleSaving}
                onChange={(result) => {
                  setModulePanImport(result);
                  setModuleSaveError("");
                  setModuleErrors({});
                  if (result) setModuleForm((prev) => ({ ...prev, module_name: result.module_name }));
                }} />
              <Grid container spacing={2}>
                {MODULE_FIELDS.filter((f) => ["module_name", "Price"].includes(f.key))
                  .map((f) => renderField(f, moduleForm, setModuleForm, moduleErrors, setModuleErrors))}
              </Grid>
            </>
          ) : (
            <>
              {/* Identity, geometry and price are needed on every route: the
                  layout engine and the cost ranking use them whatever physics
                  model runs. */}
              <Grid container spacing={2}>
                {MODULE_FIELDS.filter((f) =>
                  ["module_name", "Vendor", "Price", "length", "width", "Technology"].includes(f.key)
                ).map((f) => renderField(f, moduleForm, setModuleForm, moduleErrors, setModuleErrors))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <ModuleParameterFields
                spec={moduleParamSpec}
                values={moduleParamValues}
                onChange={(k, v) => setModuleParamValues((prev) => ({ ...prev, [k]: v }))}
              />
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              Fields marked with * are required. Numeric fields accept numbers only.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModuleOpen(false)} disabled={moduleSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveModule}
            disabled={moduleSaving || !catalogWriteAllowed || moduleParamMissing.length > 0 || (moduleParamSet === "pan" && !modulePanImport)}
          >
            {moduleSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={catalogWriteAllowed && addInvOpen} onClose={() => setAddInvOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{translateText("Add custom inverter")}</DialogTitle>
        <DialogContent dividers>
          {isAdminUser && <CatalogVisibilityFields visibility={inverterVisibility} setVisibility={setInverterVisibility}
            selected={inverterOrganizations} setSelected={setInverterOrganizations}
            organizations={catalogOrganizations} loading={catalogOrganizationsLoading}
            error={catalogOrganizationsError} greek={isGreekScreen} />}
          {/* Same guided choice as the module dialog. Without it the user had to
              know that Pdco alone yields PVWatts, fitted curves yield Sandia and
              fitted curves yield Sandia — the guesswork the module selector
              removes. Driven by the AC spec the backend already serves. */}
          {(paramSets?.inverters?.sets || []).length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: invParamSet === "pvwatts" ? 0 : 2 }}>
              <InputLabel id="pv-inverter-conversion-model-label" shrink sx={{ color: "primary.main", fontWeight: 600 }}>
                Conversion model
              </InputLabel>
              <Select
                id="pv-inverter-conversion-model"
                labelId="pv-inverter-conversion-model-label"
                notched
                label={translateText("Conversion model")}
                value={invParamSet}
                onChange={(e) => {
                  setInvParamSet(e.target.value);
                  setInvSaveError("");
                  setInvOndImport(null);
                }}
              >
                {paramSets.inverters.sets.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{inverterSetLabel(s, isGreekScreen)}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={inverterModelChip(s.resulting_model)}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {inverterSetHelp(invParamSpec, isGreekScreen) || (isGreekScreen
                  ? "Επιλέξτε τα δεδομένα που έχετε· τα πεδία παρακάτω ακολουθούν την επιλογή σας."
                  : "Choose the data you hold; the fields below follow your choice.")}
              </FormHelperText>
            </FormControl>
          )}

          {invParamSet === "pvwatts" && <PvwattsEuroControls value={invEuro} onChange={setInvEuro} result={invEuroResult} greek={isGreekScreen} />}
          {invParamSet === "ond_curve" ? (
            <>
              <OndInverterImport value={invOndImport} accessToken={accessToken} disabled={invSaving} greek={isGreekScreen}
                onChange={(result) => {
                  setInvOndImport(result);
                  setInvSaveError("");
                  setInvErrors({});
                  if (result) setInvForm((prev) => ({ ...prev, inverter_name: result.inverter_name }));
                }} />
              <Grid container spacing={2}>
                {INVERTER_FIELDS.filter((f) => ["inverter_name", "Price"].includes(f.key))
                  .map((f) => renderField(f.key === "Price" ? { ...f, required: true } : f, invForm, setInvForm, invErrors, setInvErrors))}
              </Grid>
            </>
          ) : INVERTER_FIELD_GROUPS.map((group) => {
            const fields = INVERTER_FIELDS.filter(
              (f) =>
                f.group === group.key &&
                // Model-specific fields only appear for the set that needs them.
                (f.key !== "Pnt" || invParamSet === "sandia") &&
                (!invEuroActive || !["Pdco", "eff_nom", "eff_max"].includes(f.key)) &&
                (invParamSet !== "sandia" || !["Pdco", "Vdco", "eff_nom", "eff_max"].includes(f.key)) &&
                (!invPerMppt || !["Num Inputs per MPPT", "Idcmax_per_mppt", "Iscmax_per_mppt"].includes(f.key)) &&
                // The Isc(STC) factor only qualifies a short-circuit rating, so
                // it appears once one has been entered and not before.
                (f.key !== "mppt_isc_stc_multiplier" || invShortEntered) &&
                (f.key !== "pv_array_power_limit_kind" || !isEmpty(invForm.Pdcmax))
            );
            if (fields.length === 0) return null;
            return (
              <Box key={group.key} sx={{ mb: 2.2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "text.primary",
                  }}
                >
                  {translateText(group.label)}
                </Typography>
                {group.note ? (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary", mb: 0.9, lineHeight: 1.45 }}
                  >
                    {group.key === "efficiency" && invParamSet === "pvwatts"
                      ? (isGreekScreen ? "Η ονομαστική απόδοση καθορίζει το επίπεδο της καμπύλης PVWatts. Η μέγιστη απόδοση καταχωρίζεται ως στοιχείο του κατασκευαστή." : "Nominal efficiency sets the level of the PVWatts curve. Maximum efficiency is recorded as a manufacturer specification.")
                      : translateText(group.note)}
                  </Typography>
                ) : (
                  <Box sx={{ mb: 0.9 }} />
                )}
                <Grid container spacing={2}>
                  {fields.map((f) => {
                    const labels = { Paco: "Rated AC output power (Paco)", Pnt: "Night consumption (Pnt)",
                      Pdco: "Reference DC power (Pdco)", Vdco: "Reference DC voltage (Vdco)",
                      Pdcmax: "Maximum PV array power (Pdcmax)", Vdcmax: "Maximum DC voltage (Vdcmax)",
                      Idcmax: "Total maximum DC current (Idcmax)", eff_nom: "Nominal efficiency", eff_max: "Maximum efficiency" };
                    const named = { ...f, label: labels[f.key] || f.label };
                    const field = f.key === "Vac" ? { ...named,
                      label: Number(invForm.Phases) === 3 ? "AC voltage between phases (Vac)" : Number(invForm.Phases) === 1 ? "AC phase-to-neutral voltage (Vac)" : "Rated AC voltage (Vac)",
                      helper: Number(invForm.Phases) === 3 ? "Line-to-line RMS voltage. For a 230/400 V grid, enter 400 V." : Number(invForm.Phases) === 1 ? "Phase-to-neutral RMS voltage. For a 230/400 V grid, enter 230 V." : "Select single-phase or three-phase to see the voltage convention." }
                      : f.key === "Pnt" ? { ...named, required: true, helper: "AC consumption at night, not the DC start-up power Pso. If only an upper bound is given, document the value you adopt." }
                      : ["eff_nom", "eff_max"].includes(f.key) ? { ...named, displayPercent: true,
                        helper: invParamSet === "sandia" ? "Manufacturer efficiency in percent (for example 98.5). This does not replace the Sandia fit." : "Manufacturer efficiency in percent (for example 98.5)." } : named;
                    return renderField(field, invForm, setInvForm, invErrors, setInvErrors);
                  })}
                </Grid>
                {group.key === "dc" && (
                  <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={invPerMppt} onChange={(event) => {
                        const enabled = event.target.checked;
                        setInvPerMppt(enabled);
                        if (enabled && invMpptRows.length === 0) {
                          const count = Math.min(32, Math.max(0, Number(invForm["Num MPPTs"]) || 0));
                          setInvMpptRows(Array.from({ length: count }, () => ({
                            inputs: invForm["Num Inputs per MPPT"] || "",
                            current: invForm.Idcmax_per_mppt || "",
                            short: invForm.Iscmax_per_mppt || "",
                          })));
                        }
                      }} />}
                      label={isGreekScreen ? "Ξεχωριστά όρια ανά MPPT" : "Separate limits for each MPPT"}
                    />
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
                      {isGreekScreen
                        ? "Χρησιμοποιήστε αυτή την επιλογή όταν οι MPPT έχουν διαφορετικό πλήθος εισόδων ή όριο ρεύματος. Τα παλιά ενιαία πεδία συμπληρώνονται αυτόματα για συμβατότητα."
                        : "Use when MPPTs have different input counts or current ratings. Legacy scalar fields are derived automatically for compatibility."}
                    </Typography>
                    {invPerMppt && Array.from({ length: Math.min(32, Math.max(0, Number(invForm["Num MPPTs"]) || 0)) }, (_, index) => {
                      const row = invMpptRows[index] || {};
                      const update = (key, value) => setInvMpptRows((previous) => {
                        const next = [...previous];
                        next[index] = { ...(next[index] || {}), [key]: value };
                        return next;
                      });
                      return (
                        <Stack key={index} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
                          <Typography sx={{ minWidth: 70, pt: 1 }}>MPPT {index + 1}</Typography>
                          <TextField size="small" type="number" label={isGreekScreen ? "Είσοδοι strings" : "String inputs"}
                            inputProps={{ min: 1, step: 1 }} value={row.inputs ?? ""}
                            onChange={(event) => update("inputs", event.target.value)} />
                          <TextField size="small" type="number" label={isGreekScreen ? "Μέγ. ρεύμα λειτουργίας (A)" : "Max operating current (A)"}
                            inputProps={{ min: 0, step: "any" }} value={row.current ?? ""}
                            onChange={(event) => update("current", event.target.value)} />
                          <TextField size="small" type="number" label={isGreekScreen ? "Μέγ. ρεύμα βραχυκύκλωσης (A)" : "Max short-circuit current (A)"}
                            inputProps={{ min: 0, step: "any" }} value={row.short ?? ""}
                            onChange={(event) => update("short", event.target.value)} />
                        </Stack>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}

          {invParamMissing.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="caption">
                {isGreekScreen ? "Χρειάζεται ακόμα για" : "Still needed for"} {inverterSetLabel(invParamSpec, isGreekScreen)}: <b>{invParamMissing.map(key => {
                  const names = { Paco: ["ονομαστική ισχύς εξόδου AC", "rated AC output power"], Pdco: ["ισχύς αναφοράς DC", "reference DC power"], eff_nom: ["ονομαστική απόδοση", "nominal efficiency"], eff_max: ["μέγιστη απόδοση", "maximum efficiency"], Vdco: ["τάση αναφοράς DC", "reference DC voltage"], Pnt: ["νυχτερινή κατανάλωση", "night consumption"] };
                  return names[key]?.[isGreekScreen ? 0 : 1] || translateText(INVERTER_FIELDS.find(field => field.key === key)?.label || key);
                }).join(", ")}</b>.
              </Typography>
            </Alert>
          )}

          {/* Curve entry belongs to the Sandia route only. Under PVWatts or ADR
              it would offer data the chosen model cannot use. */}
          {invParamSet === "sandia" && (
          <Box sx={{ mt: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }} useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {translateText("Efficiency curves")}
              </Typography>
              <Chip
                size="small"
                label={translateText(invCurrentFit ? "Fitted" : invPendingFit ? "Refit required" : "Not yet supplied")}
                color={invCurrentFit ? "success" : invPendingFit ? "warning" : "default"}
                variant="outlined"
                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
              {invCurrentFit
                ? "Fitted coefficients and the entered curve points will be saved with this inverter."
                : "Enter the datasheet efficiency curves at low, nominal and high MPP voltage. Required to fit Sandia; PVWatts uses a standard load-dependent curve instead."}
            </Typography>
            {invPendingFit && !invCurrentFit && <Alert severity="warning" sx={{ mt: 1 }}>
              {isGreekScreen ? "Οι τιμές Paco ή Pnt άλλαξαν. Τα σημεία των καμπυλών διατηρούνται, αλλά απαιτείται νέα προσαρμογή πριν από την αποθήκευση." : "Paco or Pnt changed. Curve points are retained, but a new fit is required before saving."}
            </Alert>}
            {invCurrentFit && <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={700}>{isGreekScreen ? "Αποτελέσματα προσαρμογής — υπολογίζονται αυτόματα" : "Fit results — calculated automatically"}</Typography>
              <Typography variant="body2">Pdco: {Number(invCurrentFit.Pdco).toFixed(2)} W · Vdco: {Number(invCurrentFit.Vdco).toFixed(2)} V</Typography>
              {invFitEfficiencies ? <>
                <Typography variant="body2">{isGreekScreen ? "Ονομαστική απόδοση (Paco/Pdco)" : "Nominal efficiency (Paco/Pdco)"}: {(100 * invFitEfficiencies.eff_nom).toFixed(2)}% · {isGreekScreen ? "Μέγιστη απόδοση μοντέλου" : "Peak model efficiency"}: {(100 * invFitEfficiencies.eff_max).toFixed(2)}%</Typography>
                <Typography variant="caption" color="text.secondary">{isGreekScreen ? "Υπολογίζονται αυτόματα από την προσαρμογή. Η ονομαστική αφορά την τάση αναφοράς Vdco και την ονομαστική ισχύ. Η μέγιστη αφορά τις τάσεις των καμπυλών· δεν είναι σταθμισμένη ευρωπαϊκή απόδοση. Η ονομαστική χρησιμοποιείται επίσης όπου απαιτείται σταθερή απόδοση, όπως στη λειτουργία μπαταρίας DC-coupled." : "Calculated automatically from the fit. Nominal efficiency is at reference voltage Vdco and rated power. Peak efficiency covers the curve voltages; it is not weighted European efficiency. Nominal efficiency is also used where a fixed efficiency is required, such as DC-coupled battery operation."}</Typography>
              </> : <Alert severity="error">{isGreekScreen ? "Η προσαρμογή δεν δίνει φυσικά αποδεκτές αποδόσεις. Ελέγξτε τις καμπύλες πριν την αποθήκευση." : "The fit does not yield physically valid efficiencies. Check the curves before saving."}</Alert>}

            </Box>}
            {!invPowerInputsReady && <Typography variant="body2" sx={{ mt: 1 }}>
              {isGreekScreen ? "Συμπληρώστε πρώτα την ονομαστική ισχύ AC (Paco) και τη νυχτερινή κατανάλωση (Pnt) στην παραπάνω φόρμα." : "First enter rated AC power (Paco) and night consumption (Pnt) in the form above."}
            </Typography>}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" disabled={!invPowerInputsReady} onClick={() => setInvNewFitOpen(true)}>
                {translateText(invPendingFit ? "Edit curves…" : "Add efficiency curves…")}
              </Button>
              {invPendingFit && (
                <Button size="small" color="warning" onClick={() => setInvPendingFit(null)}>
                  {translateText("Discard fit")}
                </Button>
              )}
            </Stack>
          </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              Fields marked with * are required. Numeric fields accept numbers only.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions disableSpacing sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, px: 3, py: 1 }}>
          {invSaveError && <Alert severity="error">{translateText(invSaveError)}</Alert>}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setAddInvOpen(false)} disabled={invSaving}>{translateText("Cancel")}</Button>
            <Button
              variant="contained"
              onClick={handleSaveInverter}
              disabled={invSaving || !catalogWriteAllowed || invParamMissing.length > 0}
            >
              {translateText(invSaving ? "Saving..." : "Save")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <InverterCurveFitDialog
        open={invNewFitOpen}
        onClose={() => setInvNewFitOpen(false)}
        inverterName={invForm.inverter_name || "new inverter"}
        initialPaco={invForm.Paco ?? ""}
        initialPnt={invForm.Pnt ?? ""}
        sharedPowerInputs
        characteristics={invCurveCharacteristics}
        onApply={({ characteristics, coefficients }) =>
          setInvPendingFit({ ...characteristics, ...(coefficients || {}) })
        }
      />

      {/* ===== TAB: Knowledge ===== */}
{/* ===== TAB: Generation =====

          The TMY reports lived at the bottom of the Electrical Design tab,
          below the design steps -- which put the thing you run AFTER designing
          inside the thing you design with. It has its own tab now. */}
      <Box sx={{ display: pvTab === "generation" ? "block" : "none" }}>
        <PvGenerationTabs active={pvTab === "generation"} reference={(active) => (
        <Paper elevation={0} sx={{ ...pvSetupSectionSx, mb: 1.1 }}>
          {/* subtitle1, not subtitle2: the section title sits one step above
              the body it heads instead of being the same 14px in bold. The
              weight comes from the theme now. */}
          <Typography variant="subtitle1" sx={{ mb: 0.2 }}>
            Run the generation model
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 760, lineHeight: 1.35, mb: 1 }}>
            Uses the weather selected in Feasibility → Weather Data for the standalone
            PV generation model. The generation, loss workbooks and PDF report all
            describe this one calculation.
          </Typography>

          {/* Which weather the yield rests on is the single biggest input to
              every number below it, and it used to be stated nowhere. What the
              stored run itself read is on the estimate panel's own chip, beside
              the numbers it qualifies -- repeating it here said the same thing
              twice and named no file. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1.25 }}>
            {active && (
              <SelectedWeatherSummary
                isGreek={isGreekScreen}
                projectId={currentProject?.id}
                accessToken={accessToken}
                simulationYear={cfg?.feasibility?.simulationYear}
                onSelection={setSelectedWeather}
              />
            )}
            {/* Directly under the source it names: next to a value that is
                already shown, "Change" is read as "take me where I can change
                it", which is exactly what it does. */}
            <Box>
              <Button
                size="small"
                startIcon={<TuneOutlinedIcon fontSize="small" />}
                onClick={() => navigate("/feasibility/weather")}
                // The button's own padding already reads as a gap; pulled back
                // level with the text above it on both axes.
                sx={{ textTransform: "none", fontWeight: 700, ml: -0.5, mt: -0.5 }}
              >
                Change weather source
              </Button>
            </Box>
            {/* Only when it has something to say. The numbers below are not
                wrong, they are answers to a question nobody is asking any
                more, and nothing else on this tab would have mentioned it. */}
            {storedRunUsesOtherWeather ? (
              <Alert severity="warning" sx={{ py: 0.25 }}>
                <span>
                  The stored estimate ran on a different weather source than the one selected
                  above, so the figures below describe that older dataset. Run the estimate
                  again to bring them in line.
                </span>
              </Alert>
            ) : null}
          </Box>

          {/* Says WHY the exports are unavailable. They used to be greyed out
              with the reason buried in a sentence of prose above them. */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <Chip size="small"
              label={isElectricalDesignSaved ? "Electrical design saved" : "Electrical design missing"}
              color={isElectricalDesignSaved ? "success" : "default"}
              variant={isElectricalDesignSaved ? "filled" : "outlined"} />
            <Chip size="small"
              label={isPhysicalLayoutSaved ? "Physical layout saved" : "Physical layout missing"}
              color={isPhysicalLayoutSaved ? "success" : "default"}
              variant={isPhysicalLayoutSaved ? "filled" : "outlined"} />
            <Chip size="small"
              label={cfg.loc_info?.lat && cfg.loc_info?.lon ? "Coordinates set" : "Coordinates missing"}
              color={cfg.loc_info?.lat && cfg.loc_info?.lon ? "success" : "default"}
              variant={cfg.loc_info?.lat && cfg.loc_info?.lon ? "filled" : "outlined"} />
          </Box>

<Box
                  sx={{
                  p: 0.8,
                  borderRadius: 2,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, rgba(18,38,29,0.78) 0%, rgba(14,26,20,0.92) 100%)"
                      : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(244,249,255,0.94) 100%)",
                  border: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(167,205,181,0.16)"
                      : "rgba(148,163,184,0.18)",
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 0.4 }}>
                  Reference-year production
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 0.8, lineHeight: 1.35, maxWidth: 560 }}
                >
                  Annual PV production using the selected weather and saved PV design,
                  before battery dispatch, export limits and operational curtailment.
                  Run Estimate again after changing the weather source or study year.
                </Typography>
                <PvTmyEstimate
                  reportBranding={reportBranding}
                  onWeather={setEstimateWeather}
                  projectId={currentProject?.id}
                  active={active}
                  config={cfg}
                  accessToken={accessToken}
                  projectName={currentProject?.name || currentProject?.project_name || null}
                  onCalculateShading={calculateProjectShading}
                  onOpenPvLosses={() => {
                    setPvTab("losses");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  disabled={
                    !cfg.loc_info?.lat || !cfg.loc_info?.lon || !areTmyExportsReady
                  }
                  disabledReason={
                    !cfg.loc_info?.lat || !cfg.loc_info?.lon
                      ? "Set the project coordinates first."
                      : planReconciliationIssues.length
                      ? planReconciliationIssues.join(" · ")
                      : !isElectricalDesignSaved
                      ? "Save the electrical design first."
                      : !isPhysicalLayoutSaved
                      ? "Save the physical layout first."
                      : sceneReadinessIssues.join(" · ")
                  }
                />
              </Box>
        </Paper>
        )} multiyear={(active) => (
              <PvUncertaintyPanel
                reportBranding={reportBranding}
                config={cfg}
                onChange={(value) => handleField("pvUncertainty", value)}
                projectId={currentProject?.id}
                accessToken={accessToken}
                active={active}
                disabled={!areTmyExportsReady}
              />
        )} />
      </Box>

      <Box sx={{ display: pvTab === "knowledge" ? "block" : "none" }}>

      

      {/* -------------------- KNOWLEDGE & ASSUMPTIONS -------------------- */}
      <Box
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2,
        }}
      >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.85rem",
              color: "text.primary",
              mb: 1.5,
            }}
          >
            Knowledge & Assumptions
          </Typography>

          <Alert
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : undefined,
            }}
          >
            These notes explain how PV components are interpreted in the platform and
            what is assumed when running PV design & optimization.
          </Alert>

          <Grid container spacing={2}>
            {/* PV modeling */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  PV modeling
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.2,
                    color: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : t.palette.text.secondary,
                  }}
                >
                  <li>
                    Module electrical parameters (Vmp, Imp, Voc, Isc, Pdc) are interpreted as
                    <b> Standard Test Conditions (STC)</b> values (1000 W/m² irradiance, 25 °C cell temperature).
                  </li>
                  <li>Annual PV estimates and feasibility studies use hourly weather for the selected weather year or imported TMY dataset. Market Operations generation uses its own run window. Select the source in Weather Data and check the source and period recorded in the generation result.</li>
                  <li>
                    Perez is the default transposition model for the free-standing monofacial path. Row-aware and bifacial calculations use the row-field model, which supports Hay-Davies or isotropic skies; a Perez selection is resolved to Hay-Davies there. Reflection (IAM), spectral correction and cell temperature follow the selected models and available module coefficients.
                  </li>
                  <li>
                    DC power comes from the module's own coefficients. Which model
                    is used depends on what the datasheet provides — CEC, De Soto,
                    PVsyst, Sandia, or PVWatts — and the parameter set is chosen when
                    the module is added to the catalogue.
                  </li>
                  <li>
                    Inverter conversion uses the fitted efficiency curves where the
                    inverter has them, so efficiency varies with load and DC voltage
                    rather than being a single nominal figure.
                  </li>
                  <li>
                    With layout-based shading selected, row shadows and current scene-shading tables are modelled explicitly. Annual-percentage-only shading skips row, obstacle and horizon calculations. Soiling, mismatch and snow use declared loss factors. Wiring can use static percentages or cable calculations. Module degradation has a separate warranty-curve or annual-rate treatment in feasibility projections.
                  </li>
                </Box>
              </Paper>
            </Grid>

            {/* Shading. Its own card because there are now three distinct
                mechanisms and people conflate them — and because the PV losses
                tab has a Shading percentage that must NOT repeat any of them. */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Shading
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.2,
                    color: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : t.palette.text.secondary,
                  }}
                >
                  <li>
                    PV Losses offers three choices: physical-layout shading only, annual-percentage-only shading, or physical layout plus an additional percentage. Percentage-only ignores row, obstacle and horizon shading. The additional percentage is applied after geometric shading and should cover only effects absent from the scene.
                  </li>
                  <li>
                    Neighbouring rows reduce diffuse sky light. The free-standing monofacial path applies an analytical correction; the row-field and bifacial paths calculate sky visibility within their geometry model, without applying the correction twice.
                  </li>
                  <li>
                    Direct row shading depends on sun position, row spacing, tilt and terrain slope. Coplanar modules on a flush-mounted roof do not shade each other, although obstacles and other roof faces still can. Electrical loss also depends on portrait/landscape mounting, half-cut configuration and bypass diodes. The selected near-shading treatment can be bypass, linear or none; it is an approximation, not a cell-by-cell circuit simulation.
                  </li>
                  <li>
                    A valid enabled horizon profile blocks direct sunlight when the sun is behind the terrain. This horizon model leaves diffuse irradiance unchanged. A missing or disabled profile does not model distant terrain shading.
                  </li>
                  <li>
                    A current scene-shading table supplies beam shading from modelled objects. If it includes PV rows, it replaces their analytical beam-shading fraction. The table does not model obstacle-induced diffuse-sky loss or reflections.
                  </li>
                  <li>
                    Saving the layout automatically updates scene shading for all surfaces. Wait for the save indicator to finish. If calculation fails, the layout is saved and you can retry from Simulation & Reports. Percentage-only shading needs no scene calculation. The generation report records the models and shading assumptions used.
                  </li>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}><Alert severity="info">A module warranty curve applies its first-year loss inside the hourly run as LID; later-year degradation feeds the feasibility projection, with AC decline recalculated through the inverter where enabled. A single annual-rate fallback has no separate first-year warranty drop. Avoid entering the same degradation twice.</Alert></Grid>

            {/* Component selection */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Component selection & filters
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.2,
                    color: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : t.palette.text.secondary,
                  }}
                >
                  <li>
                    Filter fields (<b>Min/Max Pdc, Pac, Vdc</b>, <b>Manufacturer</b>, <b>Technology</b>)
                    only restrict which components appear in selection lists; they do not
                    guarantee electrical compatibility.
                  </li>
                  <li>
                    <b>Manufacturer</b> and <b>Technology</b> list only values recorded in your
                    catalog. A component whose manufacturer or technology field is empty is excluded
                    while that filter is set, so an unexpectedly short list usually means those
                    fields are unpopulated rather than that no such component exists.
                  </li>
                  <li>
                    The Bifacial only catalogue filter does not itself add energy gain. Rear-side generation uses the selected module characteristics, bifaciality, albedo and row geometry. An opaque flush-mounted roof disables rear-side generation. The row-field approximation does not resolve every rear-side structural obstruction.
                  </li>
                  <li>Catalogue filters do not validate a design. Run the electrical design and review voltage, current, MPPT allocation and DC/AC sizing checks, including warnings and missing catalogue limits, before using its results.</li>
                </Box>
              </Paper>
            </Grid>

            {/* Inverter behavior */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Inverter behavior
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.2,
                    color: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : t.palette.text.secondary,
                  }}
                >
                  <li>
                    Paco is rated AC output, not the maximum DC input rating. In PVWatts conversion, the reference DC power is Pdco, or Paco divided by nominal efficiency when Pdco is unavailable. AC clipping follows the resolved conversion model and its parameters.
                  </li>
                  <li>
                    The electrical design checks string voltages at the configured cold and hot cell temperatures, MPPT windows and available current/input limits. Missing catalogue limits must be reviewed; the calculation does not simulate MPPT tracking dynamics.
                  </li>
                  <li>
                    Sandia conversion uses fitted inverter coefficients when available. PVWatts uses a load-dependent curve and nominal efficiency, with a 96% fallback if the nominal value is unusable. Supported explicit model selections are checked against the available coefficients.
                  </li>
                  <li>The PV yield model is not a grid-code compliance or dynamic voltage-support study. Power factor is used in cable calculations; this does not represent a complete reactive-power controller or inverter thermal-derating model.</li>
                </Box>
              </Paper>
            </Grid>

            {/* Design step */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Design step (stringing & MPPT assignment)
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.2,
                    color: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : t.palette.text.secondary,
                  }}
                >
                  <li>
                    {isGreekScreen ? (
                      <>Η καρτέλα <b>Ηλεκτρολογικός σχεδιασμός</b> αξιολογεί τη διαμόρφωση των strings, την ανάθεση στα MPPT και τα όρια τάσης DC.</>
                    ) : (
                      <>The <b>Electrical design</b> tab evaluates stringing configuration,
                      MPPT assignment, and DC voltage limits.</>
                    )}
                  </li>
                  <li>
                    For ground installations, the automatic layout-area estimate includes row spacing and a fixed 10% planning margin; one row uses its horizontal depth instead of inter-row spacing. You can enter an area manually. For drawn roofs, usable area comes from each roof face after the edge setback. Each orientation has its own area; the overall report adds these values, which need not equal the distinct parcel area if regions overlap.
                  </li>
                  <li>
                    Configurations violating MPPT windows or voltage limits must be
                    corrected before optimization.
                  </li>
                  <li>
                    The resulting design is intended for <b>feasibility analysis</b>,
                    not EPC-ready electrical design.
                  </li>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Losses modeling
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    mb: 1.25,
                    pl: 2.2,
                    color: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : t.palette.text.secondary,
                    lineHeight: 1.7,
                  }}
                >
                  <li>The PV Simulation follows a standard energy flow.</li>
                </Box>

                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(2, 6, 23, 0.025)",
                    overflowX: "auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: "max-content",
                      fontSize: "0.95rem",
                      color: "text.primary",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {[
                      "Weather",
                      "Plane-of-Array Irradiance",
                      "Effective Irradiance & Cell Temperature",
                      "DC Gross",
                      "DC Net",
                      "Inverter AC",
                      "AC Net",
                      "AC Export",
                    ].map((step, index, arr) => (
                      <React.Fragment key={`loss-flow-${step}`}>
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.7,
                            borderRadius: 999,
                            backgroundColor: (t) =>
                              t.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)",
                            border: "1px solid",
                            borderColor: (t) =>
                              t.palette.mode === "dark"
                                ? "rgba(96,165,250,0.22)"
                                : "rgba(37,99,235,0.16)",
                          }}
                        >
                          {step}
                        </Box>
                        {index < arr.length - 1 ? (
                          <Typography
                            component="span"
                            sx={{ color: "primary.main", fontWeight: 700, fontSize: "1rem" }}
                          >
                            →
                          </Typography>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                      Shared simulation basis
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        pl: 2.2,
                        color: (t) =>
                          t.palette.mode === "dark"
                            ? "rgba(255,255,255,0.8)"
                            : t.palette.text.secondary,
                        lineHeight: 1.7,
                      }}
                    >
                      <li>
                        Site weather inputs (GHI, DNI, DHI, ambient temperature, wind, humidity) are converted into plane-of-array irradiance and then into effective irradiance and cell temperature using the selected mount/orientation and pvlib-based irradiance/thermal models.
                      </li>
                      <li>
                    The DC model is inferred from the module parameter set: SAPM, CEC, De Soto, PVsyst or PVWatts. AC conversion is resolved separately from inverter coefficients and supported overrides. An ADR inference path also exists for compatible catalogue data, but ADR has a single-array limitation and is not a general multi-MPPT option. Check the resolved model in the generation report.
                  </li>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                      DC losses
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        pl: 2.2,
                        color: (t) =>
                          t.palette.mode === "dark"
                            ? "rgba(255,255,255,0.8)"
                            : t.palette.text.secondary,
                        lineHeight: 1.7,
                      }}
                    >
                      <li>
                        DC non-wiring losses are modeled explicitly through user-defined PVWatts-style loss factors. These are applied after raw DC generation and before final DC net power.
                      </li>
                      <li>
                        DC wiring losses can be modeled either as a static percentage or dynamically from cable geometry. In dynamic mode, losses are calculated per inverter/MPPT branch using the configured conductor material, cable lengths, and sizing mode.
                      </li>
                      <li>
                    Dynamic DC autosizing uses the allowed voltage drop together with cable ampacity. Panel-link and homerun circuits have different length/current conventions. The voltage-drop relations below describe only that part of sizing, not the complete electrical check.
                  </li>
                      <li>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                            DC cable sizing relations
                          </Typography>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              backgroundColor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.02)"
                                  : "rgba(2, 6, 23, 0.025)",
                            }}
                          >
                            <Typography sx={{ fontFamily: "Consolas, Menlo, monospace", color: "text.primary", mb: 0.75 }}>
                              Panel link: A_required ≈ (rho × L × I) / (dv × V)
                            </Typography>
                            <Typography sx={{ fontFamily: "Consolas, Menlo, monospace", color: "text.primary", mb: 1.25 }}>
                              String homerun: A_required ≈ (2 × rho × L × I) / (dv × V)
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.65 }}>
                              <li><b>A_required</b>: required conductor cross-section [mm²]</li>
                              <li><b>rho</b>: conductor resistivity [ohm·mm²/m]</li>
                              <li><b>L</b>: one-way cable length [m]</li>
                              <li><b>I</b>: branch current [A]</li>
                              <li><b>V</b>: operating DC voltage [V]</li>
                              <li><b>dv</b>: allowable voltage-drop fraction [-], e.g. 0.01 for 1%</li>
                            </Box>
                          </Box>
                        </Box>
                      </li>
                      <li>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                            DC ohmic loss relation
                          </Typography>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              backgroundColor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.02)"
                                  : "rgba(2, 6, 23, 0.025)",
                            }}
                          >
                            <Typography sx={{ fontFamily: "Consolas, Menlo, monospace", color: "text.primary", mb: 1.25 }}>
                              P_loss = I² × R
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.65 }}>
                              <li><b>P_loss</b>: ohmic power loss [W]</li>
                              <li><b>I</b>: conductor current [A]</li>
                              <li><b>R</b>: conductor resistance [ohm]</li>
                            </Box>
                          </Box>
                        </Box>
                      </li>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                      AC losses
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        pl: 2.2,
                        color: (t) =>
                          t.palette.mode === "dark"
                            ? "rgba(255,255,255,0.8)"
                            : t.palette.text.secondary,
                        lineHeight: 1.7,
                      }}
                    >
                      <li>
                        AC losses are also modeled either as a static percentage or dynamically. In dynamic mode, the model computes losses for each inverter AC branch and, optionally, for the plant-level main AC cable before transformer/export.
                      </li>
                      <li>
                        For AC dynamic sizing, the global field <b>ac.voltageDropPercent</b> is used as the allowed design voltage-drop target. AC cable sizing uses a voltage-drop-based area estimate combined with ampacity checks.
                      </li>
                      <li>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                            AC current sizing relations
                          </Typography>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              backgroundColor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.02)"
                                  : "rgba(2, 6, 23, 0.025)",
                            }}
                          >
                            <Typography sx={{ fontFamily: "Consolas, Menlo, monospace", color: "text.primary", mb: 0.75 }}>
                              Single-phase: I = P / (V × pf × eta)
                            </Typography>
                            <Typography sx={{ fontFamily: "Consolas, Menlo, monospace", color: "text.primary", mb: 1.25 }}>
                              Three-phase: I = P / (sqrt(3) × V × pf × eta)
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.65 }}>
                              <li><b>I</b>: AC line current [A]</li>
                              <li><b>P</b>: AC real power [W]</li>
                              <li><b>V</b>: line voltage [V]</li>
                              <li><b>pf</b>: power factor [-]</li>
                              <li><b>eta</b>: internal sizing-efficiency assumption [-]</li>
                            </Box>
                          </Box>
                        </Box>
                      </li>
                      <li>
                        AC autosizing first estimates the total required cable area from the voltage-drop criterion, then checks whether one cable run can safely carry the current. If not, it increases the number of parallel runs up to an internal maximum. In this model, parallel runs reduce resistance and losses by increasing effective conductor area and splitting current across parallel conductors.
                      </li>
                      <li>
                        AC resistivity is temperature-adjusted using the user-defined conductor temperature. This means AC losses depend not only on cable size and length, but also on material, operating temperature, installation type, power factor, and number of parallel runs.
                      </li>
                      <li>
                        AC ohmic losses are calculated from conductor resistance and current. In simplified form, they follow the standard I^2R principle, with separate handling for single-phase and three-phase circuits. Branch and main-cable losses can also be capped by the configured AC loss cap percent.
                      </li>
                      <li>
                    Transformer treatment depends on the selected topology. A transformer on the PV branch is applied after AC wiring losses. With a shared point-of-connection transformer and the chain model inside dispatch optimisation, transformer losses are applied there to the combined flows instead, avoiding double counting. PV AC output is therefore not always the final metered grid export.
                  </li>
                    </Box>
                  </Box>

                  <Divider />

                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.2,
                      color: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(255,255,255,0.8)"
                          : t.palette.text.secondary,
                      lineHeight: 1.7,
                    }}
                  >
                    <li>
                      In summary, the platform treats losses as an explicit, configurable layer on top of the physical PV simulation. Static mode is simpler and faster; dynamic mode is more detailed and uses cable lengths, conductor material, voltage-drop targets, autosizing logic, parallel runs, and thermal assumptions to estimate wiring losses more realistically.
                    </li>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            {false && (
              <Grid item xs={12}>
                {orientationError ? (
                  <Alert severity="error">{orientationError}</Alert>
                ) : (
                  <Alert severity="info">
                    Optimized orientation applied: tilt {orientationResult?.optimal_tilt} deg, azimuth{" "}
                    {orientationResult?.optimal_azimuth} deg
                    {orientationResult?.annual_kwh_per_kwdc != null
                      ? `, annual yield ${orientationResult.annual_kwh_per_kwdc} kWh/kWdc`
                      : ""}
                    .
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>{/* end Knowledge tab */}



      <Snackbar
        open={catalogToast.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={4500}
        onClose={() => setCatalogToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setCatalogToast((prev) => ({ ...prev, open: false }))}
          severity={catalogToast.severity || "success"}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {catalogToast.msg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(designReportError)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => setDesignReportError("")}
      >
        <Alert
          onClose={() => setDesignReportError("")}
          severity="error"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {designReportError}
        </Alert>
      </Snackbar>

    </Paper>
  );
}

